import { type FunctionComponent } from "react";

import { chain, uniqBy } from "lodash";
import pluralize from "pluralize";

import {
  type Breakdown,
  type CategoricalBreakdownConfig,
  type DbForecastV1DateRow,
  formatting,
  type SalesReportRow,
  type CurrencyCode,
  metrics,
  type IMCAttributionWindowDays,
  type INBAccountingMode,
  type ITimeGranularity,
  type ITimeGranularityV2,
  MCAttributionWindowDays,
  NBAccountingMode,
  type RowWithChildren,
} from "@north-beam/nb-common";

import { CreativeMetricType } from "../nb-api-graphql-generated";
import { type AbstractUser } from "../user";

export const MAX_NUMBER = 12_000_000_000;

export interface ChartableResponseRow {
  description: { key: string; value: string }[];
  series: metrics.MetricSeries;
}

export type ChartableResponseRowWithChildren =
  RowWithChildren<ChartableResponseRow>;

export const preprocessForChart = (
  rows: SalesReportRow[],
  breakdownConfigs: CategoricalBreakdownConfig[],
  breakdowns: Breakdown[],
): ChartableResponseRowWithChildren => {
  function generateDescriptors(dimensions: Record<string, string>): string[] {
    const paths: string[][] = [[]];
    for (const breakdown of breakdowns) {
      const dimension = dimensions[breakdown.key] || "";
      paths.push([...paths[paths.length - 1], dimension]);
    }

    return paths.map(pathToDescriptor);
  }

  const keyToConfig = chain(breakdownConfigs)
    .groupBy("key")
    .mapValues((v) => v[0])
    .value();

  const allAggregationPaths: string[][] = [[]];
  for (const breakdown of breakdowns) {
    const currentPaths = [...allAggregationPaths];
    const newPaths: string[][] = [];
    for (const value of breakdown.values) {
      for (const descriptor of currentPaths) {
        newPaths.push([...descriptor, value]);
      }
    }

    allAggregationPaths.push(...newPaths);
  }
  const allAggregationDescriptors = allAggregationPaths.map(pathToDescriptor);

  const descriptorToData: Record<string, metrics.MetricSeries> = chain(
    allAggregationDescriptors,
  )
    .map((v) => [v, {}])
    .fromPairs()
    .value();

  for (const row of rows) {
    const { dimensions, dailyMetrics, forecastV1Daily } = row;
    if (!dailyMetrics) {
      continue;
    }

    const dateToForecastRow: Record<string, DbForecastV1DateRow> = {};
    if (forecastV1Daily) {
      for (const forecastRow of forecastV1Daily) {
        dateToForecastRow[forecastRow.date] = forecastRow;
      }
    }

    const descriptors = generateDescriptors(dimensions);
    const rowPassesFilter = descriptors.every((d) => !!descriptorToData[d]);

    if (!rowPassesFilter) {
      continue;
    }

    for (const dailyMetricPoint of dailyMetrics) {
      const metricPoint = metrics.makeMetricPoint(
        dailyMetricPoint,
        dateToForecastRow[dailyMetricPoint.date],
      );
      for (const descriptor of descriptors) {
        const data = descriptorToData[descriptor];
        metrics.addToMetricSeries(data, dailyMetricPoint.date, metricPoint);
      }
    }
  }

  const descriptorToRow: Record<string, ChartableResponseRowWithChildren> =
    chain(descriptorToData)
      .pickBy((value) => Object.keys(value).length > 0)
      .mapValues((value, key) => {
        const path = descriptorToPath(key);
        const description: { key: string; value: string }[] = [];
        for (let i = 0; i < path.length; ++i) {
          const breakdownKey = breakdowns[i].key;
          const choices = keyToConfig[breakdownKey]?.choices || [];
          const name =
            choices.find((v) => v.value === path[i])?.label ?? path[i];
          description.push({ key: breakdownKey, value: name });
        }
        return {
          description,
          series: value!,
          children: [],
        };
      })
      .value();

  for (const descriptor in descriptorToRow) {
    const path = descriptorToPath(descriptor);
    if (path.length === 0) {
      continue;
    }

    const parentPath = [...path];
    parentPath.pop();

    const parentDescriptor = pathToDescriptor(parentPath);
    descriptorToRow[parentDescriptor].children.push(
      descriptorToRow[descriptor],
    );
  }

  return descriptorToRow[pathToDescriptor([])];
};

type MetricFormatOptions = {
  precision?: "approximate" | "exact";
  totalLength?: number;
  average?: boolean;
  thousandSeparated?: boolean;
};

export type MetricFormatter = (
  v: number | null | undefined,
  code: CurrencyCode,
  options: MetricFormatOptions,
) => string;

type BenchmarkFormatter = (
  v: number,
  options: formatting.PrecisionLessFormatMetricOptions<"percentage"> &
    MetricFormatOptions,
) => string;

const benchmarkFormatter: BenchmarkFormatter = (v, options) =>
  formatting.formatPercentage.approximate(v, options);

type MetricFormattersType = {
  currency: MetricFormatter;
  currencyRatio: MetricFormatter;
  percentage: MetricFormatter;
  decimal: MetricFormatter;
  integer: MetricFormatter;
  multiplier: MetricFormatter;
};

export const MetricFormatters: MetricFormattersType = {
  currency: (
    v: number | null | undefined,
    code: CurrencyCode,
    options: MetricFormatOptions,
  ) => formatting.formatCurrency(v, { code, ...options }),
  currencyRatio: (
    v: number | null | undefined,
    code: CurrencyCode,
    options: MetricFormatOptions,
  ) => formatting.formatCurrency(v, { code, ...options }),
  percentage: (
    v: number | null | undefined,
    code: CurrencyCode,
    options: MetricFormatOptions,
  ) => formatting.formatPercentage(v, options),
  decimal: (
    v: number | null | undefined,
    code: CurrencyCode,
    options: MetricFormatOptions,
  ) => formatting.formatDecimal(v, options),
  multiplier: (
    v: number | null | undefined,
    code: CurrencyCode,
    options: MetricFormatOptions,
  ) => formatting.formatMultiplier(v, options),
  integer: (
    v: number | null | undefined,
    code: CurrencyCode,
    options: MetricFormatOptions,
  ) => formatting.formatInteger(v, options),
};

function getRandomNumber(min: number, max: number): number {
  // Generate a random number between min (inclusive) and max (exclusive)
  return Math.random() * (max - min) + min;
}

const getFullTypeName = (type: "ft" | "rtn" | "other" | "recurring" | "") => {
  switch (type) {
    case "ft":
      return "First Time";
    case "rtn":
      return "Returning";
    case "recurring":
      return "Recurring";
    case "other":
      return "Other";
    default:
      return "";
  }
};

const SCALING_FACTOR = getRandomNumber(0.1, 10) + 0.2;

function calculateWithAnon(
  calculate: SalesPageMetric["calculate"],
  doAnon?: boolean,
) {
  if (doAnon) {
    return (value: metrics.MetricPoint) => calculate(value) * SCALING_FACTOR;
  }
  return calculate;
}

type PlatformEquivalentSpec = {
  id?: string;
};

export interface SalesPageMetricSpec {
  id: string;
  group:
    | "default"
    | "custom"
    | "subscriptions"
    | "meta"
    | CreativeMetricType.tiktok
    | CreativeMetricType.google
    | CreativeMetricType.pinterest
    | CreativeMetricType.snapchat
    | CreativeMetricType.twitter
    | CreativeMetricType.tradedesk;
  name: string;
  modifiers: {
    attributionModel: "none" | "all" | "northbeam_custom";
    attributionWindows: IMCAttributionWindowDays[];
    accountingModes: INBAccountingMode[];
  };
}

/* TODO: Make this type generic based on MetricType so that we can strongly type the formatting fn.
   TODO: Move this to nb-common. */
export type SalesPageMetric = {
  id: string;
  name: string;
  dashboardV2Name?: string;
  descriptionHTML: string;
  isNonModal?: boolean;
  format: metrics.MetricType;
  formatter: MetricFormatter; // deprecated -- phasing this out in favor of funcs
  benchmarkFormatter?: BenchmarkFormatter;
  isPositiveChangeGood: boolean;
  requiredNBCalculatedMetrics?: string[];
  baseMetrics?: SalesPageMetric[];
  isRate: boolean;
  calculate: (v: metrics.MetricPoint) => number;
  calculateBand?: (v: metrics.MetricPoint) => [number, number];
  calculateCompare?: (v: metrics.MetricPoint) => number;
  requiredBaseMetrics: string[];

  changeFractionAreaTextForTable?: string;
  changeFractionAreaTextForTabDisplay?: string;
  chartCompareSuffix?: string;
  chartBandSuffix?: [string, string];
  displayCumulativeForChart?: boolean;
  hideFromChartDisplay?: boolean;
  hideFromTableDisplay?: boolean;
  hideComparisonFromTableCell?: boolean;
  hideComparisonFromTabDisplay?: boolean;
  pagesEnabled?: {
    creativeAnalytics?: boolean;
    salesV2?: boolean;
    salesV3?: boolean;
  };
  composition: {
    type: CreativeMetricType;
    metric: string;
    customComposed: boolean;

    details?: {
      am: string;
      aw: IMCAttributionWindowDays | "";
      type: "ft" | "rtn" | "recurring" | "other" | "";
      accountingMode: INBAccountingMode;
    };
  };
  platformEquivalent?: {
    fb?: PlatformEquivalentSpec;
    tiktok?: PlatformEquivalentSpec;
    snapchat?: PlatformEquivalentSpec;
    adwords?: PlatformEquivalentSpec;
    youtube?: PlatformEquivalentSpec;
    pinterest?: PlatformEquivalentSpec;
    twitter?: PlatformEquivalentSpec;
  };
  metadata: {
    baseId: string;
    metricId?:
      | "rev"
      | "txns"
      | "roas"
      | "ecr"
      | "cac"
      | "aov"
      | "rpv"
      | "orderDiscounts"
      | "taxValue"
      | "shippingValue"
      | "orderRefundedValue"
      | "orderDiscountsOrderSubscriptions"
      | "taxValueOrderSubscriptions"
      | "shippingValueOrderSubscriptions"
      | "orderRefundedValueOrderSubscriptions"
      | "fbBenchmarkCac"
      | "fbBenchmarkRoas"
      | "subscriptionCac"
      | "subscriptionTxns"
      | "subscriptionRev";
    name?: string;
    isLtv?: boolean;
    type?: "First Time" | "Returning" | "Recurring" | "Other" | "Blended" | "";
    isGoalRelated?: boolean;
    salesTableMetricId?: string;
    overviewPageMetricId: string;
    dashboardV2Id: string;
    overviewPageMetricName?: string;
    isBenchmark?: boolean;
    tooltip?: {
      reported:
        | "Northbeam"
        | "User-defined custom"
        | "Forecasted Northbeam"
        | "Facebook"
        | "Google"
        | "TikTok"
        | "Snapchat"
        | "Pinterest"
        | "Twitter"
        | "Trade Desk";
      description: string;
      showAttributionWindowInfo?: boolean;
      calculationInfo?: string;
    };
  };

  spec?: SalesPageMetricSpec;

  TableCell?: FunctionComponent<{ point: metrics.MetricPoint }>;
};

const SpendMetric: SalesPageMetric = {
  id: "spend",
  name: "Spend",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.spend,
  requiredBaseMetrics: ["spend"],
  descriptionHTML:
    "How much money was spent on this set of ads over the given time period",
  composition: {
    type: CreativeMetricType.base,
    metric: "spend",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "spend",
    overviewPageMetricId: "spend",
    dashboardV2Id: "spend",
    tooltip: {
      reported: "Northbeam",
      description:
        "How much money was spent on this set of ads over the given time period",
    },
  },
  spec: {
    id: "spend",
    group: "default",
    name: "Spend",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const SHOPS = ["Web", "Meta", "TikTok"];

const makeShopTxnsMetrics = (
  am: string,
  aw: IMCAttributionWindowDays | "",
  type: "ft" | "recurring" | "other" | "",
  accountingMode: INBAccountingMode,
) =>
  SHOPS.map<SalesPageMetric>((shop) => {
    const isLtv = aw === "infinity";
    const metricKey: any = `transactions${shop}Shops`;
    const isAccrualLtv = isLtv && accountingMode === "accrual";
    const id = `${metricKey}${isAccrualLtv ? "Ltv" : ""}`;

    const fullName = makeSubscriptionMetricName(
      `${shop}${shop === "Web" ? "" : " Shops"} Transactions`,
      aw,
      type,
      accountingMode,
    );
    const shortName = makeSubscriptionMetricName(
      `${shop}${shop === "Web" ? "" : " Shops"} Transactions`,
      aw,
      type,
      accountingMode,
      true,
    );
    const description = `Transactions attributed to your ads in ${shop} shops`;
    return {
      id,
      name: shortName,
      format: metrics.MetricTypes.DECIMAL,
      formatter: MetricFormatters.decimal,
      isPositiveChangeGood: true,
      isRate: false,
      calculate: (v) => findConv(v, am, aw, metricKey),
      requiredBaseMetrics: [metricKey],
      descriptionHTML: description,
      changeFractionAreaTextForTable:
        aw === "infinity" ? "Realized" : undefined,
      changeFractionAreaTextForTabDisplay:
        aw === "infinity" ? "Realized" : undefined,
      hideComparisonFromTableCell:
        aw === "infinity" && accountingMode === "accrual",
      hideComparisonFromTabDisplay:
        aw === "infinity" && accountingMode === "accrual",
      composition: {
        type: CreativeMetricType.base,
        metric: metricKey,
        customComposed: true,
        details: {
          aw,
          am,
          accountingMode,
          type: "",
        },
      },
      pagesEnabled: { creativeAnalytics: true },
      metadata: {
        baseId: metricKey,
        metricId: metricKey,
        overviewPageMetricId: id,
        dashboardV2Id: metricKey,
        isLtv,
        name: shortName,
        tooltip: {
          reported: "Northbeam",
          description,
        },
      },
      spec: {
        id: `${metricKey}${isAccrualLtv ? "Ltv" : ""}`,
        group: "default",
        name: fullName,
        modifiers: {
          attributionModel: "all",
          attributionWindows:
            accountingMode === "accrual" && aw === "infinity"
              ? ["infinity"]
              : ["1", "3", "7", "14", "30", "60", "90"],
          accountingModes:
            accountingMode === "accrual" && aw === "infinity"
              ? ["accrual"]
              : ["cash", "accrual"],
        },
      },
    };
  });

const makeShopRevenueMetrics = (
  am: string,
  aw: IMCAttributionWindowDays | "",
  type: "ft" | "recurring" | "other" | "",
  accountingMode: INBAccountingMode,
) =>
  SHOPS.map<SalesPageMetric>((shop) => {
    const isLtv = aw === "infinity";
    const metricKey: any = `revenue${shop}Shops`;
    const isAccrualLtv = isLtv && accountingMode === "accrual";
    const id = `${metricKey}${isAccrualLtv ? "Ltv" : ""}`;
    const fullName = makeSubscriptionMetricName(
      `${shop}${shop === "Web" ? "" : " Shops"} Revenue`,
      aw,
      type,
      accountingMode,
    );
    const shortName = makeSubscriptionMetricName(
      `${shop}${shop === "Web" ? "" : " Shops"} Revenue`,
      aw,
      type,
      accountingMode,
      true,
    );
    const description = `Revenue attributed to your ads in ${shop} shops`;
    return {
      id,
      name: shortName,
      format: metrics.MetricTypes.CURRENCY,
      formatter: MetricFormatters.currency,
      isPositiveChangeGood: true,
      isRate: false,
      calculate: (v) => findConv(v, am, aw, metricKey),
      requiredBaseMetrics: [metricKey],
      descriptionHTML: description,
      changeFractionAreaTextForTable:
        aw === "infinity" ? "Realized" : undefined,
      changeFractionAreaTextForTabDisplay:
        aw === "infinity" ? "Realized" : undefined,
      hideComparisonFromTableCell:
        aw === "infinity" && accountingMode === "accrual",
      hideComparisonFromTabDisplay:
        aw === "infinity" && accountingMode === "accrual",
      composition: {
        type: CreativeMetricType.base,
        metric: metricKey,
        customComposed: true,
        details: {
          aw,
          am,
          accountingMode,
          type: "",
        },
      },
      pagesEnabled: { creativeAnalytics: true },
      metadata: {
        baseId: metricKey,
        metricId: metricKey,
        overviewPageMetricId: id,
        dashboardV2Id: metricKey,
        isLtv,
        name: shortName,
        tooltip: {
          reported: "Northbeam",
          description,
        },
      },
      spec: {
        id: `${metricKey}${isAccrualLtv ? "Ltv" : ""}`,
        group: "default",
        name: fullName,
        modifiers: {
          attributionModel: "all",
          attributionWindows:
            accountingMode === "accrual" && aw === "infinity"
              ? ["infinity"]
              : ["1", "3", "7", "14", "30", "60", "90"],
          accountingModes:
            accountingMode === "accrual" && aw === "infinity"
              ? ["accrual"]
              : ["cash", "accrual"],
        },
      },
    };
  });

const ImpressionsMetric: SalesPageMetric = {
  id: "impressions",
  name: "Imprs",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.imprs,
  requiredBaseMetrics: ["imprs"],
  descriptionHTML:
    "How many times your ads were served, as determined by the platform",
  pagesEnabled: {
    creativeAnalytics: true,
  },
  composition: {
    type: CreativeMetricType.base,
    metric: "imprs",
    customComposed: false,
  },
  metadata: {
    baseId: "impressions",
    overviewPageMetricId: "impressions",
    dashboardV2Id: "imprs",
    tooltip: {
      reported: "Northbeam",
      description:
        "Impressions; how many times your ads were served, as determined by the platform",
    },
  },
  spec: {
    id: "impressions",
    group: "default",
    name: "Imprs",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const CPMMetric: SalesPageMetric = {
  id: "cpm",
  name: "CPM",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: false,
  isRate: true,
  calculate: (v) => (1000 * v.spend) / v.imprs,
  requiredBaseMetrics: ["spend", "imprs"],
  descriptionHTML: "Cost Per Mille - computed as 1,000 × Spend ÷ Impressions",
  composition: {
    type: CreativeMetricType.base,
    metric: "cpm",
    customComposed: true,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "cpm",
    overviewPageMetricId: "cpm",
    dashboardV2Id: "cpm",
    tooltip: {
      reported: "Northbeam",
      description:
        /* eslint-disable-next-line quotes */
        '"Cost Per Mille"; cost of reaching 1,000 impressions (views)',
      calculationInfo: "1,000 × Spend ÷ Impressions",
    },
  },
  spec: {
    id: "cpm",
    group: "default",
    name: "CPM",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const TradedeskImpressions: SalesPageMetric = {
  id: "tradedeskImpressions",
  name: "Trade Desk Impressions",
  format: metrics.MetricTypes.DECIMAL,
  formatter: MetricFormatters.decimal,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.tradedeskImpressions,
  requiredBaseMetrics: ["tradedeskImpressions"],
  descriptionHTML: "Trade Desk Impressions",
  composition: {
    type: CreativeMetricType.tradedesk,
    metric: "tradedesk_impressions",
    customComposed: false,
  },
  metadata: {
    baseId: "tradedeskImpressions",
    overviewPageMetricId: "tradedeskImpressions",
    dashboardV2Id: "tradedeskImpressions",
    tooltip: {
      reported: "Trade Desk",
      description: "Trade Desk Impressions",
    },
  },
  spec: {
    id: "tradedeskImpressions",
    group: CreativeMetricType.tradedesk,
    name: "Trade Desk Impressions",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaTotalPurchases1DClick: SalesPageMetric = {
  id: "metaTotalPurchases1DClick",
  name: "FB Purchases (1D Click)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaTotalPurchases1DClick,
  requiredBaseMetrics: ["metaTotalPurchases1DClick"],
  descriptionHTML:
    "Facebook reported total number of conversions across all channels (i.e. website, mobile " +
    "app, offline, and on-facebook) attributed to your ads",
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_total_purchases_1d_click",
    customComposed: false,
  },
  metadata: {
    baseId: "metaTotalPurchases1DClick",
    overviewPageMetricId: "metaTotalPurchases1DClick",
    dashboardV2Id: "metaTotalPurchases1DClick",
    tooltip: {
      reported: "Facebook",
      description:
        "Total number of conversions across all channels (website, mobile app, offline, and " +
        "on-Facebook) attributed to your ads",
    },
  },
  spec: {
    id: "metaTotalPurchases1DClick",
    group: "meta",
    name: "FB Purchases (1D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaTotalPurchases1DView: SalesPageMetric = {
  id: "metaTotalPurchases1DView",
  name: "FB Purchases (1D View)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaTotalPurchases1DView,
  requiredBaseMetrics: ["metaTotalPurchases1DView"],
  descriptionHTML:
    "Facebook reported total number of conversions across all channels (i.e. website, mobile " +
    "app, offline, and on-facebook) attributed to your ads",
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_total_purchases_1d_view",
    customComposed: false,
  },
  metadata: {
    baseId: "metaTotalPurchases1DView",
    overviewPageMetricId: "metaTotalPurchases1DView",
    dashboardV2Id: "metaTotalPurchases1DView",
    tooltip: {
      reported: "Facebook",
      description:
        "Total number of conversions across all channels (website, mobile app, offline, and " +
        "on-Facebook) attributed to your ads",
    },
  },
  spec: {
    id: "metaTotalPurchases1DView",
    group: "meta",
    name: "FB Purchases (1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaTotalPurchases7DClick: SalesPageMetric = {
  id: "metaTotalPurchases7DClick",
  name: "FB Purchases (7D Click)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaTotalPurchases7DClick,
  requiredBaseMetrics: ["metaTotalPurchases7DClick"],
  descriptionHTML:
    "Facebook reported total number of conversions across all " +
    "channels (i.e. website, mobile app, " +
    "offline, and on-facebook) attributed to your ads",
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_total_purchases_7d_click",
    customComposed: false,
  },
  metadata: {
    baseId: "metaTotalPurchases7DClick",
    overviewPageMetricId: "metaTotalPurchases7DClick",
    dashboardV2Id: "metaTotalPurchases7DClick",
    tooltip: {
      reported: "Facebook",
      description:
        "Total number of conversions across all channels (website, mobile app, offline, and " +
        "on-Facebook) attributed to your ads",
    },
  },
  spec: {
    id: "metaTotalPurchases7DClick",
    group: "meta",
    name: "FB Purchases (7D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaTotalPurchases7DClick1DView: SalesPageMetric = {
  id: "metaTotalPurchases7DClick1DView",
  name: "FB Purchases (7D Click, 1D View)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaTotalPurchases7DClick1DView,
  requiredBaseMetrics: ["metaTotalPurchases7DClick1DView"],
  descriptionHTML:
    "Facebook reported total number of conversions across all " +
    "channels (i.e. website, mobile app, " +
    "offline, and on-facebook) attributed to your ads",
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_total_purchases_7d_click_1d_view",
    customComposed: false,
  },
  metadata: {
    baseId: "metaTotalPurchases7DClick1DView",
    overviewPageMetricId: "metaTotalPurchases7DClick1DView",
    dashboardV2Id: "metaTotalPurchases7DClick1DView",
    tooltip: {
      reported: "Facebook",
      description:
        "Total number of conversions across all channels (website, mobile app, offline, and " +
        "on-Facebook) attributed to your ads",
    },
  },
  spec: {
    id: "metaTotalPurchases7DClick1DView",
    group: "meta",
    name: "FB Purchases (7D Click, 1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaShopPurchases1DClick: SalesPageMetric = {
  id: "metaShopPurchases1DClick",
  name: "FB Meta shop purchases (1D Click)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaShopPurchases1DClick,
  requiredBaseMetrics: ["metaShopPurchases1DClick"],
  descriptionHTML:
    "Facebook reported number of conversions from Facebook Meta shop attributed to your ads",
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_shop_purchases_1d_click",
    customComposed: false,
  },
  metadata: {
    baseId: "metaShopPurchases1DClick",
    overviewPageMetricId: "metaShopPurchases1DClick",
    dashboardV2Id: "metaShopPurchases1DClick",
    tooltip: {
      reported: "Facebook",
      description:
        "Number of Conversion from Facebook Meta Shop attributed to your ads",
    },
  },
  spec: {
    id: "metaShopPurchases1DClick",
    group: "meta",
    name: "FB Meta shop purchases (1D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaShopPurchases7DClick: SalesPageMetric = {
  id: "metaShopPurchases7DClick",
  name: "FB Meta shop purchases (7D Click)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaShopPurchases7DClick,
  requiredBaseMetrics: ["metaShopPurchases7DClick"],
  descriptionHTML:
    "Facebook reported number of conversions from Facebook Meta shop attributed to your ads",
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_shop_purchases_7d_click",
    customComposed: false,
  },
  metadata: {
    baseId: "metaShopPurchases7DClick",
    overviewPageMetricId: "metaShopPurchases7DClick",
    dashboardV2Id: "metaShopPurchases7DClick",
    tooltip: {
      reported: "Facebook",
      description:
        "Number of Conversion from Facebook Meta Shop attributed to your ads",
    },
  },
  spec: {
    id: "metaShopPurchases7DClick",
    group: "meta",
    name: "FB Meta shop purchases (7D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaShopPurchases7DClick1DView: SalesPageMetric = {
  id: "metaShopPurchases7DClick1DView",
  name: "FB Meta shop purchases (7D Click, 1D View)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaShopPurchases7DClick1DView,
  requiredBaseMetrics: ["metaShopPurchases7DClick1DView"],
  descriptionHTML:
    "Facebook reported number of conversions from Facebook Meta shop attributed to your ads",
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_shop_purchases_7d_click_1d_view",
    customComposed: false,
  },
  metadata: {
    baseId: "metaShopPurchases7DClick1DView",
    overviewPageMetricId: "metaShopPurchases7DClick1DView",
    dashboardV2Id: "metaShopPurchases7DClick1DView",
    tooltip: {
      reported: "Facebook",
      description:
        "Number of Conversion from Facebook Meta Shop attributed to your ads",
    },
  },
  spec: {
    id: "metaShopPurchases7DClick1DView",
    group: "meta",
    name: "FB Meta shop purchases (7D Click, 1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaShopPurchases1DView: SalesPageMetric = {
  id: "metaShopPurchases1DView",
  name: "FB Meta shop purchases (1D View)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaShopPurchases1DView,
  requiredBaseMetrics: ["metaShopPurchases1DView"],
  descriptionHTML:
    "Facebook reported number of conversions from Facebook Meta shop attributed to your ads",
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_shop_purchases_1d_view",
    customComposed: false,
  },
  metadata: {
    baseId: "metaShopPurchases1DView",
    overviewPageMetricId: "metaShopPurchases1DView",
    dashboardV2Id: "metaShopPurchases1DView",
    tooltip: {
      reported: "Facebook",
      description:
        "Number of Conversion from Facebook Meta Shop attributed to your ads",
    },
  },
  spec: {
    id: "metaShopPurchases1DView",
    group: "meta",
    name: "FB Meta shop purchases (1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaOffsitePixelPurchases1DView: SalesPageMetric = {
  id: "metaOffsitePixelPurchases1DView",
  name: "FB Website purchases (1D View)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaOffsitePixelPurchases1DView,
  requiredBaseMetrics: ["metaOffsitePixelPurchases1DView"],
  descriptionHTML:
    "Facebook reported number of conversions from your brand site attributed to your ads",
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_offsite_pixel_purchases_1d_view",
    customComposed: false,
  },
  metadata: {
    baseId: "metaOffsitePixelPurchases1DView",
    overviewPageMetricId: "metaOffsitePixelPurchases1DView",
    dashboardV2Id: "metaOffsitePixelPurchases1DView",
    tooltip: {
      reported: "Facebook",
      description:
        "Number of conversions from your brand site attributed to your ads",
    },
  },
  spec: {
    id: "metaOffsitePixelPurchases1DView",
    group: "meta",
    name: "FB Website purchases (1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaOffsitePixelPurchases1DClick: SalesPageMetric = {
  id: "metaOffsitePixelPurchases1DClick",
  name: "FB Website purchases (1D Click)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaOffsitePixelPurchases1DClick,
  requiredBaseMetrics: ["metaOffsitePixelPurchases1DClick"],
  descriptionHTML:
    "Facebook reported number of conversions from your brand site attributed to your ads",
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_offsite_pixel_purchases_1d_click",
    customComposed: false,
  },
  metadata: {
    baseId: "metaOffsitePixelPurchases1DClick",
    overviewPageMetricId: "metaOffsitePixelPurchases1DClick",
    dashboardV2Id: "metaOffsitePixelPurchases1DClick",
    tooltip: {
      reported: "Facebook",
      description:
        "Number of conversions from your brand site attributed to your ads",
    },
  },
  spec: {
    id: "metaOffsitePixelPurchases1DClick",
    group: "meta",
    name: "FB Website purchases (1D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaOffsitePixelPurchases7DClick: SalesPageMetric = {
  id: "metaOffsitePixelPurchases7DClick",
  name: "FB Website purchases (7D Click)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaOffsitePixelPurchases7DClick,
  requiredBaseMetrics: ["metaOffsitePixelPurchases7DClick"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_offsite_pixel_purchases_7d_click",
    customComposed: false,
  },
  metadata: {
    baseId: "metaOffsitePixelPurchases7DClick",
    overviewPageMetricId: "metaOffsitePixelPurchases7DClick",
    dashboardV2Id: "metaOffsitePixelPurchases7DClick",
    tooltip: {
      reported: "Facebook",
      description:
        "Number of conversions from your brand site attributed to your ads",
    },
  },
  descriptionHTML:
    "Facebook reported number of conversions from your brand site attributed to your ads",
  spec: {
    id: "metaOffsitePixelPurchases7DClick",
    group: "meta",
    name: "FB Website purchases (7D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaOffsitePixelPurchases7DClick1DView: SalesPageMetric = {
  id: "metaOffsitePixelPurchases7DClick1DView",
  name: "FB Website purchases (7D Click, 1D View)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaOffsitePixelPurchases7DClick1DView,
  requiredBaseMetrics: ["metaOffsitePixelPurchases7DClick1DView"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_offsite_pixel_purchases_7d_click_1d_view",
    customComposed: false,
  },
  metadata: {
    baseId: "metaOffsitePixelPurchases7DClick1DView",
    overviewPageMetricId: "metaOffsitePixelPurchases7DClick1DView",
    dashboardV2Id: "metaOffsitePixelPurchases7DClick1DView",
    tooltip: {
      reported: "Facebook",
      description:
        "Number of conversions from your brand site attributed to your ads",
    },
  },
  descriptionHTML:
    "Facebook reported number of conversions from your brand site attributed to your ads",
  spec: {
    id: "metaOffsitePixelPurchases7DClick1DView",
    group: "meta",
    name: "FB Website purchases (7D Click, 1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaTotalRevenue1DView: SalesPageMetric = {
  id: "metaTotalRevenue1DView",
  name: "FB Purchases conversion value (1D View)",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaTotalRevenue1DView,
  requiredBaseMetrics: ["metaTotalRevenue1DView"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_total_revenue_1d_view",
    customComposed: false,
  },
  metadata: {
    baseId: "metaTotalRevenue1DView",
    overviewPageMetricId: "metaTotalRevenue1DView",
    dashboardV2Id: "metaTotalRevenue1DView",
    tooltip: {
      reported: "Facebook",
      description:
        "Total revenue conversions across all channels (website, mobile app, offline, and " +
        "on-Facebook) attributed to your ads",
    },
  },
  descriptionHTML:
    "Facebook reported total revenue from conversions across all channels (i.e. website, mobile " +
    "app, offline, and on-facebook) attributed to your ads",
  spec: {
    id: "metaTotalRevenue1DView",
    group: "meta",
    name: "FB Purchases conversion value (1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaTotalRevenue1DClick: SalesPageMetric = {
  id: "metaTotalRevenue1DClick",
  name: "FB Purchases conversion value (1D Click)",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaTotalRevenue1DClick,
  requiredBaseMetrics: ["metaTotalRevenue1DClick"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_total_revenue_1d_click",
    customComposed: false,
  },
  metadata: {
    baseId: "metaTotalRevenue1DClick",
    overviewPageMetricId: "metaTotalRevenue1DClick",
    dashboardV2Id: "metaTotalRevenue1DClick",
    tooltip: {
      reported: "Facebook",
      description:
        "Total revenue conversions across all channels (website, mobile app, offline, and " +
        "on-Facebook) attributed to your ads",
    },
  },
  descriptionHTML:
    "Facebook reported total revenue from conversions across all channels (i.e. website, mobile " +
    "app, offline, and on-facebook) attributed to your ads",
  spec: {
    id: "metaTotalRevenue1DClick",
    group: "meta",
    name: "FB Purchases conversion value (1D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaTotalRevenue7DClick: SalesPageMetric = {
  id: "metaTotalRevenue7DClick",
  name: "FB Purchases conversion value (7D Click)",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaTotalRevenue7DClick,
  requiredBaseMetrics: ["metaTotalRevenue7DClick"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_total_revenue_7d_click",
    customComposed: false,
  },
  metadata: {
    baseId: "metaTotalRevenue7DClick",
    overviewPageMetricId: "metaTotalRevenue7DClick",
    dashboardV2Id: "metaTotalRevenue7DClick",
    tooltip: {
      reported: "Facebook",
      description:
        "Total revenue conversions across all channels (website, mobile app, offline, and " +
        "on-Facebook) attributed to your ads",
    },
  },
  descriptionHTML:
    "Facebook reported total revenue from conversions across all channels (i.e. website, mobile " +
    "app, offline, and on-facebook) attributed to your ads",
  spec: {
    id: "metaTotalRevenue7DClick",
    group: "meta",
    name: "FB Purchases conversion value (7D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaTotalRevenue7DClick1DView: SalesPageMetric = {
  id: "metaTotalRevenue7DClick1DView",
  name: "FB Purchases conversion value (7D Click, 1D View)",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaTotalRevenue7DClick1DView,
  requiredBaseMetrics: ["metaTotalRevenue7DClick1DView"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_total_revenue_7d_click_1d_view",
    customComposed: false,
  },
  metadata: {
    baseId: "metaTotalRevenue7DClick1DView",
    overviewPageMetricId: "metaTotalRevenue7DClick1DView",
    dashboardV2Id: "metaTotalRevenue7DClick1DView",
    tooltip: {
      reported: "Facebook",
      description:
        "Total revenue conversions across all channels (website, mobile app, offline, and " +
        "on-Facebook) attributed to your ads",
    },
  },
  descriptionHTML:
    "Facebook reported total revenue from conversions across all channels (i.e. website, mobile " +
    "app, offline, and on-facebook) attributed to your ads",
  spec: {
    id: "metaTotalRevenue7DClick1DView",
    group: "meta",
    name: "FB Purchases conversion value (7D Click, 1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaShopRevenue1DView: SalesPageMetric = {
  id: "metaShopRevenue1DView",
  name: "FB Meta shop purchases conversion value (1D View)",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaShopRevenue1DView,
  requiredBaseMetrics: ["metaShopRevenue1DView"],
  descriptionHTML:
    "Facebook reported revenue from conversions from Facebook Meta shop attributed to your ads",
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_shop_revenue_1d_view",
    customComposed: false,
  },
  metadata: {
    baseId: "metaShopRevenue1DView",
    overviewPageMetricId: "metaShopRevenue1DView",
    dashboardV2Id: "metaShopRevenue1DView",
    tooltip: {
      reported: "Facebook",
      description:
        "Revenue from conversions from Facebook Meta Shops attributed to your ads",
    },
  },
  spec: {
    id: "metaShopRevenue1DView",
    group: "meta",
    name: "FB Meta shop purchases conversion value (1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaShopRevenue1DClick: SalesPageMetric = {
  id: "metaShopRevenue1DClick",
  name: "FB Meta shop purchases conversion value (1D Click)",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaShopRevenue1DClick,
  requiredBaseMetrics: ["metaShopRevenue1DClick"],
  descriptionHTML:
    "Facebook reported revenue from conversions from Facebook Meta shop attributed to your ads",
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_shop_revenue_1d_click",
    customComposed: false,
  },
  metadata: {
    baseId: "metaShopRevenue1DClick",
    overviewPageMetricId: "metaShopRevenue1DClick",
    dashboardV2Id: "metaShopRevenue1DClick",
    tooltip: {
      reported: "Facebook",
      description:
        "Revenue from conversions from Facebook Meta Shops attributed to your ads",
    },
  },
  spec: {
    id: "metaShopRevenue1DClick",
    group: "meta",
    name: "FB Meta shop purchases conversion value (1D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaShopRevenue7DClick: SalesPageMetric = {
  id: "metaShopRevenue7DClick",
  name: "FB Meta shop purchases conversion value (7D Click)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaShopRevenue7DClick,
  requiredBaseMetrics: ["metaShopRevenue7DClick"],
  descriptionHTML:
    "Facebook reported revenue from conversions from Facebook Meta shop attributed to your ads",
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_shop_revenue_7d_click",
    customComposed: false,
  },
  metadata: {
    baseId: "metaShopRevenue7DClick",
    overviewPageMetricId: "metaShopRevenue7DClick",
    dashboardV2Id: "metaShopRevenue7DClick",
    tooltip: {
      reported: "Facebook",
      description:
        "Revenue from conversions from Facebook Meta Shops attributed to your ads",
    },
  },
  spec: {
    id: "metaShopRevenue7DClick",
    group: "meta",
    name: "FB Meta shop purchases conversion value (7D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaShopRevenue7DClick1DView: SalesPageMetric = {
  id: "metaShopRevenue7DClick1DView",
  name: "FB Meta shop purchases conversion value (7D Click, 1D View)",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaShopRevenue7DClick1DView,
  requiredBaseMetrics: ["metaShopRevenue7DClick1DView"],
  descriptionHTML:
    "Facebook reported revenue from conversions from Facebook Meta shop attributed to your ads",
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_shop_revenue_7d_click_1d_view",
    customComposed: false,
  },
  metadata: {
    baseId: "metaShopRevenue7DClick1DView",
    overviewPageMetricId: "metaShopRevenue7DClick1DView",
    dashboardV2Id: "metaShopRevenue7DClick1DView",
    tooltip: {
      reported: "Facebook",
      description:
        "Revenue from conversions from Facebook Meta Shops attributed to your ads",
    },
  },
  spec: {
    id: "metaShopRevenue7DClick1DView",
    group: "meta",
    name: "FB Meta shop purchases conversion value (7D Click, 1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaOffsitePixelRevenue1DView: SalesPageMetric = {
  id: "metaOffsitePixelRevenue1DView",
  name: "FB Website purchases conversion value (1D View)",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaOffsitePixelRevenue1DView,
  requiredBaseMetrics: ["metaOffsitePixelRevenue1DView"],
  descriptionHTML:
    "Facebook reported revenue from conversions from your brand site attributed to your ads",
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_offsite_pixel_revenue_1d_view",
    customComposed: false,
  },
  metadata: {
    baseId: "metaOffsitePixelRevenue1DView",
    overviewPageMetricId: "metaOffsitePixelRevenue1DView",
    dashboardV2Id: "metaOffsitePixelRevenue1DView",
    tooltip: {
      reported: "Facebook",
      description:
        "Revenue from conversions from your brand site attributed to your ads",
    },
  },
  spec: {
    id: "metaOffsitePixelRevenue1DView",
    group: "meta",
    name: "FB Website purchases conversion value (1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaOffsitePixelRevenue1DClick: SalesPageMetric = {
  id: "metaOffsitePixelRevenue1DClick",
  name: "FB Website purchases conversion value (1D Click)",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaOffsitePixelRevenue1DClick,
  requiredBaseMetrics: ["metaOffsitePixelRevenue1DClick"],
  descriptionHTML:
    "Facebook reported revenue from conversions from your brand site attributed to your ads",
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_offsite_pixel_revenue_1d_click",
    customComposed: false,
  },
  metadata: {
    baseId: "metaOffsitePixelRevenue1DClick",
    overviewPageMetricId: "metaOffsitePixelRevenue1DClick",
    dashboardV2Id: "metaOffsitePixelRevenue1DClick",
    tooltip: {
      reported: "Facebook",
      description:
        "Revenue from conversions from your brand site attributed to your ads",
    },
  },
  spec: {
    id: "metaOffsitePixelRevenue1DClick",
    group: "meta",
    name: "FB Website purchases conversion value (1D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaOffsitePixelRevenue7DClick: SalesPageMetric = {
  id: "metaOffsitePixelRevenue7DClick",
  name: "FB Website purchases conversion value (7D Click)",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaOffsitePixelRevenue7DClick,
  requiredBaseMetrics: ["metaOffsitePixelRevenue7DClick"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_offsite_pixel_revenue_7d_click",
    customComposed: false,
  },
  metadata: {
    baseId: "metaOffsitePixelRevenue7DClick",
    overviewPageMetricId: "metaOffsitePixelRevenue7DClick",
    dashboardV2Id: "metaOffsitePixelRevenue7DClick",
    tooltip: {
      reported: "Facebook",
      description:
        "Revenue from conversions from your brand site attributed to your ads",
    },
  },
  descriptionHTML:
    "Facebook reported revenue from conversions from your brand site attributed to your ads",
  spec: {
    id: "metaOffsitePixelRevenue7DClick",
    group: "meta",
    name: "FB Website purchases conversion value (7D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaOffsitePixelRevenue7DClick1DView: SalesPageMetric = {
  id: "metaOffsitePixelRevenue7DClick1DView",
  name: "FB Website purchases conversion value (7D Click, 1D View)",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaOffsitePixelRevenue7DClick1DView,
  requiredBaseMetrics: ["metaOffsitePixelRevenue7DClick1DView"],
  descriptionHTML:
    "Facebook reported revenue from conversions from your brand site attributed to your ads",
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_offsite_pixel_revenue_7d_click_1d_view",
    customComposed: false,
  },
  metadata: {
    baseId: "metaOffsitePixelRevenue7DClick1DView",
    overviewPageMetricId: "metaOffsitePixelRevenue7DClick1DView",
    dashboardV2Id: "metaOffsitePixelRevenue7DClick1DView",
    tooltip: {
      reported: "Facebook",
      description:
        "Revenue from conversions from your brand site attributed to your ads",
    },
  },
  spec: {
    id: "metaOffsitePixelRevenue7DClick1DView",
    group: "meta",
    name: "FB Website purchases conversion value (7D Click, 1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaThumbstopRateDefault: SalesPageMetric = {
  id: "metaThumbstopRateDefault",
  name: "FB thumbstop rate",
  format: metrics.MetricTypes.PERCENTAGE,
  formatter: MetricFormatters.percentage,
  isPositiveChangeGood: true,
  isRate: true,
  isNonModal: true,
  composition: {
    type: CreativeMetricType.facebook,
    metric: "metaThumbstopRateDefault",
    customComposed: true,
  },
  calculate: (v) => {
    if (metrics.isMetricPointForFacebookAd(v) && v.imprs > 0) {
      return v.meta3SVideoViewsDefault / v.imprs;
    } else {
      return 0;
    }
  },
  requiredBaseMetrics: ["meta3SVideoViewsDefault", "imprs"],
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "metaThumbstopRateDefault",
    overviewPageMetricId: "metaThumbstopRateDefault",
    dashboardV2Id: "metaThumbstopRateDefault",
    tooltip: {
      reported: "Facebook",
      description:
        "Percentage of your audience who views the first three seconds of your video",
    },
  },
  descriptionHTML:
    "Facebook reported thumbstop rate, calculated using: 3-second video views / impressions",
  spec: {
    id: "metaThumbstopRateDefault",
    group: "meta",
    name: "FB thumbstop rate",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaHoldRate1DView: SalesPageMetric = {
  id: "metaHoldRate1DView",
  name: "FB hold ratio (1D View)",
  format: metrics.MetricTypes.PERCENTAGE,
  formatter: MetricFormatters.percentage,
  isPositiveChangeGood: true,
  isRate: true,
  calculate: (v) => {
    if (metrics.isMetricPointForFacebookAd(v) && v.imprs > 0) {
      return v.metaVideoThruplayWatchedActions1DView / v.imprs;
    } else {
      return 0;
    }
  },
  requiredBaseMetrics: ["metaVideoThruplayWatchedActions1DView", "imprs"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "metaHoldRate1DView",
    customComposed: true,
  },
  metadata: {
    baseId: "metaHoldRate1DView",
    overviewPageMetricId: "metaHoldRate1DView",
    dashboardV2Id: "metaHoldRate1DView",
    tooltip: {
      reported: "Facebook",
      description:
        "Percentage of people who watch at least 15 seconds of video ad",
    },
  },
  descriptionHTML:
    "Facebook reported hold ratio, calculated using: thruplays / impressions",
  spec: {
    id: "metaHoldRate1DView",
    group: "meta",
    name: "FB hold ratio (1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaHoldRate7DClick1DView: SalesPageMetric = {
  id: "metaHoldRate7DClick1DView",
  name: "FB hold ratio (7D Click, 1D View)",
  format: metrics.MetricTypes.PERCENTAGE,
  formatter: MetricFormatters.percentage,
  isPositiveChangeGood: true,
  isRate: true,
  calculate: (v) => {
    if (metrics.isMetricPointForFacebookAd(v) && v.imprs > 0) {
      return v.metaVideoThruplayWatchedActions7DClick1DView / v.imprs;
    } else {
      return 0;
    }
  },
  requiredBaseMetrics: [
    "metaVideoThruplayWatchedActions7DClick1DView",
    "imprs",
  ],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "metaHoldRate7DClick1DView",
    customComposed: true,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "metaHoldRate7DClick1DView",
    overviewPageMetricId: "metaHoldRate7DClick1DView",
    dashboardV2Id: "metaHoldRate7DClick1DView",
    tooltip: {
      reported: "Facebook",
      description:
        "Percentage of people who watch at least 15 seconds of video ad",
    },
  },
  descriptionHTML:
    "Facebook reported hold ratio, calculated using: thruplays / impressions",
  spec: {
    id: "metaHoldRate7DClick1DView",
    group: "meta",
    name: "FB hold ratio (7D Click, 1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaVideoPlayActions1DView: SalesPageMetric = {
  id: "metaVideoPlayActions1DView",
  name: "FB video play actions (1D View)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaVideoPlayActions1DView,
  requiredBaseMetrics: ["metaVideoPlayActions1DView"],
  descriptionHTML:
    "The number of times your video starts to play. This is counted for each impression of " +
    "a video, and excludes replays",
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_video_play_actions_1d_view",
    customComposed: false,
  },
  metadata: {
    baseId: "metaVideoPlayActions1DView",
    overviewPageMetricId: "metaVideoPlayActions1DView",
    dashboardV2Id: "metaVideoPlayActions1DView",
    tooltip: {
      reported: "Facebook",
      description:
        "Number of times your video starts to play. This is counted for each impression of a " +
        "video, and excludes replays",
    },
  },
  spec: {
    id: "metaVideoPlayActions1DView",
    group: "meta",
    name: "FB video play actions (1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaVideoPlayActions7DClick1DView: SalesPageMetric = {
  id: "metaVideoPlayActions7DClick1DView",
  name: "FB video play actions (7D Click, 1D View)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaVideoPlayActions7DClick1DView,
  requiredBaseMetrics: ["metaVideoPlayActions7DClick1DView"],
  descriptionHTML:
    "The number of times your video starts to play. This is counted for each impression of a " +
    "video, and excludes replays",
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_video_play_actions_7d_click_1d_view",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "metaVideoPlayActions7DClick1DView",
    overviewPageMetricId: "metaVideoPlayActions7DClick1DView",
    dashboardV2Id: "metaVideoPlayActions7DClick1DView",
    tooltip: {
      reported: "Facebook",
      description:
        "Number of times your video starts to play. This is counted for each impression of a " +
        "video, and excludes replays",
    },
  },
  spec: {
    id: "metaVideoPlayActions7DClick1DView",
    group: "meta",
    name: "FB video play actions (7D Click, 1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaCostPerView1DView: SalesPageMetric = {
  id: "metaCostPerView1DView",
  name: "FB cost per content view (1D View)",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: false,
  isRate: true,
  composition: {
    type: CreativeMetricType.facebook,
    metric: "metaCostPerView1DView",
    customComposed: true,
  },
  calculate: (v) => {
    if (v.metaVideoPlayActions1DView) {
      return v.metaSpend / v.metaVideoPlayActions1DView;
    } else {
      return 0;
    }
  },
  requiredBaseMetrics: ["metaVideoPlayActions1DView", "metaSpend"],
  metadata: {
    baseId: "metaCostPerView1DView",
    overviewPageMetricId: "metaCostPerView1DView",
    dashboardV2Id: "metaCostPerView1DView",
    tooltip: {
      reported: "Facebook",
      description: "Cost per content view",
    },
  },
  descriptionHTML:
    "Facebook reported cost per content view, calculated using: spend / video play actions",
  spec: {
    id: "metaCostPerView1DView",
    group: "meta",
    name: "FB cost per content view (1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaCostPerView7DClick1DView: SalesPageMetric = {
  id: "metaCostPerView7DClick1DView",
  name: "FB cost per content view (7D Click, 1D View)",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: false,
  isRate: true,
  composition: {
    type: CreativeMetricType.facebook,
    metric: "metaCostPerView7DClick1DView",
    customComposed: true,
  },
  calculate: (v) => {
    if (v.metaVideoPlayActions7DClick1DView) {
      return v.metaSpend / v.metaVideoPlayActions7DClick1DView;
    } else {
      return 0;
    }
  },
  requiredBaseMetrics: ["metaVideoPlayActions7DClick1DView", "metaSpend"],
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "metaCostPerView7DClick1DView",
    overviewPageMetricId: "metaCostPerView7DClick1DView",
    dashboardV2Id: "metaCostPerView7DClick1DView",
    tooltip: {
      reported: "Facebook",
      description: "Cost per content view",
    },
  },
  descriptionHTML:
    "Facebook reported cost per view, calculated using: spend / video play actions",
  spec: {
    id: "metaCostPerView7DClick1DView",
    group: "meta",
    name: "FB cost per content view (7D Click, 1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaVideoThruplayWatchedActions1DView: SalesPageMetric = {
  id: "metaVideoThruplayWatchedActions1DView",
  name: "FB thruplay (1D View)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaVideoThruplayWatchedActions1DView,
  requiredBaseMetrics: ["metaVideoThruplayWatchedActions1DView"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_video_thruplay_watched_actions_1d_view",
    customComposed: false,
  },
  metadata: {
    baseId: "metaVideoThruplayWatchedActions1DView",
    overviewPageMetricId: "metaVideoThruplayWatchedActions1DView",
    dashboardV2Id: "metaVideoThruplayWatchedActions1DView",
    tooltip: {
      reported: "Facebook",
      description:
        "Number of times your video was played to completion, or at least 15 seconds",
    },
  },
  descriptionHTML:
    "The number of times your video was played to completion, or for at least 15 seconds",
  spec: {
    id: "metaVideoThruplayWatchedActions1DView",
    group: "meta",
    name: "FB thruplay (1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaVideoThruplayWatchedActions7DClick1DView: SalesPageMetric = {
  id: "metaVideoThruplayWatchedActions7DClick1DView",
  name: "FB thruplay (7D Click, 1D View)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaVideoThruplayWatchedActions7DClick1DView,
  requiredBaseMetrics: ["metaVideoThruplayWatchedActions7DClick1DView"],
  descriptionHTML:
    "The number of times your video was played to completion, or for at least 15 seconds",
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_video_thruplay_watched_actions_7d_click_1d_view",
    customComposed: false,
  },
  metadata: {
    baseId: "metaVideoThruplayWatchedActions7DClick1DView",
    overviewPageMetricId: "metaVideoThruplayWatchedActions7DClick1DView",
    dashboardV2Id: "metaVideoThruplayWatchedActions7DClick1DView",
    tooltip: {
      reported: "Facebook",
      description:
        "Number of times your video was played to completion, or at least 15 seconds",
    },
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  spec: {
    id: "metaVideoThruplayWatchedActions7DClick1DView",
    group: "meta",
    name: "FB thruplay (7D Click, 1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaThruplayRate1DView: SalesPageMetric = {
  id: "metaThruplayRate1DView",
  name: "FB thruplay rate (1D View)",
  format: metrics.MetricTypes.PERCENTAGE,
  formatter: MetricFormatters.percentage,
  isPositiveChangeGood: true,
  isRate: true,
  calculate: (v) => {
    if (
      metrics.isMetricPointForFacebookAd(v) &&
      v.metaVideoPlayActions1DView > 0
    ) {
      return (
        v.metaVideoThruplayWatchedActions1DView / v.metaVideoPlayActions1DView
      );
    } else {
      return 0;
    }
  },
  requiredBaseMetrics: [
    "metaVideoThruplayWatchedActions1DView",
    "metaVideoPlayActions1DView",
  ],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "metaThruplayRate1DView",
    customComposed: true,
  },
  metadata: {
    baseId: "metaThruplayRate1DView",
    overviewPageMetricId: "metaThruplayRate1DView",
    dashboardV2Id: "metaThruplayRate1DView",
    tooltip: {
      reported: "Facebook",
      description:
        "Percent of your audience who watched your video to completion or for at least 15 seconds",
    },
  },
  descriptionHTML:
    "The number of times your video was played to completion, or for at least 15 seconds",
  spec: {
    id: "metaThruplayRate1DView",
    group: "meta",
    name: "FB thruplay rate (1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaThruplayRate7DClick1DView: SalesPageMetric = {
  id: "metaThruplayRate7DClick1DView",
  name: "FB thruplay rate (7D Click, 1D View)",
  format: metrics.MetricTypes.PERCENTAGE,
  formatter: MetricFormatters.percentage,
  isPositiveChangeGood: true,
  isRate: true,
  calculate: (v) => {
    if (
      metrics.isMetricPointForFacebookAd(v) &&
      v.metaVideoPlayActions7DClick1DView
    ) {
      return (
        v.metaVideoThruplayWatchedActions7DClick1DView /
        v.metaVideoPlayActions7DClick1DView
      );
    } else {
      return 0;
    }
  },
  requiredBaseMetrics: [
    "metaVideoThruplayWatchedActions7DClick1DView",
    "metaVideoPlayActions7DClick1DView",
  ],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "metaThruplayRate7DClick1DView",
    customComposed: true,
  },
  metadata: {
    baseId: "metaThruplayRate7DClick1DView",
    overviewPageMetricId: "metaThruplayRate7DClick1DView",
    dashboardV2Id: "metaThruplayRate7DClick1DView",
    tooltip: {
      reported: "Facebook",
      description:
        "Percent of your audience who watched your video to completion or for at least 15 seconds",
    },
  },
  descriptionHTML:
    "The number of times your video was played to completion, or for at least 15 seconds",
  spec: {
    id: "metaThruplayRate7DClick1DView",
    group: "meta",
    name: "FB thruplay rate (7D Click, 1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaCostPerThruplay1DView: SalesPageMetric = {
  id: "metaCostPerThruplay1DView",
  name: "FB cost per thruplay (1D View)",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: false,
  isRate: true,
  calculate: (v) => {
    if (v.metaVideoThruplayWatchedActions1DView) {
      return v.metaSpend / v.metaVideoThruplayWatchedActions1DView;
    } else {
      return 0;
    }
  },
  requiredBaseMetrics: ["metaVideoThruplayWatchedActions1DView", "metaSpend"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "metaCostPerThruplay1DView",
    customComposed: true,
  },
  metadata: {
    baseId: "metaCostPerThruplay1DView",
    overviewPageMetricId: "metaCostPerThruplay1DView",
    dashboardV2Id: "metaCostPerThruplay1DView",
    tooltip: {
      reported: "Facebook",
      description:
        "Average cost for a video played to completion or at least 15 seconds",
    },
  },
  descriptionHTML: "The average cost for each ThruPlay",
  spec: {
    id: "metaCostPerThruplay1DView",
    group: "meta",
    name: "FB cost per thruplay (1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaCostPerThruplay7DClick1DView: SalesPageMetric = {
  id: "metaCostPerThruplay7DClick1DView",
  name: "FB cost per thruplay (7D Click, 1D View)",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: false,
  isRate: true,
  calculate: (v) => {
    if (v.metaVideoThruplayWatchedActions7DClick1DView) {
      return v.metaSpend / v.metaVideoThruplayWatchedActions7DClick1DView;
    } else {
      return 0;
    }
  },
  requiredBaseMetrics: ["metaVideoThruplayWatchedActions1DView", "metaSpend"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "metaCostPerThruplay7DClick1DView",
    customComposed: true,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "metaCostPerThruplay7DClick1DView",
    overviewPageMetricId: "metaCostPerThruplay7DClick1DView",
    dashboardV2Id: "metaCostPerThruplay7DClick1DView",
    tooltip: {
      reported: "Facebook",
      description:
        "Average cost for a video played to completion or at least 15 seconds",
    },
  },
  descriptionHTML: "The average cost for each ThruPlay",
  spec: {
    id: "metaCostPerThruplay7DClick1DView",
    group: "meta",
    name: "FB cost per thruplay (7D Click, 1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaROAS1DView: SalesPageMetric = {
  id: "metaROAS1DView",
  name: "FB ROAS (1D View)",
  format: metrics.MetricTypes.MULTIPLIER,
  formatter: MetricFormatters.multiplier,
  isPositiveChangeGood: true,
  isRate: true,
  calculate: (v) => {
    if (metrics.isMetricPointForFacebookAd(v) && v.metaSpend > 0) {
      return v.metaTotalRevenue1DView / v.metaSpend;
    } else {
      return 0;
    }
  },
  requiredBaseMetrics: ["metaTotalRevenue1DView", "metaSpend"],
  pagesEnabled: {
    creativeAnalytics: true,
  },
  composition: {
    type: CreativeMetricType.facebook,
    metric: "metaROAS1DView",
    customComposed: true,
  },
  metadata: {
    baseId: "metaROAS1DView",
    overviewPageMetricId: "metaROAS1DView",
    dashboardV2Id: "metaROAS1DView",
    tooltip: {
      reported: "Facebook",
      /* eslint-disable-next-line quotes */
      description: '"Return on Ad Spend"',
    },
  },
  descriptionHTML:
    "Facebook reported ROAS, this is calculated by taking the total 1D click revenue reported " +
    "by Facebook and dividing by spend",
  spec: {
    id: "metaROAS1DView",
    group: "meta",
    name: "FB ROAS (1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaROAS1DClick: SalesPageMetric = {
  id: "metaROAS1DClick",
  name: "FB ROAS (1D Click)",
  format: metrics.MetricTypes.MULTIPLIER,
  formatter: MetricFormatters.multiplier,
  isPositiveChangeGood: true,
  isRate: true,
  calculate: (v) => {
    if (metrics.isMetricPointForFacebookAd(v) && v.metaSpend > 0) {
      return v.metaTotalRevenue1DClick / v.metaSpend;
    } else {
      return 0;
    }
  },
  requiredBaseMetrics: ["metaTotalRevenue1DClick", "metaSpend"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "metaROAS1DClick",
    customComposed: true,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "metaROAS1DClick",
    overviewPageMetricId: "metaROAS1DClick",
    dashboardV2Id: "metaROAS1DClick",
    tooltip: {
      reported: "Facebook",
      /* eslint-disable-next-line quotes */
      description: '"Return on Ad Spend"',
    },
  },
  descriptionHTML:
    "Facebook reported ROAS, this is calculated by taking the total 1D click revenue reported " +
    "by Facebook and dividing by spend",
  spec: {
    id: "metaROAS1DClick",
    group: "meta",
    name: "FB ROAS (1D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaROAS7DClick: SalesPageMetric = {
  id: "metaROAS7DClick",
  name: "FB ROAS (7D Click)",
  format: metrics.MetricTypes.MULTIPLIER,
  formatter: MetricFormatters.multiplier,
  isPositiveChangeGood: true,
  isRate: true,
  calculate: (v) => {
    if (metrics.isMetricPointForFacebookAd(v) && v.metaSpend > 0) {
      return v.metaTotalRevenue7DClick / v.metaSpend;
    } else {
      return 0;
    }
  },
  requiredBaseMetrics: ["metaTotalRevenue7DClick", "metaSpend"],
  pagesEnabled: {
    creativeAnalytics: true,
  },
  composition: {
    type: CreativeMetricType.facebook,
    metric: "metaROAS7DClick",
    customComposed: true,
  },
  metadata: {
    baseId: "metaROAS7DClick",
    overviewPageMetricId: "metaROAS7DClick",
    dashboardV2Id: "metaROAS7DClick",
    tooltip: {
      reported: "Facebook",
      /* eslint-disable-next-line quotes  */
      description: '"Return on Ad Spend"',
    },
  },
  descriptionHTML:
    /* eslint-disable-next-line max-len  */
    "Facebook reported ROAS, this is calculated by taking the total 7D click revenue reported by Facebook and dividing by spend",
  spec: {
    id: "metaROAS7DClick",
    group: "meta",
    name: "FB ROAS (7D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaROAS7DClick1DView: SalesPageMetric = {
  id: "metaROAS7DClick1DView",
  name: "FB ROAS (7D Click, 1D View)",
  format: metrics.MetricTypes.MULTIPLIER,
  formatter: MetricFormatters.multiplier,
  isPositiveChangeGood: true,
  isRate: true,
  calculate: (v) => {
    if (metrics.isMetricPointForFacebookAd(v) && v.metaSpend > 0) {
      return v.metaTotalRevenue7DClick1DView / v.metaSpend;
    } else {
      return 0;
    }
  },
  requiredBaseMetrics: ["metaTotalRevenue7DClick1DView", "metaSpend"],
  pagesEnabled: {
    creativeAnalytics: true,
  },
  composition: {
    type: CreativeMetricType.facebook,
    metric: "metaROAS7DClick1DView",
    customComposed: true,
  },
  metadata: {
    baseId: "metaROAS7DClick1DView",
    overviewPageMetricId: "metaROAS7DClick1DView",
    dashboardV2Id: "metaROAS7DClick1DView",
    tooltip: {
      reported: "Facebook",
      /* eslint-disable-next-line quotes */
      description: '"Return on Ad Spend"',
    },
  },
  descriptionHTML:
    /* eslint-disable-next-line max-len  */
    "Facebook reported ROAS, this is calculated by taking the total 7D click, 1D view revenue reported by Facebook and dividing by spend",
  spec: {
    id: "metaROAS7DClick1DView",
    group: "meta",
    name: "FB ROAS (7D Click, 1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

// HACK: need to migrate this to real names
const GoogleAllConversionEvents: SalesPageMetric = {
  id: "googleTotalPurchases",

  name: "Google All Conversion Events",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.googleAllConversions,
  requiredBaseMetrics: ["googleAllConversions"],
  descriptionHTML:
    "Google reported number of all conversion events (including purchases) across all channels",
  composition: {
    type: CreativeMetricType.google,
    metric: "google_all_conversions",
    customComposed: false,
  },
  metadata: {
    baseId: "googleTotalPurchases",
    overviewPageMetricId: "googleTotalPurchases",
    dashboardV2Id: "googleAllConversions",
    tooltip: {
      reported: "Google",
      description:
        "Google reported number of all conversion events (including purchases) across all channels",
    },
  },
  spec: {
    id: "googleTotalPurchases",
    group: CreativeMetricType.google,
    name: "Google All Conversion Events",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const GooglePurchases: SalesPageMetric = {
  id: "googlePurchases",
  name: "Google Purchases",
  format: "integer",
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.googleConversions,
  requiredBaseMetrics: ["googleConversions"],
  descriptionHTML:
    "Google reported number of purchase conversions across all channels ",
  composition: {
    type: CreativeMetricType.google,
    metric: "google_conversions",
    customComposed: false,
  },
  metadata: {
    baseId: "googlePurchases",
    overviewPageMetricId: "googlePurchases",
    dashboardV2Id: "googleConversions",
    tooltip: {
      reported: "Google",
      description:
        "Google reported number of purchase conversions across all channels",
    },
  },
  spec: {
    id: "googlePurchases",
    group: CreativeMetricType.google,
    name: "Google Purchases",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

// HACK: to migrate
const GoogleAllConversionEventsRevenue: SalesPageMetric = {
  id: "googleTotalRevenue",
  name: "Google All Conversion Events Revenue",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.googleAllConversionsValue,
  requiredBaseMetrics: ["googleAllConversionsValue"],
  descriptionHTML:
    "Google reported revenue from all conversions (including purchases) across all channels ",
  composition: {
    type: CreativeMetricType.google,
    metric: "google_all_conversions_value",
    customComposed: false,
  },
  metadata: {
    baseId: "googleTotalRevenue",
    overviewPageMetricId: "googleTotalRevenue",
    dashboardV2Id: "googleAllConversionsValue",
    tooltip: {
      reported: "Google",
      description:
        "Google reported revenue from all conversions (including purchases) across all channels ",
    },
  },
  spec: {
    id: "googleTotalRevenue",
    group: CreativeMetricType.google,
    name: "Google All Conversion Events Revenue",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const GooglePurchaseRevenue: SalesPageMetric = {
  id: "googlePurchaseRevenue",
  name: "Google Purchase Revenue",
  format: "dollars",
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.googleConversionsValue,
  requiredBaseMetrics: ["googleConversionsValue"],
  descriptionHTML:
    "Google reported revenue from all purchase conversions across all channels",
  composition: {
    type: CreativeMetricType.google,
    metric: "google_conversions_value",
    customComposed: false,
  },
  metadata: {
    baseId: "googlePurchaseRevenue",
    overviewPageMetricId: "googlePurchaseRevenue",
    dashboardV2Id: "googleConversionsValue",
    tooltip: {
      reported: "Google",
      description:
        "Google reported revenue from all purchase conversions across all channels",
    },
  },
  spec: {
    id: "googlePurchaseRevenue",
    group: CreativeMetricType.google,
    name: "Google Purchases Revenue",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const GoogleVideoViews: SalesPageMetric = {
  id: "googleVideoViews",
  name: "Google video views",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.googleVideoViews,
  requiredBaseMetrics: ["googleVideoViews"],
  descriptionHTML: "Google reported video views",
  composition: {
    type: CreativeMetricType.google,
    metric: "google_video_views",
    customComposed: false,
  },
  metadata: {
    baseId: "googleVideoViews",
    overviewPageMetricId: "googleVideoViews",
    dashboardV2Id: "googleVideoViews",
    tooltip: {
      reported: "Google",
      description: "Video views",
    },
  },
  spec: {
    id: "googleVideoViews",
    group: CreativeMetricType.google,
    name: "Google video views",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const GoogleClicks: SalesPageMetric = {
  id: "googleClicks",
  name: "Google Clicks",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.googleClicks,
  requiredBaseMetrics: ["googleClicks"],
  descriptionHTML: "Google Clicks",
  composition: {
    type: CreativeMetricType.google,
    metric: "google_clicks",
    customComposed: false,
  },
  metadata: {
    baseId: "googleClicks",
    overviewPageMetricId: "googleClicks",
    dashboardV2Id: "googleClicks",
    tooltip: {
      reported: "Google",
      description: "User clicks a Google ad",
    },
  },
  spec: {
    id: "googleClicks",
    group: CreativeMetricType.google,
    name: "Google Clicks",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const GoogleImpressions: SalesPageMetric = {
  id: "googleImpressions",
  name: "Google Impressions",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.googleImpressions,
  requiredBaseMetrics: ["googleImpressions"],
  descriptionHTML: "Google Impressions",
  composition: {
    type: CreativeMetricType.google,
    metric: "google_impressions",
    customComposed: false,
  },
  metadata: {
    baseId: "googleImpressions",
    overviewPageMetricId: "googleImpressions",
    dashboardV2Id: "googleImpressions",
    tooltip: {
      reported: "Google",
      description:
        "An impression is counted whenever an item appears on the current page of results",
    },
  },
  spec: {
    id: "googleImpressions",
    group: CreativeMetricType.google,
    name: "Google Impressions",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const GoogleViewThroughConversions: SalesPageMetric = {
  id: "googleViewThroughConversions",
  name: "Google view-through conversions",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.googleViewThroughConversions,
  requiredBaseMetrics: ["googleViewThroughConversions"],
  descriptionHTML: "Google reported view-through conversions",
  composition: {
    type: CreativeMetricType.google,
    metric: "google_view_through_conversions",
    customComposed: false,
  },
  metadata: {
    baseId: "googleViewThroughConversions",
    overviewPageMetricId: "googleViewThroughConversions",
    dashboardV2Id: "googleViewThroughConversions",
    tooltip: {
      reported: "Google",
      description:
        "Occur after an ad impression, if the user doesn't interact with the ad, then " +
        "later converts",
    },
  },
  spec: {
    id: "googleViewThroughConversions",
    group: CreativeMetricType.google,
    name: "Google view-through conversions",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const GoogleROAS: SalesPageMetric = {
  id: "googleROAS",
  name: "Google ROAS",
  format: metrics.MetricTypes.MULTIPLIER,
  formatter: MetricFormatters.multiplier,
  isPositiveChangeGood: true,
  isRate: true,
  calculate: (v) => {
    if (v.googleConversionsValue) {
      return v.googleConversionsValue / v.googleSpend;
    } else {
      return 0;
    }
  },
  requiredBaseMetrics: ["googleConversionsValue", "googleSpend"],
  composition: {
    type: CreativeMetricType.google,
    metric: "googleROAS",
    customComposed: true,
  },
  metadata: {
    baseId: "googleROAS",
    overviewPageMetricId: "googleROAS",
    dashboardV2Id: "googleROAS",
    tooltip: {
      reported: "Google",
      /* eslint-disable-next-line quotes */
      description: '"Return on Ad Spend" from customers',
      calculationInfo: "Google Revenue ÷ Google Marketing Spend",
    },
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  descriptionHTML:
    "Google reported ROAS, this is calculated by taking the total revenue reported by Google " +
    "and dividing by spend",
  spec: {
    id: "googleROAS",
    group: CreativeMetricType.google,
    name: "Google ROAS",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const GoogleCAC: SalesPageMetric = {
  id: "googleCAC",
  name: "Google CAC",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currencyRatio,
  isPositiveChangeGood: false,
  isRate: true,
  calculate: (v) => {
    if (v.googleConversions && v.googleSpend) {
      return v.googleSpend / v.googleConversions;
    }
    return 0;
  },
  requiredBaseMetrics: ["googleConversions", "googleSpend"],
  metadata: {
    baseId: "googleCAC",
    overviewPageMetricId: "googleCAC",
    dashboardV2Id: "googleCAC",
    tooltip: {
      reported: "Google",
      /* eslint-disable-next-line quotes  */
      description: '"Customer Acquisition Cost"; cost of acquiring a customer',
      calculationInfo: "Google Spend ÷ Google Purchases",
    },
  },
  descriptionHTML:
    "Calculated by taking the total spend reported by Google and dividing by total number " +
    "of purchases",
  composition: {
    type: CreativeMetricType.google,
    metric: "googleCAC",
    customComposed: true,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  spec: {
    id: "googleCAC",
    group: CreativeMetricType.google,
    name: "Google CAC",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const GoogleCostPerConversion: SalesPageMetric = {
  id: "googleCostPerConversion",
  name: "Google cost per conversion",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: false,
  isRate: true,
  composition: {
    type: CreativeMetricType.google,
    metric: "googleCostPerConversion",
    customComposed: true,
  },
  calculate: (v) => {
    if (v.googleConversions) {
      return v.googleSpend / v.googleConversions;
    } else {
      return 0;
    }
  },
  requiredBaseMetrics: ["googleConversions", "googleSpend"],
  metadata: {
    baseId: "googleCostPerConversion",
    overviewPageMetricId: "googleCostPerConversion",
    dashboardV2Id: "googleCostPerConversion",
    tooltip: {
      reported: "Google",
      description:
        /* eslint-disable-next-line quotes, max-len */
        '"Cost Per Click"; average cost incurred for each click that leads to a specific action or conversion',
      calculationInfo: "Google Spend ÷ Google Visits",
    },
  },
  descriptionHTML:
    "Google reported cost per conversion, this is calculated by taking spend and dividing by " +
    "the number of total conversions reported by Google",
  spec: {
    id: "googleCostPerConversion",
    group: CreativeMetricType.google,
    name: "Google cost per conversion",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const GoogleAverageCPC: SalesPageMetric = {
  id: "googleAverageCPC",
  name: "Google Average CPC",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: false,
  isRate: true,
  composition: {
    type: CreativeMetricType.google,
    metric: "googleAverageCPC",
    customComposed: true,
  },
  calculate: (v) => {
    if (v.googleClicks) {
      return v.googleSpend / v.googleClicks;
    } else {
      return 0;
    }
  },
  requiredBaseMetrics: ["googleClicks", "googleSpend"],
  metadata: {
    baseId: "googleAverageCPC",
    overviewPageMetricId: "googleAverageCPC",
    dashboardV2Id: "googleAverageCPC",
    tooltip: {
      reported: "Google",
      /* eslint-disable-next-line quotes */
      description: 'Average "Cost Per Click"',
      calculationInfo: "Google Spend ÷ Google Visits",
    },
  },
  descriptionHTML:
    "Google reported average CPC, this is calculated by taking spend and dividing by the number " +
    "of total clicks reported by Google",
  spec: {
    id: "googleAverageCPC",
    group: CreativeMetricType.google,
    name: "Google Average CPC",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const TikTokVideoViews: SalesPageMetric = {
  id: "tiktokVideoViews",
  name: "TikTok video views",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.tiktokVideoViews,
  requiredBaseMetrics: ["tiktokVideoViews"],
  descriptionHTML:
    "The number of times your video starts to play. Replays will not be counted",
  composition: {
    type: CreativeMetricType.tiktok,
    metric: "tiktok_video_views",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "tiktokVideoViews",
    overviewPageMetricId: "tiktokVideoViews",
    dashboardV2Id: "tiktokVideoViews",
    tooltip: {
      reported: "TikTok",
      description:
        "The number of times your video starts to play. Replays will not be counted",
    },
  },
  spec: {
    id: "tiktokVideoViews",
    group: CreativeMetricType.tiktok,
    name: "TikTok video views",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const TikTokVideoViews2s: SalesPageMetric = {
  id: "tiktokVideoViews2s",
  name: "TikTok two-second video views",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.tiktokVideoViews2s,
  requiredBaseMetrics: ["tiktokVideoViews2s"],
  descriptionHTML:
    "Number of times your video was played for at least 2 seconds. Replays will not be counted",
  composition: {
    type: CreativeMetricType.tiktok,
    metric: "tiktok_video_views_2s",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "tiktokVideoViews2s",
    overviewPageMetricId: "tiktokVideoViews2s",
    dashboardV2Id: "tiktokVideoViews2s",
    tooltip: {
      reported: "TikTok",
      description:
        "Number of times your video was played for at least 2 seconds. Replays will not be counted",
    },
  },
  spec: {
    id: "tiktokVideoViews2s",
    group: CreativeMetricType.tiktok,
    name: "TikTok two-second video views",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const TikTokVideoViews6s: SalesPageMetric = {
  id: "tiktokVideoViews6s",
  name: "TikTok six-second video views",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.tiktokVideoViews6s,
  requiredBaseMetrics: ["tiktokVideoViews6s"],
  descriptionHTML:
    "Number of times your video was played for at least 6 seconds. Replays will not be counted",
  composition: {
    type: CreativeMetricType.tiktok,
    metric: "tiktok_video_views_6s",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "tiktokVideoViews6s",
    overviewPageMetricId: "tiktokVideoViews6s",
    dashboardV2Id: "tiktokVideoViews6s",
    tooltip: {
      reported: "TikTok",
      description:
        "Number of times your video was played for at least 6 seconds. Replays will not be counted",
    },
  },
  spec: {
    id: "tiktokVideoViews6s",
    group: CreativeMetricType.tiktok,
    name: "TikTok six-second video views",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const TikTokVideoViewsP100: SalesPageMetric = {
  id: "tiktokVideoViewsP100",
  name: "TikTok P100 Video Views",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.tiktokVideoViewsP100,
  requiredBaseMetrics: ["tiktokVideoViewsP100"],
  descriptionHTML:
    "The number of times your video was played at 100% of its length. Replays will not be counted",
  composition: {
    type: CreativeMetricType.tiktok,
    metric: "tiktok_video_views_P100",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "tiktokVideoViewsP100",
    overviewPageMetricId: "tiktokVideoViewsP100",
    dashboardV2Id: "tiktokVideoViewsP100",
    tooltip: {
      reported: "TikTok",
      description:
        "Number of times your video was played at 100% of its length, including plays that " +
        "skipped to this point",
    },
  },
  spec: {
    id: "tiktokVideoViewsP100",
    group: CreativeMetricType.tiktok,
    name: "TikTok P100 Video Views",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const TikTokVideoViewsP75: SalesPageMetric = {
  id: "tiktokVideoViewsP75",
  name: "TikTok P75 Video Views",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.tiktokVideoViewsP75,
  requiredBaseMetrics: ["tiktokVideoViewsP75"],
  descriptionHTML:
    "The number of times your video was played at 75% of its length. Replays will not be counted",
  composition: {
    type: CreativeMetricType.tiktok,
    metric: "tiktok_video_views_P75",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "tiktokVideoViewsP75",
    overviewPageMetricId: "tiktokVideoViewsP75",
    dashboardV2Id: "tiktokVideoViewsP75",
    tooltip: {
      reported: "TikTok",
      description:
        "Number of times your video was played at 75% of its length, including plays that " +
        "skipped to this point",
    },
  },
  spec: {
    id: "tiktokVideoViewsP75",
    group: CreativeMetricType.tiktok,
    name: "TikTok P75 Video Views",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const TikTokVideoViewsP50: SalesPageMetric = {
  id: "tiktokVideoViewsP50",
  name: "TikTok P50 Video Views",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.tiktokVideoViewsP50,
  requiredBaseMetrics: ["tiktokVideoViewsP50"],
  descriptionHTML:
    "The number of times your video was played at 50% of its length. Replays will not be counted",
  composition: {
    type: CreativeMetricType.tiktok,
    metric: "tiktok_video_views_P50",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "tiktokVideoViewsP50",
    overviewPageMetricId: "tiktokVideoViewsP50",
    dashboardV2Id: "tiktokVideoViewsP50",
    tooltip: {
      reported: "TikTok",
      description:
        "Number of times your video was played at 50% of its length, including plays " +
        "that skipped to this point",
    },
  },
  spec: {
    id: "tiktokVideoViewsP50",
    group: CreativeMetricType.tiktok,
    name: "TikTok P50 Video Views",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const TikTokVideoViewsP25: SalesPageMetric = {
  id: "tiktokVideoViewsP25",
  name: "TikTok P25 Video Views",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.tiktokVideoViewsP25,
  requiredBaseMetrics: ["tiktokVideoViewsP25"],
  descriptionHTML:
    "The number of times your video was played at 25% of its length. Replays will not be counted",
  composition: {
    type: CreativeMetricType.tiktok,
    metric: "tiktok_video_views_P25",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "tiktokVideoViewsP25",
    overviewPageMetricId: "tiktokVideoViewsP25",
    dashboardV2Id: "tiktokVideoViewsP25",
    tooltip: {
      reported: "TikTok",
      description:
        "Number of times your video was played at 255% of its length, including plays that " +
        "skipped to this point",
    },
  },
  spec: {
    id: "tiktokVideoViewsP25",
    group: CreativeMetricType.tiktok,
    name: "TikTok P25 Video Views",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const TikTokImpressions: SalesPageMetric = {
  id: "tiktokImpressions",
  name: "TikTok impressions",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.tiktokImprs,
  requiredBaseMetrics: ["tiktokImprs"],
  descriptionHTML: "The number of times your ads were on screen",
  composition: {
    type: CreativeMetricType.tiktok,
    metric: "tiktok_imprs",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "tiktokImpressions",
    overviewPageMetricId: "tiktokImpressions",
    dashboardV2Id: "tiktokImprs",
    tooltip: {
      reported: "TikTok",
      description: "The number of times your ads were on screen",
    },
  },
  spec: {
    id: "tiktokImpressions",
    group: CreativeMetricType.tiktok,
    name: "TikTok impressions",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const TikTokClicks: SalesPageMetric = {
  id: "tiktokClicks",
  name: "TikTok clicks",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.tiktokClicks,
  requiredBaseMetrics: ["tiktokClicks"],
  descriptionHTML:
    "The number of clicks on your ads that led users to advertiser-specified destinations " +
    "like websites, apps, or to the app store",
  composition: {
    type: CreativeMetricType.tiktok,
    metric: "tiktok_clicks",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "tiktokClicks",
    overviewPageMetricId: "tiktokClicks",
    dashboardV2Id: "tiktokClicks",
    tooltip: {
      reported: "TikTok",
      description:
        "The number of clicks on your ads that led users to advertiser-specified destinations " +
        "like websites, apps, or to the app store",
    },
  },
  spec: {
    id: "tiktokClicks",
    group: CreativeMetricType.tiktok,
    name: "TikTok clicks",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const TikTokLikes: SalesPageMetric = {
  id: "tiktokLikes",
  name: "TikTok Paid Likes",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.tiktokLikes,
  requiredBaseMetrics: ["tiktokLikes"],
  descriptionHTML:
    "The number of likes your video creative received within 1 day of a user seeing a paid ad",
  composition: {
    type: CreativeMetricType.tiktok,
    metric: "tiktok_likes",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "tiktokLikes",
    overviewPageMetricId: "tiktokLikes",
    dashboardV2Id: "tiktokLikes",
    tooltip: {
      reported: "TikTok",
      description:
        "The number of likes your video creative received within 1 day of a user seeing a paid ad",
    },
  },
  spec: {
    id: "tiktokLikes",
    group: CreativeMetricType.tiktok,
    name: "TikTok Paid Likes",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const TikTokGMVNetCost: SalesPageMetric = {
  id: "tiktokGMVNetCost",
  name: "TikTok GMV Net Cost",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: false,
  isRate: false,
  calculate: (v) => v.tiktokGMVNetCost,
  requiredBaseMetrics: ["tiktokGMVNetCost"],
  descriptionHTML:
    "The Net Cost (Delayed) metric is a feature in TikTok Ads Manager that provides " +
    "advertisers with the true cost of their campaigns after adjusting for ad credits or coupons used",
  composition: {
    type: CreativeMetricType.tiktok,
    metric: "tiktok_gmv_net_cost",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: false,
  },
  metadata: {
    baseId: "tiktokGMVNetCost",
    overviewPageMetricId: "tiktokGMVNetCost",
    dashboardV2Id: "tiktokGMVNetCost",
    tooltip: {
      reported: "TikTok",
      description:
        "The Net Cost (Delayed) metric is a feature in TikTok Ads Manager that provides " +
        "advertisers with the true cost of their campaigns after adjusting for ad credits or coupons used",
    },
  },
  spec: {
    id: "tiktokGMVNetCost",
    group: CreativeMetricType.tiktok,
    name: "TikTok GMV Net Cost",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const TikTokGMVGrossRevenue: SalesPageMetric = {
  id: "tiktokGMVGrossRevenue",
  name: "TikTok GMV Gross Revenue",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.tiktokGMVGrossRevenue,
  requiredBaseMetrics: ["tiktokGMVGrossRevenue"],
  descriptionHTML: "The gross revenue from TikTok GMV",
  composition: {
    type: CreativeMetricType.tiktok,
    metric: "tiktok_gmv_gross_revenue",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: false,
  },
  metadata: {
    baseId: "tiktokGMVGrossRevenue",
    overviewPageMetricId: "tiktokGMVGrossRevenue",
    dashboardV2Id: "tiktokGMVGrossRevenue",
    tooltip: {
      reported: "TikTok",
      description: "The gross revenue from TikTok GMV",
    },
  },
  spec: {
    id: "tiktokGMVGrossRevenue",
    group: CreativeMetricType.tiktok,
    name: "TikTok GMV Gross Revenue",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const TikTokGMVCostPerLiveView: SalesPageMetric = {
  id: "tiktokGMVCostPerLiveView",
  name: "TikTok GMV Cost Per Live View",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: false,
  isRate: false,
  calculate: (v) => v.tiktokGMVCostPerLiveView,
  requiredBaseMetrics: ["tiktokGMVCostPerLiveView"],
  descriptionHTML: "The cost per live view for TikTok GMV",
  composition: {
    type: CreativeMetricType.tiktok,
    metric: "tiktok_gmv_cost_per_live_view",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: false,
  },
  metadata: {
    baseId: "tiktokGMVCostPerLiveView",
    overviewPageMetricId: "tiktokGMVCostPerLiveView",
    dashboardV2Id: "tiktokGMVCostPerLiveView",
    tooltip: {
      reported: "TikTok",
      description: "The cost per live view for TikTok GMV",
    },
  },
  spec: {
    id: "tiktokGMVCostPerLiveView",
    group: CreativeMetricType.tiktok,
    name: "TikTok GMV Cost Per Live View",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const TikTokGMVCostPer10SecondLiveView: SalesPageMetric = {
  id: "tiktokGMVCostPer10SecondLiveView",
  name: "TikTok GMV Cost Per 10 Second Live View",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: false,
  isRate: false,
  calculate: (v) => v.tiktokGMVCostPer10SecondLiveView,
  requiredBaseMetrics: ["tiktokGMVCostPer10SecondLiveView"],
  descriptionHTML: "The cost per 10 second live view for TikTok GMV",
  composition: {
    type: CreativeMetricType.tiktok,
    metric: "tiktok_gmv_cost_per_10_second_live_view",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: false,
  },
  metadata: {
    baseId: "tiktokGMVCostPer10SecondLiveView",
    overviewPageMetricId: "tiktokGMVCostPer10SecondLiveView",
    dashboardV2Id: "tiktokGMVCostPer10SecondLiveView",
    tooltip: {
      reported: "TikTok",
      description: "The cost per 10 second live view for TikTok GMV",
    },
  },
  spec: {
    id: "tiktokGMVCostPer10SecondLiveView",
    group: CreativeMetricType.tiktok,
    name: "TikTok GMV Cost Per 10 Second Live View",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const TikTokGMVOrders: SalesPageMetric = {
  id: "tiktokGMVOrders",
  name: "TikTok GMV Orders",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.tiktokGMVOrders,
  requiredBaseMetrics: ["tiktokGMVOrders"],
  descriptionHTML: "The number of orders from TikTok GMV",
  composition: {
    type: CreativeMetricType.tiktok,
    metric: "tiktok_gmv_orders",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: false,
  },
  metadata: {
    baseId: "tiktokGMVOrders",
    overviewPageMetricId: "tiktokGMVOrders",
    dashboardV2Id: "tiktokGMVOrders",
    tooltip: {
      reported: "TikTok",
      description: "The number of orders from TikTok GMV",
    },
  },
  spec: {
    id: "tiktokGMVOrders",
    group: CreativeMetricType.tiktok,
    name: "TikTok GMV Orders",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const TikTokGMVLiveViews: SalesPageMetric = {
  id: "tiktokGMVLiveViews",
  name: "TikTok GMV Live Views",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.tiktokGMVLiveViews,
  requiredBaseMetrics: ["tiktokGMVLiveViews"],
  descriptionHTML: "The number of live views from TikTok GMV",
  composition: {
    type: CreativeMetricType.tiktok,
    metric: "tiktok_gmv_live_views",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: false,
  },
  metadata: {
    baseId: "tiktokGMVLiveViews",
    overviewPageMetricId: "tiktokGMVLiveViews",
    dashboardV2Id: "tiktokGMVLiveViews",
    tooltip: {
      reported: "TikTok",
      description: "The number of live views from TikTok GMV",
    },
  },
  spec: {
    id: "tiktokGMVLiveViews",
    group: CreativeMetricType.tiktok,
    name: "TikTok GMV Live Views",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const TikTokGMVTenSecondLiveViews: SalesPageMetric = {
  id: "tiktokGMVTenSecondLiveViews",
  name: "TikTok GMV 10 Second Live Views",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.tiktokGMVTenSecondLiveViews,
  requiredBaseMetrics: ["tiktokGMVTenSecondLiveViews"],
  descriptionHTML: "The number of 10 second live views from TikTok GMV",
  composition: {
    type: CreativeMetricType.tiktok,
    metric: "tiktok_gmv_ten_second_live_views",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: false,
  },
  metadata: {
    baseId: "tiktokGMVTenSecondLiveViews",
    overviewPageMetricId: "tiktokGMVTenSecondLiveViews",
    dashboardV2Id: "tiktokGMVTenSecondLiveViews",
    tooltip: {
      reported: "TikTok",
      description: "The number of 10 second live views from TikTok GMV",
    },
  },
  spec: {
    id: "tiktokGMVTenSecondLiveViews",
    group: CreativeMetricType.tiktok,
    name: "TikTok GMV 10 Second Live Views",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const TikTokGMVLiveFollows: SalesPageMetric = {
  id: "tiktokGMVLiveFollows",
  name: "TikTok GMV Live Follows",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.tiktokGMVLiveFollows,
  requiredBaseMetrics: ["tiktokGMVLiveFollows"],
  descriptionHTML: "The number of live follows from TikTok GMV",
  composition: {
    type: CreativeMetricType.tiktok,
    metric: "tiktok_gmv_live_follows",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: false,
  },
  metadata: {
    baseId: "tiktokGMVLiveFollows",
    overviewPageMetricId: "tiktokGMVLiveFollows",
    dashboardV2Id: "tiktokGMVLiveFollows",
    tooltip: {
      reported: "TikTok",
      description: "The number of live follows from TikTok GMV",
    },
  },
  spec: {
    id: "tiktokGMVLiveFollows",
    group: CreativeMetricType.tiktok,
    name: "TikTok GMV Live Follows",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const TikTokGMVCostPerOrder: SalesPageMetric = {
  id: "tiktokGMVCostPerOrder",
  name: "TikTok GMV cost per order",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: false,
  isRate: false,
  calculate: (v) => v.tiktokGMVCostPerOrder,
  requiredBaseMetrics: ["tiktokGMVCostPerOrder"],
  descriptionHTML: "The cost per order of the GMV metric",
  composition: {
    type: CreativeMetricType.tiktok,
    metric: "tiktok_gmv_cost_per_order",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: false,
  },
  metadata: {
    baseId: "tiktokGMVCostPerOrder",
    overviewPageMetricId: "tiktokGMVCostPerOrder",
    dashboardV2Id: "tiktokGMVCostPerOrder",
    tooltip: {
      reported: "TikTok",
      description: "The cost per order of the GMV metric",
    },
  },
  spec: {
    id: "tiktokGMVCostPerOrder",
    group: CreativeMetricType.tiktok,
    name: "TikTok GMV Cost Per Order",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const TikTokCompletePayment: SalesPageMetric = {
  id: "tiktokCompletePayment",
  name: "TikTok complete payments",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.tiktokCompletePayment,
  requiredBaseMetrics: ["tiktokCompletePayment"],
  descriptionHTML: "The total number of purchases attributed to TikTok spend",
  composition: {
    type: CreativeMetricType.tiktok,
    metric: "tiktok_complete_payment",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "tiktokCompletePayment",
    overviewPageMetricId: "tiktokCompletePayment",
    dashboardV2Id: "tiktokCompletePayment",
    tooltip: {
      reported: "TikTok",
      description: "The total number of purchases attributed to TikTok spend",
    },
  },
  spec: {
    id: "tiktokCompletePayment",
    group: CreativeMetricType.tiktok,
    name: "TikTok complete payments",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const TikTokTotalCompletePayment: SalesPageMetric = {
  id: "tiktokTotalCompletePayment",
  name: "TikTok complete payment revenue",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.tiktokTotalCompletePayment,
  requiredBaseMetrics: ["tiktokTotalCompletePayment"],
  descriptionHTML: "The total value returned from each Complete Payment event",
  composition: {
    type: CreativeMetricType.tiktok,
    metric: "tiktok_total_complete_payment",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "tiktokTotalCompletePayment",
    overviewPageMetricId: "tiktokTotalCompletePayment",
    dashboardV2Id: "tiktokTotalCompletePayment",
    tooltip: {
      reported: "TikTok",
      description: "The total value returned from each Complete Payment event",
    },
  },
  spec: {
    id: "tiktokTotalCompletePayment",
    group: CreativeMetricType.tiktok,
    name: "TikTok complete payment revenue",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const TikTokEngagements: SalesPageMetric = {
  id: "tiktokEngagements",
  name: "TikTok Total Engagement",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.tiktokEngagements,
  requiredBaseMetrics: ["tiktokEngagements"],
  descriptionHTML:
    "The overall amount of engagement on your ad that includes paid likes, comments, shares, " +
    /* eslint-disable-next-line quotes */
    'follows, and clicks. Clicks in the "Total Engagement" metric also include music, ' +
    "hashtag, anchor, and interactive add-on clicks.",
  metadata: {
    baseId: "tiktokEngagements",
    overviewPageMetricId: "tiktokEngagements",
    dashboardV2Id: "tiktokEngagements",
    tooltip: {
      reported: "TikTok",
      description:
        "The overall amount of engagement on your ad that includes paid likes, comments, " +
        /* eslint-disable-next-line quotes */
        'shares, follows, and clicks. Clicks in the Total Engagement" metric also include music',
    },
  },
  composition: {
    type: CreativeMetricType.tiktok,
    metric: "tiktok_engagements",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  spec: {
    id: "tiktokEngagements",
    group: CreativeMetricType.tiktok,
    name: "TikTok Total Engagement",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const TikTokCPM: SalesPageMetric = {
  id: "tiktokCPM",
  name: "TikTok CPM",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: false,
  isRate: true,
  calculate: (v) => {
    if (v.tiktokImprs) {
      return (v.tiktokSpend * 1000) / v.tiktokImprs;
    }
    return 0;
  },
  requiredBaseMetrics: ["tiktokImprs", "tiktokSpend"],
  composition: {
    type: CreativeMetricType.tiktok,
    metric: "tiktokCPM",
    customComposed: true,
  },
  metadata: {
    baseId: "tiktokCPM",
    overviewPageMetricId: "tiktokCPM",
    dashboardV2Id: "tiktokCPM",
    tooltip: {
      reported: "TikTok",
      description:
        /* eslint-disable-next-line quotes */
        '"Cost Per Mille"; cost of reaching 1,000 impressions (views)',
      calculationInfo: "1,000 × TikTok Spend ÷ TikTok Impressions",
    },
  },
  descriptionHTML: "TikTok reported CPM",
  spec: {
    id: "tiktokCPM",
    group: CreativeMetricType.tiktok,
    name: "TikTok CPM",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const TikTokCTR: SalesPageMetric = {
  id: "tiktokCTR",
  name: "TikTok CTR",
  format: metrics.MetricTypes.PERCENTAGE,
  formatter: MetricFormatters.percentage,
  isPositiveChangeGood: true,
  isRate: true,
  calculate: (v) => {
    if (v.tiktokImprs && v.tiktokClicks) {
      return v.tiktokClicks / v.tiktokImprs;
    }
    return 0;
  },
  requiredBaseMetrics: ["tiktokClicks", "tiktokImprs"],
  composition: {
    type: CreativeMetricType.tiktok,
    metric: "tiktokCTR",
    customComposed: true,
  },
  metadata: {
    baseId: "tiktokCTR",
    overviewPageMetricId: "tiktokCTR",
    dashboardV2Id: "tiktokCTR",
    tooltip: {
      reported: "TikTok",
      description:
        /* eslint-disable-next-line quotes */
        '"Click Through Rate"; number of clicks an ad receives relative to the number of ' +
        "impressions (views) it generates",
      calculationInfo: "TikTok Visits ÷ TikTok Impressions",
    },
  },
  descriptionHTML: "TikTok CTR",
  spec: {
    id: "tiktokCTR",
    group: CreativeMetricType.tiktok,
    name: "TikTok CTR",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const TikTokCPC: SalesPageMetric = {
  id: "tiktokCPC",
  name: "TikTok CPC",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: false,
  isRate: true,
  calculate: (v) => {
    if (v.tiktokClicks) {
      return v.tiktokSpend / v.tiktokClicks;
    }
    return 0;
  },
  requiredBaseMetrics: ["tiktokClicks", "tiktokSpend"],
  composition: {
    type: CreativeMetricType.tiktok,
    metric: "tiktokCPC",
    customComposed: true,
  },
  metadata: {
    baseId: "tiktokCPC",
    overviewPageMetricId: "tiktokCPC",
    dashboardV2Id: "tiktokCPC",
    tooltip: {
      reported: "TikTok",
      description:
        /* eslint-disable-next-line quotes */
        '"Effective Cost Per Click"; average cost incurred for each click that leads to a ' +
        "specific action or conversion",
      calculationInfo: "TikTok Spend ÷ TikTok Visits",
    },
  },
  descriptionHTML: "TikTok CPC",
  spec: {
    id: "tiktokCPC",
    group: CreativeMetricType.tiktok,
    name: "TikTok CPC",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const TikTokROAS: SalesPageMetric = {
  id: "tiktokROAS",
  name: "TikTok ROAS",
  format: metrics.MetricTypes.MULTIPLIER,
  formatter: MetricFormatters.multiplier,
  isPositiveChangeGood: true,
  isRate: true,
  calculate: (v) => {
    if (v.tiktokTotalCompletePayment && v.tiktokSpend) {
      return v.tiktokTotalCompletePayment / v.tiktokSpend;
    }
    return 0;
  },
  requiredBaseMetrics: ["tiktokTotalCompletePayment", "tiktokSpend"],
  pagesEnabled: {
    creativeAnalytics: true,
  },
  composition: {
    type: CreativeMetricType.tiktok,
    metric: "tiktokROAS",
    customComposed: true,
  },
  metadata: {
    baseId: "tiktokROAS",
    overviewPageMetricId: "tiktokROAS",
    dashboardV2Id: "tiktokROAS",
    tooltip: {
      reported: "TikTok",
      /* eslint-disable-next-line quotes */
      description: '"Return on Ad Spend" from customers',
      calculationInfo: "TikTok Revenue ÷ TikTok Marketing Spend",
    },
  },
  descriptionHTML:
    "Calculated by taking the total revenue reported by tiktok and dividing by spend",
  spec: {
    id: "tiktokROAS",
    group: CreativeMetricType.tiktok,
    name: "TikTok ROAS",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const TikTokCAC: SalesPageMetric = {
  id: "tiktokCAC",
  name: "TikTok CAC",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currencyRatio,
  isPositiveChangeGood: false,
  isRate: true,
  calculate: (v) => {
    if (v.tiktokCompletePayment && v.tiktokSpend) {
      return v.tiktokSpend / v.tiktokCompletePayment;
    }
    return 0;
  },
  requiredBaseMetrics: ["tiktokCompletePayment", "tiktokSpend"],
  metadata: {
    baseId: "tiktokCAC",
    overviewPageMetricId: "tiktokCAC",
    dashboardV2Id: "tiktokCAC",
    tooltip: {
      reported: "TikTok",
      /* eslint-disable-next-line quotes */
      description: '"Customer Acquisition Cost"; cost of acquiring a customer',
      calculationInfo: "TikTok Spend ÷ TikTok Purchases",
    },
  },
  descriptionHTML:
    "Calculated by taking the total spend reported by TikTok and dividing by total " +
    "number of purchases",
  composition: {
    type: CreativeMetricType.tiktok,
    metric: "tiktokCAC",
    customComposed: true,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  spec: {
    id: "tiktokCAC",
    group: CreativeMetricType.tiktok,
    name: "TikTok CAC",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const TikTokThumbstopRate: SalesPageMetric = {
  id: "tiktokThumbstopRate",
  name: "TikTok thumbstop rate",
  format: metrics.MetricTypes.PERCENTAGE,
  formatter: MetricFormatters.percentage,
  isPositiveChangeGood: true,
  isRate: true,
  calculate: (v) => {
    if (v.tiktokVideoViews2s && v.tiktokVideoViews) {
      return v.tiktokVideoViews2s / v.tiktokVideoViews;
    } else {
      return 0;
    }
  },
  requiredBaseMetrics: ["tiktokVideoViews2s", "tiktokVideoViews"],
  composition: {
    type: CreativeMetricType.tiktok,
    metric: "tiktokThumbstopRate",
    customComposed: true,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "tiktokThumbstopRate",
    overviewPageMetricId: "tiktokThumbstopRate",
    dashboardV2Id: "tiktokThumbstopRate",
    tooltip: {
      reported: "TikTok",
      description:
        "Thumbstop rate is calculated by dividing 2-second video views by impressions",
    },
  },
  descriptionHTML:
    "Thumbstop rate is calculated by dividing 2-second video views by impressions",
  spec: {
    id: "tiktokThumbstopRate",
    group: CreativeMetricType.tiktok,
    name: "TikTok thumbstop rate",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const PinterestCheckoutConversions: SalesPageMetric = {
  id: "pinterestCheckoutConversions",
  name: "Pinterest Checkout Conversions",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => {
    return v.pinterestCheckoutConversions;
  },
  requiredBaseMetrics: ["pinterestCheckoutConversions"],
  composition: {
    type: CreativeMetricType.pinterest,
    metric: "pinterest_checkout_conversions",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "pinterestCheckoutConversions",
    overviewPageMetricId: "pinterestCheckoutConversions",
    dashboardV2Id: "pinterestCheckoutConversions",
    tooltip: {
      reported: "Pinterest",
      description:
        "Total number of checkout events that resulted from interactions with your ad. This " +
        "metric may include conversions that are modeled.",
    },
  },
  descriptionHTML:
    "Total number of checkout events that resulted from interactions with your ad. This metric " +
    "may include conversions that are modeled.",
  spec: {
    id: "pinterestCheckoutConversions",
    group: CreativeMetricType.pinterest,
    name: "Pinterest Checkout Conversions",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};
const PinterestCheckoutOrderValue: SalesPageMetric = {
  id: "pinterestCheckoutOrderValue",
  name: "Pinterest Checkout Order Value",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.decimal,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => {
    return v.pinterestCheckoutOrderValue;
  },
  requiredBaseMetrics: ["pinterestCheckoutOrderValue"],
  composition: {
    type: CreativeMetricType.pinterest,
    metric: "pinterest_checkout_order_value",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "pinterestCheckoutOrderValue",
    overviewPageMetricId: "pinterestCheckoutOrderValue",
    dashboardV2Id: "pinterestCheckoutOrderValue",
    tooltip: {
      reported: "Pinterest",
      description:
        "Total value of checkout events resulting from interactions with your ad. This metric " +
        "may include conversions that are modeled",
    },
  },
  descriptionHTML:
    "Total value of checkout events resulting from interactions with your ad. This metric " +
    "may include conversions that are modeled",
  spec: {
    id: "pinterestCheckoutOrderValue",
    group: CreativeMetricType.pinterest,
    name: "Pinterest Checkout Order Value",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};
const PinterestAddToCartRate: SalesPageMetric = {
  id: "pinterestAddToCartRate",
  name: "Pinterest Add To Cart Rate",
  format: metrics.MetricTypes.PERCENTAGE,
  formatter: MetricFormatters.percentage,
  isPositiveChangeGood: true,
  isRate: true,
  calculate: (v) => {
    if (
      v.pinterestAddToCartClickConversions &&
      v.pinterestPageVisitConversions
    ) {
      return (
        v.pinterestAddToCartClickConversions / v.pinterestPageVisitConversions
      );
    } else {
      return 0;
    }
  },
  requiredBaseMetrics: [
    "pinterestAddToCartClickConversions",
    "pinterestPageVisitConversions",
  ],
  composition: {
    type: CreativeMetricType.pinterest,
    metric: "pinterestAddToCartRate",
    customComposed: true,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "pinterestAddToCartRate",
    overviewPageMetricId: "pinterestAddToCartRate",
    dashboardV2Id: "pinterestAddToCartRate",
    tooltip: {
      reported: "Pinterest",
      description:
        "The number of conversions per the number of total ad impressions that can be tracked " +
        "to a conversion during the same time period.",
    },
  },
  descriptionHTML:
    "The number of conversions per the number of total ad impressions that can be tracked to " +
    "a conversion during the same time period.",
  spec: {
    id: "pinterestAddToCartRate",
    group: CreativeMetricType.pinterest,
    name: "Pinterest Add To Cart Rate",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const PinterestPaidImpressions: SalesPageMetric = {
  id: "pinterestPaidImpressions",
  name: "Pinterest Paid Impressions",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => {
    return v.pinterestPaidImpressions;
  },
  requiredBaseMetrics: ["pinterestPaidImpressions"],
  composition: {
    type: CreativeMetricType.pinterest,
    metric: "pinterest_paid_impressions",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "pinterestPaidImpressions",
    overviewPageMetricId: "pinterestPaidImpressions",
    dashboardV2Id: "pinterestPaidImpressions",
    tooltip: {
      reported: "Pinterest",
      description: "The number of times your ad was seen",
    },
  },
  descriptionHTML: "The number of times your ad was seen",
  spec: {
    id: "pinterestPaidImpressions",
    group: CreativeMetricType.pinterest,
    name: "Pinterest Paid Impressions",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const PinterestPaidEngagements: SalesPageMetric = {
  id: "pinterestPaidEngagements",
  name: "Pinterest Paid Engagements",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => {
    return v.pinterestPaidEngagements;
  },
  requiredBaseMetrics: ["pinterestPaidEngagements"],
  composition: {
    type: CreativeMetricType.pinterest,
    metric: "pinterest_paid_engagements",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "pinterestPaidEngagements",
    overviewPageMetricId: "pinterestPaidEngagements",
    dashboardV2Id: "pinterestPaidEngagements",
    tooltip: {
      reported: "Pinterest",
      description: "Paid actions taken as a result of your ads",
    },
  },
  descriptionHTML: "Paid actions taken as a result of your ads",
  spec: {
    id: "pinterestPaidEngagements",
    group: CreativeMetricType.pinterest,
    name: "Pinterest Paid Engagements",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const PinterestPinClick: SalesPageMetric = {
  id: "pinterestPinClicks",
  name: "Pinterest Pin Clicks",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => {
    return v.pinterestPaidPinClicks;
  },
  requiredBaseMetrics: ["pinterestPaidPinClicks"],
  composition: {
    type: CreativeMetricType.pinterest,
    metric: "pinterest_paid_pin_clicks",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "pinterestPinClicks",
    overviewPageMetricId: "pinterestPinClicks",
    dashboardV2Id: "pinterestPinClicks",
    tooltip: {
      reported: "Pinterest",
      description:
        "The total number of clicks on your Pin to content on or off of Pinterest",
    },
  },
  descriptionHTML:
    "The total number of clicks on your Pin to content on or off of Pinterest",
  spec: {
    id: "pinterestPinClicks",
    group: CreativeMetricType.pinterest,
    name: "Pinterest Pin Clicks",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const PinterestPaidSaveRate: SalesPageMetric = {
  id: "pinterestPaidSaveRate",
  name: "Pinterest Paid Save Rate",
  format: metrics.MetricTypes.PERCENTAGE,
  formatter: MetricFormatters.percentage,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => {
    if (v.pinterestPaidSaves && v.pinterestPaidImpressions) {
      return v.pinterestPaidSaves / v.pinterestPaidImpressions;
    } else {
      return 0;
    }
  },
  requiredBaseMetrics: ["pinterestPaidSaves", "pinterestPaidImpressions"],
  composition: {
    type: CreativeMetricType.pinterest,
    metric: "pinterestPaidSaveRate",
    customComposed: true,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "pinterestPaidSaveRate",
    overviewPageMetricId: "pinterestPaidSaveRate",
    dashboardV2Id: "pinterestPaidSaveRate",
    tooltip: {
      reported: "Pinterest",
      description: "Total number of saves for your ad / Impressions",
    },
  },
  descriptionHTML: "Total number of saves for your ad / Impressions",
  spec: {
    id: "pinterestPaidSaveRate",
    group: CreativeMetricType.pinterest,
    name: "Pinterest Paid Save Rate",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const PinterestPaidSaves: SalesPageMetric = {
  id: "pinterestPaidSaves",
  name: "Pinterest Paid Saves",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => {
    return v.pinterestPaidSaves;
  },
  requiredBaseMetrics: ["pinterestPaidSaves"],
  composition: {
    type: CreativeMetricType.pinterest,
    metric: "pinterest_paid_saves",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "pinterestPaidSaves",
    overviewPageMetricId: "pinterestPaidSaves",
    dashboardV2Id: "pinterestPaidSaves",
    tooltip: {
      reported: "Pinterest",
      description: "Total number of saves for your ad",
    },
  },
  descriptionHTML: "Total number of saves for your ad",
  spec: {
    id: "pinterestPaidSaves",
    group: CreativeMetricType.pinterest,
    name: "Pinterest Paid Saves",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const PinterestVideoViews: SalesPageMetric = {
  id: "pinterestPaidVideoViews",
  name: "Pinterest Video Views",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => {
    return v.pinterestPaidVideoViews;
  },
  requiredBaseMetrics: ["pinterestPaidVideoViews"],
  composition: {
    type: CreativeMetricType.pinterest,
    metric: "pinterest_paid_video_views",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "pinterestPaidVideoViews",
    overviewPageMetricId: "pinterestPaidVideoViews",
    dashboardV2Id: "pinterestPaidVideoViews",
    tooltip: {
      reported: "Pinterest",
      description:
        "Total number of times your video ad played continuously for at least 2 seconds " +
        "while 50% visible (paid and earned) - The amount of views for at least 2 seconds " +
        "with 50% of video in view.",
    },
  },
  descriptionHTML:
    "Total number of times your video ad played continuously for at least 2 seconds " +
    "while 50% visible (paid and earned) - The amount of views for at least 2 seconds " +
    "with 50% of video in view.",
  spec: {
    id: "pinterestPaidVideoViews",
    group: CreativeMetricType.pinterest,
    name: "Pinterest Video Views",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};
const PinterestPaid3SecVideoViews: SalesPageMetric = {
  id: "pinterestPaid3SecVideoViews",
  name: "Pinterest Paid 3 second Video Views",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => {
    return v.pinterestPaid3SecVideoViews;
  },
  requiredBaseMetrics: ["pinterestPaid3SecVideoViews"],
  composition: {
    type: CreativeMetricType.pinterest,
    metric: "pinterest_paid_3sec_video_views",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "pinterestPaid3SecVideoViews",
    overviewPageMetricId: "pinterestPaid3SecVideoViews",
    dashboardV2Id: "pinterestPaid3SecVideoViews",
    tooltip: {
      reported: "Pinterest",
      description:
        "Total number of times your video ad played continuously for at least 3 seconds " +
        "while 50% visible (paid and earned)",
    },
  },
  descriptionHTML:
    "Total number of times your video ad played continuously for at least 3 seconds " +
    "while 50% visible (paid and earned)",
  spec: {
    id: "pinterestPaid3SecVideoViews",
    group: CreativeMetricType.pinterest,
    name: "Pinterest Paid 3 second Video Views",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const PinterestPaidVideoPlayedAt50: SalesPageMetric = {
  id: "pinterestPaidVideoPlayedAt50",
  name: "Pinterest Paid Video Played At 50%",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => {
    return v.pinterestPaidVideoPlayedAt50;
  },
  requiredBaseMetrics: ["pinterestPaidVideoPlayedAt50"],
  composition: {
    type: CreativeMetricType.pinterest,
    metric: "pinterest_paid_video_played_at_50",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "pinterestPaidVideoPlayedAt50",
    overviewPageMetricId: "pinterestPaidVideoPlayedAt50",
    dashboardV2Id: "pinterestPaidVideoPlayedAt50",
    tooltip: {
      reported: "Pinterest",
      description:
        "Total number of times your video ad played 50% of its length, including views that " +
        "skipped ahead to this point (paid and earned)",
    },
  },
  descriptionHTML:
    "Total number of times your video ad played 50% of its length, including views that " +
    "skipped ahead to this point (paid and earned)",
  spec: {
    id: "pinterestPaidVideoPlayedAt50",
    group: CreativeMetricType.pinterest,
    name: "Pinterest Paid Video Played At 50%",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const PinterestPaidVideoPlayedAt95: SalesPageMetric = {
  id: "pinterestPaidVideoPlayedAt95",
  name: "Pinterest Paid Video Played At 95%",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => {
    return v.pinterestPaidVideoPlayedAt95;
  },
  requiredBaseMetrics: ["pinterestPaidVideoPlayedAt95"],
  composition: {
    type: CreativeMetricType.pinterest,
    metric: "pinterest_paid_video_played_at_95",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "pinterestPaidVideoPlayedAt95",
    overviewPageMetricId: "pinterestPaidVideoPlayedAt95",
    dashboardV2Id: "pinterestPaidVideoPlayedAt95",
    tooltip: {
      reported: "Pinterest",
      description:
        "Total number of times your video ad played 95% of its length, including views that " +
        "skipped ahead to this point (paid and earned)",
    },
  },
  descriptionHTML:
    "Total number of times your video ad played 95% of its length, including views that skipped " +
    "ahead to this point (paid and earned)",
  spec: {
    id: "pinterestPaidVideoPlayedAt95",
    group: CreativeMetricType.pinterest,
    name: "Pinterest Paid Video Played At 95%",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const PinterestPaidVideoPlayedAt75: SalesPageMetric = {
  id: "pinterestPaidVideoPlayedAt75",
  name: "Pinterest Paid Video Played At 75%",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => {
    return v.pinterestPaidVideoPlayedAt75;
  },
  requiredBaseMetrics: ["pinterestPaidVideoPlayedAt75"],
  composition: {
    type: CreativeMetricType.pinterest,
    metric: "pinterest_paid_video_played_at_75",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "pinterestPaidVideoPlayedAt75",
    overviewPageMetricId: "pinterestPaidVideoPlayedAt75",
    dashboardV2Id: "pinterestPaidVideoPlayedAt75",
    tooltip: {
      reported: "Pinterest",
      description:
        "Total number of times your video ad played 75% of its length, including views that " +
        "skipped ahead to this point (paid and earned)",
    },
  },
  descriptionHTML:
    "Total number of times your video ad played 75% of its length, including views that skipped " +
    "ahead to this point (paid and earned)",
  spec: {
    id: "pinterestPaidVideoPlayedAt75",
    group: CreativeMetricType.pinterest,
    name: "Pinterest Paid Video Played At 75%",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const PinterestPaidVideoPlayedAt100: SalesPageMetric = {
  id: "pinterestPaidVideoPlayedAt100",
  name: "Pinterest Paid Video Played At 100%",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => {
    return v.pinterestPaidVideoPlayedAt100;
  },
  requiredBaseMetrics: ["pinterestPaidVideoPlayedAt100"],
  composition: {
    type: CreativeMetricType.pinterest,
    metric: "pinterest_paid_video_played_at_100",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "pinterestPaidVideoPlayedAt100",
    overviewPageMetricId: "pinterestPaidVideoPlayedAt100",
    dashboardV2Id: "pinterestPaidVideoPlayedAt100",
    tooltip: {
      reported: "Pinterest",
      description:
        "Total number of times your video ad played 100% of its length, including views that " +
        "skipped ahead to this point (paid and earned)",
    },
  },
  descriptionHTML:
    "Total number of times your video ad played 100% of its length, including views that skipped " +
    "ahead to this point (paid and earned)",
  spec: {
    id: "pinterestPaidVideoPlayedAt100",
    group: CreativeMetricType.pinterest,
    name: "Pinterest Paid Video Played At 100%",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const PinterestPaidVideoPlayedAt25: SalesPageMetric = {
  id: "pinterestPaidVideoPlayedAt25",
  name: "Pinterest Paid Video Played At 25%",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => {
    return v.pinterestPaidVideoPlayedAt25;
  },
  requiredBaseMetrics: ["pinterestPaidVideoPlayedAt25"],
  composition: {
    type: CreativeMetricType.pinterest,
    metric: "pinterest_paid_video_played_at_25",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "pinterestPaidVideoPlayedAt25",
    overviewPageMetricId: "pinterestPaidVideoPlayedAt25",
    dashboardV2Id: "pinterestPaidVideoPlayedAt25",
    tooltip: {
      reported: "Pinterest",
      description:
        "Total number of times your video ad played 25% of its length, including views that " +
        "skipped ahead to this point (paid and earned)",
    },
  },
  descriptionHTML:
    "Total number of times your video ad played 25% of its length, including views that skipped " +
    "ahead to this point (paid and earned)",
  spec: {
    id: "pinterestPaidVideoPlayedAt25",
    group: CreativeMetricType.pinterest,
    name: "Pinterest Paid Video Played At 25%",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const PinterestPaidVideoStarts: SalesPageMetric = {
  id: "pinterestPaidVideoStarts",
  name: "Pinterest Paid Video Starts",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => {
    return v.pinterestPaidVideoStarts;
  },
  requiredBaseMetrics: ["pinterestPaidVideoStarts"],
  composition: {
    type: CreativeMetricType.pinterest,
    metric: "pinterest_paid_video_starts",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "pinterestPaidVideoStarts",
    overviewPageMetricId: "pinterestPaidVideoStarts",
    dashboardV2Id: "pinterestPaidVideoStarts",
    tooltip: {
      reported: "Pinterest",
      description:
        "Total number of times your video ad started playing (paid and earned)",
    },
  },
  descriptionHTML:
    "Total number of times your video ad started playing (paid and earned)",
  spec: {
    id: "pinterestPaidVideoStarts",
    group: CreativeMetricType.pinterest,
    name: "Pinterest Paid Video Starts",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const PinterestPaidIdeaPinProductTagVisit: SalesPageMetric = {
  id: "pinterestPaidIdeaPinProductTagVisit",
  name: "Pinterest Paid Idea Pin Product Tag Visit",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => {
    return v.pinterestPaidIdeaPinProductTagVisit;
  },
  requiredBaseMetrics: ["pinterestPaidIdeaPinProductTagVisit"],
  composition: {
    type: CreativeMetricType.pinterest,
    metric: "pinterest_paid_idea_pin_product_tag_visit",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "pinterestPaidIdeaPinProductTagVisit",
    overviewPageMetricId: "pinterestPaidIdeaPinProductTagVisit",
    dashboardV2Id: "pinterestPaidIdeaPinProductTagVisit",
    tooltip: {
      reported: "Pinterest",
      description:
        "The number of times someone taps on a product tagged within an Idea Pin you promoted",
    },
  },
  descriptionHTML:
    "The number of times someone taps on a product tagged within an Idea Pin you promoted",
  spec: {
    id: "pinterestPaidIdeaPinProductTagVisit",
    group: CreativeMetricType.pinterest,
    name: "Pinterest Paid Idea Pin Product Tag Visit",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

// pinterestSpendInAccountCurrency only reports in UTC. We don't shift it to client's
// timezone so we are able to match Pinterest ads manager since it always reports in UTC.
// Because of this, there will be a small discrepancy
// between Northbeam spend and Pinterest reported spend.
const PinterestROAS: SalesPageMetric = {
  id: "pinterestROAS",
  name: "Pinterest ROAS",
  format: metrics.MetricTypes.MULTIPLIER,
  formatter: MetricFormatters.multiplier,
  isPositiveChangeGood: true,
  isRate: true,
  calculate: (v) => {
    if (v.pinterestCheckoutOrderValue) {
      return v.pinterestCheckoutOrderValue / v.pinterestSpendInAccountCurrency;
    } else {
      return 0;
    }
  },
  requiredBaseMetrics: [
    "pinterestCheckoutOrderValue",
    "pinterestSpendInAccountCurrency",
  ],
  composition: {
    type: CreativeMetricType.pinterest,
    metric: "pinterestROAS",
    customComposed: true,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "pinterestROAS",
    overviewPageMetricId: "pinterestROAS",
    dashboardV2Id: "pinterestROAS",
    tooltip: {
      reported: "Pinterest",
      /* eslint-disable-next-line quotes */
      description: '"Return on Ad Spend" from customers',
      calculationInfo: "Pinterest Revenue ÷ Pinterest Marketing Spend",
    },
  },
  descriptionHTML:
    "Pinterest reported ROAS, this is calculated by taking the total revenue reported by " +
    "Pinterest and dividing by spend",
  spec: {
    id: "pinterestROAS",
    group: CreativeMetricType.pinterest,
    name: "Pinterest ROAS",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const PinterestCAC: SalesPageMetric = {
  id: "pinterestCAC",
  name: "Pinterest CAC",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currencyRatio,
  isPositiveChangeGood: true,
  isRate: true,
  calculate: (v) => {
    if (v.pinterestCheckoutConversions) {
      return v.pinterestSpendInAccountCurrency / v.pinterestCheckoutConversions;
    } else {
      return 0;
    }
  },
  requiredBaseMetrics: [
    "pinterestCheckoutConversions",
    "pinterestSpendInAccountCurrency",
  ],
  composition: {
    type: CreativeMetricType.pinterest,
    metric: "pinterestCAC",
    customComposed: true,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "pinterestCAC",
    overviewPageMetricId: "pinterestCAC",
    dashboardV2Id: "pinterestCAC",
    tooltip: {
      reported: "Pinterest",
      /* eslint-disable-next-line quotes */
      description: '"Customer Acquisition Cost"; cost of acquiring a customer',
      calculationInfo: "Pinterest Spend ÷ Pinterest Purchases",
    },
  },
  descriptionHTML:
    "Calculated by taking the total spend reported by Pinterest and dividing by total number " +
    "of purchases",
  spec: {
    id: "pinterestCAC",
    group: CreativeMetricType.pinterest,
    name: "Pinterest CAC",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

export const makeTransactionsOrderSubscription = (
  am: string,
  aw: IMCAttributionWindowDays | "",
  type: "ft" | "recurring" | "other" | "",
  accountingMode: INBAccountingMode,
): SalesPageMetric => {
  const metricKey = (
    {
      ft: "transactionsOrderSubscriptionFirstTime",
      recurring: "transactionsOrderSubscriptionRecurring",
      other: "transactionsOrderSubscriptionOther",
      "": "transactionsOrderSubscription",
    } as const
  )[type] as any;
  const isLtv = aw === "infinity";
  const isAccrualLtv = isLtv && accountingMode === "accrual";
  const id = `${metricKey}${isAccrualLtv ? "Ltv" : ""}`;

  const metricColumn = (
    {
      ft: "TXNS_ORDER_SUBSCRIPTIONS_FIRST_TIME",
      recurring: "TXNS_ORDER_SUBSCRIPTIONS_RECURRING",
      other: "TXNS_ORDER_SUBSCRIPTIONS_OTHER",
      "": "TXNS_ORDER_SUBSCRIPTIONS",
    } as const
  )[type];

  const description = (
    {
      ft: "Includes order tags you've mapped to the 'first time' category",
      recurring:
        "Includes order tags you've mapped to the 'recurring' category",
      other:
        "Includes all order tags that were mapped to 'other', or could not be mapped to " +
        "'recurring' or 'first time'",
      "": "Includes all subscription categories",
    } as const
  )[type];

  const realName = "Subscription transactions";
  const shortName = makeSubscriptionMetricName(
    realName,
    aw,
    type,
    accountingMode,
    true,
  );
  const fullName = makeSubscriptionMetricName(
    realName,
    aw,
    type,
    accountingMode,
  );

  return {
    id,
    name: fullName,
    format: metrics.MetricTypes.INTEGER,
    formatter: MetricFormatters.decimal,
    isPositiveChangeGood: true,
    isRate: false,
    calculate: (v) => findConv(v, am, aw, metricKey),
    requiredBaseMetrics: [metricKey],
    descriptionHTML: description,
    changeFractionAreaTextForTable: aw === "infinity" ? "Realized" : undefined,
    changeFractionAreaTextForTabDisplay:
      aw === "infinity" ? "Realized" : undefined,
    hideComparisonFromTableCell:
      aw === "infinity" && accountingMode === "accrual",
    hideComparisonFromTabDisplay:
      aw === "infinity" && accountingMode === "accrual",
    composition: {
      type: CreativeMetricType.base,
      metric: metricColumn,
      customComposed: false,
      details: {
        aw,
        am,
        accountingMode,
        type: "",
      },
    },
    metadata: {
      baseId: id,
      overviewPageMetricId: id,
      metricId: "subscriptionTxns",
      dashboardV2Id: metricKey,
      isLtv,
      name: shortName,
      type: getFullTypeName(type),
      tooltip: {
        reported: "Northbeam",
        description,
      },
    },
    pagesEnabled: { creativeAnalytics: true },
    spec: {
      id: `${metricKey}${isAccrualLtv ? "Ltv" : ""}`,
      group: "subscriptions",
      name: fullName,
      modifiers: {
        attributionModel: "all",
        attributionWindows: isAccrualLtv
          ? ["infinity"]
          : ["1", "3", "7", "14", "30", "60", "90"],
        accountingModes: isAccrualLtv ? ["accrual"] : ["cash", "accrual"],
      },
    },
  };
};

export const makeRevenueOrderSubscription = (
  am: string,
  aw: IMCAttributionWindowDays | "",
  type: "ft" | "recurring" | "other" | "",
  accountingMode: INBAccountingMode,
): SalesPageMetric => {
  const metricKey = (
    {
      ft: "revenueOrderSubscriptionFirstTime",
      recurring: "revenueOrderSubscriptionRecurring",
      other: "revenueOrderSubscriptionOther",
      "": "revenueOrderSubscription",
    } as const
  )[type] as any;
  const isLtv = aw === "infinity";
  const isAccrualLtv = isLtv && accountingMode === "accrual";
  const id = `${metricKey}${isAccrualLtv ? "Ltv" : ""}`;

  const metricColumn = (
    {
      ft: "REV_ORDER_SUBSCRIPTIONS_FIRST_TIME",
      recurring: "REV_ORDER_SUBSCRIPTIONS_RECURRING",
      other: "REV_ORDER_SUBSCRIPTIONS_OTHER",
      "": "REV_ORDER_SUBSCRIPTIONS",
    } as const
  )[type];

  const description = (
    {
      ft: "Includes order tags you've mapped to the 'first time' category",
      recurring:
        "Includes order tags you've mapped to the 'recurring' category",
      other:
        "Includes all order tags that were mapped to 'other', or could not be mapped to " +
        "'recurring' or 'first time'",
      "": "Includes all subscription categories",
    } as const
  )[type];

  const realName = `Subscription ${
    accountingMode === "accrual" ? "attributed " : ""
  }revenue`;
  const shortName = makeSubscriptionMetricName(
    realName,
    aw,
    type,
    accountingMode,
    true,
  );
  const fullName = makeSubscriptionMetricName(
    realName,
    aw,
    type,
    accountingMode,
  );

  return {
    id,
    name: fullName,
    composition: {
      type: CreativeMetricType.base,
      metric: metricColumn,
      customComposed: false,
      details: {
        aw,
        am,
        accountingMode,
        type: "",
      },
    },
    pagesEnabled: { creativeAnalytics: true },
    format: metrics.MetricTypes.CURRENCY,
    formatter: MetricFormatters.currency,
    isPositiveChangeGood: true,
    isRate: false,
    calculate: (v) => findConv(v, am, aw, metricKey),
    requiredBaseMetrics: [metricKey],
    descriptionHTML: description,
    changeFractionAreaTextForTable: aw === "infinity" ? "Realized" : undefined,
    changeFractionAreaTextForTabDisplay:
      aw === "infinity" ? "Realized" : undefined,
    hideComparisonFromTableCell:
      aw === "infinity" && accountingMode === "accrual",
    hideComparisonFromTabDisplay:
      aw === "infinity" && accountingMode === "accrual",
    metadata: {
      baseId: id,
      overviewPageMetricId: id,
      metricId: "subscriptionRev",
      dashboardV2Id: metricKey,
      name: shortName,
      isLtv,
      type: getFullTypeName(type),
      tooltip: {
        reported: "Northbeam",
        description,
      },
    },
    spec: {
      id: `${metricKey}${accountingMode === "accrual" ? "Attributed" : ""}${
        isAccrualLtv ? "Ltv" : ""
      }`,
      group: "subscriptions",
      name: fullName,
      modifiers: {
        attributionModel: "all",
        attributionWindows: isAccrualLtv
          ? ["infinity"]
          : ["1", "3", "7", "14", "30", "60", "90"],
        accountingModes: isAccrualLtv ? ["accrual"] : ["cash", "accrual"],
      },
    },
  };
};

export const makeCacOrderSubscriptions = (
  am: string,
  aw: IMCAttributionWindowDays | "",
  type: "ft" | "recurring" | "other" | "",
  accountingMode: INBAccountingMode,
): SalesPageMetric => {
  const metricId = (
    {
      ft: "cacOrderSubscriptionFirstTime",
      recurring: "cacOrderSubscriptionRecurring",
      other: "cacOrderSubscriptionOther",
      "": "cacOrderSubscription",
    } as const
  )[type];

  const requiredMetric = (
    {
      ft: "transactionsOrderSubscriptionFirstTime",
      recurring: "transactionsOrderSubscriptionRecurring",
      other: "transactionsOrderSubscriptionOther",
      "": "transactionsOrderSubscription",
    } as const
  )[type] as any;

  const isLtv = aw === "infinity";
  const isAccrualLtv = isLtv && accountingMode === "accrual";
  const id = `${metricId}${isAccrualLtv ? "Ltv" : ""}`;

  const description = (
    {
      ft: "Includes order tags you've mapped to the 'first time' category",
      recurring:
        "Includes order tags you've mapped to the 'recurring' category",
      other:
        "Includes all order tags that were mapped to 'other', or could not be mapped to " +
        "'recurring' or 'first time'",
      "": "Includes all subscription categories",
    } as const
  )[type];

  const realName = "Subscription CAC";
  const shortName = makeSubscriptionMetricName(
    realName,
    aw,
    type,
    accountingMode,
    true,
  );
  const fullName = makeSubscriptionMetricName(
    realName,
    aw,
    type,
    accountingMode,
  );

  return {
    id,
    name: fullName,
    format: metrics.MetricTypes.CURRENCY,
    formatter: MetricFormatters.currencyRatio,
    isPositiveChangeGood: false,
    isRate: true,
    calculate: (v) => v.spend / findConv(v, am, aw, requiredMetric),
    requiredBaseMetrics: [requiredMetric, "spend"],
    descriptionHTML: description,
    changeFractionAreaTextForTable: aw === "infinity" ? "Realized" : undefined,
    changeFractionAreaTextForTabDisplay:
      aw === "infinity" ? "Realized" : undefined,
    hideComparisonFromTableCell:
      aw === "infinity" && accountingMode === "accrual",
    hideComparisonFromTabDisplay:
      aw === "infinity" && accountingMode === "accrual",
    composition: {
      type: CreativeMetricType.base,
      metric: metricId,
      customComposed: true,
      details: {
        aw,
        am,
        accountingMode,
        type: "",
      },
    },
    pagesEnabled: { creativeAnalytics: true },
    metadata: {
      baseId: id,
      overviewPageMetricId: id,
      dashboardV2Id: metricId,
      metricId: "subscriptionCac",
      name: shortName,
      isLtv,
      type: getFullTypeName(type),
      tooltip: {
        reported: "Northbeam",
        description,
      },
    },
    spec: {
      id: `${metricId}${isAccrualLtv ? "Ltv" : ""}`,
      group: "subscriptions",
      name: fullName,
      modifiers: {
        attributionModel: "all",
        attributionWindows: isAccrualLtv
          ? ["infinity"]
          : ["1", "3", "7", "14", "30", "60", "90"],
        accountingModes: isAccrualLtv ? ["accrual"] : ["cash", "accrual"],
      },
    },
  };
};

const MetaOfflineRevenue1DView: SalesPageMetric = {
  id: "metaOfflineRevenue1DView",
  name: "FB Offline purchases conversion value (1D View)",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaOfflineRevenue1DView,
  requiredBaseMetrics: ["metaOfflineRevenue1DView"],
  descriptionHTML:
    "Facebook reported revenue from conversions from offline Facebook attributed to your ads",
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_offline_revenue_1d_view",
    customComposed: false,
  },
  metadata: {
    baseId: "metaOfflineRevenue1DView",
    overviewPageMetricId: "metaOfflineRevenue1DView",
    dashboardV2Id: "metaOfflineRevenue1DView",
    tooltip: {
      reported: "Facebook",
      description:
        "Revenue from conversions from offline events data Facebook attributed to your ads",
    },
  },
  spec: {
    id: "metaOfflineRevenue1DView",
    group: "meta",
    name: "FB Offline purchases conversion value (1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaOfflineRevenue1DClick: SalesPageMetric = {
  id: "metaOfflineRevenue1DClick",
  name: "FB Offline purchases conversion value (1D Click)",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaOfflineRevenue1DClick,
  requiredBaseMetrics: ["metaOfflineRevenue1DClick"],
  descriptionHTML:
    "Facebook reported revenue from conversions from offline Facebook attributed to your ads",
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_offline_revenue_1d_click",
    customComposed: false,
  },
  metadata: {
    baseId: "metaOfflineRevenue1DClick",
    overviewPageMetricId: "metaOfflineRevenue1DClick",
    dashboardV2Id: "metaOfflineRevenue1DClick",
    tooltip: {
      reported: "Facebook",
      description:
        "Revenue from conversions from offline events data Facebook attributed to your ads",
    },
  },
  spec: {
    id: "metaOfflineRevenue1DClick",
    group: "meta",
    name: "FB Offline purchases conversion value (1D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaOfflineRevenue7DClick: SalesPageMetric = {
  id: "metaOfflineRevenue7DClick",
  name: "FB Offline purchases conversion value (7D Click)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaOfflineRevenue7DClick,
  requiredBaseMetrics: ["metaOfflineRevenue7DClick"],
  descriptionHTML:
    "Facebook reported revenue from conversions from offline Facebook attributed to your ads",
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_offline_revenue_7d_click",
    customComposed: false,
  },
  metadata: {
    baseId: "metaOfflineRevenue7DClick",
    overviewPageMetricId: "metaOfflineRevenue7DClick",
    dashboardV2Id: "metaOfflineRevenue7DClick",
    tooltip: {
      reported: "Facebook",
      description:
        "Revenue from conversions from offline events data Facebook attributed to your ads",
    },
  },
  spec: {
    id: "metaOfflineRevenue7DClick",
    group: "meta",
    name: "FB Offline purchases conversion value (7D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaOfflineRevenue7DClick1DView: SalesPageMetric = {
  id: "metaOfflineRevenue7DClick1DView",
  name: "FB Offline purchases conversion value (7D Click, 1D View)",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaOfflineRevenue7DClick1DView,
  requiredBaseMetrics: ["metaOfflineRevenue7DClick1DView"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_offline_revenue_7d_click_1d_view",
    customComposed: false,
  },
  metadata: {
    baseId: "metaOfflineRevenue7DClick1DView",
    overviewPageMetricId: "metaOfflineRevenue7DClick1DView",
    dashboardV2Id: "metaOfflineRevenue7DClick1DView",
    tooltip: {
      reported: "Facebook",
      description:
        "Revenue from conversions from offline events data Facebook attributed to your ads",
    },
  },
  descriptionHTML:
    "Facebook reported revenue from conversions from offline Facebook attributed to your ads",
  spec: {
    id: "metaOfflineRevenue7DClick1DView",
    group: "meta",
    name: "FB Offline purchases conversion value (7D Click, 1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaMobileAppRevenue1DView: SalesPageMetric = {
  id: "metaMobileAppRevenue1DView",
  name: "FB Mobile app purchases conversion value (1D View)",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaMobileAppRevenue1DView,
  requiredBaseMetrics: ["metaMobileAppRevenue1DView"],
  descriptionHTML:
    "Facebook reported revenue from conversions from your mobile app attributed to your ads",
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_mobile_app_revenue_1d_view",
    customComposed: false,
  },
  metadata: {
    baseId: "metaMobileAppRevenue1DView",
    overviewPageMetricId: "metaMobileAppRevenue1DView",
    dashboardV2Id: "metaMobileAppRevenue1DView",
    tooltip: {
      reported: "Facebook",
      description:
        "Revenue from your mobile app that was recorded as app events and attributed to your ads",
    },
  },
  spec: {
    id: "metaMobileAppRevenue1DView",
    group: "meta",
    name: "FB Mobile app purchases conversion value (1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaMobileAppRevenue1DClick: SalesPageMetric = {
  id: "metaMobileAppRevenue1DClick",
  name: "FB Mobile app purchases conversion value (1D Click)",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaMobileAppRevenue1DClick,
  requiredBaseMetrics: ["metaMobileAppRevenue1DClick"],
  descriptionHTML:
    "Facebook reported revenue from conversions from your mobile app attributed to your ads",
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_mobile_app_revenue_1d_click",
    customComposed: false,
  },
  metadata: {
    baseId: "metaMobileAppRevenue1DClick",
    overviewPageMetricId: "metaMobileAppRevenue1DClick",
    dashboardV2Id: "metaMobileAppRevenue1DClick",
    tooltip: {
      reported: "Facebook",
      description:
        "Revenue from your mobile app that was recorded as app events and attributed to your ads",
    },
  },
  spec: {
    id: "metaMobileAppRevenue1DClick",
    group: "meta",
    name: "FB Mobile app purchases conversion value (1D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaMobileAppRevenue7DClick: SalesPageMetric = {
  id: "metaMobileAppRevenue7DClick",
  name: "FB Mobile app purchases conversion value (7D Click)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaMobileAppRevenue7DClick,
  requiredBaseMetrics: ["metaMobileAppRevenue7DClick"],
  descriptionHTML:
    "Facebook reported revenue from conversions from your mobile app attributed to your ads",
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_mobile_app_revenue_7d_click",
    customComposed: false,
  },
  metadata: {
    baseId: "metaMobileAppRevenue7DClick",
    overviewPageMetricId: "metaMobileAppRevenue7DClick",
    dashboardV2Id: "metaMobileAppRevenue7DClick",
    tooltip: {
      reported: "Facebook",
      description:
        "Revenue from your mobile app that was recorded as app events and attributed to your ads",
    },
  },
  spec: {
    id: "metaMobileAppRevenue7DClick",
    group: "meta",
    name: "FB Mobile app purchases conversion value (7D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaMobileAppRevenue7DClick1DView: SalesPageMetric = {
  id: "metaMobileAppRevenue7DClick1DView",
  name: "FB Mobile app purchases conversion value (7D Click, 1D View)",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaMobileAppRevenue7DClick1DView,
  requiredBaseMetrics: ["metaMobileAppRevenue7DClick1DView"],
  descriptionHTML:
    "Facebook reported revenue from conversions from your mobile app attributed to your ads",
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_mobile_app_revenue_7d_click_1d_view",
    customComposed: false,
  },
  metadata: {
    baseId: "metaMobileAppRevenue7DClick1DView",
    overviewPageMetricId: "metaMobileAppRevenue7DClick1DView",
    dashboardV2Id: "metaMobileAppRevenue7DClick1DView",
    tooltip: {
      reported: "Facebook",
      description:
        "Revenue from your mobile app that was recorded as app events and attributed to your ads",
    },
  },
  spec: {
    id: "metaMobileAppRevenue7DClick1DView",
    group: "meta",
    name: "FB Mobile app purchases conversion value (7D Click, 1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaOfflinePurchases1DView: SalesPageMetric = {
  id: "metaOfflinePurchases1DView",
  name: "FB Offline purchases (1D View)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaOfflinePurchases1DView,
  requiredBaseMetrics: ["metaOfflinePurchases1DView"],
  descriptionHTML:
    "Facebook reported number of conversions from offline Facebook attributed to your ads",
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_offline_purchases_1d_view",
    customComposed: false,
  },
  metadata: {
    baseId: "metaOfflinePurchases1DView",
    overviewPageMetricId: "metaOfflinePurchases1DView",
    dashboardV2Id: "metaOfflinePurchases1DView",
    tooltip: {
      reported: "Facebook",
      description:
        "Number of purchase events that were recorded by your offline events data Facebook " +
        "attributed to your ads",
    },
  },
  spec: {
    id: "metaOfflinePurchases1DView",
    group: "meta",
    name: "FB Offline purchases (1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaOfflinePurchases1DClick: SalesPageMetric = {
  id: "metaOfflinePurchases1DClick",
  name: "FB Offline purchases (1D Click)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaOfflinePurchases1DClick,
  requiredBaseMetrics: ["metaOfflinePurchases1DClick"],
  descriptionHTML:
    "Facebook reported number of conversions from offline Facebook attributed to your ads",
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_offline_purchases_1d_click",
    customComposed: false,
  },
  metadata: {
    baseId: "metaOfflinePurchases1DClick",
    overviewPageMetricId: "metaOfflinePurchases1DClick",
    dashboardV2Id: "metaOfflinePurchases1DClick",
    tooltip: {
      reported: "Facebook",
      description:
        "Number of purchase events that were recorded by your offline events data " +
        "Facebook attributed to your ads",
    },
  },
  spec: {
    id: "metaOfflinePurchases1DClick",
    group: "meta",
    name: "FB Offline purchases (1D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaOfflinePurchases7DClick: SalesPageMetric = {
  id: "metaOfflinePurchases7DClick",
  name: "FB Offline purchases (7D Click)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaOfflinePurchases7DClick,
  requiredBaseMetrics: ["metaOfflinePurchases7DClick"],
  descriptionHTML:
    "Facebook reported number of conversions from offline Facebook attributed to your ads",
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_offline_purchases_7d_click",
    customComposed: false,
  },
  metadata: {
    baseId: "metaOfflinePurchases7DClick",
    overviewPageMetricId: "metaOfflinePurchases7DClick",
    dashboardV2Id: "metaOfflinePurchases7DClick",
    tooltip: {
      reported: "Facebook",
      description:
        "Number of purchase events that were recorded by your offline events data Facebook " +
        "attributed to your ads",
    },
  },
  spec: {
    id: "metaOfflinePurchases7DClick",
    group: "meta",
    name: "FB Offline purchases (7D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaOfflinePurchases7DClick1DView: SalesPageMetric = {
  id: "metaOfflinePurchases7DClick1DView",
  name: "FB Offline purchases (7D Click, 1D View)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaOfflinePurchases7DClick1DView,
  requiredBaseMetrics: ["metaOfflinePurchases7DClick1DView"],
  descriptionHTML:
    "Facebook reported number of conversions from offline Facebook attributed to your ads",
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_offline_purchases_7d_click_1d_view",
    customComposed: false,
  },
  metadata: {
    baseId: "metaOfflinePurchases7DClick1DView",
    overviewPageMetricId: "metaOfflinePurchases7DClick1DView",
    dashboardV2Id: "metaOfflinePurchases7DClick1DView",
    tooltip: {
      reported: "Facebook",
      description:
        "Number of purchase events that were recorded by your offline events data " +
        "Facebook attributed to your ads",
    },
  },
  spec: {
    id: "metaOfflinePurchases7DClick1DView",
    group: "meta",
    name: "FB Offline purchases (7D Click, 1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaMobileAppPurchases1DView: SalesPageMetric = {
  id: "metaMobileAppPurchases1DView",
  name: "FB Mobile app purchases (1D View)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaMobileAppPurchases1DView,
  requiredBaseMetrics: ["metaMobileAppPurchases1DView"],
  descriptionHTML:
    "Facebook reported number of conversions from your mobile app attributed to your ads",
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_mobile_app_purchases_1d_view",
    customComposed: false,
  },
  metadata: {
    baseId: "metaMobileAppPurchases1DView",
    overviewPageMetricId: "metaMobileAppPurchases1DView",
    dashboardV2Id: "metaMobileAppPurchases1DView",
    tooltip: {
      reported: "Facebook",
      description:
        "Number of conversions from your mobile app that were recorded as app events and " +
        "attributed to your ads",
    },
  },
  spec: {
    id: "metaMobileAppPurchases1DView",
    group: "meta",
    name: "FB Mobile app purchases (1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaMobileAppPurchases1DClick: SalesPageMetric = {
  id: "metaMobileAppPurchases1DClick",
  name: "FB Mobile app purchases (1D Click)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaMobileAppPurchases1DClick,
  requiredBaseMetrics: ["metaMobileAppPurchases1DClick"],
  descriptionHTML:
    "Facebook reported number of conversions from your mobile app attributed to your ads",
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_mobile_app_purchases_1d_click",
    customComposed: false,
  },
  metadata: {
    baseId: "metaMobileAppPurchases1DClick",
    overviewPageMetricId: "metaMobileAppPurchases1DClick",
    dashboardV2Id: "metaMobileAppPurchases1DClick",
    tooltip: {
      reported: "Facebook",
      description:
        "Number of conversions from your mobile app that were recorded as app events " +
        "and attributed to your ads",
    },
  },
  spec: {
    id: "metaMobileAppPurchases1DClick",
    group: "meta",
    name: "FB Mobile app purchases (1D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaMobileAppPurchases7DClick: SalesPageMetric = {
  id: "metaMobileAppPurchases7DClick",
  name: "FB Mobile app purchases (7D Click)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaMobileAppPurchases7DClick,
  requiredBaseMetrics: ["metaMobileAppPurchases7DClick"],
  descriptionHTML:
    "Facebook reported number of conversions from your mobile app attributed to your ads",
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_mobile_app_purchases_7d_click",
    customComposed: false,
  },
  metadata: {
    baseId: "metaMobileAppPurchases7DClick",
    overviewPageMetricId: "metaMobileAppPurchases7DClick",
    dashboardV2Id: "metaMobileAppPurchases7DClick",
    tooltip: {
      reported: "Facebook",
      description:
        "Number of conversions from your mobile app that were recorded as app events and " +
        "attributed to your ads",
    },
  },
  spec: {
    id: "metaMobileAppPurchases7DClick",
    group: "meta",
    name: "FB Mobile app purchases (7D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaMobileAppPurchases7DClick1DView: SalesPageMetric = {
  id: "metaMobileAppPurchases7DClick1DView",
  name: "FB Mobile app purchases (7D Click, 1D View)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaMobileAppPurchases7DClick1DView,
  requiredBaseMetrics: ["metaMobileAppPurchases7DClick1DView"],
  descriptionHTML:
    "Facebook reported number of conversions from your mobile app attributed to your ads",
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_mobile_app_purchases_7d_click_1d_view",
    customComposed: false,
  },
  metadata: {
    baseId: "metaMobileAppPurchases7DClick1DView",
    overviewPageMetricId: "metaMobileAppPurchases7DClick1DView",
    dashboardV2Id: "metaMobileAppPurchases7DClick1DView",
    tooltip: {
      reported: "Facebook",
      description:
        "Number of conversions from your mobile app that were recorded as app events and " +
        "attributed to your ads",
    },
  },
  spec: {
    id: "metaMobileAppPurchases7DClick1DView",
    group: "meta",
    name: "FB Mobile app purchases (7D Click, 1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const SnapchatPaidImpressions: SalesPageMetric = {
  id: "snapchatPaidImpressions",
  name: "Snapchat Paid Impressions",
  format: metrics.MetricTypes.DECIMAL,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.snapchatPaidImpressions,
  requiredBaseMetrics: ["snapchatPaidImpressions"],
  descriptionHTML:
    "The total number of times your ad was served to a Snapchatter. Tracked when the ad " +
    "fully renders on a device for the first time during a Snapchatter’s viewing session",
  metadata: {
    baseId: "snapchatPaidImpressions",
    overviewPageMetricId: "snapchatPaidImpressions",
    dashboardV2Id: "snapchatPaidImpressions",
    tooltip: {
      reported: "Snapchat",
      description:
        "Number of times your ad was served to a Snapchatter. Tracked when the ad fully " +
        "renders on a device for the first time during a Snapchatter’s viewing session",
    },
  },
  composition: {
    type: CreativeMetricType.snapchat,
    metric: "snapchat_paid_impressions",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  spec: {
    id: "snapchatPaidImpressions",
    group: CreativeMetricType.snapchat,
    name: "Snapchat Paid Impressions",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};
const SnapchatEarnedImpressions: SalesPageMetric = {
  id: "snapchatEarnedImpressions",
  name: "Snapchat Earned Impressions",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.snapchatEarnedImpressions,
  requiredBaseMetrics: ["snapchatEarnedImpressions"],
  descriptionHTML:
    "The total number of times a Snapchatter created an impression for your ad when " +
    "it was not served to them",
  metadata: {
    baseId: "snapchatEarnedImpressions",
    overviewPageMetricId: "snapchatEarnedImpressions",
    dashboardV2Id: "snapchatEarnedImpressions",
    tooltip: {
      reported: "Snapchat",
      description:
        "Number of times a Snapchatter created an impression for your ad when " +
        "it was not served to them",
    },
  },
  composition: {
    type: CreativeMetricType.snapchat,
    metric: "snapchat_earned_impressions",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  spec: {
    id: "snapchatEarnedImpressions",
    group: CreativeMetricType.snapchat,
    name: "Snapchat Earned Impressions",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};
const SnapchatTotalImpressions: SalesPageMetric = {
  id: "snapchatTotalImpressions",
  name: "Snapchat Total Impressions",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.snapchatTotalImpressions,
  requiredBaseMetrics: ["snapchatTotalImpressions"],
  descriptionHTML:
    "The total number of times your ad was served to a Snapchatter - the total of paid " +
    "and earned impressions",
  metadata: {
    baseId: "snapchatTotalImpressions",
    overviewPageMetricId: "snapchatTotalImpressions",
    dashboardV2Id: "snapchatTotalImpressions",
    tooltip: {
      reported: "Snapchat",
      description:
        "The total number of times your ad was served to a Snapchatter",
      calculationInfo:
        "Snapchat Paid Impressions + Snapchat Earned Impressions",
    },
  },
  composition: {
    type: CreativeMetricType.snapchat,
    metric: "snapchat_total_impressions",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  spec: {
    id: "snapchatTotalImpressions",
    group: CreativeMetricType.snapchat,
    name: "Snapchat Total Impressions",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};
const SnapchatSwipes: SalesPageMetric = {
  id: "snapchatSwipes",
  name: "Snapchat Swipes",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.snapchatSwipes,
  requiredBaseMetrics: ["snapchatSwipes"],
  metadata: {
    baseId: "snapchatSwipes",
    overviewPageMetricId: "snapchatSwipes",
    dashboardV2Id: "snapchatSwipes",
    tooltip: {
      reported: "Snapchat",
      description:
        "The total number of times a Snapchatter has swiped up on attachments within your " +
        "Story Ad (aggregated across all attachments)",
    },
  },
  descriptionHTML:
    "The total number of times a Snapchatter has swiped up on attachments within your " +
    "Story Ad (aggregated across all attachments)",
  composition: {
    type: CreativeMetricType.snapchat,
    metric: "snapchat_swipes",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  spec: {
    id: "snapchatSwipes",
    group: CreativeMetricType.snapchat,
    name: "Snapchat Swipes",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const SnapchatSwipesRate: SalesPageMetric = {
  id: "snapchatSwipesRate",
  name: "Snapchat Swipes Rate",
  format: metrics.MetricTypes.PERCENTAGE,
  formatter: MetricFormatters.percentage,
  isPositiveChangeGood: true,
  isRate: true,
  calculate: (v) => {
    if (v.snapchatSwipes && v.snapchatTotalImpressions) {
      return v.snapchatSwipes / v.snapchatTotalImpressions;
    } else {
      return 0;
    }
  },
  requiredBaseMetrics: ["snapchatSwipes", "snapchatTotalImpressions"],
  metadata: {
    baseId: "snapchatSwipesRate",
    overviewPageMetricId: "snapchatSwipesRate",
    dashboardV2Id: "snapchatSwipesRate",
    tooltip: {
      reported: "Snapchat",
      description:
        "The rate at which Snapchatters swiped up on swipeable videos or images within " +
        "your story ad to reveal the attachment below (averaged across all attachments)",
    },
  },
  descriptionHTML:
    "The rate at which Snapchatters swiped up on swipeable videos or images within your " +
    "story ad to reveal the attachment below (averaged across all attachments)",
  composition: {
    type: CreativeMetricType.snapchat,
    metric: "snapchatSwipesRate",
    customComposed: true,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  spec: {
    id: "snapchatSwipesRate",
    group: CreativeMetricType.snapchat,
    name: "Snapchat Swipes Rate",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const SnapchatThumbstopRate: SalesPageMetric = {
  id: "snapchatThumbstopRate",
  name: "Snapchat Thumbstop Rate",
  format: metrics.MetricTypes.PERCENTAGE,
  formatter: MetricFormatters.percentage,
  isPositiveChangeGood: true,
  isRate: true,
  calculate: (v) => {
    if (v.snapchatTotalImpressions && v.snapchatVideoViewsTimeBased) {
      return v.snapchatVideoViewsTimeBased / v.snapchatTotalImpressions;
    } else {
      return 0;
    }
  },
  requiredBaseMetrics: [
    "snapchatVideoViewsTimeBased",
    "snapchatTotalImpressions",
  ],
  metadata: {
    baseId: "snapchatThumbstopRate",
    overviewPageMetricId: "snapchatThumbstopRate",
    dashboardV2Id: "snapchatThumbstopRate",
    tooltip: {
      reported: "Snapchat",
      description:
        "Percentage of your audience who views the first three seconds of your video",
    },
  },
  descriptionHTML: "2 second video views / Total video views",
  composition: {
    type: CreativeMetricType.snapchat,
    metric: "snapchatThumbstopRate",
    customComposed: true,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  spec: {
    id: "snapchatThumbstopRate",
    group: CreativeMetricType.snapchat,
    name: "Snapchat Thumbstop Rate",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};
const SnapchatProfileClicks: SalesPageMetric = {
  id: "snapchatProfileClicks",
  name: "Snapchat Profile Clicks",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.snapchatProfileClicks,
  requiredBaseMetrics: ["snapchatProfileClicks"],
  descriptionHTML:
    "The total number of taps on your Public Profile from your ads",
  composition: {
    type: CreativeMetricType.snapchat,
    metric: "snapchat_profile_clicks",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "snapchatProfileClicks",
    overviewPageMetricId: "snapchatProfileClicks",
    dashboardV2Id: "snapchatProfileClicks",
    tooltip: {
      reported: "Snapchat",
      description:
        "The total number of taps on your Public Profile from your ads",
    },
  },
  spec: {
    id: "snapchatProfileClicks",
    group: CreativeMetricType.snapchat,
    name: "Snapchat Profile Clicks",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};
const SnapchatVideoViews: SalesPageMetric = {
  id: "snapchatVideoViews",
  name: "Snapchat Video Views",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.snapchatVideoViews,
  requiredBaseMetrics: ["snapchatVideoViews"],
  metadata: {
    baseId: "snapchatVideoViews",
    overviewPageMetricId: "snapchatVideoViews",
    dashboardV2Id: "snapchatVideoViews",
    tooltip: {
      reported: "Snapchat",
      description:
        "The total number of impressions that meet the qualifying video view criteria of " +
        "at least 2 seconds of consecutive watch time or a swipe up action on the Top Snap",
    },
  },
  descriptionHTML:
    "The total number of impressions that meet the qualifying video view criteria of at " +
    "least 2 seconds of consecutive watch time or a swipe up action on the Top Snap",
  composition: {
    type: CreativeMetricType.snapchat,
    metric: "snapchat_video_views",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  spec: {
    id: "snapchatVideoViews",
    group: CreativeMetricType.snapchat,
    name: "Snapchat Video Views",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};
const SnapchatVideoViewsTimeBased: SalesPageMetric = {
  id: "snapchatVideoViewsTimeBased",
  name: "Snapchat Video Views Time Based",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.snapchatVideoViewsTimeBased,
  requiredBaseMetrics: ["snapchatVideoViewsTimeBased"],
  metadata: {
    baseId: "snapchatVideoViewsTimeBased",
    overviewPageMetricId: "snapchatVideoViewsTimeBased",
    dashboardV2Id: "snapchatVideoViewsTimeBased",
    tooltip: {
      reported: "Snapchat",
      description:
        "The total number of impressions that meet the qualifying video view criteria of " +
        "at least 2 seconds, not including swipe ups",
    },
  },
  descriptionHTML:
    "The total number of impressions that meet the qualifying video view criteria of at " +
    "least 2 seconds, not including swipe ups",
  composition: {
    type: CreativeMetricType.snapchat,
    metric: "snapchat_video_views_time_based",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  spec: {
    id: "snapchatVideoViewsTimeBased",
    group: CreativeMetricType.snapchat,
    name: "Snapchat Video Views Time Based",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};
const SnapchatVideoViews15S: SalesPageMetric = {
  id: "snapchatVideoViews15S",
  name: "Snapchat Video Views 15s",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.snapchatVideoViews15S,
  requiredBaseMetrics: ["snapchatVideoViews15S"],
  metadata: {
    baseId: "snapchatVideoViews15S",
    overviewPageMetricId: "snapchatVideoViews15S",
    dashboardV2Id: "snapchatVideoViews15S",
    tooltip: {
      reported: "Snapchat",
      description:
        "15 Second Video Views: The total number of impressions that meet the qualifying " +
        "video view criteria of at least 15 seconds, or 97% completion if it's shorter " +
        "than 15 seconds, or a swipe up action on the ad (15 Second Video Views are not " +
        "supported in the Delivery Insight Export)",
    },
  },
  descriptionHTML:
    "15 Second Video Views: The total number of impressions that meet the qualifying video " +
    "view criteria of at least 15 seconds, or 97% completion if it's shorter than 15 seconds, " +
    "or a swipe up action on the ad (15 Second Video Views are not supported in the " +
    "Delivery Insight Export)",
  composition: {
    type: CreativeMetricType.snapchat,
    metric: "snapchat_video_views_15s",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  spec: {
    id: "snapchatVideoViews15S",
    group: CreativeMetricType.snapchat,
    name: "Snapchat Video Views 15s",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};
const SnapchatQuartile1: SalesPageMetric = {
  id: "snapchatQuartile1",
  name: "Snapchat video p25 watched",
  format: metrics.MetricTypes.DECIMAL,
  formatter: MetricFormatters.decimal,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.snapchatQuartile1,
  requiredBaseMetrics: ["snapchatQuartile1"],
  metadata: {
    baseId: "snapchatQuartile1",
    overviewPageMetricId: "snapchatQuartile1",
    dashboardV2Id: "snapchatQuartile1",
    tooltip: {
      reported: "Snapchat",
      description:
        "Video Plays at 25%: Number of times your Top Snap was viewed to 25% or swiped by " +
        "a user. This metric is accredited by the Media Rating Council for some ad formats",
    },
  },
  descriptionHTML:
    "Video Plays at 25%: Number of times your Top Snap was viewed to 25% or swiped by a " +
    "user. This metric is accredited by the Media Rating Council for some ad formats",
  composition: {
    type: CreativeMetricType.snapchat,
    metric: "snapchat_quartile_1",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  spec: {
    id: "snapchatQuartile1",
    group: CreativeMetricType.snapchat,
    name: "Snapchat video p25 watched",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};
const SnapchatQuartile2: SalesPageMetric = {
  id: "snapchatQuartile2",
  name: "Snapchat video p50 watched",
  format: metrics.MetricTypes.DECIMAL,
  formatter: MetricFormatters.decimal,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.snapchatQuartile2,
  requiredBaseMetrics: ["snapchatQuartile2"],
  metadata: {
    baseId: "snapchatQuartile2",
    overviewPageMetricId: "snapchatQuartile2",
    dashboardV2Id: "snapchatQuartile2",
    tooltip: {
      reported: "Snapchat",
      description:
        "Video Plays at 50%: Number of times your Top Snap was viewed to 50% " +
        "or swiped by a user. " +
        "This metric is accredited by the Media Rating Council for some ad formats",
    },
  },
  descriptionHTML:
    "Video Plays at 50%: Number of times your Top Snap was viewed to 50% or " +
    "swiped by a user. " +
    "This metric is accredited by the Media Rating Council for some ad formats",
  composition: {
    type: CreativeMetricType.snapchat,
    metric: "snapchat_quartile_2",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  spec: {
    id: "snapchatQuartile2",
    group: CreativeMetricType.snapchat,
    name: "Snapchat video p50 watched",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};
const SnapchatQuartile3: SalesPageMetric = {
  id: "snapchatQuartile3",
  name: "Snapchat video p75 watched",
  format: metrics.MetricTypes.DECIMAL,
  formatter: MetricFormatters.decimal,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.snapchatQuartile3,
  requiredBaseMetrics: ["snapchatQuartile3"],
  metadata: {
    baseId: "snapchatQuartile3",
    overviewPageMetricId: "snapchatQuartile3",
    dashboardV2Id: "snapchatQuartile3",
    tooltip: {
      reported: "Snapchat",
      description:
        "Video Plays at 75%: Number of times your Top Snap was viewed to 75% or swiped by a " +
        "user. This metric is accredited by the Media Rating Council for some ad formats",
    },
  },
  descriptionHTML:
    "Video Plays at 75%: Number of times your Top Snap was viewed to 75% or swiped by a user. " +
    "This metric is accredited by the Media Rating Council for some ad formats",
  composition: {
    type: CreativeMetricType.snapchat,
    metric: "snapchat_quartile_3",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  spec: {
    id: "snapchatQuartile3",
    group: CreativeMetricType.snapchat,
    name: "Snapchat video p75 watched",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};
const SnapchatViewCompletion: SalesPageMetric = {
  id: "snapchatViewCompletion",
  name: "Snapchat View Completion",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.snapchatViewCompletion,
  requiredBaseMetrics: ["snapchatViewCompletion"],
  metadata: {
    baseId: "snapchatViewCompletion",
    overviewPageMetricId: "snapchatViewCompletion",
    dashboardV2Id: "snapchatViewCompletion",
    tooltip: {
      reported: "Snapchat",
      description:
        "Number of times your Top Snap was viewed to 97% or swiped by a user. This metric is " +
        "accredited by the Media Rating Council for some ad formats",
    },
  },
  descriptionHTML:
    "Number of times your Top Snap was viewed to 97% or swiped by a user. This metric is " +
    "accredited by the Media Rating Council for some ad formats",
  composition: {
    type: CreativeMetricType.snapchat,
    metric: "snapchat_view_completion",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  spec: {
    id: "snapchatViewCompletion",
    group: CreativeMetricType.snapchat,
    name: "Snapchat View Completion",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};
const SnapchatAttachmentTotalViewTimeMillis: SalesPageMetric = {
  id: "snapchatAttachmentTotalViewTimeMillis",
  name: "Snapchat Attachment Total View Time Millis",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.snapchatAttachmentTotalViewTimeMillis,
  requiredBaseMetrics: ["snapchatAttachmentTotalViewTimeMillis"],
  descriptionHTML:
    "The total number of seconds spent viewing your attachment across all swipe ups",
  composition: {
    type: CreativeMetricType.snapchat,
    metric: "snapchat_attachment_total_view_time_millis",
    customComposed: false,
  },
  metadata: {
    baseId: "snapchatAttachmentTotalViewTimeMillis",
    overviewPageMetricId: "snapchatAttachmentTotalViewTimeMillis",
    dashboardV2Id: "snapchatAttachmentTotalViewTimeMillis",
    tooltip: {
      reported: "Snapchat",
      description:
        "The total number of seconds spent viewing your attachment across all swipe ups",
    },
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  spec: {
    id: "snapchatAttachmentTotalViewTimeMillis",
    group: CreativeMetricType.snapchat,
    name: "Snapchat Attachment Total View Time Millis",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const SnapchatRevenue: SalesPageMetric = {
  id: "snapchatRevenue",
  name: "Snapchat Revenue",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.snapchatRevenueMicrocurrency / 1_000_000,
  requiredBaseMetrics: ["snapchatRevenueMicrocurrency"],
  descriptionHTML: "The total value of purchases made in your app or website",
  metadata: {
    baseId: "snapchatRevenue",
    overviewPageMetricId: "snapchatRevenue",
    dashboardV2Id: "snapchatRevenueMicrocurrency",
    tooltip: {
      reported: "Snapchat",
      description:
        "Revenue from conversions from Snapchat attributed to your ads",
    },
  },
  composition: {
    type: CreativeMetricType.snapchat,
    metric: "snapchatRevenue",
    customComposed: true,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  spec: {
    id: "snapchatRevenue",
    group: CreativeMetricType.snapchat,
    name: "Snapchat Revenue",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const SnapchatTransactions: SalesPageMetric = {
  id: "snapchatTransactions",
  name: "Snapchat Transactions",
  format: metrics.MetricTypes.DECIMAL,
  formatter: MetricFormatters.decimal,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.snapchatTransactions,
  requiredBaseMetrics: ["snapchatTransactions"],
  descriptionHTML: "The total number of purchases made in your app or website",
  metadata: {
    baseId: "snapchatTransactions",
    overviewPageMetricId: "snapchatTransactions",
    dashboardV2Id: "snapchatTransactions",
    tooltip: {
      reported: "Snapchat",
      description: "Number of transactions from customers",
    },
  },
  composition: {
    type: CreativeMetricType.snapchat,
    metric: "snapchat_transactions",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  spec: {
    id: "snapchatTransactions",
    group: CreativeMetricType.snapchat,
    name: "Snapchat Transactions",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const SnapchatROAS: SalesPageMetric = {
  id: "snapchatROAS",
  name: "Snapchat ROAS",
  format: metrics.MetricTypes.MULTIPLIER,
  formatter: MetricFormatters.multiplier,
  isPositiveChangeGood: true,
  isRate: true,
  calculate: (v) => {
    if (v.snapchatRevenueMicrocurrency / 1_000_000 && v.snapchatSpend) {
      return v.snapchatRevenueMicrocurrency / 1_000_000 / v.snapchatSpend;
    }
    return 0;
  },
  requiredBaseMetrics: ["snapchatRevenueMicrocurrency", "snapchatSpend"],
  metadata: {
    baseId: "snapchatROAS",
    overviewPageMetricId: "snapchatROAS",
    dashboardV2Id: "snapchatROAS",
    tooltip: {
      reported: "Snapchat",
      /* eslint-disable-next-line quotes */
      description: '"Return on Ad Spend" from customers',
      calculationInfo: "Snapchat Revenue ÷ Snapchat Marketing Spend",
    },
  },
  descriptionHTML:
    "Snapchat reported return on ad spend, this is calculated by taking the total revenue " +
    "reported by Snapchat and dividing by spend",
  composition: {
    type: CreativeMetricType.snapchat,
    metric: "snapchatROAS",
    customComposed: true,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  spec: {
    id: "snapchatROAS",
    group: CreativeMetricType.snapchat,
    name: "Snapchat ROAS",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const SnapchatCAC: SalesPageMetric = {
  id: "snapchatCAC",
  name: "Snapchat CAC",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currencyRatio,
  isPositiveChangeGood: false,
  isRate: true,
  calculate: (v) => {
    if (v.snapchatTransactions && v.snapchatSpend) {
      return v.snapchatSpend / v.snapchatTransactions;
    }
    return 0;
  },
  requiredBaseMetrics: ["snapchatTransactions", "snapchatSpend"],
  metadata: {
    baseId: "snapchatCAC",
    overviewPageMetricId: "snapchatCAC",
    dashboardV2Id: "snapchatCAC",
    tooltip: {
      reported: "Snapchat",
      /* eslint-disable-next-line quotes */
      description: '"Customer Acquisition Cost"; cost of acquiring a customer',
      calculationInfo: "Snapchat Spend ÷ Snapchat Purchases",
    },
  },
  descriptionHTML: "Spend / transactions",
  composition: {
    type: CreativeMetricType.snapchat,
    metric: "snapchatCAC",
    customComposed: true,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  spec: {
    id: "snapchatCAC",
    group: CreativeMetricType.snapchat,
    name: "Snapchat CAC",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const TwitterClicks: SalesPageMetric = {
  id: "twitterClicks",
  name: "Twitter Clicks",
  format: metrics.MetricTypes.DECIMAL,
  formatter: MetricFormatters.decimal,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.twitterClicks,
  requiredBaseMetrics: ["twitterClicks"],
  descriptionHTML: "User clicks a Twitter ad",
  metadata: {
    baseId: "twitterClicks",
    overviewPageMetricId: "twitterClicks",
    dashboardV2Id: "twitterClicks",
    tooltip: {
      reported: "Twitter",
      description: "Number of clicks from users",
    },
  },
  composition: {
    type: CreativeMetricType.twitter,
    metric: "twitter_clicks",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: false,
  },
  spec: {
    id: "twitterClicks",
    group: CreativeMetricType.twitter,
    name: "Twitter Clicks",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const TwitterAppClicks: SalesPageMetric = {
  id: "twitterAppClicks",
  name: "Twitter App Clicks",
  format: metrics.MetricTypes.DECIMAL,
  formatter: MetricFormatters.decimal,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.twitterAppClicks,
  requiredBaseMetrics: ["twitterAppClicks"],
  descriptionHTML: "User clicks a Twitter ad in app",
  metadata: {
    baseId: "twitterAppClicks",
    overviewPageMetricId: "twitterAppClicks",
    dashboardV2Id: "twitterAppClicks",
    tooltip: {
      reported: "Twitter",
      description: "Number of in app clicks from users",
    },
  },
  composition: {
    type: CreativeMetricType.twitter,
    metric: "twitter_app_clicks",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: false,
  },
  spec: {
    id: "twitterAppClicks",
    group: CreativeMetricType.twitter,
    name: "Twitter App Clicks",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const TwitterLikes: SalesPageMetric = {
  id: "twitterLikes",
  name: "Twitter Likes",
  format: metrics.MetricTypes.DECIMAL,
  formatter: MetricFormatters.decimal,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.twitterLikes,
  requiredBaseMetrics: ["twitterLikes"],
  descriptionHTML: "Twitter likes",
  metadata: {
    baseId: "twitterLikes",
    overviewPageMetricId: "twitterLikes",
    dashboardV2Id: "twitterLikes",
    tooltip: {
      reported: "Twitter",
      description: "Number of likes from users",
    },
  },
  composition: {
    type: CreativeMetricType.twitter,
    metric: "twitter_likes",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: false,
  },
  spec: {
    id: "twitterLikes",
    group: CreativeMetricType.twitter,
    name: "Twitter Likes",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const TwitterFollows: SalesPageMetric = {
  id: "twitterFollows",
  name: "Twitter Follows",
  format: metrics.MetricTypes.DECIMAL,
  formatter: MetricFormatters.decimal,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.twitterFollows,
  requiredBaseMetrics: ["twitterFollows"],
  descriptionHTML: "Twitter Follows",
  metadata: {
    baseId: "twitterFollows",
    overviewPageMetricId: "twitterFollows",
    dashboardV2Id: "twitterFollows",
    tooltip: {
      reported: "Twitter",
      description: "Twitter Follows",
    },
  },
  composition: {
    type: CreativeMetricType.twitter,
    metric: "twitter_follows",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: false,
  },
  spec: {
    id: "twitterFollows",
    group: CreativeMetricType.twitter,
    name: "Twitter Follows",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const TwitterRetweets: SalesPageMetric = {
  id: "twitterRetweets",
  name: "Twitter Retweets",
  format: metrics.MetricTypes.DECIMAL,
  formatter: MetricFormatters.decimal,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.twitterRetweets,
  requiredBaseMetrics: ["twitterRetweets"],
  descriptionHTML: "Twitter Retweets",
  metadata: {
    baseId: "twitterRetweets",
    overviewPageMetricId: "twitterRetweets",
    dashboardV2Id: "twitterRetweets",
    tooltip: {
      reported: "Twitter",
      description: "Twitter Retweets",
    },
  },
  composition: {
    type: CreativeMetricType.twitter,
    metric: "twitter_retweets",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: false,
  },
  spec: {
    id: "twitterRetweets",
    group: CreativeMetricType.twitter,
    name: "Twitter Retweets",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const TwitterUnfollows: SalesPageMetric = {
  id: "twitterUnfollows",
  name: "Twitter Unfollows",
  format: metrics.MetricTypes.DECIMAL,
  formatter: MetricFormatters.decimal,
  isPositiveChangeGood: false,
  isRate: false,
  calculate: (v) => v.twitterUnfollows,
  requiredBaseMetrics: ["twitterUnfollows"],
  descriptionHTML: "Twitter Unfollows",
  metadata: {
    baseId: "twitterUnfollows",
    overviewPageMetricId: "twitterUnfollows",
    dashboardV2Id: "twitterUnfollows",
    tooltip: {
      reported: "Twitter",
      description: "Twitter Unfollows",
    },
  },
  composition: {
    type: CreativeMetricType.twitter,
    metric: "twitter_unfollows",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: false,
  },
  spec: {
    id: "twitterUnfollows",
    group: CreativeMetricType.twitter,
    name: "Twitter Unfollows",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const TwitterEngagements: SalesPageMetric = {
  id: "twitterEngagements",
  name: "Twitter Engagements",
  format: metrics.MetricTypes.DECIMAL,
  formatter: MetricFormatters.decimal,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.twitterEngagements,
  requiredBaseMetrics: ["twitterEngagements"],
  descriptionHTML: "Twitter Engagements",
  metadata: {
    baseId: "twitterEngagements",
    overviewPageMetricId: "twitterEngagements",
    dashboardV2Id: "twitterEngagements",
    tooltip: {
      reported: "Twitter",
      description: "Twitter Engagements",
    },
  },
  composition: {
    type: CreativeMetricType.twitter,
    metric: "twitter_engagements",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: false,
  },
  spec: {
    id: "twitterEngagements",
    group: CreativeMetricType.twitter,
    name: "Twitter Engagements",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const TwitterReplies: SalesPageMetric = {
  id: "twitterReplies",
  name: "Twitter Replies",
  format: metrics.MetricTypes.DECIMAL,
  formatter: MetricFormatters.decimal,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.twitterReplies,
  requiredBaseMetrics: ["twitterReplies"],
  descriptionHTML: "Twitter Replies",
  metadata: {
    baseId: "twitterReplies",
    overviewPageMetricId: "twitterReplies",
    dashboardV2Id: "twitterReplies",
    tooltip: {
      reported: "Twitter",
      description: "Twitter Replies",
    },
  },
  composition: {
    type: CreativeMetricType.twitter,
    metric: "twitter_replies",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: false,
  },
  spec: {
    id: "twitterReplies",
    group: CreativeMetricType.twitter,
    name: "Twitter Replies",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const TwitterUrlClicks: SalesPageMetric = {
  id: "twitterUrlClicks",
  name: "Twitter URL Clicks",
  format: metrics.MetricTypes.DECIMAL,
  formatter: MetricFormatters.decimal,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.twitterUrlClicks,
  requiredBaseMetrics: ["twitterUrlClicks"],
  descriptionHTML: "Twitter URL Clicks",
  metadata: {
    baseId: "twitterUrlClicks",
    overviewPageMetricId: "twitterUrlClicks",
    dashboardV2Id: "twitterUrlClicks",
    tooltip: {
      reported: "Twitter",
      description: "Twitter URL Clicks",
    },
  },
  composition: {
    type: CreativeMetricType.twitter,
    metric: "twitter_url_clicks",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: false,
  },
  spec: {
    id: "twitterUrlClicks",
    group: CreativeMetricType.twitter,
    name: "Twitter URL Clicks",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

function findConv(
  v: metrics.MetricPoint,
  am: string,
  aw: IMCAttributionWindowDays | "",
  metric: Exclude<keyof metrics.MetricPoint["convs"][0], "aw" | "am">,
) {
  return v.convs.find((w) => w.am === am && w.aw === aw)?.[metric] ?? 0;
}

function findGoal(
  v: metrics.MetricPoint,
  am: string,
  aw: IMCAttributionWindowDays | "",
  gt: string,
  metric: Exclude<keyof metrics.MetricPoint["goals"][0], "aw" | "am" | "gt">,
) {
  return (
    v.goals.find((w) => w.am === am && w.aw === aw && w.gt === gt)?.[metric] ??
    0
  );
}

function makeMetricName(
  base: string,
  am: string,
  aw: IMCAttributionWindowDays | "",
  type: "ft" | "rtn" | "",
  accountingMode: INBAccountingMode,
  skipAttributionWindow?: boolean,
) {
  if (accountingMode === "cash") {
    if (base === "Attributed Rev") {
      base = "Rev";
    }

    if (base === "Attributed Transactions") {
      base = "Transactions";
    }

    if (base === "ROAS") {
      base = "MER";
    }
  }

  const parts: string[] = [];
  if (aw === "infinity") {
    if (accountingMode === "accrual") {
      base = `LTV ${base}`;
    }
  } else if (!skipAttributionWindow) {
    parts.push(`${aw}d`);
  }

  if (type) {
    parts.push({ ft: "1st time", rtn: "Returning" }[type]);
  }

  let partsSuffix = "";
  if (parts.length > 0) {
    partsSuffix = ` (${parts.join(", ")})`;
  }
  return `${base}${partsSuffix}`;
}

function makeSubscriptionMetricName(
  base: string,
  aw: IMCAttributionWindowDays | "",
  type: "ft" | "recurring" | "other" | "",
  accountingMode: INBAccountingMode,
  skipAttributionWindow?: boolean,
) {
  const parts: string[] = [];
  if (aw === "infinity") {
    if (accountingMode === "accrual") {
      base = `LTV ${base}`;
    }
  } else if (!skipAttributionWindow) {
    parts.push(`${aw}d`);
  }

  if (type) {
    parts.push(
      { ft: "1st time", recurring: "Recurring", other: "Other" }[type],
    );
  }

  let partsSuffix = "";
  if (parts.length > 0) {
    partsSuffix = ` (${parts.join(", ")})`;
  }
  return `${base}${partsSuffix}`;
}

function makeMetricSpecName(
  base: string,
  type: "ft" | "rtn" | "",
  attributionWindow: IMCAttributionWindowDays | "",
  accountingMode: INBAccountingMode,
) {
  if (accountingMode === "cash") {
    if (base === "Attributed Rev") {
      base = "Rev";
    }

    if (base === "Attributed Transactions") {
      base = "Transactions";
    }

    if (base === "ROAS") {
      base = "MER";
    }
  }

  if (accountingMode === "accrual" && attributionWindow === "infinity") {
    base = `LTV ${base}`;
  }

  let partsSuffix = "";

  if (type) {
    const txt = { ft: "1st time", rtn: "Returning" }[type];
    partsSuffix = ` (${txt})`;
  }

  return `${base}${partsSuffix}`;
}

function makeMetricId(
  base: string,
  am: string,
  aw: IMCAttributionWindowDays | "",
  type: "other" | "recurring" | "ft" | "rtn" | "",
  accountingMode: INBAccountingMode,
) {
  return `${base}__${am}__${aw || "none"}__${type}__${accountingMode}`;
}

function makeSalesTableMetricName(
  base: string,
  am: string,
  aw: IMCAttributionWindowDays | "",
  type: "other" | "recurring" | "ft" | "rtn" | "",
  accountingMode: INBAccountingMode,
) {
  if (accountingMode === "cash") {
    if (base === "Attributed Rev") {
      base = "Rev";
    }
    if (base === "Attributed Transactions") {
      base = "Transactions";
    }
    if (base === "ROAS") {
      base = "MER";
    }

    return base;
  }

  if (aw === "infinity") {
    return `LTV ${base}`;
  }
  return base;
}

function makeSalesTableMetricId(
  base: string,
  am: string,
  aw: IMCAttributionWindowDays | "",
  type: "other" | "recurring" | "ft" | "rtn" | "",
  accountingMode: INBAccountingMode,
) {
  let id = makeSalesTableMetricName(base, am, aw, type, accountingMode);
  // There can be multiple forecasted columns at a time with different attribution windows
  if (base.includes("Forecasted")) {
    id += `__${aw}`;
  } else if (aw === "infinity") {
    id += "__infinity";
  }

  if (type) {
    id += `__${type}`;
  }
  return id;
}

function makeAttributionWindowDescription(aw: IMCAttributionWindowDays) {
  if (aw === "infinity") {
    return `This LTV metric is reported with an <b>infinity-day attribution window</b>.
    Touchpoints receive credit for both delayed transactions and repeat
    transactions.`;
  }

  return `This metric is reported with a <b>${aw}-day attribution window</b>.
    Touchpoints only receive credit for transactions that happen within ${aw}
    ${aw === "1" ? "day" : "days"} of the touchpoint.`;
}

export function makeRoas(
  am: string,
  aw: IMCAttributionWindowDays | "",
  awDescriptionHTML: string,
  type: "ft" | "rtn" | "",
  accountingMode: INBAccountingMode,
): SalesPageMetric {
  const fullTypeName = getFullTypeName(type);
  const common = {
    id: makeMetricId("ROAS", am, aw, type, accountingMode),
    name: makeMetricName("ROAS", am, aw, type, accountingMode),
    dashboardV2Name:
      (accountingMode === "accrual" ? "ROAS" : "MER") +
      (type === "ft" ? " (New)" : type === "rtn" ? " (Returning)" : ""),
    format: metrics.MetricTypes.MULTIPLIER,
    formatter: MetricFormatters.multiplier,
    isPositiveChangeGood: true,
    isRate: true,
    changeFractionAreaTextForTable: aw === "infinity" ? "Realized" : undefined,
    changeFractionAreaTextForTabDisplay:
      aw === "infinity" ? "Realized" : undefined,
    hideComparisonFromTableCell:
      aw === "infinity" && accountingMode === "accrual",
    hideComparisonFromTabDisplay:
      aw === "infinity" && accountingMode === "accrual",
    platformEquivalent: {
      fb: {
        id:
          {
            "1-northbeam_custom": "metaROAS1DClick",
            "7-northbeam_custom": "metaROAS7DClick",
            "7-northbeam_custom__va": "metaROAS7DClick1DView",
          }[`${aw}-${am}` as string] ?? "metaROAS7DClick1DView",
      },
      tiktok: {
        id: "tiktokROAS",
      },
      snapchat: {
        id: "snapchatROAS",
      },
      adwords: {
        id: "googleROAS",
      },
      youtube: {
        id: "googleROAS",
      },
      pinterest: {
        id: "pinterestROAS",
      },
    },
  } as const;

  let descriptionHTML = "Media efficiency ratio - computed as Rev ÷ Spend";

  if (accountingMode === "accrual") {
    descriptionHTML = "Return on Ad Spend - computed as Rev ÷ Spend";
    if (awDescriptionHTML) {
      descriptionHTML += `<hr />${awDescriptionHTML}`;
    }
  }

  const metricKey = (
    {
      ft: "ftRev",
      rtn: "rtnRev",
      "": "rev",
    } as const
  )[type];

  const idType = (
    {
      ft: "Ft",
      rtn: "Rtn",
      "": "",
    } as const
  )[type];

  const id = accountingMode === "accrual" ? "roas" : "mer";
  const ltv = accountingMode === "accrual" && aw === "infinity" ? "Ltv" : "";

  const specId = `${id}${idType}${ltv}`;
  return {
    calculate: (v) => findConv(v, am, aw, metricKey) / v.spend,
    requiredBaseMetrics: ["spend", metricKey],
    descriptionHTML,
    ...common,
    composition: {
      type: CreativeMetricType.base,
      metric: "roas",
      details: {
        aw,
        am,
        accountingMode,
        type,
      },
      customComposed: true,
    },
    pagesEnabled: {
      creativeAnalytics: accountingMode === "accrual" && aw !== "infinity",
    },
    metadata: {
      baseId: `roas${idType}`,
      overviewPageMetricId: `roas${idType}${ltv}`,
      dashboardV2Id: `roas${idType}`,
      overviewPageMetricName: makeMetricName(
        "ROAS",
        am,
        aw,
        type,
        accountingMode,
        true,
      ),
      metricId: "roas",
      name: makeSalesTableMetricName("ROAS", am, aw, type, accountingMode),
      isLtv: aw === "infinity",
      type: fullTypeName,
      salesTableMetricId: makeSalesTableMetricId(
        "ROAS",
        am,
        aw,
        type,
        accountingMode,
      ),
      tooltip: {
        reported: "Northbeam",
        description: `"Return on Ad Spend" from ${fullTypeName} customers`,
        calculationInfo: `Total Revenue from ${fullTypeName} Customers ÷ Total Marketing Spend`,
        showAttributionWindowInfo: true,
      },
    },
    spec: {
      id: specId,
      group: "default",
      name: makeMetricSpecName("ROAS", type, aw, accountingMode),
      modifiers: {
        attributionModel: "all",
        attributionWindows:
          accountingMode === "accrual" && aw === "infinity"
            ? ["infinity"]
            : ["1", "3", "7", "14", "30", "60", "90"],
        accountingModes: [accountingMode],
      },
    },
  };
}

export function makeASP(
  am: string,
  aw: IMCAttributionWindowDays | "",
  awDescriptionHTML: string,
  type: "ft" | "rtn" | "",
  accountingMode: INBAccountingMode,
): SalesPageMetric {
  const metricName = makeMetricName("AOV", am, aw, type, accountingMode);
  const fullTypeName = getFullTypeName(type);
  const common = {
    id: makeMetricId("ASP", am, aw, type, accountingMode),
    name: metricName,
    format: metrics.MetricTypes.CURRENCY,
    formatter: MetricFormatters.currency,
    isPositiveChangeGood: true,
    isRate: true,
    changeFractionAreaTextForTable: aw === "infinity" ? "Realized" : undefined,
    changeFractionAreaTextForTabDisplay:
      aw === "infinity" ? "Realized" : undefined,
    hideComparisonFromTableCell:
      aw === "infinity" && accountingMode === "accrual",
    hideComparisonFromTabDisplay:
      aw === "infinity" && accountingMode === "accrual",
  } as const;

  let descriptionHTML =
    "Average Order Value - computed as Revenue / Transactions";

  if (accountingMode === "accrual" && awDescriptionHTML) {
    descriptionHTML += `<hr />${awDescriptionHTML}`;
  }

  const revMetricKey = (
    {
      ft: "ftRev",
      rtn: "rtnRev",
      "": "rev",
    } as const
  )[type];

  const unitsMetricKey = (
    {
      ft: "ftQuantity",
      rtn: "rtnQuantity",
      "": "quantity",
    } as const
  )[type];

  const idType = (
    {
      ft: "Ft",
      rtn: "Rtn",
      "": "",
    } as const
  )[type];

  const ltv = accountingMode === "accrual" && aw === "infinity" ? "Ltv" : "";
  const specId = `aov${idType}${ltv}`;

  return {
    calculate: (v: any) => {
      const denominator = v.convs[0][unitsMetricKey] || 0;
      const MINIMUM_TRANSACTIONS_FOR_AOV_CALCULATION = 10 ** -3;

      if (denominator >= MINIMUM_TRANSACTIONS_FOR_AOV_CALCULATION) {
        return findConv(v, am, aw, revMetricKey) / denominator;
      }
      return 0;
    },
    requiredBaseMetrics: [revMetricKey, unitsMetricKey],
    descriptionHTML,
    ...common,
    composition: {
      type: CreativeMetricType.base,
      metric: "aov",
      details: {
        aw,
        am,
        accountingMode,
        type,
      },
      customComposed: true,
    },
    pagesEnabled: {
      creativeAnalytics: accountingMode === "accrual" && aw !== "infinity",
    },
    metadata: {
      baseId: `aov${idType}`,
      overviewPageMetricId: specId,
      dashboardV2Id: `aov${idType}`,
      metricId: "aov",
      name: makeSalesTableMetricName("AOV", am, aw, type, accountingMode),
      overviewPageMetricName: makeMetricName(
        "AOV",
        am,
        aw,
        type,
        accountingMode,
        true,
      ),
      isLtv: aw === "infinity",
      type: fullTypeName,
      salesTableMetricId: makeSalesTableMetricId(
        "AOV",
        am,
        aw,
        type,
        accountingMode,
      ),
      tooltip: {
        reported: "Northbeam",
        description: `"Average Order Value" placed by ${fullTypeName} customers`,
        calculationInfo: `Total Revenue from ${fullTypeName} Customers ÷ Total Number of Transactions from ${fullTypeName} Customers`,
        showAttributionWindowInfo: true,
      },
    },
    spec: {
      id: specId,
      group: "default",
      name: makeMetricSpecName("AOV", type, aw, accountingMode),
      modifiers: {
        attributionModel: "all",
        attributionWindows:
          accountingMode === "accrual" && aw === "infinity"
            ? ["infinity"]
            : ["1", "3", "7", "14", "30", "60", "90"],
        accountingModes:
          accountingMode === "accrual" && aw === "infinity"
            ? ["accrual"]
            : ["cash", "accrual"],
      },
    },
  };
}

export function makeAOV(
  am: string,
  aw: IMCAttributionWindowDays | "",
  awDescriptionHTML: string,
  type: "ft" | "rtn" | "",
  accountingMode: INBAccountingMode,
): SalesPageMetric {
  const metricName = makeMetricName("AOV", am, aw, type, accountingMode);
  const fullTypeName = getFullTypeName(type);
  const common = {
    id: makeMetricId("AOV", am, aw, type, accountingMode),
    name: metricName,
    format: metrics.MetricTypes.CURRENCY,
    formatter: MetricFormatters.currency,
    isPositiveChangeGood: true,
    isRate: true,
    changeFractionAreaTextForTable: aw === "infinity" ? "Realized" : undefined,
    changeFractionAreaTextForTabDisplay:
      aw === "infinity" ? "Realized" : undefined,
    hideComparisonFromTableCell:
      aw === "infinity" && accountingMode === "accrual",
    hideComparisonFromTabDisplay:
      aw === "infinity" && accountingMode === "accrual",
  } as const;

  let descriptionHTML =
    "Average Order Value - computed as Revenue / Transactions";

  if (accountingMode === "accrual" && awDescriptionHTML) {
    descriptionHTML += `<hr />${awDescriptionHTML}`;
  }

  const revMetricKey = (
    {
      ft: "ftRev",
      rtn: "rtnRev",
      "": "rev",
    } as const
  )[type];

  const txnsMetricKey = (
    {
      ft: "ftTxns",
      rtn: "rtnTxns",
      "": "txns",
    } as const
  )[type];

  const idType = (
    {
      ft: "Ft",
      rtn: "Rtn",
      "": "",
    } as const
  )[type];

  const ltv = accountingMode === "accrual" && aw === "infinity" ? "Ltv" : "";
  const specId = `aov${idType}${ltv}`;

  return {
    calculate: (v) => {
      const denominator = findConv(v, am, aw, txnsMetricKey);
      const MINIMUM_TRANSACTIONS_FOR_AOV_CALCULATION = 10 ** -3;

      if (denominator >= MINIMUM_TRANSACTIONS_FOR_AOV_CALCULATION) {
        return findConv(v, am, aw, revMetricKey) / denominator;
      }
      return 0;
    },
    requiredBaseMetrics: [revMetricKey, txnsMetricKey],
    descriptionHTML,
    ...common,
    composition: {
      type: CreativeMetricType.base,
      metric: "aov",
      details: {
        aw,
        am,
        accountingMode,
        type,
      },
      customComposed: true,
    },
    pagesEnabled: {
      creativeAnalytics: accountingMode === "accrual" && aw !== "infinity",
    },
    metadata: {
      baseId: `aov${idType}`,
      overviewPageMetricId: specId,
      dashboardV2Id: `aov${idType}`,
      metricId: "aov",
      name: makeSalesTableMetricName("AOV", am, aw, type, accountingMode),
      overviewPageMetricName: makeMetricName(
        "AOV",
        am,
        aw,
        type,
        accountingMode,
        true,
      ),
      isLtv: aw === "infinity",
      type: fullTypeName,
      salesTableMetricId: makeSalesTableMetricId(
        "AOV",
        am,
        aw,
        type,
        accountingMode,
      ),
      tooltip: {
        reported: "Northbeam",
        description: `"Average Order Value" placed by ${fullTypeName} customers`,
        calculationInfo: `Total Revenue from ${fullTypeName} Customers ÷ Total Number of Transactions from ${fullTypeName} Customers`,
        showAttributionWindowInfo: true,
      },
    },
    spec: {
      id: specId,
      group: "default",
      name: makeMetricSpecName("AOV", type, aw, accountingMode),
      modifiers: {
        attributionModel: "all",
        attributionWindows:
          accountingMode === "accrual" && aw === "infinity"
            ? ["infinity"]
            : ["1", "3", "7", "14", "30", "60", "90"],
        accountingModes:
          accountingMode === "accrual" && aw === "infinity"
            ? ["accrual"]
            : ["cash", "accrual"],
      },
    },
  };
}

function makeCustomGoal(
  am: string,
  aw: IMCAttributionWindowDays | "",
  awDescriptionHTML: string,
  accountingMode: INBAccountingMode,
  customGoalId: string,
  customGoalNameSignular: string,
  attributionMethodEnum: any,
): SalesPageMetric {
  const pluralGoalName = pluralize(customGoalNameSignular);
  const common = {
    id: makeMetricId(pluralGoalName, am, aw, "", accountingMode),
    name: makeMetricName(pluralGoalName, am, aw, "", accountingMode),
    format: metrics.MetricTypes.DECIMAL,
    formatter: MetricFormatters.decimal,
    isPositiveChangeGood: true,
    isRate: false,
    changeFractionAreaTextForTable: aw === "infinity" ? "Realized" : undefined,
    changeFractionAreaTextForTabDisplay:
      aw === "infinity" ? "Realized" : undefined,
    hideComparisonFromTableCell:
      aw === "infinity" && accountingMode === "accrual",
    hideComparisonFromTabDisplay:
      aw === "infinity" && accountingMode === "accrual",
  } as const;

  let descriptionHTML = `${pluralGoalName} attributable to this ad using the ${attributionMethodEnum.getLabel(
    am,
  )} attribution model.`;
  if (awDescriptionHTML) {
    descriptionHTML += `<hr />${awDescriptionHTML}`;
  }

  const ltv = accountingMode === "accrual" && aw === "infinity" ? "Ltv" : "";

  return {
    calculate: (v) => findGoal(v, am, aw, customGoalId, "gc"),
    requiredBaseMetrics: ["customGoals"],
    descriptionHTML,
    ...common,
    composition: {
      type: CreativeMetricType.custom,
      metric: customGoalId,
      customComposed: false,
      details: {
        aw,
        accountingMode,
        am,
        type: "",
      },
    },
    pagesEnabled: { creativeAnalytics: true },
    metadata: {
      baseId: `customGoal:${customGoalId}`,
      overviewPageMetricId: `customGoal${ltv}:${customGoalId}`,
      dashboardV2Id: `customGoals:${customGoalId}`,
      overviewPageMetricName: makeMetricName(
        pluralGoalName,
        am,
        aw,
        "",
        accountingMode,
        true,
      ),
      isGoalRelated: true,
      name: makeSalesTableMetricName(
        pluralGoalName,
        am,
        aw,
        "",
        accountingMode,
      ),
      salesTableMetricId: makeSalesTableMetricId(
        pluralGoalName,
        am,
        aw,
        "",
        accountingMode,
      ),
      tooltip: {
        reported: "Northbeam",
        description: `${customGoalNameSignular} attributable to this ad using the ${attributionMethodEnum.getLabel(
          am,
        )} attribution model`,
        showAttributionWindowInfo: true,
      },
    },
    spec: {
      id: `customGoal${ltv}:${customGoalId}`,
      group: "default",
      name: makeMetricName(pluralGoalName, am, aw, "", accountingMode),
      modifiers: {
        attributionModel: "all",
        attributionWindows:
          accountingMode === "accrual" && aw === "infinity"
            ? ["infinity"]
            : ["1", "3", "7", "14", "30", "60", "90"],
        accountingModes:
          accountingMode === "accrual" && aw === "infinity"
            ? ["accrual"]
            : ["cash", "accrual"],
      },
    },
  };
}

function makeCustomGoalCost(
  am: string,
  aw: IMCAttributionWindowDays | "",
  awDescriptionHTML: string,
  accountingMode: INBAccountingMode,
  customGoalId: string,
  customGoalNameSignular: string,
  attributionMethodEnum: any,
): SalesPageMetric {
  const name = `Cost per ${customGoalNameSignular}`;

  const common = {
    id: makeMetricId(name, am, aw, "", accountingMode),
    name: makeMetricName(name, am, aw, "", accountingMode),
    format: metrics.MetricTypes.CURRENCY,
    formatter: MetricFormatters.currency,
    isPositiveChangeGood: false,
    isRate: false,
    changeFractionAreaTextForTable: aw === "infinity" ? "Realized" : undefined,
    changeFractionAreaTextForTabDisplay:
      aw === "infinity" ? "Realized" : undefined,
    hideComparisonFromTableCell:
      aw === "infinity" && accountingMode === "accrual",
    hideComparisonFromTabDisplay:
      aw === "infinity" && accountingMode === "accrual",
  } as const;

  let descriptionHTML = `Average cost of new visits that end in an ${customGoalNameSignular}.`;
  if (awDescriptionHTML) {
    descriptionHTML += `<hr />${awDescriptionHTML}`;
  }

  const ltv = accountingMode === "accrual" && aw === "infinity" ? "Ltv" : "";
  const specId = `customGoalCost${ltv}:${customGoalId}`;

  return {
    calculate: (v) => {
      const goal = findGoal(v, am, aw, customGoalId, "gc");
      if (goal !== 0) {
        return v.spend / goal;
      }
      return 0;
    },
    requiredBaseMetrics: ["customGoals", "spend"],
    descriptionHTML,
    ...common,
    composition: {
      type: CreativeMetricType.custom,
      metric: `customGoalCost:${customGoalId}`,
      customComposed: true,
      details: {
        aw,
        accountingMode,
        am,
        type: "",
      },
    },
    pagesEnabled: { creativeAnalytics: true },
    metadata: {
      baseId: `customGoalCost:${customGoalId}`,
      overviewPageMetricId: specId,
      dashboardV2Id: `customGoalsCost:${customGoalId}`,
      overviewPageMetricName: makeMetricName(
        name,
        am,
        aw,
        "",
        accountingMode,
        true,
      ),
      isGoalRelated: true,
      name: makeSalesTableMetricName(name, am, aw, "", accountingMode),
      salesTableMetricId: makeSalesTableMetricId(
        name,
        am,
        aw,
        "",
        accountingMode,
      ),
      tooltip: {
        reported: "Northbeam",
        description: `${customGoalNameSignular} attributable to this ad using the ${attributionMethodEnum.getLabel(
          am,
        )} attribution model`,
        showAttributionWindowInfo: true,
      },
    },
    spec: {
      id: specId,
      group: "default",
      name: makeMetricName(name, am, aw, "", accountingMode),
      modifiers: {
        attributionModel: "all",
        attributionWindows:
          accountingMode === "accrual" && aw === "infinity"
            ? ["infinity"]
            : ["1", "3", "7", "14", "30", "60", "90"],
        accountingModes:
          accountingMode === "accrual" && aw === "infinity"
            ? ["accrual"]
            : ["cash", "accrual"],
      },
    },
  };
}

function makeCustomGoalRate(
  am: string,
  aw: IMCAttributionWindowDays | "",
  awDescriptionHTML: string,
  accountingMode: INBAccountingMode,
  customGoalId: string,
  customGoalNameSignular: string,
  attributionMethodEnum: any,
): SalesPageMetric {
  const name = `${customGoalNameSignular} Rate`;

  const common = {
    id: makeMetricId(name, am, aw, "", accountingMode),
    name: makeMetricName(name, am, aw, "", accountingMode),
    format: metrics.MetricTypes.PERCENTAGE,
    formatter: MetricFormatters.percentage,
    isPositiveChangeGood: true,
    isRate: false,
    changeFractionAreaTextForTable: aw === "infinity" ? "Realized" : undefined,
    changeFractionAreaTextForTabDisplay:
      aw === "infinity" ? "Realized" : undefined,
    hideComparisonFromTableCell:
      aw === "infinity" && accountingMode === "accrual",
    hideComparisonFromTabDisplay:
      aw === "infinity" && accountingMode === "accrual",
  } as const;

  let descriptionHTML = `Percentage of new visits that end in an ${customGoalNameSignular}.`;
  if (awDescriptionHTML) {
    descriptionHTML += `<hr />${awDescriptionHTML}`;
  }

  const ltv = accountingMode === "accrual" && aw === "infinity" ? "Ltv" : "";

  const specId = `customGoalRate${ltv}:${customGoalId}`;
  return {
    calculate: (v) => {
      const goal = findGoal(v, am, aw, customGoalId, "gc");
      if (v.newVisits !== 0) {
        return goal / v.newVisits;
      }
      return 0;
    },
    requiredBaseMetrics: ["customGoals", "newVisits"],
    descriptionHTML,
    ...common,
    composition: {
      type: CreativeMetricType.custom,
      metric: `customGoalRate:${customGoalId}`,
      customComposed: true,
      details: {
        aw,
        accountingMode,
        am,
        type: "",
      },
    },
    pagesEnabled: { creativeAnalytics: true },
    metadata: {
      baseId: `customGoalRate:${customGoalId}`,
      overviewPageMetricId: specId,
      dashboardV2Id: `customGoalsRate:${customGoalId}`,
      overviewPageMetricName: makeMetricName(
        name,
        am,
        aw,
        "",
        accountingMode,
        true,
      ),
      isGoalRelated: true,
      name: makeSalesTableMetricName(name, am, aw, "", accountingMode),
      salesTableMetricId: makeSalesTableMetricId(
        name,
        am,
        aw,
        "",
        accountingMode,
      ),
      tooltip: {
        reported: "Northbeam",
        description: `${customGoalNameSignular} attributable to this ad using the ${attributionMethodEnum.getLabel(
          am,
        )} attribution model`,
        showAttributionWindowInfo: true,
      },
    },
    spec: {
      id: specId,
      group: "default",
      name: makeMetricName(name, am, aw, "", accountingMode),
      modifiers: {
        attributionModel: "all",
        attributionWindows:
          accountingMode === "accrual" && aw === "infinity"
            ? ["infinity"]
            : ["1", "3", "7", "14", "30", "60", "90"],
        accountingModes:
          accountingMode === "accrual" && aw === "infinity"
            ? ["accrual"]
            : ["cash", "accrual"],
      },
    },
  };
}

function makeTxns(
  am: string,
  aw: IMCAttributionWindowDays | "",
  awDescriptionHTML: string,
  type: "ft" | "rtn" | "",
  accountingMode: INBAccountingMode,
  attributionMethodEnum: any,
): SalesPageMetric {
  const fullTypeName = getFullTypeName(type);
  const common = {
    id: makeMetricId("Txns", am, aw, type, accountingMode),
    name: makeMetricName("Transactions", am, aw, type, accountingMode),
    format: metrics.MetricTypes.DECIMAL,
    formatter: MetricFormatters.decimal,
    isPositiveChangeGood: true,
    isRate: false,
    changeFractionAreaTextForTable: aw === "infinity" ? "Realized" : undefined,
    changeFractionAreaTextForTabDisplay:
      aw === "infinity" ? "Realized" : undefined,
    hideComparisonFromTableCell:
      aw === "infinity" && accountingMode === "accrual",
    hideComparisonFromTabDisplay:
      aw === "infinity" && accountingMode === "accrual",
  } as const;

  let descriptionHTML = "Transactions during the given date range";

  if (accountingMode === "accrual") {
    descriptionHTML = `Transactions attributable to this ad using the ${attributionMethodEnum.getLabel(
      am,
    )} attribution model.`;
    if (awDescriptionHTML) {
      descriptionHTML += `<hr />${awDescriptionHTML}`;
    }
  }

  const metricKey = (
    {
      ft: "ftTxns",
      rtn: "rtnTxns",
      "": "txns",
    } as const
  )[type];

  const idType = (
    {
      ft: "Ft",
      rtn: "Rtn",
      "": "",
    } as const
  )[type];

  const ltv = accountingMode === "accrual" && aw === "infinity" ? "Ltv" : "";

  const specId = `txns${idType}${ltv}`;
  return {
    calculate: (v) => findConv(v, am, aw, metricKey),
    requiredBaseMetrics: [metricKey],
    descriptionHTML,
    ...common,
    composition: {
      type: CreativeMetricType.base,
      metric: "txns",
      details: {
        aw,
        am,
        accountingMode,
        type,
      },
      customComposed: true,
    },
    pagesEnabled: {
      creativeAnalytics: accountingMode === "accrual" && aw !== "infinity",
    },
    metadata: {
      baseId: `txns${idType}`,
      overviewPageMetricId: specId,
      dashboardV2Id: metricKey,
      overviewPageMetricName: makeMetricName(
        "Transactions",
        am,
        aw,
        type,
        accountingMode,
        true,
      ),
      metricId: "txns",
      name: makeSalesTableMetricName(
        "Transactions",
        am,
        aw,
        type,
        accountingMode,
      ),
      isLtv: aw === "infinity",
      type: fullTypeName,
      salesTableMetricId: makeSalesTableMetricId(
        "Transactions",
        am,
        aw,
        type,
        accountingMode,
      ),
      tooltip: {
        reported: "Northbeam",
        description: `Number of transactions from ${getFullTypeName(
          type,
        )} customers`,
        showAttributionWindowInfo: true,
      },
    },
    spec: {
      id: specId,
      group: "default",
      name: makeMetricSpecName("Transactions", type, aw, accountingMode),
      modifiers: {
        attributionModel: "all",
        attributionWindows:
          accountingMode === "accrual" && aw === "infinity"
            ? ["infinity"]
            : ["1", "3", "7", "14", "30", "60", "90"],
        accountingModes:
          accountingMode === "accrual" && aw === "infinity"
            ? ["accrual"]
            : ["cash", "accrual"],
      },
    },
  };
}

function MetaShopRevenuePlusNBAttributedRevenue1DClick(
  am: string,
  aw: IMCAttributionWindowDays | "",
): SalesPageMetric {
  return {
    id: "metaShopRevenuePlusNBAttributedRevenue1DClick",
    name: "FB Meta shops revenue (1D Click) + NB attributed revenue",
    format: metrics.MetricTypes.CURRENCY,
    formatter: MetricFormatters.currency,
    isPositiveChangeGood: true,
    isRate: false,
    calculate: (v) => {
      if (metrics.isMetricPointForFacebookAd(v)) {
        return v.metaShopRevenue1DClick + findConv(v, am, aw, "rev");
      }
      return 0;
    },
    requiredBaseMetrics: ["metaShopRevenue1DClick", "rev"],
    descriptionHTML: "Facebook Meta shop revenue + NB attributed revenue",
    composition: {
      type: CreativeMetricType.facebook,
      metric: "metaShopRevenuePlusNBAttributedRevenue1DClick",
      customComposed: true,
    },
    metadata: {
      baseId: "metaShopRevenuePlusNBAttributedRevenue1DClick",
      overviewPageMetricId: "metaShopRevenuePlusNBAttributedRevenue1DClick",
      dashboardV2Id: "metaShopRevenuePlusNBAttributedRevenue1DClick",
    },
    spec: {
      id: "metaShopRevenuePlusNBAttributedRevenue1DClick",
      group: "meta",
      name: "FB Meta shops revenue (1D Click) + NB attributed revenue",
      modifiers: {
        attributionModel: "all",
        attributionWindows: ["1", "3", "7", "14", "30", "60", "90"],
        accountingModes: ["cash", "accrual"],
      },
    },
  };
}

function MetaShopRevenuePlusNBAttributedRevenue7DClick(
  am: string,
  aw: IMCAttributionWindowDays | "",
): SalesPageMetric {
  return {
    id: "metaShopRevenuePlusNBAttributedRevenue7DClick",
    name: "FB Meta shops revenue (7D Click) + NB attributed revenue",
    format: metrics.MetricTypes.CURRENCY,
    formatter: MetricFormatters.currency,
    isPositiveChangeGood: true,
    isRate: false,
    calculate: (v) => {
      if (metrics.isMetricPointForFacebookAd(v)) {
        return v.metaShopRevenue7DClick + findConv(v, am, aw, "rev");
      }
      return 0;
    },
    requiredBaseMetrics: ["metaShopRevenue7DClick", "rev"],
    composition: {
      type: CreativeMetricType.facebook,
      metric: "metaShopRevenuePlusNBAttributedRevenue7DClick",
      customComposed: true,
    },
    descriptionHTML: "Facebook Meta shop revenue + NB attributed revenue",
    metadata: {
      baseId: "metaShopRevenuePlusNBAttributedRevenue7DClick",
      overviewPageMetricId: "metaShopRevenuePlusNBAttributedRevenue7DClick",
      dashboardV2Id: "metaShopRevenuePlusNBAttributedRevenue7DClick",
    },
    spec: {
      id: "metaShopRevenuePlusNBAttributedRevenue7DClick",
      group: "meta",
      name: "FB Meta shops revenue (7D Click) + NB attributed revenue",
      modifiers: {
        attributionModel: "all",
        attributionWindows: ["1", "3", "7", "14", "30", "60", "90"],
        accountingModes: ["cash", "accrual"],
      },
    },
  };
}

function MetaShopRevenuePlusNBAttributedRevenue7DClick1DView(
  am: string,
  aw: IMCAttributionWindowDays | "",
): SalesPageMetric {
  return {
    id: "metaShopRevenuePlusNBAttributedRevenue7DClick1DView",
    name: "FB Meta shops revenue (7D Click, 1D View) + NB attributed revenue",
    format: metrics.MetricTypes.CURRENCY,
    formatter: MetricFormatters.currency,
    isPositiveChangeGood: true,
    isRate: false,
    calculate: (v) => {
      if (metrics.isMetricPointForFacebookAd(v)) {
        return v.metaShopRevenue7DClick1DView + findConv(v, am, aw, "rev");
      }
      return 0;
    },
    requiredBaseMetrics: ["metaShopRevenue7DClick1DView", "rev"],
    composition: {
      type: CreativeMetricType.facebook,
      metric: "metaShopRevenuePlusNBAttributedRevenue7DClick1DView",
      customComposed: true,
    },
    descriptionHTML: "Facebook Meta shop revenue + NB attributed revenue",
    metadata: {
      baseId: "metaShopRevenuePlusNBAttributedRevenue7DClick1DView",
      overviewPageMetricId:
        "metaShopRevenuePlusNBAttributedRevenue7DClick1DView",
      dashboardV2Id: "metaShopRevenuePlusNBAttributedRevenue7DClick1DView",
    },
    spec: {
      id: "metaShopRevenuePlusNBAttributedRevenue7DClick1DView",
      group: "meta",
      name: "FB Meta shops revenue (7D Click, 1D View) + NB attributed revenue",
      modifiers: {
        attributionModel: "all",
        attributionWindows: ["1", "3", "7", "14", "30", "60", "90"],
        accountingModes: ["cash", "accrual"],
      },
    },
  };
}

function MetaShopTransactionsPlusNBTransactions1DView(
  am: string,
  aw: IMCAttributionWindowDays | "",
): SalesPageMetric {
  return {
    id: "metaShopTransactionsPlusNBTransactions1DView",
    name: "FB Meta shops transactions (1D View) + NB transactions",
    format: metrics.MetricTypes.INTEGER,
    formatter: MetricFormatters.decimal,
    isPositiveChangeGood: true,
    isRate: false,
    calculate: (v) => {
      if (metrics.isMetricPointForFacebookAd(v)) {
        return v.metaShopPurchases1DView + findConv(v, am, aw, "txns");
      }
      return 0;
    },
    requiredBaseMetrics: ["metaShopPurchases1DView", "txns"],
    composition: {
      type: CreativeMetricType.facebook,
      metric: "metaShopTransactionsPlusNBTransactions1DView",
      customComposed: true,
    },
    descriptionHTML: "Facebook Meta shop purchases + NB transactions",
    metadata: {
      baseId: "metaShopTransactionsPlusNBTransactions1DView",
      overviewPageMetricId: "metaShopTransactionsPlusNBTransactions1DView",
      dashboardV2Id: "metaShopTransactionsPlusNBTransactions1DView",
    },
    spec: {
      id: "metaShopTransactionsPlusNBTransactions1DView",
      group: "meta",
      name: "FB Meta shops transactions (1D View) + NB transactions",
      modifiers: {
        attributionModel: "all",
        attributionWindows: ["1", "3", "7", "14", "30", "60", "90"],
        accountingModes: ["cash", "accrual"],
      },
    },
  };
}

function MetaShopTransactionsPlusNBTransactions1DClick(
  am: string,
  aw: IMCAttributionWindowDays | "",
): SalesPageMetric {
  return {
    id: "metaShopTransactionsPlusNBTransactions1DClick",
    name: "FB Meta shops transactions (1D Click) + NB transactions",
    format: metrics.MetricTypes.INTEGER,
    formatter: MetricFormatters.decimal,
    isPositiveChangeGood: true,
    isRate: false,
    calculate: (v) => {
      if (metrics.isMetricPointForFacebookAd(v)) {
        return v.metaShopPurchases1DClick + findConv(v, am, aw, "txns");
      }
      return 0;
    },
    requiredBaseMetrics: ["metaShopPurchases1DClick", "txns"],
    descriptionHTML: "Facebook Meta shop purchases + NB transactions",
    composition: {
      type: CreativeMetricType.facebook,
      metric: "metaShopTransactionsPlusNBTransactions1DClick",
      customComposed: true,
    },
    metadata: {
      baseId: "metaShopTransactionsPlusNBTransactions1DClick",
      overviewPageMetricId: "metaShopTransactionsPlusNBTransactions1DClick",
      dashboardV2Id: "metaShopTransactionsPlusNBTransactions1DClick",
    },
    spec: {
      id: "metaShopTransactionsPlusNBTransactions1DClick",
      group: "meta",
      name: "FB Meta shops transactions (1D Click) + NB transactions",
      modifiers: {
        attributionModel: "all",
        attributionWindows: ["1", "3", "7", "14", "30", "60", "90"],
        accountingModes: ["cash", "accrual"],
      },
    },
  };
}

function MetaShopTransactionsPlusNBTransactions7DClick(
  am: string,
  aw: IMCAttributionWindowDays | "",
): SalesPageMetric {
  return {
    id: "metaShopTransactionsPlusNBTransactions7DClick",
    name: "FB Meta shops transactions (7D Click) + NB transactions",
    format: metrics.MetricTypes.INTEGER,
    formatter: MetricFormatters.decimal,
    isPositiveChangeGood: true,
    isRate: false,
    calculate: (v) => {
      if (metrics.isMetricPointForFacebookAd(v)) {
        return v.metaShopPurchases7DClick + findConv(v, am, aw, "txns");
      }
      return 0;
    },
    requiredBaseMetrics: ["metaShopPurchases7DClick", "txns"],
    composition: {
      type: CreativeMetricType.facebook,
      metric: "metaShopTransactionsPlusNBTransactions7DClick",
      customComposed: true,
    },
    descriptionHTML: "Facebook Meta shop purchases + NB transactions",
    metadata: {
      baseId: "metaShopTransactionsPlusNBTransactions7DClick",
      overviewPageMetricId: "metaShopTransactionsPlusNBTransactions7DClick",
      dashboardV2Id: "metaShopTransactionsPlusNBTransactions7DClick",
    },
    spec: {
      id: "metaShopTransactionsPlusNBTransactions7DClick",
      group: "meta",
      name: "FB Meta shops transactions (7D Click) + NB transactions",
      modifiers: {
        attributionModel: "all",
        attributionWindows: ["1", "3", "7", "14", "30", "60", "90"],
        accountingModes: ["cash", "accrual"],
      },
    },
  };
}

function MetaShopTransactionsPlusNBTransactions7DClick1DView(
  am: string,
  aw: IMCAttributionWindowDays | "",
): SalesPageMetric {
  return {
    id: "metaShopTransactionsPlusNBTransactions7DClick1DView",
    name: "FB Meta shops transactions (7D Click, 1D View) + NB transactions",
    format: metrics.MetricTypes.INTEGER,
    formatter: MetricFormatters.decimal,
    isPositiveChangeGood: true,
    isRate: false,
    calculate: (v) => {
      if (metrics.isMetricPointForFacebookAd(v)) {
        return v.metaShopPurchases7DClick1DView + findConv(v, am, aw, "txns");
      }
      return 0;
    },
    requiredBaseMetrics: ["metaShopPurchases7DClick1DView", "txns"],
    composition: {
      type: CreativeMetricType.facebook,
      metric: "metaShopTransactionsPlusNBTransactions7DClick1DView",
      customComposed: true,
    },
    descriptionHTML: "Facebook Meta shop purchases + NB transactions",
    metadata: {
      baseId: "metaShopTransactionsPlusNBTransactions7DClick1DView",
      overviewPageMetricId:
        "metaShopTransactionsPlusNBTransactions7DClick1DView",
      dashboardV2Id: "metaShopTransactionsPlusNBTransactions7DClick1DView",
    },
    spec: {
      id: "metaShopTransactionsPlusNBTransactions7DClick1DView",
      group: "meta",
      name: "FB Meta shops transactions (7D Click, 1D View) + NB transactions",
      modifiers: {
        attributionModel: "all",
        attributionWindows: ["1", "3", "7", "14", "30", "60", "90"],
        accountingModes: ["cash", "accrual"],
      },
    },
  };
}

function MetaCacWithMetaShopTransactionsIncluded1DView(
  am: string,
  aw: IMCAttributionWindowDays | "",
): SalesPageMetric {
  return {
    id: "metaCacWithMetaShopTransactionsIncluded1DView",
    name: "FB CAC with Meta Shop transactions included (1D View)",
    format: metrics.MetricTypes.CURRENCY,
    formatter: MetricFormatters.currency,
    isPositiveChangeGood: true,
    isRate: false,
    calculate: (v) => {
      if (metrics.isMetricPointForFacebookAd(v) && v.spend !== 0) {
        return (
          (v.metaShopPurchases1DView + findConv(v, am, aw, "txns")) / v.spend
        );
      }
      return 0;
    },
    requiredBaseMetrics: ["metaShopPurchases1DView", "txns", "spend"],
    composition: {
      type: CreativeMetricType.facebook,
      metric: "metaCacWithMetaShopTransactionsIncluded1DView",
      customComposed: true,
    },
    descriptionHTML: "FB CAC with Meta Shop transactions included",
    metadata: {
      baseId: "metaCacWithMetaShopTransactionsIncluded1DView",
      overviewPageMetricId: "metaCacWithMetaShopTransactionsIncluded1DView",
      dashboardV2Id: "metaCacWithMetaShopTransactionsIncluded1DView",
    },
    spec: {
      id: "metaCacWithMetaShopTransactionsIncluded1DView",
      group: "meta",
      name: "FB CAC with Meta Shop transactions included (1D View)",
      modifiers: {
        attributionModel: "all",
        attributionWindows: ["1", "3", "7", "14", "30", "60", "90"],
        accountingModes: ["cash", "accrual"],
      },
    },
  };
}

function MetaCacWithMetaShopTransactionsIncluded1DClick(
  am: string,
  aw: IMCAttributionWindowDays | "",
): SalesPageMetric {
  return {
    id: "metaCacWithMetaShopTransactionsIncluded1DClick",
    name: "FB CAC with Meta Shop transactions included (1D Click)",
    format: metrics.MetricTypes.CURRENCY,
    formatter: MetricFormatters.currency,
    isPositiveChangeGood: true,
    isRate: false,
    calculate: (v) => {
      if (metrics.isMetricPointForFacebookAd(v) && v.spend !== 0) {
        return (
          (v.metaShopPurchases1DClick + findConv(v, am, aw, "txns")) / v.spend
        );
      }
      return 0;
    },
    requiredBaseMetrics: ["metaShopPurchases1DClick", "txns", "spend"],
    descriptionHTML: "FB CAC with Meta Shop transactions included",
    composition: {
      type: CreativeMetricType.facebook,
      metric: "metaCacWithMetaShopTransactionsIncluded1DClick",
      customComposed: true,
    },
    metadata: {
      baseId: "metaCacWithMetaShopTransactionsIncluded1DClick",
      overviewPageMetricId: "metaCacWithMetaShopTransactionsIncluded1DClick",
      dashboardV2Id: "metaCacWithMetaShopTransactionsIncluded1DClick",
    },
    spec: {
      id: "metaCacWithMetaShopTransactionsIncluded1DClick",
      group: "meta",
      name: "FB CAC with Meta Shop transactions included (1D Click)",
      modifiers: {
        attributionModel: "all",
        attributionWindows: ["1", "3", "7", "14", "30", "60", "90"],
        accountingModes: ["cash", "accrual"],
      },
    },
  };
}

function MetaCacWithMetaShopTransactionsIncluded7DClick(
  am: string,
  aw: IMCAttributionWindowDays | "",
): SalesPageMetric {
  return {
    id: "metaCacWithMetaShopTransactionsIncluded7DClick",
    name: "FB CAC with Meta Shop transactions included (7D Click)",
    format: metrics.MetricTypes.CURRENCY,
    formatter: MetricFormatters.currency,
    isPositiveChangeGood: true,
    isRate: false,
    calculate: (v) => {
      if (metrics.isMetricPointForFacebookAd(v) && v.spend !== 0) {
        return (
          (v.metaShopPurchases7DClick + findConv(v, am, aw, "txns")) / v.spend
        );
      }
      return 0;
    },
    requiredBaseMetrics: ["metaShopPurchases7DClick", "txns", "spend"],
    composition: {
      type: CreativeMetricType.facebook,
      metric: "metaCacWithMetaShopTransactionsIncluded7DClick",
      customComposed: true,
    },
    descriptionHTML: "FB CAC with Meta Shop transactions included",
    metadata: {
      baseId: "metaCacWithMetaShopTransactionsIncluded7DClick",
      overviewPageMetricId: "metaCacWithMetaShopTransactionsIncluded7DClick",
      dashboardV2Id: "metaCacWithMetaShopTransactionsIncluded7DClick",
    },
    spec: {
      id: "metaCacWithMetaShopTransactionsIncluded7DClick",
      group: "meta",
      name: "FB CAC with Meta Shop transactions included (7D Click)",
      modifiers: {
        attributionModel: "all",
        attributionWindows: ["1", "3", "7", "14", "30", "60", "90"],
        accountingModes: ["cash", "accrual"],
      },
    },
  };
}

function MetaCacWithMetaShopTransactionsIncluded7DClick1DView(
  am: string,
  aw: IMCAttributionWindowDays | "",
): SalesPageMetric {
  return {
    id: "metaCacWithMetaShopTransactionsIncluded7DClick1DView",
    name: "FB CAC with Meta Shop transactions included (7D Click, 1D View)",
    format: metrics.MetricTypes.CURRENCY,
    formatter: MetricFormatters.currency,
    isPositiveChangeGood: true,
    isRate: false,
    calculate: (v) => {
      if (metrics.isMetricPointForFacebookAd(v) && v.spend !== 0) {
        return (
          (v.metaShopPurchases7DClick1DView + findConv(v, am, aw, "txns")) /
          v.spend
        );
      }
      return 0;
    },
    requiredBaseMetrics: ["metaShopPurchases7DClick1DView", "txns", "spend"],
    composition: {
      type: CreativeMetricType.facebook,
      metric: "metaCacWithMetaShopTransactionsIncluded7DClick1DView",
      customComposed: true,
    },
    descriptionHTML: "FB CAC with Meta Shop transactions included",
    metadata: {
      baseId: "metaCacWithMetaShopTransactionsIncluded7DClick1DView",
      overviewPageMetricId:
        "metaCacWithMetaShopTransactionsIncluded7DClick1DView",
      dashboardV2Id: "metaCacWithMetaShopTransactionsIncluded7DClick1DView",
    },
    spec: {
      id: "metaCacWithMetaShopTransactionsIncluded7DClick1DView",
      group: "meta",
      name: "FB CAC with Meta Shop transactions included (7D Click, 1D View)",
      modifiers: {
        attributionModel: "all",
        attributionWindows: ["1", "3", "7", "14", "30", "60", "90"],
        accountingModes: ["cash", "accrual"],
      },
    },
  };
}

function MetaShopRevenuePlusNBAttributedRevenue1DView(
  am: string,
  aw: IMCAttributionWindowDays | "",
): SalesPageMetric {
  return {
    id: "metaShopRevenuePlusNBAttributedRevenue1DView",
    name: "FB Meta shops revenue (1D View) + NB attributed revenue",
    format: metrics.MetricTypes.CURRENCY,
    formatter: MetricFormatters.currency,
    isPositiveChangeGood: true,
    isRate: false,
    calculate: (v) => {
      if (metrics.isMetricPointForFacebookAd(v)) {
        return v.metaShopRevenue1DView + findConv(v, am, aw, "rev");
      }
      return 0;
    },
    requiredBaseMetrics: ["metaShopRevenue1DView", "rev"],
    composition: {
      type: CreativeMetricType.facebook,
      metric: "metaShopRevenuePlusNBAttributedRevenue1DView",
      customComposed: true,
    },
    descriptionHTML: "Facebook Meta shop revenue + NB attributed revenue",
    metadata: {
      baseId: "metaShopRevenuePlusNBAttributedRevenue1DView",
      overviewPageMetricId: "metaShopRevenuePlusNBAttributedRevenue1DView",
      dashboardV2Id: "metaShopRevenuePlusNBAttributedRevenue1DView",
    },
    spec: {
      id: "metaShopRevenuePlusNBAttributedRevenue1DView",
      group: "meta",
      name: "FB Meta shops revenue (1D View) + NB attributed revenue",
      modifiers: {
        attributionModel: "all",
        attributionWindows: ["1", "3", "7", "14", "30", "60", "90"],
        accountingModes: ["cash", "accrual"],
      },
    },
  };
}

function MetaROASMetaShopRevenueIncluded1DView(
  am: string,
  aw: IMCAttributionWindowDays | "",
): SalesPageMetric {
  return {
    id: "metaROASMetaShopRevenueIncluded1DView",
    name: "FB ROAS with Meta Shop revenue included (1D View)",
    format: metrics.MetricTypes.MULTIPLIER,
    formatter: MetricFormatters.multiplier,
    isPositiveChangeGood: true,
    isRate: true,
    calculate: (v) => {
      if (metrics.isMetricPointForFacebookAd(v) && v.spend > 0) {
        return (v.metaShopRevenue1DView + findConv(v, am, aw, "rev")) / v.spend;
      }
      return 0;
    },
    requiredBaseMetrics: ["metaShopRevenue1DView", "rev", "spend"],
    composition: {
      type: CreativeMetricType.facebook,
      metric: "metaROASMetaShopRevenueIncluded1DView",
      customComposed: true,
    },
    descriptionHTML:
      "Facebook ROAS calculated as (Meta Shop Revenue + NB Attributed Revenue) / Spend",
    metadata: {
      baseId: "metaROASMetaShopRevenueIncluded1DView",
      overviewPageMetricId: "metaROASMetaShopRevenueIncluded1DView",
      dashboardV2Id: "metaROASMetaShopRevenueIncluded1DView",
    },
    spec: {
      id: "metaROASMetaShopRevenueIncluded1DView",
      group: "meta",
      name: "FB ROAS with Meta Shop revenue included (1D View)",
      modifiers: {
        attributionModel: "all",
        attributionWindows: ["1", "3", "7", "14", "30", "60", "90"],
        accountingModes: ["cash", "accrual"],
      },
    },
  };
}

function MetaROASMetaShopRevenueIncluded1DClick(
  am: string,
  aw: IMCAttributionWindowDays | "",
): SalesPageMetric {
  return {
    id: "metaROASMetaShopRevenueIncluded1DClick",
    name: "FB ROAS with Meta Shop revenue included (1D Click)",
    format: metrics.MetricTypes.MULTIPLIER,
    formatter: MetricFormatters.multiplier,
    isPositiveChangeGood: true,
    isRate: true,
    calculate: (v) => {
      if (metrics.isMetricPointForFacebookAd(v) && v.spend > 0) {
        return (
          (v.metaShopRevenue1DClick + findConv(v, am, aw, "rev")) / v.spend
        );
      }
      return 0;
    },
    requiredBaseMetrics: ["metaShopRevenue1DClick", "rev", "spend"],
    composition: {
      type: CreativeMetricType.facebook,
      metric: "metaROASMetaShopRevenueIncluded1DClick",
      customComposed: true,
    },
    descriptionHTML:
      "Facebook ROAS calculated as (Meta Shop Revenue + NB Attributed Revenue) / Spend",
    metadata: {
      baseId: "metaROASMetaShopRevenueIncluded1DClick",
      overviewPageMetricId: "metaROASMetaShopRevenueIncluded1DClick",
      dashboardV2Id: "metaROASMetaShopRevenueIncluded1DClick",
    },
    spec: {
      id: "metaROASMetaShopRevenueIncluded1DClick",
      group: "meta",
      name: "FB ROAS with Meta Shop revenue included (1D Click)",
      modifiers: {
        attributionModel: "all",
        attributionWindows: ["1", "3", "7", "14", "30", "60", "90"],
        accountingModes: ["cash", "accrual"],
      },
    },
  };
}

function MetaROASMetaShopRevenueIncluded7DClick(
  am: string,
  aw: IMCAttributionWindowDays | "",
): SalesPageMetric {
  return {
    id: "metaROASMetaShopRevenueIncluded7DClick",
    name: "FB ROAS with Meta Shop revenue included (7D Click)",
    format: metrics.MetricTypes.MULTIPLIER,
    formatter: MetricFormatters.multiplier,
    isPositiveChangeGood: true,
    isRate: true,
    calculate: (v) => {
      if (metrics.isMetricPointForFacebookAd(v) && v.spend > 0) {
        return (
          (v.metaShopRevenue7DClick + findConv(v, am, aw, "rev")) / v.spend
        );
      }
      return 0;
    },
    requiredBaseMetrics: ["metaShopRevenue7DClick", "rev", "spend"],
    composition: {
      type: CreativeMetricType.facebook,
      metric: "metaROASMetaShopRevenueIncluded7DClick",
      customComposed: true,
    },
    descriptionHTML:
      "Facebook ROAS calculated as (Meta Shop Revenue + NB Attributed Revenue) / Spend",
    metadata: {
      baseId: "metaROASMetaShopRevenueIncluded7DClick",
      overviewPageMetricId: "metaROASMetaShopRevenueIncluded7DClick",
      dashboardV2Id: "metaROASMetaShopRevenueIncluded7DClick",
    },
    spec: {
      id: "metaROASMetaShopRevenueIncluded7DClick",
      group: "meta",
      name: "FB ROAS with Meta Shop revenue included (7D Click)",
      modifiers: {
        attributionModel: "all",
        attributionWindows: ["1", "3", "7", "14", "30", "60", "90"],
        accountingModes: ["cash", "accrual"],
      },
    },
  };
}

function MetaROASMetaShopRevenueIncluded7DClick1DView(
  am: string,
  aw: IMCAttributionWindowDays | "",
): SalesPageMetric {
  return {
    id: "metaROASMetaShopRevenueIncluded7DClick1DView",
    name: "FB ROAS with Meta Shop revenue included (7D Click, 1D View)",
    format: metrics.MetricTypes.MULTIPLIER,
    formatter: MetricFormatters.multiplier,
    isPositiveChangeGood: true,
    isRate: true,
    calculate: (v) => {
      if (metrics.isMetricPointForFacebookAd(v) && v.spend > 0) {
        return (
          (v.metaShopRevenue7DClick1DView + findConv(v, am, aw, "rev")) /
          v.spend
        );
      }
      return 0;
    },
    requiredBaseMetrics: ["metaShopRevenue7DClick1DView", "rev", "spend"],
    composition: {
      type: CreativeMetricType.facebook,
      metric: "metaROASMetaShopRevenueIncluded7DClick1DView",
      customComposed: true,
    },
    descriptionHTML:
      "Facebook ROAS calculated as (Meta Shop Revenue + NB Attributed Revenue) / Spend",
    metadata: {
      baseId: "metaROASMetaShopRevenueIncluded7DClick1DView",
      overviewPageMetricId: "metaROASMetaShopRevenueIncluded7DClick1DView",
      dashboardV2Id: "metaROASMetaShopRevenueIncluded7DClick1DView",
    },
    spec: {
      id: "metaROASMetaShopRevenueIncluded7DClick1DView",
      group: "meta",
      name: "FB ROAS with Meta Shop revenue included (7D Click, 1D View)",
      modifiers: {
        attributionModel: "all",
        attributionWindows: ["1", "3", "7", "14", "30", "60", "90"],
        accountingModes: ["cash", "accrual"],
      },
    },
  };
}

const MetaCAC1DClick: SalesPageMetric = {
  id: "metaCAC1DClick",
  name: "FB CAC (1D Click)",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: false,
  isRate: true,
  calculate: (v) => {
    if (
      metrics.isMetricPointForFacebookAd(v) &&
      v.metaTotalPurchases1DClick > 0
    ) {
      return v.metaSpend / v.metaTotalPurchases1DClick;
    }
    return 0;
  },
  requiredBaseMetrics: ["metaTotalPurchases1DClick", "metaSpend"],
  pagesEnabled: {
    creativeAnalytics: true,
  },
  composition: {
    type: CreativeMetricType.facebook,
    metric: "metaCAC1DClick",
    customComposed: true,
  },
  metadata: {
    baseId: "metaCAC1DClick",
    overviewPageMetricId: "metaCAC1DClick",
    dashboardV2Id: "metaCAC1DClick",
    tooltip: {
      reported: "Facebook",
      /* eslint-disable-next-line quotes */
      description: '"Customer Acquisition Cost"; cost of acquiring a customer',
      calculationInfo: "FB Spend ÷ FB Purchases",
    },
  },
  descriptionHTML: "Facebook CAC calculated as Spend / FB Purchases",
  spec: {
    id: "metaCAC1DClick",
    group: "meta",
    name: "FB CAC (1D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaCAC7DClick: SalesPageMetric = {
  id: "metaCAC7DClick",
  name: "FB CAC (7D Click)",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: false,
  isRate: true,
  calculate: (v) => {
    if (
      metrics.isMetricPointForFacebookAd(v) &&
      v.metaTotalPurchases7DClick > 0
    ) {
      return v.metaSpend / v.metaTotalPurchases7DClick;
    }
    return 0;
  },
  requiredBaseMetrics: ["metaTotalPurchases7DClick", "metaSpend"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "metaCAC7DClick",
    customComposed: true,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "metaCAC7DClick",
    overviewPageMetricId: "metaCAC7DClick",
    dashboardV2Id: "metaCAC7DClick",
    tooltip: {
      reported: "Facebook",
      /* eslint-disable-next-line quotes */
      description: '"Customer Acquisition Cost"; cost of acquiring a customer',
      calculationInfo: "FB Spend ÷ FB Purchases",
    },
  },
  descriptionHTML: "Facebook CAC calculated as Spend / FB Purchases",
  spec: {
    id: "metaCAC7DClick",
    group: "meta",
    name: "FB CAC (7D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaCAC7DClick1DView: SalesPageMetric = {
  id: "metaCAC7DClick1DView",
  name: "FB CAC (7D Click, 1D View)",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: false,
  isRate: true,
  calculate: (v) => {
    if (
      metrics.isMetricPointForFacebookAd(v) &&
      v.metaTotalPurchases7DClick1DView > 0
    ) {
      return v.metaSpend / v.metaTotalPurchases7DClick1DView;
    }
    return 0;
  },
  requiredBaseMetrics: ["metaTotalPurchases7DClick1DView", "metaSpend"],
  pagesEnabled: {
    creativeAnalytics: true,
  },
  composition: {
    type: CreativeMetricType.facebook,
    metric: "metaCAC7DClick1DView",
    customComposed: true,
  },
  metadata: {
    baseId: "metaCAC7DClick1DView",
    overviewPageMetricId: "metaCAC7DClick1DView",
    dashboardV2Id: "metaCAC7DClick1DView",
    tooltip: {
      reported: "Facebook",
      /* eslint-disable-next-line quotes */
      description: '"Customer Acquisition Cost"; cost of acquiring a customer',
      calculationInfo: "FB Spend ÷ FB Purchases",
    },
  },
  descriptionHTML: "Facebook CAC calculated as Spend / FB Purchases",
  spec: {
    id: "metaCAC7DClick1DView",
    group: "meta",
    name: "FB CAC (7D Click, 1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaCAC1DView: SalesPageMetric = {
  id: "metaCAC1DView",
  name: "FB CAC (1D View)",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: false,
  isRate: true,
  calculate: (v) => {
    if (
      metrics.isMetricPointForFacebookAd(v) &&
      v.metaTotalPurchases1DView > 0
    ) {
      return v.metaSpend / v.metaTotalPurchases1DView;
    }
    return 0;
  },
  requiredBaseMetrics: ["metaTotalPurchases1DView", "metaSpend"],
  pagesEnabled: {
    creativeAnalytics: true,
  },
  composition: {
    type: CreativeMetricType.facebook,
    metric: "metaCAC1DView",
    customComposed: true,
  },
  metadata: {
    baseId: "metaCAC1DView",
    overviewPageMetricId: "metaCAC1DView",
    dashboardV2Id: "metaCAC1DView",
    tooltip: {
      reported: "Facebook",
      /* eslint-disable-next-line quotes */
      description: '"Customer Acquisition Cost"; cost of acquiring a customer',
      calculationInfo: "FB Spend ÷ FB Purchases",
    },
  },
  descriptionHTML: "Facebook CAC calculated as Spend / FB Purchases",
  spec: {
    id: "metaCAC1DView",
    group: "meta",
    name: "FB CAC (1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaAOV1DView: SalesPageMetric = {
  id: "metaAOV1DView",
  name: "FB AOV (1D View)",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: true,
  isRate: true,
  calculate: (v) => {
    if (
      metrics.isMetricPointForFacebookAd(v) &&
      v.metaTotalPurchases1DView > 0
    ) {
      return v.metaTotalRevenue1DView / v.metaTotalPurchases1DView;
    }
    return 0;
  },
  requiredBaseMetrics: ["metaTotalRevenue1DView", "metaTotalPurchases1DView"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "metaAOV1DView",
    customComposed: true,
  },
  metadata: {
    baseId: "metaAOV1DView",
    overviewPageMetricId: "metaAOV1DView",
    dashboardV2Id: "metaAOV1DView",
    tooltip: {
      reported: "Facebook",
      /* eslint-disable-next-line quotes */
      description: '"Average Order Value" made by a customer',
      calculationInfo: "FB Revenue ÷ FB Purchases",
    },
  },
  descriptionHTML: "Facebook AOV calculated as FB Revenue / FB Purcahses",
  spec: {
    id: "metaAOV1DView",
    group: "meta",
    name: "FB AOV (1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaAOV1DClick: SalesPageMetric = {
  id: "metaAOV1DClick",
  name: "FB AOV (1D Click)",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: true,
  isRate: true,
  calculate: (v) => {
    if (
      metrics.isMetricPointForFacebookAd(v) &&
      v.metaTotalPurchases1DClick > 0
    ) {
      return v.metaTotalRevenue1DClick / v.metaTotalPurchases1DClick;
    }
    return 0;
  },
  requiredBaseMetrics: ["metaTotalRevenue1DClick", "metaTotalPurchases1DClick"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "metaAOV1DClick",
    customComposed: true,
  },
  metadata: {
    baseId: "metaAOV1DClick",
    overviewPageMetricId: "metaAOV1DClick",
    dashboardV2Id: "metaAOV1DClick",
    tooltip: {
      reported: "Facebook",
      /* eslint-disable-next-line quotes */
      description: '"Average Order Value" made by a customer',
      calculationInfo: "FB Revenue ÷ FB Purchases",
    },
  },
  descriptionHTML: "Facebook AOV calculated as FB Revenue / FB Purcahses",
  spec: {
    id: "metaAOV1DClick",
    group: "meta",
    name: "FB AOV (1D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaAOV7DClick: SalesPageMetric = {
  id: "metaAOV7DClick",
  name: "FB AOV (7D Click)",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: true,
  isRate: true,
  calculate: (v) => {
    if (
      metrics.isMetricPointForFacebookAd(v) &&
      v.metaTotalPurchases7DClick > 0
    ) {
      return v.metaTotalRevenue7DClick / v.metaTotalPurchases7DClick;
    }
    return 0;
  },
  requiredBaseMetrics: ["metaTotalRevenue7DClick", "metaTotalPurchases7DClick"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "metaAOV7DClick",
    customComposed: true,
  },
  metadata: {
    baseId: "metaAOV7DClick",
    overviewPageMetricId: "metaAOV7DClick",
    dashboardV2Id: "metaAOV7DClick",
    tooltip: {
      reported: "Facebook",
      /* eslint-disable-next-line quotes */
      description: '"Average Order Value" made by a customer',
      calculationInfo: "FB Revenue ÷ FB Purchases",
    },
  },
  descriptionHTML: "Facebook AOV calculated as FB Revenue / FB Purcahses",
  spec: {
    id: "metaAOV7DClick",
    group: "meta",
    name: "FB AOV (7D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaAOV7DClick1DView: SalesPageMetric = {
  id: "metaAOV7DClick1DView",
  name: "FB AOV (7D Click, 1D View)",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: true,
  isRate: true,
  calculate: (v) => {
    if (
      metrics.isMetricPointForFacebookAd(v) &&
      v.metaTotalPurchases7DClick1DView > 0
    ) {
      return (
        v.metaTotalRevenue7DClick1DView / v.metaTotalPurchases7DClick1DView
      );
    }
    return 0;
  },
  requiredBaseMetrics: [
    "metaTotalRevenue7DClick1DView",
    "metaTotalPurchases7DClick1DView",
  ],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "metaAOV7DClick1DView",
    customComposed: true,
  },
  metadata: {
    baseId: "metaAOV7DClick1DView",
    overviewPageMetricId: "metaAOV7DClick1DView",
    dashboardV2Id: "metaAOV7DClick1DView",
    tooltip: {
      reported: "Facebook",
      /* eslint-disable-next-line quotes */
      description: '"Average Order Value" made by a customer',
      calculationInfo: "FB Revenue ÷ FB Purchases",
    },
  },
  descriptionHTML: "Facebook AOV calculated as FB Revenue / FB Purcahses",
  spec: {
    id: "metaAOV7DClick1DView",
    group: "meta",
    name: "FB AOV (7D Click, 1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaPostEngagement1DView: SalesPageMetric = {
  id: "metaPostEngagement1DView",
  name: "FB Post Engagement (1D View)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaPostEngagement1DView,
  requiredBaseMetrics: ["metaPostEngagement1DView"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_post_engagement_1d_view",
    customComposed: false,
  },
  metadata: {
    baseId: "metaPostEngagement1DView",
    overviewPageMetricId: "metaPostEngagement1DView",
    dashboardV2Id: "metaPostEngagement1DView",
    tooltip: {
      reported: "Facebook",
      description:
        "Total number of action that users take involving your ads on Facebook",
    },
  },
  descriptionHTML:
    "Post engagements are the total number of actions that people take involving " +
    "your ads on Facebook",
  spec: {
    id: "metaPostEngagement1DView",
    group: "meta",
    name: "FB Post Engagement (1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaPostEngagement1DClick: SalesPageMetric = {
  id: "metaPostEngagement1DClick",
  name: "FB Post Engagement (1D Click)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaPostEngagement1DClick,
  requiredBaseMetrics: ["metaPostEngagement1DClick"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_post_engagement_1d_click",
    customComposed: false,
  },
  metadata: {
    baseId: "metaPostEngagement1DClick",
    overviewPageMetricId: "metaPostEngagement1DClick",
    dashboardV2Id: "metaPostEngagement1DClick",
    tooltip: {
      reported: "Facebook",
      description:
        "Total number of action that users take involving your ads on Facebook",
    },
  },
  descriptionHTML:
    "Post engagements are the total number of actions that people take involving " +
    "your ads on Facebook",
  spec: {
    id: "metaPostEngagement1DClick",
    group: "meta",
    name: "FB Post Engagement (1D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaPostEngagement7DClick: SalesPageMetric = {
  id: "metaPostEngagement7DClick",
  name: "FB Post Engagement (7D Click)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaPostEngagement7DClick,
  requiredBaseMetrics: ["metaPostEngagement7DClick"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_post_engagement_7d_click",
    customComposed: false,
  },
  metadata: {
    baseId: "metaPostEngagement7DClick",
    overviewPageMetricId: "metaPostEngagement7DClick",
    dashboardV2Id: "metaPostEngagement7DClick",
    tooltip: {
      reported: "Facebook",
      description:
        "Total number of action that users take involving your ads on Facebook",
    },
  },
  descriptionHTML:
    "Post engagements are the total number of actions that people take involving " +
    "your ads on Facebook",
  spec: {
    id: "metaPostEngagement7DClick",
    group: "meta",
    name: "FB Post Engagement (7D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaPostEngagement7DClick1DView: SalesPageMetric = {
  id: "metaPostEngagement7DClick1DView",
  name: "FB Post Engagement (7D Click, 1D View)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaPostEngagement7DClick1DView,
  requiredBaseMetrics: ["metaPostEngagement7DClick1DView"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_post_engagement_7d_click_1d_view",
    customComposed: false,
  },
  metadata: {
    baseId: "metaPostEngagement7DClick1DView",
    overviewPageMetricId: "metaPostEngagement7DClick1DView",
    dashboardV2Id: "metaPostEngagement7DClick1DView",
    tooltip: {
      reported: "Facebook",
      description:
        "Total number of action that users take involving your ads on Facebook",
    },
  },
  descriptionHTML:
    "Post engagements are the total number of actions that people take involving " +
    "your ads on Facebook",
  spec: {
    id: "metaPostEngagement7DClick1DView",
    group: "meta",
    name: "FB Post Engagement (7D Click, 1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaPostEngagementDefault: SalesPageMetric = {
  id: "metaPostEngagementDefault",
  name: "FB Post Engagement Default",
  format: metrics.MetricTypes.INTEGER,
  isNonModal: true,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaPostEngagementDefault,
  requiredBaseMetrics: ["metaPostEngagementDefault"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_post_engagement_default",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "metaPostEngagementDefault",
    overviewPageMetricId: "metaPostEngagementDefault",
    dashboardV2Id: "metaPostEngagementDefault",
    tooltip: {
      reported: "Facebook",
      description:
        "Total number of action that users take involving your ads on Facebook",
    },
  },
  descriptionHTML:
    "Post engagements are the total number of actions that people take involving " +
    "your ads on Facebook",
  spec: {
    id: "metaPostEngagementDefault",
    group: "meta",
    name: "FB Post Engagement Default",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaPageEngagement1DView: SalesPageMetric = {
  id: "metaPageEngagement1DView",
  name: "FB Page Engagement (1D View)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaPageEngagement1DView,
  requiredBaseMetrics: ["metaPageEngagement1DView"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_page_engagement_1d_view",
    customComposed: false,
  },
  metadata: {
    baseId: "metaPageEngagement1DView",
    overviewPageMetricId: "metaPageEngagement1DView",
    dashboardV2Id: "metaPageEngagement1DView",
    tooltip: {
      reported: "Facebook",
      description:
        "Total number of actions that users took on your Facebook Page and its posts, " +
        "attributed to your ads",
    },
  },
  descriptionHTML:
    "The total number of actions that people took on your Facebook Page and its posts, " +
    "attributed to your ads",
  spec: {
    id: "metaPageEngagement1DView",
    group: "meta",
    name: "FB Page Engagement (1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaPageEngagement1DClick: SalesPageMetric = {
  id: "metaPageEngagement1DClick",
  name: "FB Page Engagement (1D Click)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaPageEngagement1DClick,
  requiredBaseMetrics: ["metaPageEngagement1DClick"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_page_engagement_1d_click",
    customComposed: false,
  },
  metadata: {
    baseId: "metaPageEngagement1DClick",
    overviewPageMetricId: "metaPageEngagement1DClick",
    dashboardV2Id: "metaPageEngagement1DClick",
    tooltip: {
      reported: "Facebook",
      description:
        "Total number of actions that users took on your Facebook Page and its posts, " +
        "attributed to your ads",
    },
  },
  descriptionHTML:
    "The total number of actions that people took on your Facebook Page and its posts, " +
    "attributed to your ads",
  spec: {
    id: "metaPageEngagement1DClick",
    group: "meta",
    name: "FB Page Engagement (1D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaPageEngagement7DClick: SalesPageMetric = {
  id: "metaPageEngagement7DClick",
  name: "FB Page Engagement (7D Click)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaPageEngagement7DClick,
  requiredBaseMetrics: ["metaPageEngagement7DClick"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_page_engagement_7d_click",
    customComposed: false,
  },
  metadata: {
    baseId: "metaPageEngagement7DClick",
    overviewPageMetricId: "metaPageEngagement7DClick",
    dashboardV2Id: "metaPageEngagement7DClick",
    tooltip: {
      reported: "Facebook",
      description:
        "Total number of actions that users took on your Facebook Page and its posts, " +
        "attributed to your ads",
    },
  },
  descriptionHTML:
    "The total number of actions that people took on your Facebook Page and its posts, " +
    "attributed to your ads",
  spec: {
    id: "metaPageEngagement7DClick",
    group: "meta",
    name: "FB Page Engagement (7D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaPageEngagement7DClick1DView: SalesPageMetric = {
  id: "metaPageEngagement7DClick1DView",
  name: "FB Page Engagement (7D Click, 1D View)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaPageEngagement7DClick1DView,
  requiredBaseMetrics: ["metaPageEngagement7DClick1DView"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_page_engagement_7d_click_1d_view",
    customComposed: false,
  },
  metadata: {
    baseId: "metaPageEngagement7DClick1DView",
    overviewPageMetricId: "metaPageEngagement7DClick1DView",
    dashboardV2Id: "metaPageEngagement7DClick1DView",
    tooltip: {
      reported: "Facebook",
      description:
        "Total number of actions that users took on your Facebook Page and its posts, " +
        "attributed to your ads",
    },
  },
  descriptionHTML:
    "The total number of actions that people took on your Facebook Page and its posts, " +
    "attributed to your ads",
  spec: {
    id: "metaPageEngagement7DClick1DView",
    group: "meta",
    name: "FB Page Engagement (7D Click, 1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaPageEngagementDefault: SalesPageMetric = {
  id: "metaPageEngagementDefault",
  name: "FB Page Engagement Default",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  isNonModal: true,
  calculate: (v) => v.metaPageEngagementDefault,
  requiredBaseMetrics: ["metaPageEngagementDefault"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_page_engagement_default",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "metaPageEngagementDefault",
    overviewPageMetricId: "metaPageEngagementDefault",
    dashboardV2Id: "metaPageEngagementDefault",
    tooltip: {
      reported: "Facebook",
      description:
        "Total number of actions that users took on your Facebook Page and its " +
        "posts, attributed to your ads",
    },
  },
  descriptionHTML:
    "The total number of actions that people took on your Facebook Page and its posts, " +
    "attributed to your ads",
  spec: {
    id: "metaPageEngagementDefault",
    group: "meta",
    name: "FB Page Engagement Default",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

function makeBenchmarkCAC(
  am: string,
  aw: IMCAttributionWindowDays | "",
  type: "ft" | "rtn" | "",
): SalesPageMetric {
  const cacMetricKey = (
    {
      ft: "ftTxns",
      rtn: "rtnTxns",
      "": "txns",
    } as const
  )[type];

  const name = (
    {
      ft: "Benchmark CAC (1st Time)",
      rtn: "Benchmark CAC (Returning)",
      "": "Benchmark CAC (Blended)",
    } as const
  )[type];

  const fullTypeName = getFullTypeName(type) || "First Time and Returning";

  return {
    id: `benchmarkCAC${type}`,
    name,
    format: metrics.MetricTypes.CURRENCY,
    formatter: MetricFormatters.currencyRatio,
    benchmarkFormatter,
    isPositiveChangeGood: false,
    isRate: true,
    calculate: (v) => {
      const conv = findConv(v, am, aw, cacMetricKey);
      if (conv !== 0) {
        return v.spend / conv;
      }
      return 0;
    },
    requiredBaseMetrics: ["spend", cacMetricKey],
    descriptionHTML: "Calculated",
    composition: {
      type: CreativeMetricType.base,
      metric: "benchmarkCAC",
      customComposed: true,
      details: {
        aw,
        am,
        accountingMode: "accrual",
        type,
      },
    },
    pagesEnabled: {
      creativeAnalytics: false,
      salesV2: false,
      salesV3: true,
    },
    metadata: {
      baseId: `benchmarkCAC${type}`,
      overviewPageMetricId: `benchmarkCAC${type}`,
      dashboardV2Id: `benchmarkCAC${type}`,
      isBenchmark: true,
      tooltip: {
        reported: "Northbeam",
        description: `Your target "Customer Acquisition Cost" for ${fullTypeName} customers`,
      },
    },
  };
}

function makeBenchmarkROAS(
  am: string,
  aw: IMCAttributionWindowDays | "",
  type: "ft" | "rtn" | "",
): SalesPageMetric {
  const roasMetricKey = (
    {
      ft: "ftRev",
      rtn: "rtnRev",
      "": "rev",
    } as const
  )[type];

  const name = (
    {
      ft: "Benchmark ROAS (1st Time)",
      rtn: "Benchmark ROAS (Returning)",
      "": "Benchmark ROAS (Blended)",
    } as const
  )[type];

  const fullTypeName = getFullTypeName(type) || "First Time and Returning";

  return {
    id: `benchmarkROAS${type}`,
    name,
    format: metrics.MetricTypes.CURRENCY,
    formatter: MetricFormatters.multiplier,

    benchmarkFormatter,
    isPositiveChangeGood: true,
    isRate: true,
    calculate: (v) =>
      v.spend !== 0 ? findConv(v, am, aw, roasMetricKey) / v.spend : 0,
    requiredBaseMetrics: ["spend", roasMetricKey],
    descriptionHTML: "Calculated",
    composition: {
      type: CreativeMetricType.base,
      metric: "benchmarkROAS",
      customComposed: true,
      details: {
        aw,
        am,
        accountingMode: "accrual",
        type,
      },
    },
    pagesEnabled: {
      creativeAnalytics: false,
      salesV2: false,
      salesV3: true,
    },
    metadata: {
      baseId: `benchmarkROAS${type}`,
      overviewPageMetricId: `benchmarkROAS${type}`,
      dashboardV2Id: `benchmarkROAS${type}`,
      isBenchmark: true,
      tooltip: {
        reported: "Northbeam",
        description: `Your target "Customer Acquisition Cost" for ${fullTypeName} customers`,
      },
    },
  };
}

const MetaCostPerPageEngagement1DView: SalesPageMetric = {
  id: "metaCostPerPageEngagement1DView",
  name: "FB Cost Per Page Engagement (1D View)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaSpend / v.metaPageEngagement1DView,
  requiredBaseMetrics: ["metaSpend", "metaPageEngagement1DView"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "metaCostPerPageEngagement1DView",
    customComposed: true,
  },
  metadata: {
    baseId: "metaCostPerPageEngagement1DView",
    overviewPageMetricId: "metaCostPerPageEngagement1DView",
    dashboardV2Id: "metaCostPerPageEngagement1DView",
    tooltip: {
      reported: "Facebook",
      description: "Average cost for each post engagement",
    },
  },
  descriptionHTML: "The average cost for each post engagement",
  spec: {
    id: "metaCostPerPageEngagement1DView",
    group: "meta",
    name: "FB Cost Per Page Engagement (1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaCostPerPageEngagement1DClick: SalesPageMetric = {
  id: "metaCostPerPageEngagement1DClick",
  name: "FB Cost Per Page Engagement (1D Click)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaSpend / v.metaPageEngagement1DClick,
  requiredBaseMetrics: ["metaSpend", "metaPageEngagement1DClick"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "metaCostPerPageEngagement1DClick",
    customComposed: true,
  },
  metadata: {
    baseId: "metaCostPerPageEngagement1DClick",
    overviewPageMetricId: "metaCostPerPageEngagement1DClick",
    dashboardV2Id: "metaCostPerPageEngagement1DClick",
    tooltip: {
      reported: "Facebook",
      description: "Average cost for each post engagement",
    },
  },
  descriptionHTML: "The average cost for each post engagement",
  spec: {
    id: "metaCostPerPageEngagement1DClick",
    group: "meta",
    name: "FB Cost Per Page Engagement (1D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaCostPerPageEngagement7DClick: SalesPageMetric = {
  id: "metaCostPerPageEngagement7DClick",
  name: "FB Cost Per Page Engagement (7D Click)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaSpend / v.metaPageEngagement7DClick,
  requiredBaseMetrics: ["metaSpend", "metaPageEngagement7DClick"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "metaCostPerPageEngagement7DClick",
    customComposed: true,
  },
  metadata: {
    baseId: "metaCostPerPageEngagement7DClick",
    overviewPageMetricId: "metaCostPerPageEngagement7DClick",
    dashboardV2Id: "metaCostPerPageEngagement7DClick",
    tooltip: {
      reported: "Facebook",
      description: "Average cost for each post engagement",
    },
  },
  descriptionHTML: "The average cost for each post engagement",
  spec: {
    id: "metaCostPerPageEngagement7DClick",
    group: "meta",
    name: "FB Cost Per Page Engagement (7D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaCostPerPageEngagement7DClick1DView: SalesPageMetric = {
  id: "metaCostPerPageEngagement7DClick1DView",
  name: "FB Cost Per Page Engagement (7D Click, 1D View)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaSpend / v.metaPageEngagement7DClick1DView,
  requiredBaseMetrics: ["metaSpend", "metaPageEngagement7DClick1DView"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "metaCostPerPageEngagement7DClick1DView",
    customComposed: true,
  },
  metadata: {
    baseId: "metaCostPerPageEngagement7DClick1DView",
    overviewPageMetricId: "metaCostPerPageEngagement7DClick1DView",
    dashboardV2Id: "metaCostPerPageEngagement7DClick1DView",
    tooltip: {
      reported: "Facebook",
      description: "Average cost for each post engagement",
    },
  },
  descriptionHTML: "The average cost for each post engagement",
  spec: {
    id: "metaCostPerPageEngagement7DClick1DView",
    group: "meta",
    name: "FB Cost Per Page Engagement (7D Click, 1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaCostPerPageEngagementDefault: SalesPageMetric = {
  id: "metaCostPerPageEngagementDefault",
  name: "FB Cost Per Page Engagement Default",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  isNonModal: true,
  metadata: {
    baseId: "metaCostPerPageEngagementDefault",
    overviewPageMetricId: "metaCostPerPageEngagementDefault",
    dashboardV2Id: "metaCostPerPageEngagementDefault",
    tooltip: {
      reported: "Facebook",
      description: "Average cost for each post engagement",
    },
  },
  calculate: (v) => v.metaSpend / v.metaPageEngagementDefault,
  requiredBaseMetrics: ["metaSpend", "metaPageEngagementDefault"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "metaCostPerPageEngagementDefault",
    customComposed: true,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  descriptionHTML: "The average cost for each post engagement",
  spec: {
    id: "metaCostPerPageEngagementDefault",
    group: "meta",
    name: "FB Cost Per Page Engagement Default",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaCostPerPageLikes1DView: SalesPageMetric = {
  id: "metaCostPerPageLikes1DView",
  name: "FB Cost Per Page Likes (1D View)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaSpend / v.metaPageLikes1DView,
  requiredBaseMetrics: ["metaSpend", "metaPageLikes1DView"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "metaCostPerPageLikes1DView",
    customComposed: true,
  },
  metadata: {
    baseId: "metaCostPerPageLikes1DView",
    overviewPageMetricId: "metaCostPerPageLikes1DView",
    dashboardV2Id: "metaCostPerPageLikes1DView",
    tooltip: {
      reported: "Facebook",
      description: "Average cost for each page like",
    },
  },
  descriptionHTML: "The average cost for each Page like",
  spec: {
    id: "metaCostPerPageLikes1DView",
    group: "meta",
    name: "FB Cost Per Page Likes (1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaCostPerPageLikes1DClick: SalesPageMetric = {
  id: "metaCostPerPageLikes1DClick",
  name: "FB Cost Per Page Likes (1D Click)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaSpend / v.metaPageLikes1DClick,
  requiredBaseMetrics: ["metaSpend", "metaPageLikes1DClick"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "metaCostPerPageLikes1DClick",
    customComposed: true,
  },
  metadata: {
    baseId: "metaCostPerPageLikes1DClick",
    overviewPageMetricId: "metaCostPerPageLikes1DClick",
    dashboardV2Id: "metaCostPerPageLikes1DClick",
    tooltip: {
      reported: "Facebook",
      description: "Average cost for each page like",
    },
  },
  descriptionHTML: "The average cost for each Page like",
  spec: {
    id: "metaCostPerPageLikes1DClick",
    group: "meta",
    name: "FB Cost Per Page Likes (1D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaCostPerPageLikes7DClick: SalesPageMetric = {
  id: "metaCostPerPageLikes7DClick",
  name: "FB Cost Per Page Likes (7D Click)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaSpend / v.metaPageLikes7DClick,
  requiredBaseMetrics: ["metaSpend", "metaPageLikes7DClick"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "metaCostPerPageLikes7DClick",
    customComposed: true,
  },
  metadata: {
    baseId: "metaCostPerPageLikes7DClick",
    overviewPageMetricId: "metaCostPerPageLikes7DClick",
    dashboardV2Id: "metaCostPerPageLikes7DClick",
    tooltip: {
      reported: "Facebook",
      description: "Average cost for each page like",
    },
  },
  descriptionHTML: "The average cost for each Page like",
  spec: {
    id: "metaCostPerPageLikes7DClick",
    group: "meta",
    name: "FB Cost Per Page Likes (7D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaCostPerPageLikes7DClick1DView: SalesPageMetric = {
  id: "metaCostPerPageLikes7DClick1DView",
  name: "FB Cost Per Page Likes (7D Click, 1D View)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaSpend / v.metaPageLikes7DClick1DView,
  requiredBaseMetrics: ["metaSpend", "metaPageLikes7DClick1DView"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "metaCostPerPageLikes7DClick1DView",
    customComposed: true,
  },
  metadata: {
    baseId: "metaCostPerPageLikes7DClick1DView",
    overviewPageMetricId: "metaCostPerPageLikes7DClick1DView",
    dashboardV2Id: "metaCostPerPageLikes7DClick1DView",
    tooltip: {
      reported: "Facebook",
      description: "Average cost for each page like",
    },
  },
  descriptionHTML: "The average cost for each Page like",
  spec: {
    id: "metaCostPerPageLikes7DClick1DView",
    group: "meta",
    name: "FB Cost Per Page Likes (7D Click, 1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaCostPerPageLikesDefault: SalesPageMetric = {
  id: "metaCostPerPageLikesDefault",
  name: "FB Cost Per Page Likes Default",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  isNonModal: true,
  calculate: (v) => v.metaSpend / v.metaPageLikesDefault,
  requiredBaseMetrics: ["metaSpend", "metaPageLikesDefault"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "metaCostPerPageLikesDefault",
    customComposed: true,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "metaCostPerPageLikesDefault",
    overviewPageMetricId: "metaCostPerPageLikesDefault",
    dashboardV2Id: "metaCostPerPageLikesDefault",
    tooltip: {
      reported: "Facebook",
      description: "Average cost for each page like",
    },
  },
  descriptionHTML: "The average cost for each Page like",
  spec: {
    id: "metaCostPerPageLikesDefault",
    group: "meta",
    name: "FB Cost Per Page Likes Default",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaCostPerPostEngagement1DView: SalesPageMetric = {
  id: "metaCostPerPostEngagement1DView",
  name: "FB Cost Per Post Engagement (1D View)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaSpend / v.metaPostEngagement1DView,
  requiredBaseMetrics: ["metaSpend", "metaPostEngagement1DView"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "metaCostPerPostEngagement1DView",
    customComposed: true,
  },
  metadata: {
    baseId: "metaCostPerPostEngagement1DView",
    overviewPageMetricId: "metaCostPerPostEngagement1DView",
    dashboardV2Id: "metaCostPerPostEngagement1DView",
    tooltip: {
      reported: "Facebook",
      description: "Average cost for each post engagement",
    },
  },
  descriptionHTML: "The average cost for each post engagement",
  spec: {
    id: "metaCostPerPostEngagement1DView",
    group: "meta",
    name: "FB Cost Per Post Engagement (1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaCostPerPostEngagement1DClick: SalesPageMetric = {
  id: "metaCostPerPostEngagement1DClick",
  name: "FB Cost Per Post Engagement (1D Click)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaSpend / v.metaPostEngagement1DClick,
  requiredBaseMetrics: ["metaSpend", "metaPostEngagement1DClick"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "metaCostPerPostEngagement1DClick",
    customComposed: true,
  },
  metadata: {
    baseId: "metaCostPerPostEngagement1DClick",
    overviewPageMetricId: "metaCostPerPostEngagement1DClick",
    dashboardV2Id: "metaCostPerPostEngagement1DClick",
    tooltip: {
      reported: "Facebook",
      description: "Average cost for each post engagement",
    },
  },
  descriptionHTML: "The average cost for each post engagement",
  spec: {
    id: "metaCostPerPostEngagement1DClick",
    group: "meta",
    name: "FB Cost Per Post Engagement (1D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaCostPerPostEngagement7DClick: SalesPageMetric = {
  id: "metaCostPerPostEngagement7DClick",
  name: "FB Cost Per Post Engagement (7D Click)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaSpend / v.metaPostEngagement7DClick,
  requiredBaseMetrics: ["metaSpend", "metaPostEngagement7DClick"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "metaCostPerPostEngagement7DClick",
    customComposed: true,
  },
  metadata: {
    baseId: "metaCostPerPostEngagement7DClick",
    overviewPageMetricId: "metaCostPerPostEngagement7DClick",
    dashboardV2Id: "metaCostPerPostEngagement7DClick",
    tooltip: {
      reported: "Facebook",
      description: "Average cost for each post engagement",
    },
  },
  descriptionHTML: "The average cost for each post engagement",
  spec: {
    id: "metaCostPerPostEngagement7DClick",
    group: "meta",
    name: "FB Cost Per Post Engagement (7D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaCostPerPostEngagement7DClick1DView: SalesPageMetric = {
  id: "metaCostPerPostEngagement7DClick1DView",
  name: "FB Cost Per Post Engagement (7D Click, 1D View)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaSpend / v.metaPostEngagement7DClick1DView,
  requiredBaseMetrics: ["metaSpend", "metaPostEngagement7DClick1DView"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "metaCostPerPostEngagement7DClick1DView",
    customComposed: true,
  },
  metadata: {
    baseId: "metaCostPerPostEngagement7DClick1DView",
    overviewPageMetricId: "metaCostPerPostEngagement7DClick1DView",
    dashboardV2Id: "metaCostPerPostEngagement7DClick1DView",
    tooltip: {
      reported: "Facebook",
      description: "Average cost for each post engagement",
    },
  },
  descriptionHTML: "The average cost for each post engagement",
  spec: {
    id: "metaCostPerPostEngagement7DClick1DView",
    group: "meta",
    name: "FB Cost Per Post Engagement (7D Click, 1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaCostPerPostEngagementDefault: SalesPageMetric = {
  id: "metaCostPerPostEngagementDefault",
  name: "FB Cost Per Post Engagement Default",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  isNonModal: true,
  calculate: (v) => v.metaSpend / v.metaPostEngagementDefault,
  requiredBaseMetrics: ["metaSpend", "metaPostEngagementDefault"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "metaCostPerPostEngagementDefault",
    customComposed: true,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "metaCostPerPostEngagementDefault",
    overviewPageMetricId: "metaCostPerPostEngagementDefault",
    dashboardV2Id: "metaCostPerPostEngagementDefault",
    tooltip: {
      reported: "Facebook",
      description: "Average cost for each post engagement",
    },
  },
  descriptionHTML: "The average cost for each post engagement",
  spec: {
    id: "metaCostPerPostEngagementDefault",
    group: "meta",
    name: "FB Cost Per Post Engagement Default",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaPageLikes1DView: SalesPageMetric = {
  id: "metaPageLikes1DView",
  name: "FB Page Likes (1D View)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaPageLikes1DView,
  requiredBaseMetrics: ["metaPageLikes1DView"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_page_likes_1d_view",
    customComposed: false,
  },
  metadata: {
    baseId: "metaPageLikes1DView",
    overviewPageMetricId: "metaPageLikes1DView",
    dashboardV2Id: "metaPageLikes1DView",
    tooltip: {
      reported: "Facebook",
      description: "Facebook page likes",
    },
  },
  descriptionHTML: "FB Page Likes (1D View)",
  spec: {
    id: "metaPageLikes1DView",
    group: "meta",
    name: "FB Page Likes (1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaLinkClicks: SalesPageMetric = {
  id: "metaLinkClicks",
  name: "FB Link Clicks Default",
  format: metrics.MetricTypes.INTEGER,
  isNonModal: true,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaLinkClicks,
  requiredBaseMetrics: ["metaLinkClicks"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_link_clicks",
    customComposed: false,
  },
  metadata: {
    baseId: "metaLinkClicks",
    overviewPageMetricId: "metaLinkClicks",
    dashboardV2Id: "metaLinkClicks",
    tooltip: {
      reported: "Facebook",
      description:
        "The number of clicks on links within the ad that led to advertiser-specified " +
        "destinations, on or off Facebook technologies.",
    },
  },
  pagesEnabled: {
    salesV2: true,
    salesV3: true,
    creativeAnalytics: true,
  },
  descriptionHTML:
    "The number of clicks on links within the ad that led to advertiser-specified destinations, " +
    "on or off Facebook technologies.",
  spec: {
    id: "metaLinkClicks",
    group: "meta",
    name: "FB Link Clicks Default",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaOutboundClicks: SalesPageMetric = {
  id: "metaOutboundClicks",
  name: "FB Outbound Clicks Default",
  format: metrics.MetricTypes.INTEGER,
  isNonModal: true,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaOutboundClicks,
  requiredBaseMetrics: ["metaOutboundClicks"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_outbound_clicks",
    customComposed: false,
  },
  metadata: {
    baseId: "metaOutboundClicks",
    overviewPageMetricId: "metaOutboundClicks",
    dashboardV2Id: "metaOutboundClicks",
    tooltip: {
      reported: "Facebook",
      description:
        "The number of clicks on links that lead to destinations outside Facebook technologies",
    },
  },
  pagesEnabled: {
    salesV2: true,
    salesV3: true,
    creativeAnalytics: true,
  },
  descriptionHTML:
    "The number of clicks on links that lead to destinations outside Facebook technologies",
  spec: {
    id: "metaOutboundClicks",
    group: "meta",
    name: "FB Outbound Clicks Default",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaPageLikes1DClick: SalesPageMetric = {
  id: "metaPageLikes1DClick",
  name: "FB Page Likes (1D Click)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaPageLikes1DClick,
  requiredBaseMetrics: ["metaPageLikes1DClick"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_page_likes_1d_click",
    customComposed: false,
  },
  metadata: {
    baseId: "metaPageLikes1DClick",
    overviewPageMetricId: "metaPageLikes1DClick",
    dashboardV2Id: "metaPageLikes1DClick",
    tooltip: {
      reported: "Facebook",
      description: "Facebook page likes",
    },
  },
  descriptionHTML: "FB Page Likes (1D Click)",
  spec: {
    id: "metaPageLikes1DClick",
    group: "meta",
    name: "FB Page Likes (1D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaPageLikes7DClick: SalesPageMetric = {
  id: "metaPageLikes7DClick",
  name: "FB Page Likes (7D Click)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaPageLikes7DClick,
  requiredBaseMetrics: ["metaPageLikes7DClick"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_page_likes_7d_click",
    customComposed: false,
  },
  metadata: {
    baseId: "metaPageLikes7DClick",
    overviewPageMetricId: "metaPageLikes7DClick",
    dashboardV2Id: "metaPageLikes7DClick",
    tooltip: {
      reported: "Facebook",
      description: "Facebook page likes",
    },
  },
  descriptionHTML: "FB Page Likes (7D Click)",
  spec: {
    id: "metaPageLikes7DClick",
    group: "meta",
    name: "FB Page Likes (7D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaPageLikes7DClick1DView: SalesPageMetric = {
  id: "metaPageLikes7DClick1DView",
  name: "FB Page Likes (7D Click, 1D View)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaPageLikes7DClick1DView,
  requiredBaseMetrics: ["metaPageLikes7DClick1DView"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_page_likes_7d_click_1d_view",
    customComposed: false,
  },
  metadata: {
    baseId: "metaPageLikes7DClick1DView",
    overviewPageMetricId: "metaPageLikes7DClick1DView",
    dashboardV2Id: "metaPageLikes7DClick1DView",
    tooltip: {
      reported: "Facebook",
      description: "Facebook page likes",
    },
  },
  descriptionHTML: "FB Page Likes (7D Click, 1D View)",
  spec: {
    id: "metaPageLikes7DClick1DView",
    group: "meta",
    name: "FB Page Likes (7D Click, 1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaPageLikesDefault: SalesPageMetric = {
  id: "metaPageLikesDefault",
  name: "FB Page Likes Default",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  isNonModal: true,
  calculate: (v) => v.metaPageLikesDefault,
  requiredBaseMetrics: ["metaPageLikesDefault"],
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_page_likes_default",
    customComposed: false,
  },
  metadata: {
    baseId: "metaPageLikesDefault",
    overviewPageMetricId: "metaPageLikesDefault",
    dashboardV2Id: "metaPageLikesDefault",
    tooltip: {
      reported: "Facebook",
      description: "Facebook page likes",
    },
  },
  descriptionHTML: "FB Page Likes Default",
  spec: {
    id: "metaPageLikesDefault",
    group: "meta",
    name: "FB Page Likes Default",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

/* const MetaCostPerVideoContinuous2SecWatchedActions: SalesPageMetric = {
     id: "metaCostPerVideoContinuous2SecWatchedActions",
     name: "FB Cost Per 2-second Video Play",
     format: metrics.MetricTypes.INTEGER,
     formatter: MetricFormatters.integer,

     isPositiveChangeGood: true,
     isRate: false,
     calculate: (v) => {
       return v.spend / v.metaVideoContinuous2SecWatchedActions;
     },
     isNonModal: true,
     composition: {
       type: CreativeMetricType.facebook,
       metric: "metaCostPerVideoContinuous2SecWatchedActions",
       customComposed: true,
     },
     pagesEnabled: {
       creativeAnalytics: true,
     },
     descriptionHTML: "FB Cost Per 2-second Video Play",
     // spec: {
     // },
   }; */

const Meta3SVideoViewsDefault: SalesPageMetric = {
  id: "meta3SVideoViewsDefault",
  name: "FB 3 Second Video Plays Default",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.meta3SVideoViewsDefault,
  requiredBaseMetrics: ["meta3SVideoViewsDefault"],
  isNonModal: true,
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_video_views_default",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "meta3SVideoViewsDefault",
    overviewPageMetricId: "meta3SVideoViewsDefault",
    dashboardV2Id: "meta3SVideoViewsDefault",
    tooltip: {
      reported: "Facebook",
      description:
        "The number of times your video played for at least 3 seconds",
    },
  },
  descriptionHTML:
    "The number of times your video played for at least 3 seconds, or for nearly its total " +
    "length if it's shorter than 3 seconds",
  spec: {
    id: "meta3SVideoViewsDefault",
    group: "meta",
    name: "FB 3 Second Video Plays Default",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};
const Meta3SVideoViews1DClick: SalesPageMetric = {
  id: "meta3SVideoViews1DClick",
  name: "FB Cost Per 3 Second Video Plays (1D Click)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.meta3SVideoViews1DClick,
  requiredBaseMetrics: ["meta3SVideoViews1DClick"],
  isNonModal: true,
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_video_views_1d_click",
    customComposed: false,
  },
  metadata: {
    baseId: "meta3SVideoViews1DClick",
    overviewPageMetricId: "meta3SVideoViews1DClick",
    dashboardV2Id: "meta3SVideoViews1DClick",
    tooltip: {
      reported: "Facebook",
      description:
        "The number of times your video played for at least 3 seconds",
    },
  },
  descriptionHTML: "The average cost for each 3-second video play",
  spec: {
    id: "meta3SVideoViews1DClick",
    group: "meta",
    name: "FB Cost Per 3 Second Video Plays (1D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};
const Meta3SVideoViews7DClick: SalesPageMetric = {
  id: "meta3SVideoViews7DClick",
  name: "FB 3 Second Video Plays (7D Click)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.meta3SVideoViews7DClick,
  requiredBaseMetrics: ["meta3SVideoViews7DClick"],
  isNonModal: true,
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_video_views_7d_click",
    customComposed: false,
  },
  metadata: {
    baseId: "meta3SVideoViews7DClick",
    overviewPageMetricId: "meta3SVideoViews7DClick",
    dashboardV2Id: "meta3SVideoViews7DClick",
    tooltip: {
      reported: "Facebook",
      description:
        "The number of times your video played for at least 3 seconds",
    },
  },
  descriptionHTML:
    "The number of times your video played for at least 3 seconds, or for nearly its total " +
    "length if it's shorter than 3 seconds",
  spec: {
    id: "meta3SVideoViews7DClick",
    group: "meta",
    name: "FB 3 Second Video Plays (7D Click)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};
const Meta3SVideoViews7DClick1DView: SalesPageMetric = {
  id: "meta3SVideoViews7DClick1DView",
  name: "FB 3 Second Video Plays (7D Click, 1D View)",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.meta3SVideoViews7DClick1DView,
  requiredBaseMetrics: ["meta3SVideoViews7DClick1DView"],
  isNonModal: true,
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_video_views_7d_click_1d_view",
    customComposed: false,
  },
  metadata: {
    baseId: "meta3SVideoViews7DClick1DView",
    overviewPageMetricId: "meta3SVideoViews7DClick1DView",
    dashboardV2Id: "meta3SVideoViews7DClick1DView",
    tooltip: {
      reported: "Facebook",
      description:
        "The number of times your video played for at least 3 seconds",
    },
  },
  descriptionHTML:
    "The number of times your video played for at least 3 seconds, or for nearly its total " +
    "length if it's shorter than 3 seconds",
  spec: {
    id: "meta3SVideoViews7DClick1DView",
    group: "meta",
    name: "FB 3 Second Video Plays (7D Click, 1D View)",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaCostPer3SecondVideoPlays: SalesPageMetric = {
  id: "metaCostPer3SecondVideoPlays",
  name: "FB Cost Per 3 Second Video Plays",
  format: metrics.MetricTypes.CURRENCY,
  formatter: MetricFormatters.currency,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaSpend / v.meta3SVideoViewsDefault,
  requiredBaseMetrics: ["meta3SVideoViewsDefault", "metaSpend"],
  isNonModal: true,
  composition: {
    type: CreativeMetricType.facebook,
    metric: "metaCostPer3SecondVideoPlays",
    customComposed: true,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "metaCostPer3SecondVideoPlays",
    overviewPageMetricId: "metaCostPer3SecondVideoPlays",
    dashboardV2Id: "metaCostPer3SecondVideoPlays",
    tooltip: {
      reported: "Facebook",
      description:
        "The number of times your video played for at least 3 seconds",
    },
  },
  descriptionHTML: "The average cost for each 3-second video play",
  spec: {
    id: "metaCostPer3SecondVideoPlays",
    group: "meta",
    name: "FB Cost Per 3 Second Video Plays",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

const MetaVideoP25Watched: SalesPageMetric = {
  id: "metaVideoP25Watched",
  name: "FB Video p25 Watched",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaVideoP25Watched,
  requiredBaseMetrics: ["metaVideoP25Watched"],
  isNonModal: true,
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_video_p25_watched",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "metaVideoP25Watched",
    overviewPageMetricId: "metaVideoP25Watched",
    dashboardV2Id: "metaVideoP25Watched",
    tooltip: {
      reported: "Facebook",
      description:
        "Number of times your video was played at 25% of its length, including plays " +
        "that skipped to this point",
    },
  },
  descriptionHTML:
    "The number of times your video was played at 25% of its length, including plays that " +
    "skipped to this point",
  spec: {
    id: "metaVideoP25Watched",
    group: "meta",
    name: "FB Video p25 Watched",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};
const MetaVideoP50Watched: SalesPageMetric = {
  id: "metaVideoP50Watched",
  name: "FB Video p50 Watched",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaVideoP50Watched,
  requiredBaseMetrics: ["metaVideoP50Watched"],
  isNonModal: true,
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_video_p50_watched",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "metaVideoP50Watched",
    overviewPageMetricId: "metaVideoP50Watched",
    dashboardV2Id: "metaVideoP50Watched",
    tooltip: {
      reported: "Facebook",
      description:
        "Number of times your video was played at 50% of its length, including plays that " +
        "skipped to this point",
    },
  },
  descriptionHTML:
    "The number of times your video was played at 50% of its length, including plays that " +
    "skipped to this point",
  spec: {
    id: "metaVideoP50Watched",
    group: "meta",
    name: "FB Video p50 Watched",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};
const MetaVideoP75Watched: SalesPageMetric = {
  id: "metaVideoP75Watched",
  name: "FB Video p75 Watched",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaVideoP75Watched,
  requiredBaseMetrics: ["metaVideoP75Watched"],
  isNonModal: true,
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_video_p75_watched",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "metaVideoP75Watched",
    overviewPageMetricId: "metaVideoP75Watched",
    dashboardV2Id: "metaVideoP75Watched",
    tooltip: {
      reported: "Facebook",
      description:
        "Number of times your video was played at 75% of its length, including plays that " +
        "skipped to this point",
    },
  },
  descriptionHTML:
    "The number of times your video was played at 75% of its length, including plays that " +
    "skipped to this point",
  spec: {
    id: "metaVideoP75Watched",
    group: "meta",
    name: "FB Video p75 Watched",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};
const MetaVideoP95Watched: SalesPageMetric = {
  id: "metaVideoP95Watched",
  name: "FB Video p95 Watched",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaVideoP95Watched,
  requiredBaseMetrics: ["metaVideoP95Watched"],
  isNonModal: true,
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_video_p95_watched",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "metaVideoP95Watched",
    overviewPageMetricId: "metaVideoP95Watched",
    dashboardV2Id: "metaVideoP95Watched",
    tooltip: {
      reported: "Facebook",
      description:
        "Number of times your video was played at 95% of its length, including plays that " +
        "skipped to this point",
    },
  },
  descriptionHTML:
    "The number of times your video was played at 95% of its length, including plays that " +
    "skipped to this point",
  spec: {
    id: "metaVideoP95Watched",
    group: "meta",
    name: "FB Video p95 Watched",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};
const MetaVideoP100Watched: SalesPageMetric = {
  id: "metaVideoP100Watched",
  name: "FB Video p100 Watched",
  format: metrics.MetricTypes.INTEGER,
  formatter: MetricFormatters.integer,
  isPositiveChangeGood: true,
  isRate: false,
  calculate: (v) => v.metaVideoP100Watched,
  requiredBaseMetrics: ["metaVideoP100Watched"],
  isNonModal: true,
  composition: {
    type: CreativeMetricType.facebook,
    metric: "meta_video_p100_watched",
    customComposed: false,
  },
  pagesEnabled: {
    creativeAnalytics: true,
  },
  metadata: {
    baseId: "metaVideoP100Watched",
    overviewPageMetricId: "metaVideoP100Watched",
    dashboardV2Id: "metaVideoP100Watched",
    tooltip: {
      reported: "Facebook",
      description:
        "Number of times your video was played at 100% of its length, including plays " +
        "that skipped to this point",
    },
  },
  descriptionHTML:
    "The number of times your video was played at 100% of its length, including plays " +
    "that skipped to this point",
  spec: {
    id: "metaVideoP100Watched",
    group: "meta",
    name: "FB Video p100 Watched",
    modifiers: {
      attributionModel: "none",
      attributionWindows: [],
      accountingModes: [],
    },
  },
};

function makeRev(
  am: string,
  aw: IMCAttributionWindowDays | "",
  awDescriptionHTML: string,
  type: "ft" | "rtn" | "",
  accountingMode: INBAccountingMode,
  attributionMethodEnum: any,
): SalesPageMetric {
  const fullTypeName = getFullTypeName(type);
  const common = {
    id: makeMetricId("Attributed Rev", am, aw, type, accountingMode),
    name: makeMetricName("Attributed Rev", am, aw, type, accountingMode),
    dashboardV2Name:
      "Revenue" +
      (type === "ft" ? " (New)" : type === "rtn" ? " (Returning)" : ""),
    format: metrics.MetricTypes.CURRENCY,
    formatter: MetricFormatters.currency,
    isPositiveChangeGood: true,
    isRate: false,
    changeFractionAreaTextForTable: aw === "infinity" ? "Realized" : undefined,
    changeFractionAreaTextForTabDisplay:
      aw === "infinity" ? "Realized" : undefined,
    hideComparisonFromTableCell:
      aw === "infinity" && accountingMode === "accrual",
    hideComparisonFromTabDisplay:
      aw === "infinity" && accountingMode === "accrual",
  } as const;

  let descriptionHTML = "Total revenue during this time period";

  if (accountingMode === "accrual") {
    descriptionHTML = `Revenue attributable to this ad using the ${attributionMethodEnum.getLabel(
      am,
    )} attribution model.`;
    if (awDescriptionHTML) {
      descriptionHTML += `<hr />${awDescriptionHTML}`;
    }
  }

  const metricKey = (
    {
      ft: "ftRev",
      rtn: "rtnRev",
      "": "rev",
    } as const
  )[type];

  const idType = (
    {
      ft: "Ft",
      rtn: "Rtn",
      "": "",
    } as const
  )[type];

  const id = accountingMode === "cash" ? "rev" : "revAttributed";

  const ltv = accountingMode === "accrual" && aw === "infinity" ? "Ltv" : "";

  const specId = `${id}${idType}${ltv}`;

  return {
    calculate: (v) => findConv(v, am, aw, metricKey),
    requiredBaseMetrics: [metricKey],
    descriptionHTML,
    ...common,
    composition: {
      type: CreativeMetricType.base,
      details: {
        aw,
        am,
        accountingMode,
        type,
      },
      metric: "rev",
      customComposed: true,
    },
    pagesEnabled: {
      creativeAnalytics: accountingMode === "accrual" && aw !== "infinity",
    },
    metadata: {
      baseId: `rev${idType}`,
      overviewPageMetricId: `rev${idType}${ltv}`,
      dashboardV2Id: metricKey,
      overviewPageMetricName: makeMetricName(
        "Attributed Rev",
        am,
        aw,
        type,
        accountingMode,
        true,
      ),
      metricId: "rev",
      name: makeSalesTableMetricName(
        "Attributed Rev",
        am,
        aw,
        type,
        accountingMode,
      ),
      isLtv: aw === "infinity",
      type: getFullTypeName(type),
      salesTableMetricId: makeSalesTableMetricId(
        "Attributed Rev",
        am,
        aw,
        type,
        accountingMode,
      ),
      tooltip: {
        reported: "Northbeam",
        description: `Revenue attributed to this ad by ${fullTypeName} customers`,
        calculationInfo: `Total Revenue from ${fullTypeName} customers attributed to this ad`,
        showAttributionWindowInfo: true,
      },
    },
    spec: {
      id: specId,
      group: "default",
      name: makeMetricSpecName("Attributed Rev", type, aw, accountingMode),
      modifiers: {
        attributionModel: "all",
        attributionWindows:
          accountingMode === "accrual" && aw === "infinity"
            ? ["infinity"]
            : ["1", "3", "7", "14", "30", "60", "90"],
        accountingModes: [accountingMode],
      },
    },
  };
}

function makeOrderDiscounts(
  am: string,
  aw: IMCAttributionWindowDays | "",
  type: "ft" | "rtn" | "",
  accountingMode: INBAccountingMode,
): SalesPageMetric {
  const metricKey = (
    {
      ft: "ftOrderDiscounts",
      rtn: "rtnOrderDiscounts",
      "": "orderDiscounts",
    } as const
  )[type];

  const idType = (
    {
      ft: "Ft",
      rtn: "Rtn",
      "": "",
    } as const
  )[type];

  const description = (
    {
      ft:
        "Value of all discount codes and automatic discounts applied to first time " +
        "customer orders (or PROMOS if this applied to Amazon) as reported by your " +
        "ecommerce platform(s).",
      rtn:
        "Value of all discount codes and automatic discounts applied to returning " +
        "customer orders (or PROMOS if this applied to Amazon) as reported by your " +
        "ecommerce platform(s).",
      "":
        "Value of all discount codes and automatic discounts applied to " +
        "orders (or PROMOS if this applied to Amazon) as reported by your ecommerce platform(s).",
    } as const
  )[type];

  const calculationInfo =
    "<b>Amazon:</b> The sum items promotion discount + shipping promotion discounts.<br/>" +
    "<b>Shopify:</b> The total discounts applied to the price of the order.";

  const ltv = accountingMode === "accrual" && aw === "infinity" ? "Ltv" : "";
  const specId = `orderDiscounts${idType}${ltv}`;

  return {
    id: makeMetricId("Order Discounts", am, aw, type, accountingMode),
    name: makeMetricName("Discounts", am, aw, type, accountingMode),
    format: metrics.MetricTypes.CURRENCY,
    formatter: MetricFormatters.currency,
    isPositiveChangeGood: true,
    isRate: false,
    changeFractionAreaTextForTable: aw === "infinity" ? "Realized" : undefined,
    changeFractionAreaTextForTabDisplay:
      aw === "infinity" ? "Realized" : undefined,
    hideComparisonFromTableCell:
      aw === "infinity" && accountingMode === "accrual",
    hideComparisonFromTabDisplay:
      aw === "infinity" && accountingMode === "accrual",
    calculate: (v) => findConv(v, am, aw, metricKey),
    requiredBaseMetrics: [metricKey],
    descriptionHTML: description,
    composition: {
      type: CreativeMetricType.base,
      details: {
        aw,
        am,
        accountingMode,
        type,
      },
      metric: (
        {
          "": "ORDER_DISCOUNTS",
          ft: "ORDER_DISCOUNTS_FIRST_TIME",
          rtn: "ORDER_DISCOUNTS_RETURNING",
        } as const
      )[type],
      customComposed: false,
    },
    pagesEnabled: {
      creativeAnalytics: accountingMode === "accrual" && aw !== "infinity",
    },
    metadata: {
      baseId: `orderDiscounts${idType}`,
      metricId: "orderDiscounts",
      overviewPageMetricId: `orderDiscounts${idType}${ltv}`,
      dashboardV2Id: metricKey,
      overviewPageMetricName: makeMetricName(
        "Discounts",
        am,
        aw,
        type,
        accountingMode,
        true,
      ),
      name: makeSalesTableMetricName("Discounts", am, aw, type, accountingMode),
      isLtv: aw === "infinity",
      type: getFullTypeName(type),
      salesTableMetricId: makeSalesTableMetricId(
        "Order Discounts",
        am,
        aw,
        type,
        accountingMode,
      ),
      tooltip: {
        reported: "Northbeam",
        description,
        calculationInfo,
        showAttributionWindowInfo: true,
      },
    },
    spec: {
      id: specId,
      group: "default",
      name: makeMetricSpecName("Discounts", type, aw, accountingMode),
      modifiers: {
        attributionModel: "all",
        attributionWindows:
          accountingMode === "accrual" && aw === "infinity"
            ? ["infinity"]
            : ["1", "3", "7", "14", "30", "60", "90"],
        accountingModes: [accountingMode],
      },
    },
  };
}

function makeTaxValue(
  am: string,
  aw: IMCAttributionWindowDays | "",
  type: "ft" | "rtn" | "",
  accountingMode: INBAccountingMode,
): SalesPageMetric {
  const metricKey = (
    {
      ft: "ftTaxValue",
      rtn: "rtnTaxValue",
      "": "taxValue",
    } as const
  )[type];

  const idType = (
    {
      ft: "Ft",
      rtn: "Rtn",
      "": "",
    } as const
  )[type];

  const description = (
    {
      ft: "The sum of all the taxes applied to first time customer orders as reported by your ecommerce platform(s).",
      rtn: "The sum of all the taxes applied to returning customer orders as reported by your ecommerce platform(s).",
      "": "The sum of all the taxes applied to orders as reported by your ecommerce platform(s).",
    } as const
  )[type];

  const calculationInfo =
    "<b>Amazon:</b> The sum of all item taxes, shipping taxes and gift wrap taxes.<br/>" +
    "<b>Shopify:</b> The sum of all the taxes applied to the order.";

  const ltv = accountingMode === "accrual" && aw === "infinity" ? "Ltv" : "";
  const specId = `taxValue${idType}${ltv}`;

  return {
    id: makeMetricId("Tax Value", am, aw, type, accountingMode),
    name: makeMetricName("Tax", am, aw, type, accountingMode),
    format: metrics.MetricTypes.CURRENCY,
    formatter: MetricFormatters.currency,
    isPositiveChangeGood: true,
    isRate: false,
    changeFractionAreaTextForTable: aw === "infinity" ? "Realized" : undefined,
    changeFractionAreaTextForTabDisplay:
      aw === "infinity" ? "Realized" : undefined,
    hideComparisonFromTableCell:
      aw === "infinity" && accountingMode === "accrual",
    hideComparisonFromTabDisplay:
      aw === "infinity" && accountingMode === "accrual",
    calculate: (v) => findConv(v, am, aw, metricKey),
    requiredBaseMetrics: [metricKey],
    descriptionHTML: description,
    composition: {
      type: CreativeMetricType.base,
      details: {
        aw,
        am,
        accountingMode,
        type,
      },
      metric: (
        {
          "": "TAX_VALUE",
          ft: "TAX_VALUE_FIRST_TIME",
          rtn: "TAX_VALUE_RETURNING",
        } as const
      )[type],
      customComposed: false,
    },
    pagesEnabled: {
      creativeAnalytics: accountingMode === "accrual" && aw !== "infinity",
    },
    metadata: {
      baseId: `taxValue${idType}`,
      metricId: "taxValue",
      overviewPageMetricId: `taxValue${idType}${ltv}`,
      dashboardV2Id: metricKey,
      overviewPageMetricName: makeMetricName(
        "Tax",
        am,
        aw,
        type,
        accountingMode,
        true,
      ),
      name: makeSalesTableMetricName("Tax", am, aw, type, accountingMode),
      isLtv: aw === "infinity",
      type: getFullTypeName(type),
      salesTableMetricId: makeSalesTableMetricId(
        "Tax Value",
        am,
        aw,
        type,
        accountingMode,
      ),
      tooltip: {
        reported: "Northbeam",
        description,
        calculationInfo,
        showAttributionWindowInfo: true,
      },
    },
    spec: {
      id: specId,
      group: "default",
      name: makeMetricSpecName("Tax", type, aw, accountingMode),
      modifiers: {
        attributionModel: "all",
        attributionWindows:
          accountingMode === "accrual" && aw === "infinity"
            ? ["infinity"]
            : ["1", "3", "7", "14", "30", "60", "90"],
        accountingModes: [accountingMode],
      },
    },
  };
}

function makeShippingValue(
  am: string,
  aw: IMCAttributionWindowDays | "",
  type: "ft" | "rtn" | "",
  accountingMode: INBAccountingMode,
): SalesPageMetric {
  const metricKey = (
    {
      ft: "ftShippingValue",
      rtn: "rtnShippingValue",
      "": "shippingValue",
    } as const
  )[type];

  const idType = (
    {
      ft: "Ft",
      rtn: "Rtn",
      "": "",
    } as const
  )[type];

  const description = (
    {
      ft:
        "Shipping fees charged to customers (i.e. FREE Shipping is not included) " +
        "from first time customer orders as reported by your ecommerce platform(s).",
      rtn:
        "Shipping fees charged to customers (i.e. FREE Shipping is not included) " +
        "from returning customer orders as reported by your ecommerce platform(s).",
      "":
        "Shipping fees charged to customers (i.e. FREE Shipping is not included) " +
        "from orders as reported by your ecommerce platform(s).",
    } as const
  )[type];

  const calculationInfo =
    "<b>Amazon:</b> The sum of shipping price from all relevant order items.<br/>" +
    "<b>Shopify:</b> The total shipping price of the order, excluding discounts and returns. " +
    "If you have opted to include taxes in shipping, then the total shipping price includes taxes.";

  const ltv = accountingMode === "accrual" && aw === "infinity" ? "Ltv" : "";
  const specId = `shippingValue${idType}${ltv}`;

  return {
    id: makeMetricId("Shipping Value", am, aw, type, accountingMode),
    name: makeMetricName("Customer Shipping", am, aw, type, accountingMode),
    format: metrics.MetricTypes.CURRENCY,
    formatter: MetricFormatters.currency,
    isPositiveChangeGood: true,
    isRate: false,
    changeFractionAreaTextForTable: aw === "infinity" ? "Realized" : undefined,
    changeFractionAreaTextForTabDisplay:
      aw === "infinity" ? "Realized" : undefined,
    hideComparisonFromTableCell:
      aw === "infinity" && accountingMode === "accrual",
    hideComparisonFromTabDisplay:
      aw === "infinity" && accountingMode === "accrual",
    calculate: (v) => findConv(v, am, aw, metricKey),
    requiredBaseMetrics: [metricKey],
    descriptionHTML: description,
    composition: {
      type: CreativeMetricType.base,
      details: {
        aw,
        am,
        accountingMode,
        type,
      },
      metric: (
        {
          "": "SHIPPING_VALUE",
          ft: "SHIPPING_VALUE_FIRST_TIME",
          rtn: "SHIPPING_VALUE_RETURNING",
        } as const
      )[type],
      customComposed: false,
    },
    pagesEnabled: {
      creativeAnalytics: accountingMode === "accrual" && aw !== "infinity",
    },
    metadata: {
      baseId: `shippingValue${idType}`,
      metricId: "shippingValue",
      overviewPageMetricId: `shippingValue${idType}${ltv}`,
      dashboardV2Id: metricKey,
      overviewPageMetricName: makeMetricName(
        "Customer Shipping",
        am,
        aw,
        type,
        accountingMode,
        true,
      ),
      name: makeSalesTableMetricName(
        "Customer Shipping",
        am,
        aw,
        type,
        accountingMode,
      ),
      isLtv: aw === "infinity",
      type: getFullTypeName(type),
      salesTableMetricId: makeSalesTableMetricId(
        "Shipping Value",
        am,
        aw,
        type,
        accountingMode,
      ),
      tooltip: {
        reported: "Northbeam",
        description,
        calculationInfo,
        showAttributionWindowInfo: true,
      },
    },
    spec: {
      id: specId,
      group: "default",
      name: makeMetricSpecName("Customer Shipping", type, aw, accountingMode),
      modifiers: {
        attributionModel: "all",
        attributionWindows:
          accountingMode === "accrual" && aw === "infinity"
            ? ["infinity"]
            : ["1", "3", "7", "14", "30", "60", "90"],
        accountingModes: [accountingMode],
      },
    },
  };
}

function makeOrderRefundedValue(
  am: string,
  aw: IMCAttributionWindowDays | "",
  type: "ft" | "rtn" | "",
  accountingMode: INBAccountingMode,
): SalesPageMetric {
  const metricKey = (
    {
      ft: "ftOrderRefundedValue",
      rtn: "rtnOrderRefundedValue",
      "": "orderRefundedValue",
    } as const
  )[type];

  const idType = (
    {
      ft: "Ft",
      rtn: "Rtn",
      "": "",
    } as const
  )[type];

  const description = (
    {
      ft:
        "Returns, refunds, AND cancellations from " +
        "first time customer orders as reported by your ecommerce platform(s).",
      rtn:
        "Returns, refunds, AND cancellations from " +
        "returning customer orders as reported by your ecommerce platform(s).",
      "":
        "Returns, refunds, AND cancellations from " +
        "orders as reported by your ecommerce platform(s).",
    } as const
  )[type];

  const calculationInfo =
    "<b>Amazon:</b> The sum of relevant orders refunded.<br/>" +
    "<b>Shopify:</b> Sum of all refund transactions in a relevant order " +
    "(including taxes, shipping, items). Refunded transaction is defined as: " +
    "A partial or full return of captured funds to the cardholder. " +
    "A refund can happen only after a capture is processed.";

  const ltv = accountingMode === "accrual" && aw === "infinity" ? "Ltv" : "";
  const specId = `orderRefundedValue${idType}${ltv}`;

  return {
    id: makeMetricId("Order Refunded Value", am, aw, type, accountingMode),
    name: makeMetricName("Refunds", am, aw, type, accountingMode),
    format: metrics.MetricTypes.CURRENCY,
    formatter: MetricFormatters.currency,
    isPositiveChangeGood: true,
    isRate: false,
    changeFractionAreaTextForTable: aw === "infinity" ? "Realized" : undefined,
    changeFractionAreaTextForTabDisplay:
      aw === "infinity" ? "Realized" : undefined,
    hideComparisonFromTableCell:
      aw === "infinity" && accountingMode === "accrual",
    hideComparisonFromTabDisplay:
      aw === "infinity" && accountingMode === "accrual",
    calculate: (v) => findConv(v, am, aw, metricKey),
    requiredBaseMetrics: [metricKey],
    descriptionHTML: description,
    composition: {
      type: CreativeMetricType.base,
      details: {
        aw,
        am,
        accountingMode,
        type,
      },
      metric: (
        {
          "": "ORDER_REFUNDED_VALUE",
          ft: "ORDER_REFUNDED_VALUE_FIRST_TIME",
          rtn: "ORDER_REFUNDED_VALUE_RETURNING",
        } as const
      )[type],
      customComposed: false,
    },
    pagesEnabled: {
      creativeAnalytics: accountingMode === "accrual" && aw !== "infinity",
    },
    metadata: {
      baseId: `orderRefundedValue${idType}`,
      metricId: "orderRefundedValue",
      overviewPageMetricId: `orderRefundedValue${idType}${ltv}`,
      dashboardV2Id: metricKey,
      overviewPageMetricName: makeMetricName(
        "Refunds",
        am,
        aw,
        type,
        accountingMode,
        true,
      ),
      name: makeSalesTableMetricName("Refunds", am, aw, type, accountingMode),
      isLtv: aw === "infinity",
      type: getFullTypeName(type),
      salesTableMetricId: makeSalesTableMetricId(
        "Order Refunded Value",
        am,
        aw,
        type,
        accountingMode,
      ),
      tooltip: {
        reported: "Northbeam",
        description,
        calculationInfo,
        showAttributionWindowInfo: true,
      },
    },
    spec: {
      id: specId,
      group: "default",
      name: makeMetricSpecName("Refunds", type, aw, accountingMode),
      modifiers: {
        attributionModel: "all",
        attributionWindows:
          accountingMode === "accrual" && aw === "infinity"
            ? ["infinity"]
            : ["1", "3", "7", "14", "30", "60", "90"],
        accountingModes: [accountingMode],
      },
    },
  };
}

function makeOrderDiscountsOrderSubscriptions(
  am: string,
  aw: IMCAttributionWindowDays | "",
  type: "ft" | "recurring" | "other" | "",
  accountingMode: INBAccountingMode,
): SalesPageMetric {
  const metricKey = (
    {
      ft: "ftOrderDiscountsOrderSubscriptions",
      recurring: "recurringOrderDiscountsOrderSubscriptions",
      other: "otherOrderDiscountsOrderSubscriptions",
      "": "orderDiscountsOrderSubscriptions",
    } as const
  )[type];

  const idType = (
    {
      ft: "Ft",
      recurring: "Recurring",
      other: "Other",
      "": "",
    } as const
  )[type];

  const description = (
    {
      ft:
        "Value of all discount codes and automatic discounts applied to first time customer " +
        "subscription orders (or PROMOS if this applied to Amazon) as reported by your " +
        "ecommerce platform(s).",
      recurring:
        "Value of all discount codes and automatic discounts applied to recurring customer " +
        "subscription orders (or PROMOS if this applied to Amazon) as reported by your " +
        "ecommerce platform(s).",
      other:
        "Value of all discount codes and automatic discounts applied to other customer " +
        "subscription orders (or PROMOS if this applied to Amazon) as reported by your " +
        "ecommerce platform(s).",
      "":
        "Value of all discount codes and automatic discounts applied to subscription " +
        "orders (or PROMOS if this applied to Amazon) as reported by your ecommerce platform(s).",
    } as const
  )[type];

  const calculationInfo =
    "<b>Amazon:</b> The sum items promotion discount + shipping promotion discounts.<br/>" +
    "<b>Shopify:</b> The total discounts applied to the price of the order.";

  const ltv = accountingMode === "accrual" && aw === "infinity" ? "Ltv" : "";
  const specId = `orderDiscountsOrderSubscriptions${idType}${ltv}`;

  return {
    id: makeMetricId(
      "Subscription Order Discounts",
      am,
      aw,
      type,
      accountingMode,
    ),
    name: makeSubscriptionMetricName(
      "Subscription Discounts",
      aw,
      type,
      accountingMode,
    ),
    format: metrics.MetricTypes.CURRENCY,
    formatter: MetricFormatters.currency,
    isPositiveChangeGood: true,
    isRate: false,
    changeFractionAreaTextForTable: aw === "infinity" ? "Realized" : undefined,
    changeFractionAreaTextForTabDisplay:
      aw === "infinity" ? "Realized" : undefined,
    hideComparisonFromTableCell:
      aw === "infinity" && accountingMode === "accrual",
    hideComparisonFromTabDisplay:
      aw === "infinity" && accountingMode === "accrual",
    calculate: (v) => findConv(v, am, aw, metricKey),
    requiredBaseMetrics: [metricKey],
    descriptionHTML: description,
    composition: {
      type: CreativeMetricType.base,
      details: {
        aw,
        am,
        accountingMode,
        type: "",
      },
      metric: (
        {
          "": "ORDER_DISCOUNTS_ORDER_SUBSCRIPTIONS",
          ft: "ORDER_DISCOUNTS_ORDER_SUBSCRIPTIONS_FIRST_TIME",
          recurring: "ORDER_DISCOUNTS_ORDER_SUBSCRIPTIONS_RECURRING",
          other: "ORDER_DISCOUNTS_ORDER_SUBSCRIPTIONS_OTHER",
        } as const
      )[type],
      customComposed: false,
    },
    pagesEnabled: {
      creativeAnalytics: accountingMode === "accrual" && aw !== "infinity",
    },
    metadata: {
      baseId: `orderDiscountsOrderSubscriptions${idType}`,
      metricId: "orderDiscountsOrderSubscriptions",
      overviewPageMetricId: `orderDiscountsOrderSubscriptions${idType}${ltv}`,
      dashboardV2Id: metricKey,
      overviewPageMetricName: makeSubscriptionMetricName(
        "Subscription Discounts",
        aw,
        type,
        accountingMode,
        true,
      ),
      name: makeSubscriptionMetricName(
        "Subscription Discounts",
        aw,
        type,
        accountingMode,
        true,
      ),
      isLtv: aw === "infinity",
      type: getFullTypeName(type),
      salesTableMetricId: makeSalesTableMetricId(
        "Subscription Order Discounts",
        am,
        aw,
        type,
        accountingMode,
      ),
      tooltip: {
        reported: "Northbeam",
        description,
        calculationInfo,
        showAttributionWindowInfo: true,
      },
    },
    spec: {
      id: specId,
      group: "subscriptions",
      name: makeSubscriptionMetricName(
        "Subscription Discounts",
        aw,
        type,
        accountingMode,
      ),
      modifiers: {
        attributionModel: "all",
        attributionWindows:
          accountingMode === "accrual" && aw === "infinity"
            ? ["infinity"]
            : ["1", "3", "7", "14", "30", "60", "90"],
        accountingModes: [accountingMode],
      },
    },
  };
}

function makeTaxValueOrderSubscriptions(
  am: string,
  aw: IMCAttributionWindowDays | "",
  type: "ft" | "recurring" | "other" | "",
  accountingMode: INBAccountingMode,
): SalesPageMetric {
  const metricKey = (
    {
      ft: "ftTaxValueOrderSubscriptions",
      recurring: "recurringTaxValueOrderSubscriptions",
      other: "otherTaxValueOrderSubscriptions",
      "": "taxValueOrderSubscriptions",
    } as const
  )[type];

  const idType = (
    {
      ft: "Ft",
      recurring: "Recurring",
      other: "Other",
      "": "",
    } as const
  )[type];

  const description = (
    {
      ft:
        "The sum of all the taxes applied to first time customer subscription " +
        "orders as reported by your ecommerce platform(s).",
      recurring:
        "The sum of all the taxes applied to recurring customer subscription " +
        "orders as reported by your ecommerce platform(s).",
      other:
        "The sum of all the taxes applied to other customer subscription " +
        "orders as reported by your ecommerce platform(s).",
      "":
        "The sum of all the taxes applied to subscription " +
        "orders as reported by your ecommerce platform(s).",
    } as const
  )[type];

  const calculationInfo =
    "<b>Amazon:</b> The sum all item taxes, shipping taxes and gift wrap taxes.<br/>" +
    "<b>Shopify:</b> The sum of all the taxes applied to the order.";

  const ltv = accountingMode === "accrual" && aw === "infinity" ? "Ltv" : "";
  const specId = `taxValueOrderSubscriptions${idType}${ltv}`;

  return {
    id: makeMetricId("Subscription Tax", am, aw, type, accountingMode),
    name: makeSubscriptionMetricName(
      "Subscription Tax",
      aw,
      type,
      accountingMode,
    ),
    format: metrics.MetricTypes.CURRENCY,
    formatter: MetricFormatters.currency,
    isPositiveChangeGood: true,
    isRate: false,
    changeFractionAreaTextForTable: aw === "infinity" ? "Realized" : undefined,
    changeFractionAreaTextForTabDisplay:
      aw === "infinity" ? "Realized" : undefined,
    hideComparisonFromTableCell:
      aw === "infinity" && accountingMode === "accrual",
    hideComparisonFromTabDisplay:
      aw === "infinity" && accountingMode === "accrual",
    calculate: (v) => findConv(v, am, aw, metricKey),
    requiredBaseMetrics: [metricKey],
    descriptionHTML: description,
    composition: {
      type: CreativeMetricType.base,
      details: {
        aw,
        am,
        accountingMode,
        type: "",
      },
      metric: (
        {
          "": "TAX_VALUE_ORDER_SUBSCRIPTIONS",
          ft: "TAX_VALUE_ORDER_SUBSCRIPTIONS_FIRST_TIME",
          recurring: "TAX_VALUE_ORDER_SUBSCRIPTIONS_RECURRING",
          other: "TAX_VALUE_ORDER_SUBSCRIPTIONS_OTHER",
        } as const
      )[type],
      customComposed: false,
    },
    pagesEnabled: {
      creativeAnalytics: accountingMode === "accrual" && aw !== "infinity",
    },
    metadata: {
      baseId: `taxValueOrderSubscriptions${idType}`,
      metricId: "taxValueOrderSubscriptions",
      overviewPageMetricId: `taxValueOrderSubscriptions${idType}${ltv}`,
      dashboardV2Id: metricKey,
      overviewPageMetricName: makeSubscriptionMetricName(
        "Subscription Tax",
        aw,
        type,
        accountingMode,
        true,
      ),
      name: makeSubscriptionMetricName(
        "Subscription Tax",
        aw,
        type,
        accountingMode,
        true,
      ),
      isLtv: aw === "infinity",
      type: getFullTypeName(type),
      salesTableMetricId: makeSalesTableMetricId(
        "Subscription Tax",
        am,
        aw,
        type,
        accountingMode,
      ),
      tooltip: {
        reported: "Northbeam",
        description,
        calculationInfo,
        showAttributionWindowInfo: true,
      },
    },
    spec: {
      id: specId,
      group: "subscriptions",
      name: makeSubscriptionMetricName(
        "Subscription Tax",
        aw,
        type,
        accountingMode,
      ),
      modifiers: {
        attributionModel: "all",
        attributionWindows:
          accountingMode === "accrual" && aw === "infinity"
            ? ["infinity"]
            : ["1", "3", "7", "14", "30", "60", "90"],
        accountingModes: [accountingMode],
      },
    },
  };
}

function makeShippingValueOrderSubscriptions(
  am: string,
  aw: IMCAttributionWindowDays | "",
  type: "ft" | "recurring" | "other" | "",
  accountingMode: INBAccountingMode,
): SalesPageMetric {
  const metricKey = (
    {
      ft: "ftShippingValueOrderSubscriptions",
      recurring: "recurringShippingValueOrderSubscriptions",
      other: "otherShippingValueOrderSubscriptions",
      "": "shippingValueOrderSubscriptions",
    } as const
  )[type];

  const idType = (
    {
      ft: "Ft",
      recurring: "Recurring",
      other: "Other",
      "": "",
    } as const
  )[type];

  const description = (
    {
      ft:
        "Shipping fees charged to customers (i.e. FREE Shipping is not included) " +
        "from first time customer subscription orders as reported by your ecommerce platform(s).",
      recurring:
        "Shipping fees charged to customers (i.e. FREE Shipping is not included) " +
        "from recurring customer subscription orders as reported by your ecommerce platform(s).",
      other:
        "Shipping fees charged to customers (i.e. FREE Shipping is not included) " +
        "from other customer subscription orders as reported by your ecommerce platform(s).",
      "":
        "Shipping fees charged to customers (i.e. FREE Shipping is not included) " +
        "from subscription orders as reported by your ecommerce platform(s).",
    } as const
  )[type];

  const calculationInfo =
    "<b>Amazon:</b> The sum of shipping price from all relevant order items.<br/>" +
    "<b>Shopify:</b> The total shipping price of the order, excluding discounts and " +
    "returns. If you have opted to include taxes in shipping, " +
    "then the total shipping price includes taxes.";

  const ltv = accountingMode === "accrual" && aw === "infinity" ? "Ltv" : "";
  const specId = `shippingValueOrderSubscriptions${idType}${ltv}`;

  return {
    id: makeMetricId(
      "Subscription Shipping Cost",
      am,
      aw,
      type,
      accountingMode,
    ),
    name: makeSubscriptionMetricName(
      "Customer Subscription Shipping",
      aw,
      type,
      accountingMode,
    ),
    format: metrics.MetricTypes.CURRENCY,
    formatter: MetricFormatters.currency,
    isPositiveChangeGood: true,
    isRate: false,
    changeFractionAreaTextForTable: aw === "infinity" ? "Realized" : undefined,
    changeFractionAreaTextForTabDisplay:
      aw === "infinity" ? "Realized" : undefined,
    hideComparisonFromTableCell:
      aw === "infinity" && accountingMode === "accrual",
    hideComparisonFromTabDisplay:
      aw === "infinity" && accountingMode === "accrual",
    calculate: (v) => findConv(v, am, aw, metricKey),
    requiredBaseMetrics: [metricKey],
    descriptionHTML: description,
    composition: {
      type: CreativeMetricType.base,
      details: {
        aw,
        am,
        accountingMode,
        type: "",
      },
      metric: (
        {
          "": "SHIPPING_VALUE_ORDER_SUBSCRIPTIONS",
          ft: "SHIPPING_VALUE_ORDER_SUBSCRIPTIONS_FIRST_TIME",
          recurring: "SHIPPING_VALUE_ORDER_SUBSCRIPTIONS_RECURRING",
          other: "SHIPPING_VALUE_ORDER_SUBSCRIPTIONS_OTHER",
        } as const
      )[type],
      customComposed: false,
    },
    pagesEnabled: {
      creativeAnalytics: accountingMode === "accrual" && aw !== "infinity",
    },
    metadata: {
      baseId: `shippingValueOrderSubscriptions${idType}`,
      metricId: "shippingValueOrderSubscriptions",
      overviewPageMetricId: `shippingValueOrderSubscriptions${idType}${ltv}`,
      dashboardV2Id: metricKey,
      overviewPageMetricName: makeSubscriptionMetricName(
        "Customer Subscription Shipping",
        aw,
        type,
        accountingMode,
        true,
      ),
      name: makeSubscriptionMetricName(
        "Customer Subscription Shipping",
        aw,
        type,
        accountingMode,
        true,
      ),
      isLtv: aw === "infinity",
      type: getFullTypeName(type),
      salesTableMetricId: makeSalesTableMetricId(
        "Subscription Shipping Cost",
        am,
        aw,
        type,
        accountingMode,
      ),
      tooltip: {
        reported: "Northbeam",
        description,
        calculationInfo,
        showAttributionWindowInfo: true,
      },
    },
    spec: {
      id: specId,
      group: "subscriptions",
      name: makeSubscriptionMetricName(
        "Customer Subscription Shipping",
        aw,
        type,
        accountingMode,
      ),
      modifiers: {
        attributionModel: "all",
        attributionWindows:
          accountingMode === "accrual" && aw === "infinity"
            ? ["infinity"]
            : ["1", "3", "7", "14", "30", "60", "90"],
        accountingModes: [accountingMode],
      },
    },
  };
}

function makeOrderRefundedValueOrderSubscriptions(
  am: string,
  aw: IMCAttributionWindowDays | "",
  type: "ft" | "recurring" | "other" | "",
  accountingMode: INBAccountingMode,
): SalesPageMetric {
  const metricKey = (
    {
      ft: "ftOrderRefundedValueOrderSubscriptions",
      recurring: "recurringOrderRefundedValueOrderSubscriptions",
      other: "otherOrderRefundedValueOrderSubscriptions",
      "": "orderRefundedValueOrderSubscriptions",
    } as const
  )[type];

  const idType = (
    {
      ft: "Ft",
      recurring: "Recurring",
      other: "Other",
      "": "",
    } as const
  )[type];

  const description = (
    {
      ft:
        "Returns, refunds, AND cancellations from first time customer subscription " +
        "orders as reported by your ecommerce platform(s).",
      recurring:
        "Returns, refunds, AND cancellations from recurring customer subscription " +
        "orders as reported by your ecommerce platform(s).",
      other:
        "Returns, refunds, AND cancellations from other customer subscription " +
        "orders as reported by your ecommerce platform(s).",
      "":
        "Returns, refunds, AND cancellations from subscription " +
        "orders as reported by your ecommerce platform(s).",
    } as const
  )[type];

  const calculationInfo =
    "<b>Amazon:</b> The sum of relevant orders refunded.<br/>" +
    "<b>Shopify:</b> Sum of all refund transactions in a relevant order " +
    "(including taxes, shipping, items). Refunded transaction is defined as: " +
    "A partial or full return of captured funds to the cardholder. " +
    "A refund can happen only after a capture is processed.";

  const ltv = accountingMode === "accrual" && aw === "infinity" ? "Ltv" : "";
  const specId = `orderRefundedValueOrderSubscriptions${idType}${ltv}`;

  return {
    id: makeMetricId("Subscription Refund Cost", am, aw, type, accountingMode),
    name: makeSubscriptionMetricName(
      "Subscription Refunds",
      aw,
      type,
      accountingMode,
    ),
    format: metrics.MetricTypes.CURRENCY,
    formatter: MetricFormatters.currency,
    isPositiveChangeGood: true,
    isRate: false,
    changeFractionAreaTextForTable: aw === "infinity" ? "Realized" : undefined,
    changeFractionAreaTextForTabDisplay:
      aw === "infinity" ? "Realized" : undefined,
    hideComparisonFromTableCell:
      aw === "infinity" && accountingMode === "accrual",
    hideComparisonFromTabDisplay:
      aw === "infinity" && accountingMode === "accrual",
    calculate: (v) => findConv(v, am, aw, metricKey),
    requiredBaseMetrics: [metricKey],
    descriptionHTML: description,
    composition: {
      type: CreativeMetricType.base,
      details: {
        aw,
        am,
        accountingMode,
        type: "",
      },
      metric: (
        {
          "": "ORDER_REFUNDED_VALUE_ORDER_SUBSCRIPTIONS",
          ft: "ORDER_REFUNDED_VALUE_ORDER_SUBSCRIPTIONS_FIRST_TIME",
          recurring: "ORDER_REFUNDED_VALUE_ORDER_SUBSCRIPTIONS_RECURRING",
          other: "ORDER_REFUNDED_VALUE_ORDER_SUBSCRIPTIONS_OTHER",
        } as const
      )[type],
      customComposed: false,
    },
    pagesEnabled: {
      creativeAnalytics: accountingMode === "accrual" && aw !== "infinity",
    },
    metadata: {
      baseId: `orderRefundedValueOrderSubscriptions${idType}`,
      metricId: "orderRefundedValueOrderSubscriptions",
      overviewPageMetricId: `orderRefundedValueOrderSubscriptions${idType}${ltv}`,
      dashboardV2Id: metricKey,
      overviewPageMetricName: makeSubscriptionMetricName(
        "Subscription Refunds",
        aw,
        type,
        accountingMode,
        true,
      ),
      name: makeSubscriptionMetricName(
        "Subscription Refunds",
        aw,
        type,
        accountingMode,
        true,
      ),
      isLtv: aw === "infinity",
      type: getFullTypeName(type),
      salesTableMetricId: makeSalesTableMetricId(
        "Subscription Refund Cost",
        am,
        aw,
        type,
        accountingMode,
      ),
      tooltip: {
        reported: "Northbeam",
        description,
        calculationInfo,
        showAttributionWindowInfo: true,
      },
    },
    spec: {
      id: specId,
      group: "subscriptions",
      name: makeSubscriptionMetricName(
        "Subscription Refunds",
        aw,
        type,
        accountingMode,
      ),
      modifiers: {
        attributionModel: "all",
        attributionWindows:
          accountingMode === "accrual" && aw === "infinity"
            ? ["infinity"]
            : ["1", "3", "7", "14", "30", "60", "90"],
        accountingModes: [accountingMode],
      },
    },
  };
}

function makeForecastedRev(
  am: Exclude<string, "platform">,
  days: "30" | "60" | "90",
  attributionMethodEnum: any,
): SalesPageMetric {
  const name = `Forecasted Attributed Rev (${days}d)`;

  const common = {
    id: makeMetricId("Forecasted Attributed Rev", am, days, "", "accrual"),
    name,
    format: metrics.MetricTypes.CURRENCY,
    formatter: MetricFormatters.currency,
    isPositiveChangeGood: true,
    isRate: false,
    changeFractionAreaTextForTable: `${days} day forecast`,
    changeFractionAreaTextForTabDisplay: "Forecasted",
    hideComparisonFromTabDisplay: true,
    hideComparisonFromTableCell: true,
  } as const;

  let descriptionHTML = `Forecasted revenue attributable to this ad using the ${attributionMethodEnum.getLabel(
    am,
  )} attribution model, within ${days} days.`;
  descriptionHTML += `<hr />
    This is a <b>forecasted metric</b>.
    Using your historical data, we predict the future value of today's sales and marketing efforts.
    `;

  const key = (
    {
      30: "extraRev_to_30",
      60: "extraRev_to_60",
      90: "extraRev_to_90",
    } as const
  )[days];

  return {
    calculate: (v) => findConv(v, am, "infinity", "rev") + v[key],
    requiredBaseMetrics: ["rev", "forecast"],
    descriptionHTML,
    ...common,
    composition: {
      type: CreativeMetricType.base,
      metric: `forecastedRev${days}`,
      customComposed: true,
    },
    metadata: {
      baseId: `forecastedRev${days}`,
      overviewPageMetricId: `forecastedRev${days}`,
      dashboardV2Id: `forecastedRev${days}`,
      salesTableMetricId: makeSalesTableMetricId(
        "Forecasted Attributed Rev",
        am,
        days,
        "",
        "accrual",
      ),
      tooltip: {
        reported: "Forecasted Northbeam",
        description: `Forecasted revenue attributable to this ad using the Clicks-Only attribution model, within ${days} days`,
        calculationInfo:
          "Using your historical data, we predict the future value of today's sales and " +
          "marketing efforts",
      },
    },
    spec: {
      id: `forecastedRev${days}`,
      group: "default",
      name,
      modifiers: {
        attributionModel: "northbeam_custom",
        attributionWindows: [],
        accountingModes: ["accrual"],
      },
    },
  };
}

function makeForecastedTxns(
  am: Exclude<string, "platform">,
  days: "30" | "60" | "90",
  attributionMethodEnum: any,
): SalesPageMetric {
  const name = `Forecasted Attributed Transactions (${days}d)`;

  const common = {
    id: makeMetricId("Forecasted Attributed Txns", am, days, "", "accrual"),
    name,
    format: metrics.MetricTypes.DECIMAL,
    formatter: MetricFormatters.decimal,
    isPositiveChangeGood: true,
    isRate: false,
    changeFractionAreaTextForTable: `${days} day forecast`,
    changeFractionAreaTextForTabDisplay: "Forecasted",
    hideComparisonFromTabDisplay: true,
    hideComparisonFromTableCell: true,
  } as const;

  let descriptionHTML = `Forecasted transactions attributable to this ad using the ${attributionMethodEnum.getLabel(
    am,
  )} attribution model, within ${days} days.`;
  descriptionHTML += `<hr />
    This is a <b>forecasted metric</b>.
    Using your historical data, we predict the future value of today's sales and marketing efforts.
    `;

  const key = (
    {
      30: "extraTxns_to_30",
      60: "extraTxns_to_60",
      90: "extraTxns_to_90",
    } as const
  )[days];

  return {
    calculate: (v) => findConv(v, am, "infinity", "txns") + v[key],
    requiredBaseMetrics: ["txns", "forecast"],
    descriptionHTML,
    ...common,
    composition: {
      type: CreativeMetricType.base,
      metric: `forecastedTxns${days}`,
      customComposed: true,
    },
    metadata: {
      baseId: `forecastedTxns${days}`,
      overviewPageMetricId: `forecastedTxns${days}`,
      dashboardV2Id: `forecastedTxns${days}`,
      salesTableMetricId: makeSalesTableMetricId(
        "Forecasted Attributed Transactions",
        am,
        days,
        "",
        "accrual",
      ),
      tooltip: {
        reported: "Forecasted Northbeam",
        description: `Forecasted transactions attributable to this ad using the Clicks-Only attribution model, within ${days} days`,
        calculationInfo:
          "Using your historical data, we predict the future value of today's sales and " +
          "marketing efforts",
      },
    },
    spec: {
      id: `forecastedTxns${days}`,
      group: "default",
      name: `Forecasted Attributed Transactions (${days}d)`,
      modifiers: {
        attributionModel: "northbeam_custom",
        attributionWindows: [],
        accountingModes: ["accrual"],
      },
    },
  };
}

function makeForecastedRoas(
  am: Exclude<string, "platform">,
  days: "30" | "60" | "90",
): SalesPageMetric {
  const name = `Forecasted ROAS (${days}d)`;

  const common = {
    id: makeMetricId("Forecasted ROAS", am, days, "", "accrual"),
    name,
    format: metrics.MetricTypes.MULTIPLIER,
    formatter: MetricFormatters.multiplier,
    isPositiveChangeGood: true,
    isRate: true,
    changeFractionAreaTextForTable: `${days} day forecast`,
    changeFractionAreaTextForTabDisplay: "Forecasted",
    hideComparisonFromTabDisplay: true,
    hideComparisonFromTableCell: true,
  } as const;

  let descriptionHTML = `Forecasted Return on Ad Spend within ${days} days.`;
  descriptionHTML += `<hr />
    This is a <b>forecasted metric</b>.
    Using your historical data, we predict the future value of today's sales and marketing efforts.
    `;

  const key = (
    {
      30: "extraRev_to_30",
      60: "extraRev_to_60",
      90: "extraRev_to_90",
    } as const
  )[days];

  return {
    calculate: (v) =>
      v.spend !== 0
        ? (findConv(v, am, "infinity", "rev") + v[key]) / v.spend
        : 0,
    requiredBaseMetrics: ["rev", "spend", "forecast"],
    descriptionHTML,
    ...common,
    composition: {
      type: CreativeMetricType.base,
      metric: `forecastedRoas${days}`,
      customComposed: true,
    },
    metadata: {
      baseId: `forecastedRoas${days}`,
      overviewPageMetricId: `forecastedRoas${days}`,
      dashboardV2Id: `forecastedRoas${days}`,
      salesTableMetricId: makeSalesTableMetricId(
        "Forecasted ROAS",
        am,
        days,
        "",
        "accrual",
      ),
      tooltip: {
        reported: "Forecasted Northbeam",
        description: `Forecasted Return on Ad Spend within ${days} days`,
        calculationInfo:
          "Using your historical data, we predict the future value of today's sales and " +
          "marketing efforts",
      },
    },
    spec: {
      id: `forecastedRoas${days}`,
      group: "default",
      name,
      modifiers: {
        attributionModel: "northbeam_custom",
        attributionWindows: [],
        accountingModes: ["accrual"],
      },
    },
  };
}

function makeEcr(
  am: string,
  aw: IMCAttributionWindowDays | "",
  awDescriptionHTML: string,
  type: "ft" | "rtn" | "",
  accountingMode: INBAccountingMode,
): SalesPageMetric {
  const fullTypeName = getFullTypeName(type);
  const common = {
    id: makeMetricId("ECR", am, aw, type, accountingMode),
    name: makeMetricName("ECR", am, aw, type, accountingMode),
    format: metrics.MetricTypes.PERCENTAGE,
    formatter: MetricFormatters.percentage,
    isPositiveChangeGood: true,
    isRate: true,
    changeFractionAreaTextForTable: aw === "infinity" ? "Realized" : undefined,
    changeFractionAreaTextForTabDisplay:
      aw === "infinity" ? "Realized" : undefined,
    hideComparisonFromTabDisplay:
      aw === "infinity" && accountingMode === "accrual",
  } as const;

  let descriptionHTML =
    "Ecommerce conversion rate - calculated as Txns ÷ Visits";
  if (awDescriptionHTML && accountingMode === "accrual") {
    descriptionHTML += `<hr />${awDescriptionHTML}`;
  }

  const metricKey = (
    {
      ft: "ftTxns",
      rtn: "rtnTxns",
      "": "txns",
    } as const
  )[type];

  const idType = (
    {
      ft: "Ft",
      rtn: "Rtn",
      "": "",
    } as const
  )[type];

  const ltv = accountingMode === "accrual" && aw === "infinity" ? "Ltv" : "";

  return {
    calculate: (v) =>
      v.visits !== 0 ? findConv(v, am, aw, metricKey) / v.visits : 0,
    requiredBaseMetrics: [metricKey, "visits"],
    descriptionHTML,
    ...common,
    pagesEnabled: {
      creativeAnalytics: accountingMode === "accrual" && aw !== "infinity",
    },
    composition: {
      type: CreativeMetricType.base,
      metric: "ecr",
      details: {
        aw,
        am,
        accountingMode,
        type,
      },
      customComposed: true,
    },
    metadata: {
      baseId: `ecr${idType}`,
      overviewPageMetricId: `ecr${idType}${ltv}`,
      dashboardV2Id: `ecr${idType}`,
      overviewPageMetricName: makeMetricName(
        "ECR",
        am,
        aw,
        type,
        accountingMode,
        true,
      ),
      metricId: "ecr",
      name: makeSalesTableMetricName("ECR", am, aw, type, accountingMode),
      isLtv: aw === "infinity",
      type: fullTypeName,
      salesTableMetricId: makeSalesTableMetricId(
        "ECR",
        am,
        aw,
        type,
        accountingMode,
      ),
      tooltip: {
        reported: "Northbeam",
        description: `"E-commerce Conversion Rate"; percentage of website visitors who are ${fullTypeName} customers that make a purchase`,
        calculationInfo: "Transactions ÷ Visits",
        showAttributionWindowInfo: true,
      },
    },
    spec: {
      id: `ecr${idType}${ltv}`,
      group: "default",
      name: makeMetricSpecName("ECR", type, aw, accountingMode),
      modifiers: {
        attributionModel: "all",
        attributionWindows:
          accountingMode === "accrual" && aw === "infinity"
            ? ["infinity"]
            : ["1", "3", "7", "14", "30", "60", "90"],
        accountingModes:
          accountingMode === "accrual" && aw === "infinity"
            ? ["accrual"]
            : ["cash", "accrual"],
      },
    },
  };
}

function makeRpv(
  am: string,
  aw: IMCAttributionWindowDays | "",
  awDescriptionHTML: string,
  type: "ft" | "rtn" | "",
  accountingMode: INBAccountingMode,
): SalesPageMetric {
  const fullTypeName = getFullTypeName(type);
  const common = {
    id: makeMetricId("RPV", am, aw, type, accountingMode),
    name: makeMetricName("RPV", am, aw, type, accountingMode),
    format: metrics.MetricTypes.CURRENCY,
    formatter: MetricFormatters.currency,
    isPositiveChangeGood: true,
    isRate: true,
    changeFractionAreaTextForTable: undefined,
    changeFractionAreaTextForTabDisplay: undefined,
    hideComparisonFromTabDisplay:
      aw === "infinity" && accountingMode === "accrual",
  } as const;

  let descriptionHTML = "Revenue per visit - calculated as Rev ÷ Visits";
  if (awDescriptionHTML && accountingMode === "accrual") {
    descriptionHTML += `<hr />${awDescriptionHTML}`;
  }

  const metricKey = (
    {
      ft: "ftRev",
      rtn: "rtnRev",
      "": "rev",
    } as const
  )[type];

  const idType = (
    {
      ft: "Ft",
      rtn: "Rtn",
      "": "",
    } as const
  )[type];

  const ltv = accountingMode === "accrual" && aw === "infinity" ? "Ltv" : "";

  return {
    calculate: (v) =>
      v.visits !== 0 ? findConv(v, am, aw, metricKey) / v.visits : 0,
    requiredBaseMetrics: [metricKey, "visits"],
    descriptionHTML,
    ...common,
    pagesEnabled: {
      creativeAnalytics: accountingMode === "accrual" && aw !== "infinity",
    },
    composition: {
      type: CreativeMetricType.base,
      metric: "rpv",
      details: {
        aw,
        am,
        accountingMode,
        type,
      },
      customComposed: true,
    },
    metadata: {
      baseId: `rpv${idType}`,
      overviewPageMetricId: `rpv${idType}${ltv}`,
      dashboardV2Id: `rpv${idType}`,
      overviewPageMetricName: makeMetricName(
        "RPV",
        am,
        aw,
        type,
        accountingMode,
        true,
      ),
      metricId: "rpv",
      name: makeSalesTableMetricName("RPV", am, aw, type, accountingMode),
      isLtv: aw === "infinity",
      type: fullTypeName,
      salesTableMetricId: makeSalesTableMetricId(
        "RPV",
        am,
        aw,
        type,
        accountingMode,
      ),
      tooltip: {
        reported: "Northbeam",
        description:
          /* eslint-disable-next-line quotes */
          '"E-commerce Revenue per Visit"; amount of money generated each time a customer visits your website',
        calculationInfo: "Revenue ÷ Visits",
        showAttributionWindowInfo: true,
      },
    },
    spec: {
      id: `rpv${idType}${ltv}`,
      group: "default",
      name: makeMetricSpecName("RPV", type, aw, accountingMode),
      modifiers: {
        attributionModel: "all",
        attributionWindows:
          accountingMode === "accrual" && aw === "infinity"
            ? ["infinity"]
            : ["1", "3", "7", "14", "30", "60", "90"],
        accountingModes:
          accountingMode === "accrual" && aw === "infinity"
            ? ["accrual"]
            : ["cash", "accrual"],
      },
    },
  };
}

export function makeCac(
  am: string,
  aw: IMCAttributionWindowDays | "",
  awDescriptionHTML: string,
  type: "ft" | "rtn" | "",
  accountingMode: INBAccountingMode,
): SalesPageMetric {
  const fullTypeName = getFullTypeName(type);
  const common = {
    id: makeMetricId("CAC", am, aw, type, accountingMode),
    name: makeMetricName("CAC", am, aw, type, accountingMode),
    dashboardV2Name:
      "CAC" + (type === "ft" ? " (New)" : type === "rtn" ? " (Returning)" : ""),
    format: metrics.MetricTypes.CURRENCY,
    formatter: MetricFormatters.currencyRatio,
    isPositiveChangeGood: false,
    isRate: true,
    changeFractionAreaTextForTable: aw === "infinity" ? "Realized" : undefined,
    changeFractionAreaTextForTabDisplay:
      aw === "infinity" ? "Realized" : undefined,
    hideComparisonFromTabDisplay:
      aw === "infinity" && accountingMode === "accrual",
    platformEquivalent: {
      fb: {
        id:
          {
            "1-northbeam_custom": "metaCAC1DClick",
            "7-northbeam_custom": "metaCAC7DClick",
            "7-northbeam_custom__va": "metaCAC7DClick1DView",
          }[`${aw}-${am}` as string] ?? "metaCAC7DClick1DView",
      },
      tiktok: {
        id: "tiktokCAC",
      },
      snapchat: {
        id: "snapchatCAC",
      },
      adwords: {
        id: "googleCAC",
      },
      youtube: {
        id: "googleCAC",
      },
      pinterest: {
        id: "pinterestCAC",
      },
    },
  } as const;

  let descriptionHTML =
    "Cost to acquire a customer - calculated as Spend ÷ Txns";
  if (awDescriptionHTML && accountingMode === "accrual") {
    descriptionHTML += `<hr />${awDescriptionHTML}`;
  }

  const metricKey = (
    {
      ft: "ftTxns",
      rtn: "rtnTxns",
      "": "txns",
    } as const
  )[type];

  const idType = (
    {
      ft: "Ft",
      rtn: "Rtn",
      "": "",
    } as const
  )[type];

  const ltv = accountingMode === "accrual" && aw === "infinity" ? "Ltv" : "";

  return {
    calculate: (v) => {
      const conv = findConv(v, am, aw, metricKey);
      if (conv !== 0) {
        return v.spend / conv;
      }
      return 0;
    },
    requiredBaseMetrics: [metricKey, "spend"],
    descriptionHTML,
    ...common,
    composition: {
      type: CreativeMetricType.base,
      metric: "cac",
      details: {
        aw,
        am,
        accountingMode,
        type,
      },
      customComposed: true,
    },
    pagesEnabled: {
      creativeAnalytics: accountingMode === "accrual" && aw !== "infinity",
    },
    metadata: {
      baseId: `cac${idType}`,
      overviewPageMetricId: `cac${idType}${ltv}`,
      dashboardV2Id: `cac${idType}`,
      overviewPageMetricName: makeMetricName(
        "CAC",
        am,
        aw,
        type,
        accountingMode,
        true,
      ),
      metricId: "cac",
      name: makeSalesTableMetricName("CAC", am, aw, type, accountingMode),
      isLtv: aw === "infinity",
      type: fullTypeName,
      salesTableMetricId: makeSalesTableMetricId(
        "CAC",
        am,
        aw,
        type,
        accountingMode,
      ),
      tooltip: {
        reported: "Northbeam",
        description: `"Customer Acquisition Cost"; Cost to acquire a ${
          type === "rtn" ? "returning" : "new"
        } customer who makes a purchase`,
        calculationInfo: `Total Marketing Spend ÷ Total Number of ${fullTypeName} Customers Transactions`,
        showAttributionWindowInfo: true,
      },
    },
    spec: {
      id: `cac${idType}${ltv}`,
      group: "default",
      name: makeMetricSpecName("CAC", type, aw, accountingMode),
      modifiers: {
        attributionModel: "all",
        attributionWindows:
          accountingMode === "accrual" && aw === "infinity"
            ? ["infinity"]
            : ["1", "3", "7", "14", "30", "60", "90"],
        accountingModes:
          accountingMode === "accrual" && aw === "infinity"
            ? ["accrual"]
            : ["cash", "accrual"],
      },
    },
  };
}

function makeNewVisits(): SalesPageMetric {
  return {
    id: "newVisits",
    name: "New Visits",
    calculate: (v) => v.newVisits,
    requiredBaseMetrics: ["newVisits"],
    format: metrics.MetricTypes.INTEGER,
    formatter: MetricFormatters.integer,
    isPositiveChangeGood: true,
    isRate: false,
    descriptionHTML:
      "Visits to your website from new visitors, as determined by the Northbeam Pixel",
    composition: {
      type: CreativeMetricType.base,
      metric: "new_visits",
      customComposed: false,
    },
    metadata: {
      baseId: "newVisits",
      overviewPageMetricId: "newVisits",
      dashboardV2Id: "newVisits",
      salesTableMetricId: "newVisits",
      tooltip: {
        reported: "Northbeam",
        description:
          "Visits to your website from new visitors, as determined by the Northbeam Pixel",
      },
    },
    pagesEnabled: { creativeAnalytics: true },
    spec: {
      id: "newVisits",
      group: "default",
      name: "New Visits",
      modifiers: {
        attributionModel: "none",
        attributionWindows: [],
        accountingModes: [],
      },
    },
  };
}

function makeNewVisitorPercentage(): SalesPageMetric {
  return {
    id: "percentageNewVisits",
    name: "% New Visits",
    calculate: (v) => v.newVisits / v.visits,
    requiredBaseMetrics: ["newVisits", "visits"],
    format: metrics.MetricTypes.PERCENTAGE,
    formatter: MetricFormatters.percentage,
    isPositiveChangeGood: true,
    isRate: true,
    descriptionHTML:
      "Percentage of visits to your website that are from new visitors, as determined by " +
      "the Northbeam Pixel",
    composition: {
      type: CreativeMetricType.base,
      metric: "newVisitsPercentage",
      customComposed: true,
    },
    pagesEnabled: { creativeAnalytics: true },
    metadata: {
      baseId: "percentageNewVisits",
      overviewPageMetricId: "percentageNewVisits",
      dashboardV2Id: "percentageNewVisits",
      tooltip: {
        reported: "Northbeam",
        description: "Percentage of first-time website visitors",
        calculationInfo:
          "Percentage of New Visits = (Number of New Visits ÷ Total Number of Visits) × 100",
      },
    },
    spec: {
      id: "newVisitsPercentage",
      group: "default",
      name: "% New Visits",
      modifiers: {
        attributionModel: "none",
        attributionWindows: [],
        accountingModes: [],
      },
    },
  };
}

function makeVisits(am: string): SalesPageMetric {
  const common = {
    format: metrics.MetricTypes.INTEGER,
    formatter: MetricFormatters.integer,
    isPositiveChangeGood: true,
    isRate: false,
  } as const;

  return {
    id: `${am}_visits`,
    name: "Visits",
    calculate: (v) => v.visits,
    requiredBaseMetrics: ["visits"],
    descriptionHTML: "Visits to your website",
    ...common,
    composition: {
      type: CreativeMetricType.base,
      metric: "visits",
      customComposed: false,
    },
    spec: {
      id: "visits",
      group: "default",
      name: "Visits",
      modifiers: {
        attributionModel: "none",
        attributionWindows: [],
        accountingModes: [],
      },
    },
    metadata: {
      baseId: "visits",
      salesTableMetricId: "visits",
      overviewPageMetricId: "visits",
      dashboardV2Id: "visits",
      tooltip: {
        reported: "Northbeam",
        description: "Visits to your website",
      },
    },
  };
}

function makeCtr(am: string): SalesPageMetric {
  const common = {
    format: metrics.MetricTypes.PERCENTAGE,
    formatter: MetricFormatters.percentage,
    isPositiveChangeGood: true,
    isRate: false,
  } as const;

  return {
    id: `${am}_ctr`,
    name: "CTR",
    calculate: (v) => v.visits / v.imprs,
    requiredBaseMetrics: ["visits", "imprs"],
    descriptionHTML: "Click through rate - calculated as Visits ÷ Imprs",
    ...common,
    pagesEnabled: { creativeAnalytics: true },
    composition: {
      type: CreativeMetricType.base,
      metric: "ctr",
      customComposed: true,
    },
    metadata: {
      baseId: "ctr",
      salesTableMetricId: "ctr",
      overviewPageMetricId: "ctr",
      dashboardV2Id: "ctr",
      tooltip: {
        reported: "Northbeam",
        description:
          /* eslint-disable-next-line quotes */
          '"Click Through Rate"; number of clicks an ad receives relative to the number of ' +
          "impressions (views) it generates",
        calculationInfo: "Visits ÷ Impressions",
      },
    },
    spec: {
      id: "ctr",
      group: "default",
      name: "CTR",
      modifiers: {
        attributionModel: "none",
        attributionWindows: [],
        accountingModes: [],
      },
    },
  };
}

function makeEcpc(am: string): SalesPageMetric {
  const common = {
    format: metrics.MetricTypes.CURRENCY,
    formatter: MetricFormatters.currency,
    isPositiveChangeGood: false,
    isRate: true,
  } as const;

  return {
    id: `${am}_ecpc`,
    name: "eCPC",
    calculate: (v) => v.spend / v.visits,
    requiredBaseMetrics: ["visits", "spend"],
    descriptionHTML: "Effective cost per click - calculated as Spend ÷ Visits",
    ...common,
    composition: {
      type: CreativeMetricType.base,
      metric: "ecpc",
      customComposed: true,
    },
    pagesEnabled: { creativeAnalytics: true },
    metadata: {
      baseId: "ecpc",
      salesTableMetricId: "ecpc",
      overviewPageMetricId: "ecpc",
      dashboardV2Id: "ecpc",
      tooltip: {
        reported: "Northbeam",
        description:
          /* eslint-disable-next-line quotes */
          '"Effective Cost Per Click"; average cost incurred for each click that leads ' +
          "to a specific action or conversion",
        calculationInfo: "Spend ÷ Visits",
      },
    },
    spec: {
      id: "ecpc",
      group: "default",
      name: "eCPC",
      modifiers: {
        attributionModel: "none",
        attributionWindows: [],
        accountingModes: [],
      },
    },
  };
}

function makeEcpnv(am: string): SalesPageMetric {
  const common = {
    format: metrics.MetricTypes.CURRENCY,
    formatter: MetricFormatters.currency,
    isPositiveChangeGood: false,
    isRate: true,
  } as const;

  return {
    id: `${am}_ecpnv`,
    name: "eCPNV",
    calculate: (v) => v.spend / v.newVisits,
    requiredBaseMetrics: ["newVisits", "spend"],
    descriptionHTML:
      "Effective cost per new visit - calculated as Spend ÷ New Visits",
    ...common,
    pagesEnabled: { creativeAnalytics: true },
    composition: {
      type: CreativeMetricType.base,
      metric: "ecpnv",
      customComposed: true,
    },
    metadata: {
      baseId: "ecpnv",
      salesTableMetricId: "ecpnv",
      overviewPageMetricId: "ecpnv",
      dashboardV2Id: "ecpnv",
      tooltip: {
        reported: "Northbeam",
        description:
          /* eslint-disable-next-line quotes */
          '"Effective Cost Per New Visit"; average cost incurred for each new visit',
        calculationInfo: "Spend ÷ New Visits",
      },
    },
    spec: {
      id: "ecpnv",
      group: "default",
      name: "eCPNV",
      modifiers: {
        attributionModel: "none",
        attributionWindows: [],
        accountingModes: [],
      },
    },
  };
}

const tiktokGMVColumns = [
  TikTokGMVNetCost,
  TikTokGMVGrossRevenue,
  TikTokGMVCostPerLiveView,
  TikTokGMVCostPer10SecondLiveView,
  TikTokGMVOrders,
  TikTokGMVLiveViews,
  TikTokGMVTenSecondLiveViews,
  TikTokGMVLiveFollows,
  TikTokGMVCostPerOrder,
];

export function makeTrafficRows(
  am: string,
  aw: IMCAttributionWindowDays,
  accountingMode: INBAccountingMode,
  user: AbstractUser,
  timeGranularity: ITimeGranularity | ITimeGranularityV2,
  doAnon = false,
): SalesPageMetric[] {
  const adjustedAttributionWindow =
    accountingMode === "cash" && timeGranularity !== "hourly" ? "infinity" : aw;

  const fbColumns = [
    MetaTotalPurchases1DClick,
    MetaTotalPurchases1DView,
    MetaTotalPurchases7DClick,
    MetaTotalPurchases7DClick1DView,

    MetaShopPurchases1DView,
    MetaShopPurchases1DClick,
    MetaShopPurchases7DClick,
    MetaShopPurchases7DClick1DView,

    MetaOffsitePixelPurchases1DView,
    MetaOffsitePixelPurchases1DClick,
    MetaOffsitePixelPurchases7DClick,
    MetaOffsitePixelPurchases7DClick1DView,

    MetaTotalRevenue1DView,
    MetaTotalRevenue1DClick,
    MetaTotalRevenue7DClick,
    MetaTotalRevenue7DClick1DView,

    MetaShopRevenue1DView,
    MetaShopRevenue1DClick,
    MetaShopRevenue7DClick,
    MetaShopRevenue7DClick1DView,

    MetaOffsitePixelRevenue1DView,
    MetaOffsitePixelRevenue1DClick,
    MetaOffsitePixelRevenue7DClick,
    MetaOffsitePixelRevenue7DClick1DView,

    MetaROAS1DClick,
    MetaROAS7DClick,
    MetaROAS7DClick1DView,
    MetaROAS1DView,

    MetaCAC1DClick,
    MetaCAC7DClick,
    MetaCAC7DClick1DView,
    MetaCAC1DView,

    MetaAOV1DView,
    MetaAOV1DClick,
    MetaAOV7DClick,
    MetaAOV7DClick1DView,

    MetaPostEngagement1DView,
    MetaPostEngagement1DClick,
    MetaPostEngagement7DClick,
    MetaPostEngagement7DClick1DView,
    MetaPostEngagementDefault,
    MetaPageEngagement1DView,
    MetaPageEngagement1DClick,
    MetaPageEngagement7DClick,
    MetaPageEngagement7DClick1DView,
    MetaPageEngagementDefault,
    MetaPageLikes1DView,
    MetaPageLikes1DClick,
    MetaPageLikes7DClick,
    MetaPageLikes7DClick1DView,
    MetaPageLikesDefault,

    // MetaCostPerVideoContinuous2SecWatchedActions,

    MetaCostPer3SecondVideoPlays,

    Meta3SVideoViewsDefault,
    Meta3SVideoViews1DClick,
    Meta3SVideoViews7DClick,
    Meta3SVideoViews7DClick1DView,

    MetaCostPerPageEngagement1DView,
    MetaCostPerPageEngagement1DClick,
    MetaCostPerPageEngagement7DClick1DView,
    MetaCostPerPageEngagement7DClick,
    MetaCostPerPageEngagementDefault,
    MetaCostPerPageLikes1DView,
    MetaCostPerPageLikes1DClick,
    MetaCostPerPageLikes7DClick,
    MetaCostPerPageLikes7DClick1DView,
    MetaCostPerPageLikesDefault,
    MetaCostPerPostEngagement1DClick,
    MetaCostPerPostEngagement1DView,
    MetaCostPerPostEngagement7DClick,
    MetaCostPerPostEngagement7DClick1DView,
    MetaCostPerPostEngagementDefault,
  ];

  const fbDailyOnlyColumns = [
    MetaThumbstopRateDefault,

    MetaHoldRate1DView,
    MetaHoldRate7DClick1DView,

    MetaVideoPlayActions1DView,
    MetaVideoPlayActions7DClick1DView,

    MetaCostPerView1DView,
    MetaCostPerView7DClick1DView,

    MetaVideoThruplayWatchedActions1DView,
    MetaVideoThruplayWatchedActions7DClick1DView,

    MetaThruplayRate1DView,
    MetaThruplayRate7DClick1DView,

    MetaCostPerThruplay1DView,
    MetaCostPerThruplay7DClick1DView,

    MetaVideoP25Watched,
    MetaVideoP50Watched,
    MetaVideoP75Watched,
    MetaVideoP95Watched,
    MetaVideoP100Watched,

    MetaLinkClicks,
    MetaOutboundClicks,
  ];

  const fbCustomCalculationMetrics = [
    MetaShopRevenuePlusNBAttributedRevenue1DView(am, adjustedAttributionWindow),
    MetaShopRevenuePlusNBAttributedRevenue1DClick(
      am,
      adjustedAttributionWindow,
    ),
    MetaShopRevenuePlusNBAttributedRevenue7DClick(
      am,
      adjustedAttributionWindow,
    ),
    MetaShopRevenuePlusNBAttributedRevenue7DClick1DView(
      am,
      adjustedAttributionWindow,
    ),

    MetaROASMetaShopRevenueIncluded1DView(am, adjustedAttributionWindow),
    MetaROASMetaShopRevenueIncluded1DClick(am, adjustedAttributionWindow),
    MetaROASMetaShopRevenueIncluded7DClick(am, adjustedAttributionWindow),
    MetaROASMetaShopRevenueIncluded7DClick1DView(am, adjustedAttributionWindow),

    MetaShopTransactionsPlusNBTransactions1DView(am, adjustedAttributionWindow),
    MetaShopTransactionsPlusNBTransactions1DClick(
      am,
      adjustedAttributionWindow,
    ),
    MetaShopTransactionsPlusNBTransactions7DClick(
      am,
      adjustedAttributionWindow,
    ),
    MetaShopTransactionsPlusNBTransactions7DClick1DView(
      am,
      adjustedAttributionWindow,
    ),

    MetaCacWithMetaShopTransactionsIncluded1DView(
      am,
      adjustedAttributionWindow,
    ),
    MetaCacWithMetaShopTransactionsIncluded1DClick(
      am,
      adjustedAttributionWindow,
    ),
    MetaCacWithMetaShopTransactionsIncluded7DClick(
      am,
      adjustedAttributionWindow,
    ),
    MetaCacWithMetaShopTransactionsIncluded7DClick1DView(
      am,
      adjustedAttributionWindow,
    ),
  ];

  const pinterestColumns = [
    PinterestCheckoutConversions,
    PinterestCheckoutOrderValue,
    PinterestAddToCartRate,
    PinterestPaidImpressions,
    PinterestPaidEngagements,
    PinterestPinClick,
    PinterestPaidSaveRate,
    PinterestPaidSaves,
    PinterestVideoViews,
    PinterestPaid3SecVideoViews,
    PinterestPaidVideoPlayedAt50,
    PinterestPaidVideoPlayedAt95,
    PinterestPaidVideoPlayedAt75,
    PinterestPaidVideoPlayedAt100,
    PinterestPaidVideoPlayedAt25,
    PinterestPaidVideoStarts,
    PinterestPaidIdeaPinProductTagVisit,
    PinterestROAS,
    PinterestCAC,
  ];

  const googleColumns = [
    GoogleAllConversionEvents,
    GooglePurchases,
    GoogleAllConversionEventsRevenue,
    GooglePurchaseRevenue,
    GoogleVideoViews,
    GoogleViewThroughConversions,
    GoogleROAS,
    GoogleCAC,
    GoogleCostPerConversion,
    GoogleClicks,
    GoogleImpressions,
    GoogleAverageCPC,
  ];

  const tiktokColumns = [
    TikTokVideoViewsP100,
    TikTokVideoViewsP50,
    TikTokVideoViewsP75,
    TikTokVideoViewsP25,
    TikTokVideoViews,
    TikTokVideoViews2s,
    TikTokVideoViews6s,
    TikTokImpressions,
    TikTokClicks,
    TikTokLikes,
    TikTokCompletePayment,
    TikTokTotalCompletePayment,
    TikTokEngagements,
    TikTokCPC,
    TikTokCPM,
    TikTokCTR,
    TikTokThumbstopRate,
    TikTokROAS,
    TikTokCAC,
  ];

  const twitterColumns = [
    TwitterClicks,
    TwitterAppClicks,
    TwitterLikes,
    TwitterFollows,
    TwitterRetweets,
    TwitterUnfollows,
    TwitterEngagements,
    TwitterReplies,
    TwitterUrlClicks,
  ];

  const fbOfflineAndMobileAppColumns = [
    MetaOfflineRevenue1DView,
    MetaOfflineRevenue1DClick,
    MetaOfflineRevenue7DClick,
    MetaOfflineRevenue7DClick1DView,

    MetaMobileAppRevenue1DView,
    MetaMobileAppRevenue1DClick,
    MetaMobileAppRevenue7DClick,
    MetaMobileAppRevenue7DClick1DView,

    MetaOfflinePurchases1DView,
    MetaOfflinePurchases1DClick,
    MetaOfflinePurchases7DClick,
    MetaOfflinePurchases7DClick1DView,

    MetaMobileAppPurchases1DView,
    MetaMobileAppPurchases1DClick,
    MetaMobileAppPurchases7DClick,
    MetaMobileAppPurchases7DClick1DView,
  ];

  const tradedeskColumns = [TradedeskImpressions];
  const subscriptionColumnTypes = ["", "ft", "recurring", "other"] as const;
  const subscriptionColumnFactories = [
    makeTransactionsOrderSubscription,
    makeRevenueOrderSubscription,
    makeCacOrderSubscriptions,
  ];
  const subscriptionColumns = (accountingMode: INBAccountingMode) => {
    const longAttributionWindow =
      timeGranularity !== "hourly" ? "infinity" : "1";
    const columns: SalesPageMetric[] = [];
    subscriptionColumnTypes.forEach((type) => {
      subscriptionColumnFactories.forEach((factory) => {
        if (accountingMode === "accrual" && aw !== longAttributionWindow) {
          columns.push(factory(am, aw, type, accountingMode));
        }
        columns.push(factory(am, longAttributionWindow, type, accountingMode));
      });
    });
    return columns;
  };

  const otherColumns = [
    SpendMetric,
    ImpressionsMetric,
    makeCtr(am),
    makeVisits(am),
    makeNewVisits(),
    makeNewVisitorPercentage(),
    makeEcpc(am),
    makeEcpnv(am),
    CPMMetric,
  ];

  let allColumns = otherColumns;

  /* Only show FB and Google platform metrics if:
     1. (Google only) enableGooglePlatformMetrics feature flag is on
     2. (TikTok only) enableTikTokPlatformMetrics feature flag is on
     3. (FB only) append additional columns if we are in daily mode */
  if (!user.featureFlags.disableFBPlatformPerformanceMetrics) {
    allColumns = allColumns.concat(fbColumns);
  }

  if (user.featureFlags.enableShopMetricColumns) {
    const longAttributionWindow =
      timeGranularity !== "hourly" ? "infinity" : "1";
    if (accountingMode === "accrual" && aw !== longAttributionWindow) {
      allColumns = allColumns.concat([
        ...makeShopTxnsMetrics(am, aw, "", accountingMode),
        ...makeShopRevenueMetrics(am, aw, "", accountingMode),
      ]);
    }

    allColumns = allColumns.concat([
      ...makeShopTxnsMetrics(am, longAttributionWindow, "", accountingMode),
      ...makeShopRevenueMetrics(am, longAttributionWindow, "", accountingMode),
    ]);
  }

  if (user.featureFlags.enableFBOfflineAndMobileAppColumns) {
    allColumns = allColumns.concat(fbOfflineAndMobileAppColumns);
  }

  if (
    timeGranularity !== "hourly" &&
    !user.featureFlags.disableFBPlatformVideoMetrics
  ) {
    allColumns = allColumns.concat(fbDailyOnlyColumns);
  }

  if (!user.featureFlags.disableFBCustomCalculationMetrics) {
    allColumns = allColumns.concat(fbCustomCalculationMetrics);
  }

  if (user.featureFlags.enableGooglePlatformMetrics) {
    allColumns = allColumns.concat(googleColumns);
  }

  if (user.featureFlags.enableTikTokPlatformMetrics) {
    allColumns = allColumns.concat(tiktokColumns);
  }
  if (user.featureFlags.enableTikTokGMVMetrics) {
    allColumns = allColumns.concat(tiktokGMVColumns);
  }

  if (user.featureFlags.showSubscriptionOrderTags) {
    allColumns = allColumns.concat(subscriptionColumns(accountingMode));
  }

  if (user.featureFlags.enablePinterestPlatformMetrics) {
    allColumns = allColumns.concat(pinterestColumns);
  }

  if (user.featureFlags.enableTwitterPlatformMetrics) {
    allColumns = allColumns.concat(twitterColumns);
  }

  if (user.featureFlags.enableTradeDeskPlatformMetrics) {
    allColumns = allColumns.concat(tradedeskColumns);
  }

  return allColumns.map((col) => {
    return {
      ...col,
      calculate: calculateWithAnon(col.calculate, doAnon),
    };
  });
}

export interface CustomGoalRow {
  id: string;
  displayName: string;
}

export function makeOverviewRows(
  am: string,
  aw: IMCAttributionWindowDays,
  includeV1Forecasts: boolean,
  accountingMode: INBAccountingMode,
  customGoalRows: CustomGoalRow[],
  timeGranularity: ITimeGranularity | ITimeGranularityV2,
  user: AbstractUser,
  doAnon = false,
): SalesPageMetric[] {
  const adjustedAttributionWindow =
    accountingMode === "cash" && timeGranularity !== "hourly" ? "infinity" : aw;

  const awDesc = makeAttributionWindowDescription(aw);
  const awDescInfinity = makeAttributionWindowDescription("infinity");

  const { revCols, roasCols, ecrCols, cacCols, txnCols, aovCols } =
    makeConversionCols(
      am,
      aw,
      accountingMode,
      includeV1Forecasts,
      "",
      timeGranularity,
      user.attributionMethodEnum,
    );

  const orderColumns = makeOrderCols(am, aw, accountingMode, timeGranularity);

  const fbColumns = [
    MetaTotalPurchases1DClick,
    MetaTotalPurchases1DView,
    MetaTotalPurchases7DClick,
    MetaTotalPurchases7DClick1DView,

    MetaTotalPurchases7DClick1DView,
    MetaShopPurchases1DClick,
    MetaShopPurchases7DClick,
    MetaShopPurchases7DClick1DView,

    MetaOffsitePixelPurchases1DClick,
    MetaOffsitePixelPurchases7DClick,
    MetaOffsitePixelPurchases7DClick1DView,
    MetaOffsitePixelPurchases1DView,

    MetaTotalRevenue1DView,
    MetaTotalRevenue1DClick,
    MetaTotalRevenue7DClick,
    MetaTotalRevenue7DClick1DView,

    MetaShopRevenue1DView,
    MetaShopRevenue1DClick,
    MetaShopRevenue7DClick,
    MetaShopRevenue7DClick1DView,

    MetaOffsitePixelRevenue1DView,
    MetaOffsitePixelRevenue1DClick,
    MetaOffsitePixelRevenue7DClick,
    MetaOffsitePixelRevenue7DClick1DView,

    MetaROAS1DView,
    MetaROAS1DClick,
    MetaROAS7DClick,
    MetaROAS7DClick1DView,

    MetaCAC1DView,
    MetaCAC1DClick,
    MetaCAC7DClick,
    MetaCAC7DClick1DView,

    MetaAOV1DView,
    MetaAOV1DClick,
    MetaAOV7DClick,
    MetaAOV7DClick1DView,

    MetaPostEngagement1DView,
    MetaPostEngagement1DClick,
    MetaPostEngagement7DClick,
    MetaPostEngagement7DClick1DView,
    MetaPostEngagementDefault,
    MetaPageEngagement1DView,
    MetaPageEngagement1DClick,
    MetaPageEngagement7DClick,
    MetaPageEngagement7DClick1DView,
    MetaPageEngagementDefault,
    MetaPageLikes1DView,
    MetaPageLikes1DClick,
    MetaPageLikes7DClick,
    MetaPageLikes7DClick1DView,
    MetaPageLikesDefault,

    // MetaCostPerVideoContinuous2SecWatchedActions,

    MetaCostPer3SecondVideoPlays,

    Meta3SVideoViewsDefault,
    Meta3SVideoViews1DClick,
    Meta3SVideoViews7DClick,
    Meta3SVideoViews7DClick1DView,

    MetaCostPerPageEngagement1DView,
    MetaCostPerPageEngagement1DClick,
    MetaCostPerPageEngagement7DClick1DView,
    MetaCostPerPageEngagement7DClick,
    MetaCostPerPageEngagementDefault,
    MetaCostPerPageLikes1DView,
    MetaCostPerPageLikes1DClick,
    MetaCostPerPageLikes7DClick,
    MetaCostPerPageLikes7DClick1DView,
    MetaCostPerPageLikesDefault,
    MetaCostPerPostEngagement1DClick,
    MetaCostPerPostEngagement1DView,
    MetaCostPerPostEngagement7DClick,
    MetaCostPerPostEngagement7DClick1DView,
    MetaCostPerPostEngagementDefault,
  ];

  const fbCustomCalculationMetrics = [
    MetaShopRevenuePlusNBAttributedRevenue1DClick(
      am,
      adjustedAttributionWindow,
    ),
    MetaShopRevenuePlusNBAttributedRevenue7DClick(
      am,
      adjustedAttributionWindow,
    ),
    MetaShopRevenuePlusNBAttributedRevenue7DClick1DView(
      am,
      adjustedAttributionWindow,
    ),
    MetaROASMetaShopRevenueIncluded1DClick(am, adjustedAttributionWindow),
    MetaROASMetaShopRevenueIncluded7DClick(am, adjustedAttributionWindow),
    MetaROASMetaShopRevenueIncluded7DClick1DView(am, adjustedAttributionWindow),
  ];

  const fbDailyOnlyColumns = [
    MetaThumbstopRateDefault,
    MetaHoldRate1DView,
    MetaHoldRate7DClick1DView,
    MetaVideoPlayActions1DView,
    MetaVideoPlayActions7DClick1DView,
    MetaCostPerView1DView,
    MetaCostPerView7DClick1DView,
    MetaVideoThruplayWatchedActions1DView,
    MetaVideoThruplayWatchedActions7DClick1DView,
    MetaThruplayRate1DView,
    MetaThruplayRate7DClick1DView,
    MetaCostPerThruplay1DView,
    MetaCostPerThruplay7DClick1DView,
    MetaVideoP25Watched,
    MetaVideoP50Watched,
    MetaVideoP75Watched,
    MetaVideoP95Watched,
    MetaVideoP100Watched,
    MetaLinkClicks,
    MetaOutboundClicks,
  ];

  const fbOfflineAndMobileAppColumns = [
    MetaOfflineRevenue1DClick,
    MetaOfflineRevenue7DClick,
    MetaOfflineRevenue7DClick1DView,
    MetaMobileAppRevenue1DClick,
    MetaMobileAppRevenue7DClick,
    MetaMobileAppRevenue7DClick1DView,
    MetaOfflinePurchases1DClick,
    MetaOfflinePurchases7DClick,
    MetaOfflinePurchases7DClick1DView,
    MetaMobileAppPurchases1DClick,
    MetaMobileAppPurchases7DClick,
    MetaMobileAppPurchases7DClick1DView,
  ];

  const snapchatColumns: Array<SalesPageMetric> = [
    SnapchatPaidImpressions,
    SnapchatEarnedImpressions,
    SnapchatTotalImpressions,
    SnapchatSwipes,
    SnapchatSwipesRate,
    SnapchatThumbstopRate,
    SnapchatProfileClicks,
    SnapchatVideoViews,
    SnapchatVideoViewsTimeBased,
    SnapchatVideoViews15S,
    SnapchatQuartile1,
    SnapchatQuartile2,
    SnapchatQuartile3,
    SnapchatViewCompletion,
    SnapchatAttachmentTotalViewTimeMillis,
    SnapchatRevenue,
    SnapchatTransactions,
    SnapchatROAS,
    SnapchatCAC,
  ];

  const googleColumns = [
    GoogleAllConversionEvents,
    GooglePurchases,
    GoogleAllConversionEventsRevenue,
    GooglePurchaseRevenue,
    GoogleVideoViews,
    GoogleViewThroughConversions,
    GoogleROAS,
    GoogleCAC,
    GoogleCostPerConversion,
    GoogleClicks,
    GoogleImpressions,
    GoogleAverageCPC,
  ];

  const tiktokColumns = [
    TikTokVideoViews,
    TikTokVideoViews2s,
    TikTokVideoViews6s,
    TikTokImpressions,
    TikTokClicks,
    TikTokLikes,
    TikTokCompletePayment,
    TikTokTotalCompletePayment,
    TikTokEngagements,
  ];

  const pinterestColumns = [
    PinterestCheckoutConversions,
    PinterestCheckoutOrderValue,
    PinterestAddToCartRate,
    PinterestPaidImpressions,
    PinterestPaidEngagements,
    PinterestPinClick,
    PinterestPaidSaveRate,
    PinterestPaidSaves,
    PinterestVideoViews,
    PinterestPaid3SecVideoViews,
    PinterestPaidVideoPlayedAt50,
    PinterestPaidVideoPlayedAt95,
    PinterestPaidVideoPlayedAt75,
    PinterestPaidVideoPlayedAt100,
    PinterestPaidVideoPlayedAt25,
    PinterestPaidVideoStarts,
    PinterestPaidIdeaPinProductTagVisit,
    PinterestROAS,
    PinterestCAC,
  ];

  const twitterColumns = [
    TwitterClicks,
    TwitterAppClicks,
    TwitterLikes,
    TwitterFollows,
    TwitterRetweets,
    TwitterUnfollows,
    TwitterEngagements,
    TwitterReplies,
    TwitterUrlClicks,
  ];

  const tradedeskColumns = [TradedeskImpressions];

  const otherColumns = [
    SpendMetric,
    ...revCols,
    ...roasCols,
    ImpressionsMetric,
    makeCtr(am),
    makeVisits(am),
    makeNewVisits(),
    makeEcpc(am),
    makeEcpnv(am),
    CPMMetric,
    ...ecrCols,
    ...cacCols,
    ...txnCols,
    ...aovCols,
    ...orderColumns,
  ];

  let allColumns = otherColumns;

  /* Only show FB and Google platform metrics iff:
     1. (Google only) enableGooglePlatformMetrics feature flag is on
     2. (TikTok only) enableTikTokPlatformMetrics feature flag is on
     3. (FB only) append additional columns if we are in daily mode */
  if (!user.featureFlags.disableFBPlatformPerformanceMetrics) {
    allColumns = allColumns.concat(fbColumns);
  }

  if (user.featureFlags.enableFBOfflineAndMobileAppColumns) {
    allColumns = allColumns.concat(fbOfflineAndMobileAppColumns);
  }

  if (user.featureFlags.enableSnapchatPlatformMetrics) {
    allColumns = allColumns.concat(snapchatColumns);
  }

  if (
    timeGranularity !== "hourly" &&
    !user.featureFlags.disableFBPlatformVideoMetrics
  ) {
    allColumns = allColumns.concat(fbDailyOnlyColumns);
  }

  if (!user.featureFlags.disableFBCustomCalculationMetrics) {
    allColumns = allColumns.concat(fbCustomCalculationMetrics);
  }

  if (user.featureFlags.enableGooglePlatformMetrics) {
    allColumns = allColumns.concat(googleColumns);
  }

  if (user.featureFlags.enableTikTokPlatformMetrics) {
    allColumns = allColumns.concat(tiktokColumns);
  }
  if (user.featureFlags.enableTikTokGMVMetrics) {
    allColumns = allColumns.concat(tiktokGMVColumns);
  }
  if (user.featureFlags.enablePinterestPlatformMetrics) {
    allColumns = allColumns.concat(pinterestColumns);
  }

  if (user.featureFlags.enableTwitterPlatformMetrics) {
    allColumns = allColumns.concat(twitterColumns);
  }

  if (user.featureFlags.enableTradedeskPlatformMetrics) {
    allColumns = allColumns.concat(tradedeskColumns);
  }

  if (accountingMode === "accrual") {
    for (const { id, displayName } of customGoalRows) {
      allColumns.push(
        makeCustomGoal(
          am,
          aw,
          awDesc,
          accountingMode,
          id,
          displayName,
          user.attributionMethodEnum,
        ),
      );

      if (timeGranularity !== "hourly") {
        allColumns.push(
          makeCustomGoal(
            am,
            "infinity",
            awDescInfinity,
            accountingMode,
            id,
            displayName,
            user.attributionMethodEnum,
          ),
        );
      }

      allColumns.push(
        makeCustomGoalRate(
          am,
          aw,
          awDesc,
          accountingMode,
          id,
          displayName,
          user.attributionMethodEnum,
        ),
      );
      if (timeGranularity !== "hourly") {
        allColumns.push(
          makeCustomGoalRate(
            am,
            "infinity",
            awDescInfinity,
            accountingMode,
            id,
            displayName,
            user.attributionMethodEnum,
          ),
        );
      }
      allColumns.push(
        makeCustomGoalCost(
          am,
          aw,
          awDesc,
          accountingMode,
          id,
          displayName,
          user.attributionMethodEnum,
        ),
      );
      if (timeGranularity !== "hourly") {
        allColumns.push(
          makeCustomGoalCost(
            am,
            "infinity",
            awDescInfinity,
            accountingMode,
            id,
            displayName,
            user.attributionMethodEnum,
          ),
        );
      }
    }
  } else {
    for (const { id, displayName } of customGoalRows) {
      allColumns.push(
        makeCustomGoal(
          am,
          timeGranularity === "hourly" ? aw : "infinity",
          timeGranularity === "hourly" ? awDesc : awDescInfinity,
          accountingMode,
          id,
          displayName,
          user.attributionMethodEnum,
        ),
        makeCustomGoalRate(
          am,
          timeGranularity === "hourly" ? aw : "infinity",
          timeGranularity === "hourly" ? awDesc : awDescInfinity,
          accountingMode,
          id,
          displayName,
          user.attributionMethodEnum,
        ),
        makeCustomGoalCost(
          am,
          timeGranularity === "hourly" ? aw : "infinity",
          timeGranularity === "hourly" ? awDesc : awDescInfinity,
          accountingMode,
          id,
          displayName,
          user.attributionMethodEnum,
        ),
      );
    }
  }

  return allColumns.map((col) => {
    return {
      ...col,
      calculate: calculateWithAnon(col.calculate, doAnon),
    };
  });
}

function makeConversionCols(
  am: Exclude<string, "platform">,
  aw: IMCAttributionWindowDays,
  accountingMode: INBAccountingMode,
  includeV1Forecasts: boolean,
  type: "ft" | "rtn" | "",
  timeGranularity: ITimeGranularity | ITimeGranularityV2,
  attributionMethodEnum: any,
) {
  const longAttributionWindow = timeGranularity !== "hourly" ? "infinity" : "1";
  const awDesc = makeAttributionWindowDescription(aw);
  const awDescLong = makeAttributionWindowDescription(longAttributionWindow);

  const revCols = [
    makeRev(
      am,
      longAttributionWindow,
      awDescLong,
      type,
      accountingMode,
      attributionMethodEnum,
    ),
  ];
  const roasCols = [
    makeRoas(am, longAttributionWindow, awDescLong, type, accountingMode),
  ];

  const aovCols = [
    makeAOV(am, longAttributionWindow, awDescLong, type, accountingMode),
  ];
  const ecrCols = [
    makeEcr(am, longAttributionWindow, awDescLong, type, accountingMode),
  ];
  const cacCols = [
    makeCac(am, longAttributionWindow, awDescLong, type, accountingMode),
  ];
  const txnCols = [
    makeTxns(
      am,
      longAttributionWindow,
      awDescLong,
      type,
      accountingMode,
      attributionMethodEnum,
    ),
  ];

  if (accountingMode === "accrual" && aw !== longAttributionWindow) {
    revCols.unshift(
      makeRev(am, aw, awDesc, type, accountingMode, attributionMethodEnum),
    );
    roasCols.unshift(makeRoas(am, aw, awDesc, type, accountingMode));
    ecrCols.unshift(makeEcr(am, aw, awDesc, type, accountingMode));
    cacCols.unshift(makeCac(am, aw, awDesc, type, accountingMode));
    txnCols.unshift(
      makeTxns(am, aw, awDesc, type, accountingMode, attributionMethodEnum),
    );
    aovCols.unshift(makeAOV(am, aw, awDesc, type, accountingMode));
  }

  if (
    includeV1Forecasts &&
    accountingMode === "accrual" &&
    timeGranularity !== "hourly"
  ) {
    if (aw !== "30" && aw !== "60" && aw !== "90") {
      revCols.push(makeForecastedRev(am, "30", attributionMethodEnum));
      txnCols.push(makeForecastedTxns(am, "30", attributionMethodEnum));
      roasCols.push(makeForecastedRoas(am, "30"));
    }

    if (aw !== "60" && aw !== "90") {
      revCols.push(makeForecastedRev(am, "60", attributionMethodEnum));
      txnCols.push(makeForecastedTxns(am, "60", attributionMethodEnum));
      roasCols.push(makeForecastedRoas(am, "60"));
    }

    if (aw !== "90") {
      revCols.push(makeForecastedRev(am, "90", attributionMethodEnum));
      txnCols.push(makeForecastedTxns(am, "90", attributionMethodEnum));
      roasCols.push(makeForecastedRoas(am, "90"));
    }
  }

  return {
    revCols,
    roasCols,
    ecrCols,
    cacCols,
    txnCols,
    aovCols,
  };
}

function makeOrderCols(
  am: Exclude<string, "platform">,
  aw: IMCAttributionWindowDays,
  accountingMode: INBAccountingMode,
  timeGranularity: ITimeGranularity | ITimeGranularityV2,
) {
  const longAttributionWindow = timeGranularity !== "hourly" ? "infinity" : "1";
  const cols = [];

  if (accountingMode === "accrual" && aw !== longAttributionWindow) {
    cols.push(
      makeOrderDiscounts(am, aw, "", accountingMode),
      makeOrderDiscounts(am, aw, "ft", accountingMode),
      makeOrderDiscounts(am, aw, "rtn", accountingMode),
      makeTaxValue(am, aw, "", accountingMode),
      makeTaxValue(am, aw, "ft", accountingMode),
      makeTaxValue(am, aw, "rtn", accountingMode),
      makeShippingValue(am, aw, "", accountingMode),
      makeShippingValue(am, aw, "ft", accountingMode),
      makeShippingValue(am, aw, "rtn", accountingMode),
      makeOrderRefundedValue(am, aw, "", accountingMode),
      makeOrderRefundedValue(am, aw, "ft", accountingMode),
      makeOrderRefundedValue(am, aw, "rtn", accountingMode),
      makeOrderDiscountsOrderSubscriptions(am, aw, "", accountingMode),
      makeOrderDiscountsOrderSubscriptions(am, aw, "ft", accountingMode),
      makeOrderDiscountsOrderSubscriptions(am, aw, "recurring", accountingMode),
      makeOrderDiscountsOrderSubscriptions(am, aw, "other", accountingMode),
      makeTaxValueOrderSubscriptions(am, aw, "", accountingMode),
      makeTaxValueOrderSubscriptions(am, aw, "ft", accountingMode),
      makeTaxValueOrderSubscriptions(am, aw, "recurring", accountingMode),
      makeTaxValueOrderSubscriptions(am, aw, "other", accountingMode),
      makeShippingValueOrderSubscriptions(am, aw, "", accountingMode),
      makeShippingValueOrderSubscriptions(am, aw, "ft", accountingMode),
      makeShippingValueOrderSubscriptions(am, aw, "recurring", accountingMode),
      makeShippingValueOrderSubscriptions(am, aw, "other", accountingMode),
      makeOrderRefundedValueOrderSubscriptions(am, aw, "", accountingMode),
      makeOrderRefundedValueOrderSubscriptions(am, aw, "ft", accountingMode),
      makeOrderRefundedValueOrderSubscriptions(
        am,
        aw,
        "recurring",
        accountingMode,
      ),
      makeOrderRefundedValueOrderSubscriptions(am, aw, "other", accountingMode),
    );
  }

  cols.push(
    makeOrderDiscounts(am, longAttributionWindow, "", accountingMode),
    makeOrderDiscounts(am, longAttributionWindow, "ft", accountingMode),
    makeOrderDiscounts(am, longAttributionWindow, "rtn", accountingMode),
    makeTaxValue(am, longAttributionWindow, "", accountingMode),
    makeTaxValue(am, longAttributionWindow, "ft", accountingMode),
    makeTaxValue(am, longAttributionWindow, "rtn", accountingMode),
    makeShippingValue(am, longAttributionWindow, "", accountingMode),
    makeShippingValue(am, longAttributionWindow, "ft", accountingMode),
    makeShippingValue(am, longAttributionWindow, "rtn", accountingMode),
    makeOrderRefundedValue(am, longAttributionWindow, "", accountingMode),
    makeOrderRefundedValue(am, longAttributionWindow, "ft", accountingMode),
    makeOrderRefundedValue(am, longAttributionWindow, "rtn", accountingMode),
    makeOrderDiscountsOrderSubscriptions(
      am,
      longAttributionWindow,
      "",
      accountingMode,
    ),
    makeOrderDiscountsOrderSubscriptions(
      am,
      longAttributionWindow,
      "ft",
      accountingMode,
    ),
    makeOrderDiscountsOrderSubscriptions(
      am,
      longAttributionWindow,
      "recurring",
      accountingMode,
    ),
    makeOrderDiscountsOrderSubscriptions(
      am,
      longAttributionWindow,
      "other",
      accountingMode,
    ),
    makeTaxValueOrderSubscriptions(
      am,
      longAttributionWindow,
      "",
      accountingMode,
    ),
    makeTaxValueOrderSubscriptions(
      am,
      longAttributionWindow,
      "ft",
      accountingMode,
    ),
    makeTaxValueOrderSubscriptions(
      am,
      longAttributionWindow,
      "recurring",
      accountingMode,
    ),
    makeTaxValueOrderSubscriptions(
      am,
      longAttributionWindow,
      "other",
      accountingMode,
    ),
    makeShippingValueOrderSubscriptions(
      am,
      longAttributionWindow,
      "",
      accountingMode,
    ),
    makeShippingValueOrderSubscriptions(
      am,
      longAttributionWindow,
      "ft",
      accountingMode,
    ),
    makeShippingValueOrderSubscriptions(
      am,
      longAttributionWindow,
      "recurring",
      accountingMode,
    ),
    makeShippingValueOrderSubscriptions(
      am,
      longAttributionWindow,
      "other",
      accountingMode,
    ),
    makeOrderRefundedValueOrderSubscriptions(
      am,
      longAttributionWindow,
      "",
      accountingMode,
    ),
    makeOrderRefundedValueOrderSubscriptions(
      am,
      longAttributionWindow,
      "ft",
      accountingMode,
    ),
    makeOrderRefundedValueOrderSubscriptions(
      am,
      longAttributionWindow,
      "recurring",
      accountingMode,
    ),
    makeOrderRefundedValueOrderSubscriptions(
      am,
      longAttributionWindow,
      "other",
      accountingMode,
    ),
  );

  return cols;
}

export function makeFirstTimeVsReturningRows(
  am: Exclude<string, "platform">,
  aw: IMCAttributionWindowDays,
  accountingMode: INBAccountingMode,
  timeGranularity: ITimeGranularity | ITimeGranularityV2,
  attributionMethodEnum: any,
  doAnon = false,
) {
  const rv = [SpendMetric];
  let { revCols, roasCols, ecrCols, cacCols, txnCols, aovCols } =
    makeConversionCols(
      am,
      aw,
      accountingMode,
      false,
      "ft",
      timeGranularity,
      attributionMethodEnum,
    );

  rv.push(
    ...revCols,
    ...roasCols,
    ...ecrCols,
    ...cacCols,
    ...txnCols,
    ...aovCols,
  );

  ({ revCols, roasCols, ecrCols, cacCols, txnCols, aovCols } =
    makeConversionCols(
      am,
      aw,
      accountingMode,
      false,
      "rtn",
      timeGranularity,
      attributionMethodEnum,
    ));

  rv.push(
    ...revCols,
    ...roasCols,
    ...ecrCols,
    ...cacCols,
    ...txnCols,
    ...aovCols,
  );

  return rv.map((col) => {
    return {
      ...col,
      calculate: calculateWithAnon(col.calculate, doAnon),
    };
  });
}

export function makeBenchmarkRows(
  am: Exclude<string, "platform">,
  aw: IMCAttributionWindowDays,
  doAnon = false,
) {
  const rv = [
    makeBenchmarkCAC(am, aw, ""),
    makeBenchmarkCAC(am, aw, "ft"),
    makeBenchmarkCAC(am, aw, "rtn"),
    makeBenchmarkROAS(am, aw, ""),
    makeBenchmarkROAS(am, aw, "ft"),
    makeBenchmarkROAS(am, aw, "rtn"),
  ];

  return rv.map((col) => {
    return {
      ...col,
      calculate: calculateWithAnon(col.calculate, doAnon),
    };
  });
}

export function makeForecastRows(attributionMethodEnum: any, doAnon = false) {
  const rv = [
    makeForecastedRev("northbeam_custom", "30", attributionMethodEnum),
    makeForecastedRev("northbeam_custom", "60", attributionMethodEnum),
    makeForecastedRev("northbeam_custom", "90", attributionMethodEnum),

    makeForecastedTxns("northbeam_custom", "30", attributionMethodEnum),
    makeForecastedTxns("northbeam_custom", "60", attributionMethodEnum),
    makeForecastedTxns("northbeam_custom", "90", attributionMethodEnum),

    makeForecastedRoas("northbeam_custom", "30"),
    makeForecastedRoas("northbeam_custom", "60"),
    makeForecastedRoas("northbeam_custom", "90"),
  ];

  return rv.map((col) => {
    return {
      ...col,
      calculate: calculateWithAnon(col.calculate, doAnon),
    };
  });
}

export function makeFlyoutPerformanceRows(
  am: string,
  aw: IMCAttributionWindowDays,
  accountingMode: INBAccountingMode,
  timeGranularity: ITimeGranularity | ITimeGranularityV2,
  user: AbstractUser,
): SalesPageMetric[] {
  const longAttributionWindow = timeGranularity !== "hourly" ? "infinity" : "1";
  const awDesc = makeAttributionWindowDescription(aw);
  const awDescLong = makeAttributionWindowDescription(longAttributionWindow);

  if (accountingMode === "accrual" && aw !== longAttributionWindow) {
    return [
      SpendMetric,
      makeRev(am, aw, awDesc, "", accountingMode, user.attributionMethodEnum),
      makeRoas(am, aw, awDesc, "", accountingMode),
    ];
  } else {
    return [
      SpendMetric,
      makeRev(
        am,
        longAttributionWindow,
        awDescLong,
        "",
        accountingMode,
        user.attributionMethodEnum,
      ),
      makeRoas(am, longAttributionWindow, awDescLong, "", accountingMode),
    ];
  }
}

export function makeLandingPageRows(
  am: Exclude<string, "platform">,
  aw: IMCAttributionWindowDays,
  accountingMode: INBAccountingMode,
  attributionMethodEnum: any,
): SalesPageMetric[] {
  if (accountingMode === "accrual") {
    const awDesc = makeAttributionWindowDescription(aw);

    return [
      makeRev(am, aw, awDesc, "", accountingMode, attributionMethodEnum),
      makeRev(am, aw, awDesc, "ft", accountingMode, attributionMethodEnum),
      makeRev(am, aw, awDesc, "rtn", accountingMode, attributionMethodEnum),
      makeTxns(am, aw, awDesc, "", accountingMode, attributionMethodEnum),
      makeTxns(am, aw, awDesc, "ft", accountingMode, attributionMethodEnum),
      makeTxns(am, aw, awDesc, "rtn", accountingMode, attributionMethodEnum),
      makeVisits(am),
      makeNewVisits(),
      makeEcr(am, aw, awDesc, "", accountingMode),
      makeEcr(am, aw, awDesc, "ft", accountingMode),
      makeEcr(am, aw, awDesc, "rtn", accountingMode),
      makeRpv(am, aw, awDesc, "", accountingMode),
      makeRpv(am, aw, awDesc, "ft", accountingMode),
      makeRpv(am, aw, awDesc, "rtn", accountingMode),
    ];
  } else {
    const eaw = "infinity";
    const awDesc = makeAttributionWindowDescription(eaw);

    return [
      makeRev(am, eaw, awDesc, "", accountingMode, attributionMethodEnum),
      makeRev(am, eaw, awDesc, "ft", accountingMode, attributionMethodEnum),
      makeRev(am, eaw, awDesc, "rtn", accountingMode, attributionMethodEnum),
      makeTxns(am, eaw, awDesc, "", accountingMode, attributionMethodEnum),
      makeTxns(am, eaw, awDesc, "ft", accountingMode, attributionMethodEnum),
      makeTxns(am, eaw, awDesc, "rtn", accountingMode, attributionMethodEnum),
      makeVisits(am),
      makeNewVisits(),
      makeEcr(am, eaw, awDesc, "", accountingMode),
      makeEcr(am, eaw, awDesc, "ft", accountingMode),
      makeEcr(am, eaw, awDesc, "rtn", accountingMode),
      makeRpv(am, eaw, awDesc, "", accountingMode),
      makeRpv(am, eaw, awDesc, "ft", accountingMode),
      makeRpv(am, eaw, awDesc, "rtn", accountingMode),
    ];
  }
}

export function makeUniversalBenchmarksBaselineDaysMetrics(
  am: string,
  aw: IMCAttributionWindowDays,
  accountingMode: INBAccountingMode,
  user: AbstractUser,
) {
  const awDesc = makeAttributionWindowDescription(aw);

  const rev = makeRev(
    am,
    aw,
    awDesc,
    "",
    accountingMode,
    user.attributionMethodEnum,
  );

  const ftRev = makeRev(
    am,
    aw,
    awDesc,
    "ft",
    accountingMode,
    user.attributionMethodEnum,
  );

  const rtnRev = makeRev(
    am,
    aw,
    awDesc,
    "rtn",
    accountingMode,
    user.attributionMethodEnum,
  );

  const txns = makeTxns(
    am,
    aw,
    awDesc,
    "",
    accountingMode,
    user.attributionMethodEnum,
  );

  const ftTxns = makeTxns(
    am,
    aw,
    awDesc,
    "ft",
    accountingMode,
    user.attributionMethodEnum,
  );

  const rtnTxns = makeTxns(
    am,
    aw,
    awDesc,
    "rtn",
    accountingMode,
    user.attributionMethodEnum,
  );

  const shippingValue = makeShippingValue(am, aw, "", accountingMode);
  const ftShippingValue = makeShippingValue(am, aw, "ft", accountingMode);
  const rtnShippingValue = makeShippingValue(am, aw, "rtn", accountingMode);
  const taxValue = makeTaxValue(am, aw, "", accountingMode);
  const ftTaxValue = makeTaxValue(am, aw, "ft", accountingMode);
  const rtnTaxValue = makeTaxValue(am, aw, "rtn", accountingMode);
  const orderRefundedValue = makeOrderRefundedValue(am, aw, "", accountingMode);
  const ftOrderRefundedValue = makeOrderRefundedValue(
    am,
    aw,
    "ft",
    accountingMode,
  );
  const rtnOrderRefundedValue = makeOrderRefundedValue(
    am,
    aw,
    "rtn",
    accountingMode,
  );
  const orderDiscounts = makeOrderDiscounts(am, aw, "", accountingMode);
  const ftOrderDiscounts = makeOrderDiscounts(am, aw, "ft", accountingMode);
  const rtnOrderDiscounts = makeOrderDiscounts(am, aw, "ft", accountingMode);

  const aov = makeAOV(am, aw, awDesc, "", accountingMode);
  const ftAov = makeAOV(am, aw, awDesc, "ft", accountingMode);
  const rtnAov = makeAOV(am, aw, awDesc, "ft", accountingMode);

  return {
    spend: SpendMetric,
    rev,
    ftRev,
    rtnRev,
    txns,
    ftTxns,
    rtnTxns,
    shippingValue,
    ftShippingValue,
    rtnShippingValue,
    taxValue,
    ftTaxValue,
    rtnTaxValue,
    orderRefundedValue,
    ftOrderRefundedValue,
    rtnOrderRefundedValue,
    orderDiscounts,
    ftOrderDiscounts,
    rtnOrderDiscounts,
    aov,
    ftAov,
    rtnAov,
  } as const;
}

export function makeUniversalBenchmarksKpiRows(
  am: string,
  aw: IMCAttributionWindowDays,
  accountingMode: INBAccountingMode,
  customGoalRows: CustomGoalRow[],
  user: AbstractUser,
) {
  const awDesc = makeAttributionWindowDescription(aw);

  const fbColumns = [
    MetaTotalPurchases1DClick,
    MetaTotalPurchases1DView,
    MetaTotalPurchases7DClick,
    MetaTotalPurchases7DClick1DView,

    MetaShopPurchases1DView,
    MetaShopPurchases1DClick,
    MetaShopPurchases7DClick,
    MetaShopPurchases7DClick1DView,

    MetaOffsitePixelPurchases1DClick,
    MetaOffsitePixelPurchases7DClick,
    MetaOffsitePixelPurchases7DClick1DView,
    MetaOffsitePixelPurchases1DView,

    MetaTotalRevenue1DView,
    MetaTotalRevenue1DClick,
    MetaTotalRevenue7DClick,
    MetaTotalRevenue7DClick1DView,

    MetaShopRevenue1DView,
    MetaShopRevenue1DClick,
    MetaShopRevenue7DClick,
    MetaShopRevenue7DClick1DView,

    MetaOffsitePixelRevenue1DView,
    MetaOffsitePixelRevenue1DClick,
    MetaOffsitePixelRevenue7DClick,
    MetaOffsitePixelRevenue7DClick1DView,

    MetaROAS1DView,
    MetaROAS1DClick,
    MetaROAS7DClick,
    MetaROAS7DClick1DView,

    MetaCAC1DView,
    MetaCAC1DClick,
    MetaCAC7DClick,
    MetaCAC7DClick1DView,

    MetaAOV1DView,
    MetaAOV1DClick,
    MetaAOV7DClick,
    MetaAOV7DClick1DView,

    MetaPostEngagement1DView,
    MetaPostEngagement1DClick,
    MetaPostEngagement7DClick,
    MetaPostEngagement7DClick1DView,
    MetaPostEngagementDefault,
    MetaPageEngagement1DView,
    MetaPageEngagement1DClick,
    MetaPageEngagement7DClick,
    MetaPageEngagement7DClick1DView,
    MetaPageEngagementDefault,
    MetaPageLikes1DView,
    MetaPageLikes1DClick,
    MetaPageLikes7DClick,
    MetaPageLikes7DClick1DView,
    MetaPageLikesDefault,

    // MetaCostPerVideoContinuous2SecWatchedActions,

    MetaCostPer3SecondVideoPlays,

    Meta3SVideoViewsDefault,
    Meta3SVideoViews1DClick,
    Meta3SVideoViews7DClick,
    Meta3SVideoViews7DClick1DView,

    MetaCostPerPageEngagement1DView,
    MetaCostPerPageEngagement1DClick,
    MetaCostPerPageEngagement7DClick1DView,
    MetaCostPerPageEngagement7DClick,
    MetaCostPerPageEngagementDefault,
    MetaCostPerPageLikes1DView,
    MetaCostPerPageLikes1DClick,
    MetaCostPerPageLikes7DClick,
    MetaCostPerPageLikes7DClick1DView,
    MetaCostPerPageLikesDefault,
    MetaCostPerPostEngagement1DClick,
    MetaCostPerPostEngagement1DView,
    MetaCostPerPostEngagement7DClick,
    MetaCostPerPostEngagement7DClick1DView,
    MetaCostPerPostEngagementDefault,
  ];

  const fbCustomCalculationMetrics = [
    MetaShopRevenuePlusNBAttributedRevenue1DView(am, aw),
    MetaShopRevenuePlusNBAttributedRevenue1DClick(am, aw),
    MetaShopRevenuePlusNBAttributedRevenue7DClick(am, aw),
    MetaShopRevenuePlusNBAttributedRevenue7DClick1DView(am, aw),
    MetaROASMetaShopRevenueIncluded1DView(am, aw),
    MetaROASMetaShopRevenueIncluded1DClick(am, aw),
    MetaROASMetaShopRevenueIncluded7DClick(am, aw),
    MetaROASMetaShopRevenueIncluded7DClick1DView(am, aw),
    MetaShopTransactionsPlusNBTransactions1DView(am, aw),
    MetaShopTransactionsPlusNBTransactions1DClick(am, aw),
    MetaShopTransactionsPlusNBTransactions7DClick(am, aw),
    MetaShopTransactionsPlusNBTransactions7DClick1DView(am, aw),
    MetaCacWithMetaShopTransactionsIncluded1DView(am, aw),
    MetaCacWithMetaShopTransactionsIncluded1DClick(am, aw),
    MetaCacWithMetaShopTransactionsIncluded7DClick(am, aw),
    MetaCacWithMetaShopTransactionsIncluded7DClick1DView(am, aw),
  ];

  const fbDailyOnlyColumns = [
    MetaThumbstopRateDefault,
    MetaHoldRate1DView,
    MetaHoldRate7DClick1DView,
    MetaVideoPlayActions1DView,
    MetaVideoPlayActions7DClick1DView,
    MetaCostPerView1DView,
    MetaCostPerView7DClick1DView,
    MetaVideoThruplayWatchedActions1DView,
    MetaVideoThruplayWatchedActions7DClick1DView,
    MetaThruplayRate1DView,
    MetaThruplayRate7DClick1DView,
    MetaCostPerThruplay1DView,
    MetaCostPerThruplay7DClick1DView,
    MetaVideoP25Watched,
    MetaVideoP50Watched,
    MetaVideoP75Watched,
    MetaVideoP95Watched,
    MetaVideoP100Watched,
    MetaLinkClicks,
    MetaOutboundClicks,
  ];

  const fbOfflineAndMobileAppColumns = [
    MetaOfflineRevenue1DView,
    MetaOfflineRevenue1DClick,
    MetaOfflineRevenue7DClick,
    MetaOfflineRevenue7DClick1DView,
    MetaMobileAppRevenue1DView,
    MetaMobileAppRevenue1DClick,
    MetaMobileAppRevenue7DClick,
    MetaMobileAppRevenue7DClick1DView,
    MetaOfflinePurchases1DView,
    MetaOfflinePurchases1DClick,
    MetaOfflinePurchases7DClick,
    MetaOfflinePurchases7DClick1DView,
    MetaMobileAppPurchases1DView,
    MetaMobileAppPurchases1DClick,
    MetaMobileAppPurchases7DClick,
    MetaMobileAppPurchases7DClick1DView,
  ];

  const snapchatColumns = [
    SnapchatPaidImpressions,
    SnapchatEarnedImpressions,
    SnapchatTotalImpressions,
    SnapchatSwipes,
    SnapchatSwipesRate,
    SnapchatThumbstopRate,
    SnapchatProfileClicks,
    SnapchatVideoViews,
    SnapchatVideoViewsTimeBased,
    SnapchatVideoViews15S,
    SnapchatQuartile1,
    SnapchatQuartile2,
    SnapchatQuartile3,
    SnapchatViewCompletion,
    SnapchatAttachmentTotalViewTimeMillis,
    SnapchatRevenue,
    SnapchatTransactions,
    SnapchatROAS,
    SnapchatCAC,
  ];

  const googleColumns = [
    GoogleAllConversionEvents,
    GooglePurchases,
    GoogleAllConversionEventsRevenue,
    GooglePurchaseRevenue,
    GoogleVideoViews,
    GoogleViewThroughConversions,
    GoogleROAS,
    GoogleCAC,
    GoogleCostPerConversion,
    GoogleClicks,
    GoogleImpressions,
    GoogleAverageCPC,
  ];

  const tiktokColumns = [
    TikTokVideoViewsP100,
    TikTokVideoViewsP50,
    TikTokVideoViewsP75,
    TikTokVideoViewsP25,
    TikTokVideoViews,
    TikTokVideoViews2s,
    TikTokVideoViews6s,
    TikTokImpressions,
    TikTokClicks,
    TikTokLikes,
    TikTokCompletePayment,
    TikTokTotalCompletePayment,
    TikTokEngagements,
    TikTokCPC,
    TikTokCPM,
    TikTokCTR,
    TikTokThumbstopRate,
    TikTokROAS,
    TikTokCAC,
  ];

  const pinterestColumns = [
    PinterestCheckoutConversions,
    PinterestCheckoutOrderValue,
    PinterestAddToCartRate,
    PinterestPaidImpressions,
    PinterestPaidEngagements,
    PinterestPinClick,
    PinterestPaidSaveRate,
    PinterestPaidSaves,
    PinterestVideoViews,
    PinterestPaid3SecVideoViews,
    PinterestPaidVideoPlayedAt50,
    PinterestPaidVideoPlayedAt95,
    PinterestPaidVideoPlayedAt75,
    PinterestPaidVideoPlayedAt100,
    PinterestPaidVideoPlayedAt25,
    PinterestPaidVideoStarts,
    PinterestPaidIdeaPinProductTagVisit,
    PinterestROAS,
    PinterestCAC,
  ];

  const twitterColumns = [
    TwitterClicks,
    TwitterAppClicks,
    TwitterLikes,
    TwitterFollows,
    TwitterRetweets,
    TwitterUnfollows,
    TwitterEngagements,
    TwitterReplies,
    TwitterUrlClicks,
  ];

  const tradeDeskColumns = [TradedeskImpressions];

  const otherColumns = [
    makeRev(am, aw, awDesc, "", accountingMode, user.attributionMethodEnum),
    makeRoas(am, aw, awDesc, "", accountingMode),
    makeAOV(am, aw, awDesc, "", accountingMode),
    makeEcr(am, aw, awDesc, "", accountingMode),
    makeCac(am, aw, awDesc, "", accountingMode),
    makeTxns(am, aw, awDesc, "", accountingMode, user.attributionMethodEnum),
    makeRev(am, aw, awDesc, "ft", accountingMode, user.attributionMethodEnum),
    makeRoas(am, aw, awDesc, "ft", accountingMode),
    makeAOV(am, aw, awDesc, "ft", accountingMode),
    makeEcr(am, aw, awDesc, "ft", accountingMode),
    makeCac(am, aw, awDesc, "ft", accountingMode),
    makeTxns(am, aw, awDesc, "ft", accountingMode, user.attributionMethodEnum),
    makeRev(am, aw, awDesc, "rtn", accountingMode, user.attributionMethodEnum),
    makeRoas(am, aw, awDesc, "rtn", accountingMode),
    makeAOV(am, aw, awDesc, "rtn", accountingMode),
    makeEcr(am, aw, awDesc, "rtn", accountingMode),
    makeCac(am, aw, awDesc, "rtn", accountingMode),
    makeTxns(am, aw, awDesc, "rtn", accountingMode, user.attributionMethodEnum),
    SpendMetric,
    ImpressionsMetric,
    makeCtr(am),
    makeVisits(am),
    makeNewVisits(),
    makeNewVisitorPercentage(),
    makeEcpc(am),
    makeEcpnv(am),
    CPMMetric,
    makeOrderDiscounts(am, aw, "", accountingMode),
    makeOrderDiscounts(am, aw, "ft", accountingMode),
    makeOrderDiscounts(am, aw, "rtn", accountingMode),
    makeTaxValue(am, aw, "", accountingMode),
    makeTaxValue(am, aw, "ft", accountingMode),
    makeTaxValue(am, aw, "rtn", accountingMode),
    makeShippingValue(am, aw, "", accountingMode),
    makeShippingValue(am, aw, "ft", accountingMode),
    makeShippingValue(am, aw, "rtn", accountingMode),
    makeOrderRefundedValue(am, aw, "", accountingMode),
    makeOrderRefundedValue(am, aw, "ft", accountingMode),
    makeOrderRefundedValue(am, aw, "rtn", accountingMode),
    makeOrderDiscountsOrderSubscriptions(am, aw, "", accountingMode),
    makeOrderDiscountsOrderSubscriptions(am, aw, "ft", accountingMode),
    makeOrderDiscountsOrderSubscriptions(am, aw, "recurring", accountingMode),
    makeOrderDiscountsOrderSubscriptions(am, aw, "other", accountingMode),
    makeTaxValueOrderSubscriptions(am, aw, "", accountingMode),
    makeTaxValueOrderSubscriptions(am, aw, "ft", accountingMode),
    makeTaxValueOrderSubscriptions(am, aw, "recurring", accountingMode),
    makeTaxValueOrderSubscriptions(am, aw, "other", accountingMode),
    makeShippingValueOrderSubscriptions(am, aw, "", accountingMode),
    makeShippingValueOrderSubscriptions(am, aw, "ft", accountingMode),
    makeShippingValueOrderSubscriptions(am, aw, "recurring", accountingMode),
    makeShippingValueOrderSubscriptions(am, aw, "other", accountingMode),
    makeOrderRefundedValueOrderSubscriptions(am, aw, "", accountingMode),
    makeOrderRefundedValueOrderSubscriptions(am, aw, "ft", accountingMode),
    makeOrderRefundedValueOrderSubscriptions(
      am,
      aw,
      "recurring",
      accountingMode,
    ),
    makeOrderRefundedValueOrderSubscriptions(am, aw, "other", accountingMode),
    makeBenchmarkCAC(am, aw, ""),
    makeBenchmarkCAC(am, aw, "ft"),
    makeBenchmarkCAC(am, aw, "rtn"),
    makeBenchmarkROAS(am, aw, ""),
    makeBenchmarkROAS(am, aw, "ft"),
    makeBenchmarkROAS(am, aw, "rtn"),
  ];

  const subscriptionColumns = [
    makeTransactionsOrderSubscription(am, aw, "", accountingMode),
    makeTransactionsOrderSubscription(am, aw, "ft", accountingMode),
    makeTransactionsOrderSubscription(am, aw, "recurring", accountingMode),
    makeTransactionsOrderSubscription(am, aw, "other", accountingMode),
    makeRevenueOrderSubscription(am, aw, "", accountingMode),
    makeRevenueOrderSubscription(am, aw, "ft", accountingMode),
    makeRevenueOrderSubscription(am, aw, "recurring", accountingMode),
    makeRevenueOrderSubscription(am, aw, "other", accountingMode),
    makeCacOrderSubscriptions(am, aw, "", accountingMode),
    makeCacOrderSubscriptions(am, aw, "ft", accountingMode),
    makeCacOrderSubscriptions(am, aw, "recurring", accountingMode),
    makeCacOrderSubscriptions(am, aw, "other", accountingMode),
  ];

  let allColumns = otherColumns;

  if (!user.featureFlags.disableFBPlatformPerformanceMetrics) {
    allColumns = allColumns.concat(fbColumns);
  }

  if (user.featureFlags.enableFBOfflineAndMobileAppColumns) {
    allColumns = allColumns.concat(fbOfflineAndMobileAppColumns);
  }

  if (user.featureFlags.enableSnapchatPlatformMetrics) {
    allColumns = allColumns.concat(snapchatColumns);
  }

  if (!user.featureFlags.disableFBPlatformVideoMetrics) {
    allColumns = allColumns.concat(fbDailyOnlyColumns);
  }

  if (!user.featureFlags.disableFBCustomCalculationMetrics) {
    allColumns = allColumns.concat(fbCustomCalculationMetrics);
  }

  if (user.featureFlags.enableGooglePlatformMetrics) {
    allColumns = allColumns.concat(googleColumns);
  }

  if (user.featureFlags.enableTikTokPlatformMetrics) {
    allColumns = allColumns.concat(tiktokColumns);
  }

  if (user.featureFlags.enableTikTokGMVMetrics) {
    allColumns = allColumns.concat(tiktokGMVColumns);
  }
  if (user.featureFlags.enablePinterestPlatformMetrics) {
    allColumns = allColumns.concat(pinterestColumns);
  }

  if (user.featureFlags.enableTwitterPlatformMetrics) {
    allColumns = allColumns.concat(twitterColumns);
  }

  if (user.featureFlags.enableTradeDeskPlatformMetrics) {
    allColumns = allColumns.concat(tradeDeskColumns);
  }

  if (user.featureFlags.showSubscriptionOrderTags) {
    allColumns = allColumns.concat(subscriptionColumns);
  }

  if (user.featureFlags.enableShopMetricColumns) {
    allColumns = allColumns.concat([
      ...makeShopTxnsMetrics(am, aw, "", accountingMode),
      ...makeShopRevenueMetrics(am, aw, "", accountingMode),
    ]);
  }

  for (const { id, displayName } of customGoalRows) {
    allColumns.push(
      makeCustomGoal(
        am,
        aw,
        awDesc,
        accountingMode,
        id,
        displayName,
        user.attributionMethodEnum,
      ),
    );

    allColumns.push(
      makeCustomGoalRate(
        am,
        aw,
        awDesc,
        accountingMode,
        id,
        displayName,
        user.attributionMethodEnum,
      ),
    );
    allColumns.push(
      makeCustomGoalCost(
        am,
        aw,
        awDesc,
        accountingMode,
        id,
        displayName,
        user.attributionMethodEnum,
      ),
    );
  }

  return allColumns;
}

export function makeUniversalBenchmarksAdoptableMetrics(
  am: string,
  aw: IMCAttributionWindowDays,
  accountingMode: INBAccountingMode,
  user: AbstractUser,
): {
  rev: SalesPageMetric;
  txns: SalesPageMetric;
  roas: SalesPageMetric;
  spend: SalesPageMetric;
  metrics: SalesPageMetric[];
} {
  const awDesc = makeAttributionWindowDescription(aw);

  const rev = makeRev(
    am,
    aw,
    awDesc,
    "",
    accountingMode,
    user.attributionMethodEnum,
  );

  const txns = makeTxns(
    am,
    aw,
    awDesc,
    "",
    accountingMode,
    user.attributionMethodEnum,
  );

  const roas = makeRoas(am, aw, awDesc, "", accountingMode);

  return {
    rev,
    txns,
    roas,
    spend: SpendMetric,
    metrics: [
      roas,
      makeRoas(am, aw, awDesc, "ft", accountingMode),
      makeRoas(am, aw, awDesc, "rtn", accountingMode),
      makeCac(am, aw, awDesc, "", accountingMode),
      makeCac(am, aw, awDesc, "ft", accountingMode),
      makeCac(am, aw, awDesc, "rtn", accountingMode),
      makeCtr(am),
      makeEcpc(am),
      CPMMetric,
      makeVisits(am),
      makeNewVisitorPercentage(),
      rev,
      makeRev(am, aw, awDesc, "ft", accountingMode, user.attributionMethodEnum),
      makeRev(
        am,
        aw,
        awDesc,
        "rtn",
        accountingMode,
        user.attributionMethodEnum,
      ),
      SpendMetric,
    ],
  };
}

export function pathToDescriptor(path: string[]): string {
  /* Adding root to the beginning allows prefix matches with [].
     Adding a newline at the end prevents spurious matches with value="". */
  return ["__root__", ...path, ""].join("\n");
}

export function descriptorToPath(descriptor: string): string[] {
  const path = descriptor.split("\n");
  path.pop();
  path.shift();
  return path;
}

export function getSalesPageMetricSpecs(
  user: AbstractUser,
  hasV1Forecasts: boolean,
  customGoals: { id: string; displayName: string }[],
  customMetrics: SalesPageMetric[],
) {
  const allSpecs: SalesPageMetricSpec[] = [];

  for (const attributionModel of user.attributionModels) {
    for (const attributionWindow of Object.keys(MCAttributionWindowDays.enum)) {
      for (const accountingMode of Object.keys(NBAccountingMode.enum)) {
        const ms = [
          ...makeOverviewRows(
            attributionModel.id,
            attributionWindow as IMCAttributionWindowDays,
            hasV1Forecasts,
            accountingMode as INBAccountingMode,
            customGoals,
            "daily",
            user,
          ),
          ...makeTrafficRows(
            attributionModel.id,
            attributionWindow as IMCAttributionWindowDays,
            accountingMode as INBAccountingMode,
            user,
            "daily",
          ),
          ...makeFirstTimeVsReturningRows(
            attributionModel.id,
            attributionWindow as IMCAttributionWindowDays,
            accountingMode as INBAccountingMode,
            "daily",
            user.attributionMethodEnum,
          ),
        ];

        for (const metric of ms) {
          if (!metric.spec) {
            continue;
          }
          allSpecs.push(metric.spec);
        }
      }
    }
  }

  customMetrics.forEach((metric) => {
    if (metric.spec) {
      allSpecs.push(metric.spec);
    }
  });

  return uniqBy(allSpecs, "id");
}

export function dimensionPlatformToPlatform(platform: string) {
  if (platform === "Facebook Ads") {
    return "fb";
  }
  if (platform === "TikTok") {
    return "tiktok";
  }
  if (platform === "Snapchat Ads") {
    return "snapchat";
  }
  if (platform === "Pinterest") {
    return "pinterest";
  }
  if (platform === "Google Ads") {
    return "adwords";
  }
  if (platform === "YouTube Ads") {
    return "youtube";
  }
  if (platform === "Twitter") {
    return "twitter";
  }
}
